import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Send, Mail, Paperclip, SwitchCamera, RotateCcw, ChevronLeft, ShieldCheck, CalendarRange, LocateFixed, NotebookPen, CircleAlert, Layers, List, EyeOff, Lock, Shield, Sparkles,
  Camera, MapPin, CheckCircle, LogOut, LogIn, User, Activity, Clock, Key, Star, Calendar, History, Trash2, Edit, CreditCard, PieChart, Building, FileText, AlertTriangle, X, File as FileIcon, Filter, CheckSquare, Users, Eye, ScanFace, Fingerprint, Smartphone, ChevronDown, ChevronRight, Search, MessageSquare, MessageSquareText, Upload, Check, Info, CalendarCheck, Printer, FileSpreadsheet, Loader2, CalendarDays, CloudSun, Sun, Moon, Cloud, CloudRain, CloudLightning, Snowflake, KeyRound, ScanLine, RefreshCcw, UserRoundPlus, UsersRound, SlidersHorizontal, Database, Megaphone, ClipboardList, HeartPulse, Timer, PlaneTakeoff, Palmtree, ArrowLeftRight, Coffee, ChartColumn, FileUp, Menu, ArrowUp, UserCheck } from 'lucide-react';
import { SCRIPT_URL, TIMEOUT_DURATION, TIMEOUT_LABEL, BOARD_ABSENSI_URL } from './config/constants';
import { loginLewatSupabase } from './utils/loginSupabase';
import LogLoginScreen from './screens/LogLoginScreen';
import { FRONTEND_VERSION } from './config/updateManifest';
import BackButton from './components/BackButton';
import RekapExcelScreen from './screens/RekapExcelScreen';
import GpsAuditScreen from './screens/GpsAuditScreen';
import GpsDashboardScreen from './screens/GpsDashboardScreen';
import DashboardKerani from './screens/DashboardKerani';
import GpsGateScreen from './screens/GpsGateScreen';
import ImportDbAbsen from './screens/ImportDbAbsen';
import MasterRolePanel from './screens/MasterRolePanel';
import MasterUserPanel from './screens/MasterUserPanel';
import MasterTimApproval from './screens/MasterTimApproval';
import BebasAbsenPanel from './screens/BebasAbsenPanel';
import { ImportJobProvider } from './context/ImportJobContext';
import { useHariLibur } from './utils/hariLibur';
import ImportNotifier from './components/ImportNotifier';
import ProcessingModal from './components/ProcessingModal';
import { useNotifKaryawan, NotifPengajuanCard, hitungLencana } from './components/NotifPengajuan';
import { getVerifiedGeolocation } from './utils/antiFakeGps';
import { mulaiPelacakGps, kirimTitikGps } from './utils/gpsTracker';
import { periksaGpsWajib, izinSudahDiberikan, tandaiIzinPernahOk, lupakanIzin, GPS_STATUS, adalahPcAtauLaptop } from './utils/gpsWajib';
import { startFaceLivenessTracker, periksaWajahPenuh, ambilDeskriptorWajah, deskriptorDariGambar } from './utils/faceLiveness';
import { muatPengenalWajah } from './utils/faceApiLoader';
import { bukaKameraDepan, hentikanStream } from './utils/kameraDepan';
import { ambilVersiFrontendServer, bandingkanVersi, pantauPembaruan } from './utils/pembaruan';
import { infoPerangkat } from './utils/perangkat';
import { batasPengajuanMundur, bacaPeriodeAktifTersimpan, pesanKunciPeriode, tampilYmd } from './utils/kunciPeriode';

// ============================================================
// HELPER API — token login + penanganan respons HTML dari Google
//
// 1) Menyisipkan token login ke setiap request. Backend (Apps Script)
//    menolak request tanpa token; token disimpan di dalam objek user
//    pada sessionStorage saat login.
//
// 2) Apps Script tidak bisa mengirim status HTTP 401, jadi kegagalan auth
//    dikenali dari body JSON: { result:'error', code:'AUTH_REQUIRED' }.
//
// 3) MASALAH TERUKUR: Google kadang membalas halaman HTML interstitial
//    (berisi window['ppConfig']) alih-alih JSON. Pengukuran 12 Agu 2026:
//    1 dari 7 request 'ping' membalas HTML, dan latensi 1,1 s s/d 34 s.
//    Dulu ini muncul ke user sebagai "Gagal koneksi server."
//
//    Retry hanya dilakukan untuk action BACA. Untuk action TULIS retry
//    DILARANG: tulisnya mungkin sudah berhasil di server dan hanya
//    responsnya yang berupa HTML — mengulang akan membuat data dobel.
//    Ini diduga akar dari bug "pengajuan ganda" yang sudah dua kali
//    dikoreksi (commit 017b033 dan f1f8255).
// ============================================================

// Action yang aman diulang: hanya membaca, tidak mengubah data.
const ACTION_AMAN_DIULANG = [
  // buka_aplikasi hanya MEMBACA (statistik, pengumuman, periode, status
  // pelacakan, angka approval) — sama seperti action get_* di bawah,
  // jadi aman diulang saat Google membalas halaman HTML alih-alih JSON.
  'ping', 'check_version', 'buka_aplikasi', 'login', 'get_latest_announcement',
  'get_history', 'get_db_absen', 'get_user_list_simple', 'get_stats',
  'get_remarks', 'get_shift_history', 'get_approval_list', 'get_approval_team_config', 'get_team_history',
  'get_user_list_admin', 'get_analysis_data', 'get_geofence_config', 'get_absence_period',
  'get_rekap_admin', 'get_koreksi_list', 'get_gps_audit', 'get_alamat', 'get_log_login',
  'get_gps_tracking_status', 'get_gps_live', 'get_gps_trail', 'get_gps_tracking_admin',
  // verifikasi_wajah hanya membandingkan angka dan tidak menulis apa pun,
  // jadi aman diulang — dan justru harus, karena kegagalannya menghalangi
  // karyawan mengambil foto presensi.
  'verifikasi_wajah', 'get_device_list', 'get_device_audit', 'get_wajah_list',
  'get_notif_karyawan'
];

const APPROVAL_ROLES = ['admin', 'hrd', 'manager', 'kepala', 'kepala_divisi', 'supervisor', 'spv', 'pimpinan'];
const isApprovalRole = (role) => APPROVAL_ROLES.includes(String(role || '').toLowerCase());
const approvalMenuHidden = (role) => !isApprovalRole(role);

// MASTER ROLE (24 Okt 2026). Kolom Role sheet Users berisi KODE role
// (mis. "hrd_sby"). Server menerjemahkannya lewat master role menjadi
// `tingkat` (admin/hrd/.../karyawan) + cakupan lokasi + menu. Seluruh
// pengecekan `user.role` di layar bekerja dengan TINGKAT, jadi di sini
// `role` diganti tingkat dan kode aslinya disimpan di `roleKode`.
// Login lewat jalur yang belum membawa `tingkat` dibiarkan apa adanya;
// respons buka_aplikasi (field `peran`) melengkapinya beberapa detik kemudian.
export const normalisasiPeran = (u, peran) => {
  if (!u) return u;
  const p = peran || u;
  if (!p.tingkat) return u;
  return {
    ...u,
    roleKode: String(p.roleKode || u.roleKode || u.role || '').toLowerCase(),
    roleNama: p.roleNama || u.roleNama || '',
    role: String(p.tingkat).toLowerCase(),
    tingkat: String(p.tingkat).toLowerCase(),
    lokasiAkses: p.lokasiAkses || u.lokasiAkses || u.lokasi || 'All',
    menuAkses: Array.isArray(p.menuAkses) ? p.menuAkses : (u.menuAkses || []),
  };
};
// ADMIN BERCAKUPAN LOKASI (4 Nov 2026) — mis. Admin Jakarta (admin_jkt):
// tingkat admin, kode bukan "admin", cakupan bukan All. Server
// (AksesLokasi.gs) menyaring datanya & menolak pengaturan global; di sini
// menu pengaturan global (`pusat: true`) cukup disembunyikan.
// Cerminan adminTerbatas_() di apps-script/AksesLokasi.gs.
export const adminTerbatas = (u) => {
  if (!u || String(u.role || '').toLowerCase() !== 'admin') return false;
  if (String(u.roleKode || 'admin').toLowerCase() === 'admin') return false;
  const c = String(u.lokasiAkses || 'All').trim().toLowerCase();
  return !(c === '' || c === 'all' || c === '*');
};
// Menu boleh tampil? menuAkses kosong = semua menu yang memang boleh untuk tingkatnya.
export const menuDiizinkan = (u, kunci) => {
  const m = (u && Array.isArray(u.menuAkses)) ? u.menuAkses : [];
  return m.length === 0 || m.includes(kunci);
};
// Subset APPROVAL_ROLES yang boleh dijadikan admin sebagai "kepala divisi baru"
// lewat layar Tim Approval (admin & hrd tidak termasuk — sudah diatur di tempat lain).
const APPROVAL_HEAD_ROLES = ['manager', 'kepala', 'kepala_divisi', 'supervisor', 'spv', 'pimpinan'];
const ROLE_LABEL_FALLBACK = { manager: 'Manager', kepala: 'Kepala', kepala_divisi: 'Kepala Divisi', supervisor: 'Supervisor', spv: 'SPV', pimpinan: 'Pimpinan' };

// ============================================================
// BACKEND LAMA vs FITUR BARU
//
// Frontend dan backend di-deploy TERPISAH (Vercel/hosting vs Apps Script),
// jadi selalu ada jendela waktu ketika bundle baru sudah dipakai karyawan
// sementara Apps Script masih versi lama. Dalam jendela itu, doPost membalas
// "Action tidak dikenal" untuk setiap action yang belum ada di sana.
//
// Itu kalimat untuk programmer, bukan untuk admin. Tanpa penerjemahan di
// bawah, admin yang membuka Panel hanya melihat popup "Action tidak dikenal."
// dan tidak punya petunjuk sama sekali soal apa yang harus dilakukan.
// ============================================================
const aksiBelumAdaDiBackend = (data) =>
  /tidak dikenal/i.test(String((data && data.message) || ''));

const PESAN_BACKEND_LAMA =
  'Fitur ini belum ada di backend. Salin apps-script/Devices.gs dan '
  + 'apps-script/FaceProfile.gs ke editor Apps Script, jalankan SETUP_DEVICE_LOCK() '
  + 'dan SETUP_FACE_PROFILE() sekali, lalu Deploy versi baru pada deployment yang sudah ada.';

const MAKS_PERCOBAAN = 3;

const jedaMs = (ms) => new Promise((r) => setTimeout(r, ms));

const responsSintetis = (obj) =>
  new Response(JSON.stringify(obj), {
    status: 200,
    headers: { 'Content-Type': 'application/json' }
  });

const fetchApi = async (url, opts = {}, percobaan = 1) => {
  let body = opts.body;
  let action = '';
  try {
    const o = JSON.parse(body);
    action = o.action || '';
    const saved = sessionStorage.getItem('app_user');
    if (saved) {
      const u = JSON.parse(saved);
      if (u && u.token) o.token = u.token;
    }
    body = JSON.stringify(o);
  } catch (e) { /* body bukan JSON — biarkan apa adanya */ }

  let res;
  try {
    res = await fetch(url, { ...opts, body });
  } catch (e) {
    // Kegagalan jaringan murni — aman diulang untuk action baca
    if (ACTION_AMAN_DIULANG.includes(action) && percobaan < MAKS_PERCOBAAN) {
      await jedaMs(700 * percobaan);
      return fetchApi(url, opts, percobaan + 1);
    }
    throw e;
  }

  // Baca sekali lewat clone, supaya pemanggil tetap bisa memanggil .json()
  let data = null;
  let jsonValid = true;
  try {
    data = await res.clone().json();
    if (data && typeof data === 'object' && data.serverTimestamp) {
      updateServerTime(data.serverTimestamp);
    }
  } catch (e) {
    jsonValid = false;
  }

  if (!jsonValid) {
    if (ACTION_AMAN_DIULANG.includes(action) && percobaan < MAKS_PERCOBAAN) {
      console.warn(`Google membalas non-JSON untuk '${action}' (percobaan ${percobaan}). Mengulang...`);
      await jedaMs(700 * percobaan);
      return fetchApi(url, opts, percobaan + 1);
    }

    console.error(`Google membalas non-JSON untuk '${action}' setelah ${percobaan} percobaan.`);

    if (ACTION_AMAN_DIULANG.includes(action)) {
      return responsSintetis({
        result: 'error',
        code: 'RESPONS_BUKAN_JSON',
        message: 'Server Google sedang tidak stabil dan membalas halaman, bukan data. Mohon coba lagi beberapa saat.'
      });
    }

    // Action TULIS: jangan pancing user untuk mengulang begitu saja
    return responsSintetis({
      result: 'error',
      code: 'RESPONS_BUKAN_JSON_TULIS',
      message: 'Server Google membalas halaman, bukan data, jadi status penyimpanan tidak diketahui. ' +
               'PERIKSA DULU di menu Riwayat apakah data sudah tersimpan sebelum mengulang, ' +
               'supaya tidak terjadi pengajuan ganda.'
    });
  }

  // opts.senyap = permintaan latar belakang (pelacak GPS). Sesi habis pada
  // request semacam itu TIDAK boleh memunculkan alert atau me-reload
  // halaman: user mungkin sedang mengisi form absen, dan halaman yang
  // tiba-tiba dimuat ulang akan membuang isian beserta fotonya.
  // Pemanggilnya cukup berhenti diam-diam; sesi tetap akan divalidasi
  // pada aksi berikutnya yang benar-benar dilakukan user.
  // Sesi digusur karena akun ini dipakai login di perangkat lain
  // (lihat apps-script/Devices.gs). Dibedakan dari AUTH_REQUIRED karena
  // kalimatnya berbeda: karyawan perlu tahu bahwa akunnya dipakai orang
  // lain, bukan sekadar "waktunya habis".
  if (data && data.code === 'SESI_DIGANTI' && !opts.senyap) {
    sessionStorage.clear();
    alert(data.message || 'Akun Anda dipakai login di perangkat lain. Silakan login kembali.');
    window.location.reload();
  }

  if (data && data.code === 'AUTH_REQUIRED' && !opts.senyap) {
    sessionStorage.clear();
    alert('Sesi Anda sudah berakhir. Silakan login ulang.');
    window.location.reload();
  }

  return res;
};

// ============================================================
// SINKRONISASI WAKTU SERVER (Mencegah manipulasi jam perangkat)
// ============================================================
let serverTimeOffset = (() => {
  try {
    const val = sessionStorage.getItem('server_time_offset');
    return val !== null ? parseInt(val, 10) : 0;
  } catch (e) {
    return 0;
  }
})();

export const updateServerTime = (serverTimestamp) => {
  if (typeof serverTimestamp === 'number' && !isNaN(serverTimestamp)) {
    const offset = serverTimestamp - Date.now();
    serverTimeOffset = offset;
    try {
      sessionStorage.setItem('server_time_offset', String(offset));
    } catch (e) {}
  }
};

export const getServerNow = () => {
  return new Date(Date.now() + serverTimeOffset);
};

    // HELPER FORMAT TANGGAL GLOBAL
// Tanggal hari ini mengikuti jam SERVER (WIB), format yyyy-MM-dd.
// Dipakai untuk mencocokkan catatan absen lokal dengan hari berjalan.
const tglLokal = (d = getServerNow()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const formatDateIndo = (d) => { if (!d || d === '-') return '-'; try { return new Date(d).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'}); } catch (e) { return d; } };
const formatDateShort = (d) => { if (!d || d === '-') return '-'; try { return new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric'}); } catch (e) { return d; } };
// Ikon dipilih per makna, bukan per kemiripan bentuk:
// Sakit = medis (bukan segitiga peringatan), Tukar Shift = panah dua arah
// (dulu memakai ikon kalender yang sama persis dengan Off).
// DINAS LUAR = ABSEN ONLINE (Okt 2026). Selfie kamera depan + GPS +
// keterangan, tanpa pilih tanggal/jam (server memaksa hari ini), langsung
// Verified. DL yang lupa diajukan -> Lapor HRD kategori KATEGORI_LUPA_DL.
// Cermin TIPE_DL_ONLINE / KATEGORI_REMARK_LUPA_DL di apps-script/Code.gs.
const TIPE_DL_ONLINE = ['Dinas', 'Dinas Luar'];
const KATEGORI_LUPA_DL = 'Lupa Pengajuan Dinas Luar (DL)';

// MODE ABSEN PER KARYAWAN (Okt 2026). Cermin MODE_ABSEN_VALID di
// apps-script/ModeAbsen.gs. 'gabungan' = bawaan untuk semua karyawan.
const OPSI_MODE_ABSEN = [
  { value: 'gabungan', label: 'Online + mesin', ket: 'Boleh absen lewat aplikasi maupun mesin. Semua absen terbaca: masuk = jam paling awal, pulang = jam paling akhir dari kedua sumber.' },
  { value: 'online', label: 'Online saja', ket: 'Absen hanya lewat aplikasi. Scan mesin tidak dipakai untuk jam masuk/pulang maupun status.' },
  { value: 'mesin', label: 'Mesin saja', ket: 'Tombol absen Masuk/Pulang di aplikasi ditutup (server juga menolak). Hanya scan mesin yang terbaca. Standby & DL tetap bisa.' }
];

const ICON_MAP = { 'Hadir': CheckCircle, 'Pulang': LogOut, 'Ijin': ClipboardList, 'Sakit': HeartPulse, 'Lembur': Timer, 'Dinas': PlaneTakeoff, 'Dinas Luar': PlaneTakeoff, 'Cuti': Palmtree, 'Cuti EO': Palmtree, 'Tukar Shift': ArrowLeftRight, 'Off': Coffee, 'Standby': Clock };
// Tint lembut + ikon berwarna. Blok warna penuh bikin 4 kartu saling berebut perhatian.
const COLOR_MAP = {'Hadir': 'bg-emerald-50 text-emerald-600', 'Pulang': 'bg-rose-50 text-rose-600', 'Ijin': 'bg-amber-50 text-amber-600', 'Sakit': 'bg-red-50 text-red-600', 'Lembur': 'bg-violet-50 text-violet-600', 'Dinas': 'bg-sky-50 text-sky-600', 'Cuti': 'bg-teal-50 text-teal-600', 'Tukar Shift': 'bg-indigo-50 text-indigo-600', 'Off': 'bg-slate-100 text-slate-500'};

// ============================================================
// TEMA WARNA PER JENIS FORM
//
// Satu warna aksen per jenis pengajuan, dipakai konsisten di kepala
// layar, chip ikon, cincin fokus input, dan tombol kirim. Ini yang
// membedakan "berwarna" dari "warna-warni": versi lama menumpuk kotak
// biru, oranye, indigo, dan abu di satu layar tanpa alasan — tiap blok
// berteriak sekeras blok di sebelahnya, jadi tidak ada yang menonjol.
//
// Kelasnya ditulis utuh (bukan dirakit dari potongan string) karena
// Tailwind memindai kode sebagai teks; nama kelas hasil rakitan tidak
// akan pernah ikut ter-generate.
// ============================================================
const TEMA_DEFAULT = {
  grad: 'from-slate-950 via-slate-900 to-blue-950',
  chip: 'bg-blue-500/15 text-blue-400 border border-blue-500/20',
  fokus: 'focus:border-blue-500 focus:ring-blue-500/20',
  tombol: 'bg-slate-900 hover:bg-blue-600 shadow-slate-900/25',
  garis: 'bg-blue-500',
};
const TEMA_FORM = {
  'Hadir':       { grad: 'from-slate-950 via-emerald-950/80 to-teal-950',  chip: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20', fokus: 'focus:border-emerald-500 focus:ring-emerald-500/20', tombol: 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/25', garis: 'bg-emerald-500' },
  'Pulang':      { grad: 'from-slate-950 via-rose-950/80 to-red-950',      chip: 'bg-rose-500/15 text-rose-400 border border-rose-500/20',       fokus: 'focus:border-rose-500 focus:ring-rose-500/20',       tombol: 'bg-rose-600 hover:bg-rose-700 shadow-rose-600/25',       garis: 'bg-rose-500' },
  'Ijin':        { grad: 'from-slate-950 via-amber-950/80 to-orange-950',  chip: 'bg-amber-500/15 text-amber-400 border border-amber-500/20',     fokus: 'focus:border-amber-500 focus:ring-amber-500/20',     tombol: 'bg-amber-600 hover:bg-amber-700 shadow-amber-600/25',     garis: 'bg-amber-500' },
  'Sakit':       { grad: 'from-slate-950 via-red-950/80 to-rose-950',      chip: 'bg-red-500/15 text-red-400 border border-red-500/20',         fokus: 'focus:border-red-500 focus:ring-red-500/20',         tombol: 'bg-red-600 hover:bg-red-700 shadow-red-600/25',         garis: 'bg-red-500' },
  'Lembur':      { grad: 'from-slate-950 via-purple-950/80 to-violet-950', chip: 'bg-violet-500/15 text-violet-400 border border-violet-500/20',   fokus: 'focus:border-violet-500 focus:ring-violet-500/20',   tombol: 'bg-violet-600 hover:bg-violet-700 shadow-violet-600/25', garis: 'bg-violet-500' },
  'Dinas':       { grad: 'from-slate-950 via-sky-950/80 to-blue-950',      chip: 'bg-sky-500/15 text-sky-400 border border-sky-500/20',         fokus: 'focus:border-sky-500 focus:ring-sky-500/20',         tombol: 'bg-sky-600 hover:bg-sky-700 shadow-sky-600/25',         garis: 'bg-sky-500' },
  'Dinas Luar':  { grad: 'from-slate-950 via-sky-950/80 to-cyan-950',      chip: 'bg-sky-500/15 text-sky-400 border border-sky-500/20',         fokus: 'focus:border-sky-500 focus:ring-sky-500/20',         tombol: 'bg-sky-600 hover:bg-sky-700 shadow-sky-600/25',         garis: 'bg-sky-500' },
  'Cuti':        { grad: 'from-slate-950 via-teal-950/80 to-emerald-950',  chip: 'bg-teal-500/15 text-teal-400 border border-teal-500/20',       fokus: 'focus:border-teal-500 focus:ring-teal-500/20',       tombol: 'bg-teal-600 hover:bg-teal-700 shadow-teal-600/25',       garis: 'bg-teal-500' },
  'Cuti EO':     { grad: 'from-slate-950 via-teal-950/80 to-cyan-950',     chip: 'bg-teal-500/15 text-teal-400 border border-teal-500/20',       fokus: 'focus:border-teal-500 focus:ring-teal-500/20',       tombol: 'bg-teal-600 hover:bg-teal-700 shadow-teal-600/25',       garis: 'bg-teal-500' },
  'Tukar Shift': { grad: 'from-slate-950 via-indigo-950/80 to-blue-950',   chip: 'bg-indigo-500/15 text-indigo-400 border border-indigo-500/20',   fokus: 'focus:border-indigo-500 focus:ring-indigo-500/20',   tombol: 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-600/25', garis: 'bg-indigo-500' },
  'Off':         { grad: 'from-slate-950 via-slate-900 to-slate-800',   chip: 'bg-slate-500/15 text-slate-400 border border-slate-500/20',     fokus: 'focus:border-slate-500 focus:ring-slate-500/20',     tombol: 'bg-slate-800 hover:bg-slate-900 shadow-slate-800/25',   garis: 'bg-slate-500' },
  'Standby':     { grad: 'from-slate-950 via-slate-900 to-slate-800',   chip: 'bg-slate-500/15 text-slate-400 border border-slate-500/20',     fokus: 'focus:border-slate-500 focus:ring-slate-500/20',     tombol: 'bg-slate-800 hover:bg-slate-900 shadow-slate-800/25',   garis: 'bg-slate-500' },
};
const temaFor = (tipe) => TEMA_FORM[tipe] || TEMA_DEFAULT;

// Sakelar on/off. Dulu tiap tempat menggambar sakelarnya sendiri dengan
// ukuran dan jarak berbeda — di satu layar yang sama ada dua sakelar yang
// tidak sama tingginya.
const Sakelar = ({ aktif, onToggle, nonaktif, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={aktif}
    aria-label={label}
    disabled={nonaktif}
    onClick={onToggle}
    className={`relative inline-flex h-[24px] w-[42px] shrink-0 items-center rounded-full transition-colors duration-200
      ${aktif ? 'bg-blue-600' : 'bg-slate-200'} ${nonaktif ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span className={`inline-block h-[18px] w-[18px] transform rounded-full bg-white shadow-sm transition-transform duration-200
      ${aktif ? 'translate-x-[21px]' : 'translate-x-[3px]'}`} />
  </button>
);

// Satu kartu seksi form: kepala (ikon + judul + kontrol kanan) lalu isi.
// Semua blok di layar form memakai ini, sehingga jarak dan garisnya sama.
const SeksiForm = ({ ikon: Ikon, judul, catatan, aksi, warnaIkon, padat, children }) => (
  <section className="bg-white rounded-2xl border border-slate-200/80 shadow-sm shadow-slate-900/[0.03] overflow-hidden">
    <header className="flex items-center gap-2.5 px-4 py-3">
      <span className={`w-8 h-8 shrink-0 rounded-[10px] flex items-center justify-center ${warnaIkon || 'bg-slate-100 text-slate-500'}`}>
        <Ikon className="w-[15px] h-[15px]" strokeWidth={2} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold text-slate-900 tracking-tight leading-tight">{judul}</p>
        {catatan && <p className="text-[10.5px] text-slate-400 leading-tight mt-0.5">{catatan}</p>}
      </div>
      {aksi}
    </header>
    {children && (
      <div className={`border-t border-slate-100 ${padat ? 'p-3' : 'px-4 py-3.5'}`}>{children}</div>
    )}
  </section>
);

// Satu gaya input untuk seluruh layar form.
const INPUT_FORM = 'w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-[13.5px] font-medium text-slate-900 placeholder:text-slate-400 placeholder:font-normal outline-none transition-all focus:ring-4';

// Label mikro di atas tiap field.
const LabelKecil = ({ children }) => (
  <label className="block text-[10px] font-semibold uppercase tracking-[0.09em] text-slate-400 mb-1.5">{children}</label>
);

// Lokasi pada sheet lama berbentuk "lat, lng", sedangkan data yang lebih
// baru juga membawa alamat hasil reverse geocoding. Keduanya dapat dibuka
// langsung di Google Maps; alamat dipakai sebagai cadangan bila koordinat
// tidak tersedia.
const urlGoogleMaps = (lokasi, alamat) => {
  const teksLokasi = String(lokasi || '').trim();
  const cocok = teksLokasi.match(/^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/);
  const query = cocok ? `${cocok[1]},${cocok[2]}` : String(alamat || '').trim();
  return query && query !== '-' ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : '';
};

const TombolLokasiMaps = ({ lokasi, alamat, label = 'Lokasi Maps', className = '' }) => {
  const href = urlGoogleMaps(lokasi, alamat);
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noreferrer" title={alamat || lokasi}
      className={`inline-flex items-center gap-1.5 text-[10px] font-bold text-rose-600 hover:text-rose-700 ${className}`}>
      <MapPin className="w-3.5 h-3.5" strokeWidth={2} /> {label}
    </a>
  );
};

// Dipasang sekali pada kerangka aplikasi. Listener capture juga menangkap
// layar yang memakai area scroll sendiri, bukan hanya scroll halaman utama.
const TombolKembaliKeAtas = () => {
  const [tampil, setTampil] = useState(false);
  const targetScrollRef = useRef(null);

  useEffect(() => {
    const periksaScroll = (event) => {
      const target = event && event.target;
      const scrollInternal = target && target !== document && typeof target.scrollTop === 'number'
        ? target.scrollTop : 0;
      const scrollHalaman = window.scrollY || document.documentElement.scrollTop || 0;
      if (scrollInternal > 0 && target && typeof target.scrollTo === 'function') targetScrollRef.current = target;
      setTampil(Math.max(scrollInternal, scrollHalaman) > 360);
    };
    window.addEventListener('scroll', periksaScroll, { passive: true });
    document.addEventListener('scroll', periksaScroll, true);
    periksaScroll();
    return () => {
      window.removeEventListener('scroll', periksaScroll);
      document.removeEventListener('scroll', periksaScroll, true);
    };
  }, []);

  if (!tampil) return null;
  return (
    <button type="button" onClick={() => {
      const target = targetScrollRef.current;
      if (target && target.scrollTop > 0) target.scrollTo({ top: 0, behavior: 'smooth' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }}
      className="fixed bottom-5 right-4 z-[200] inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-900 text-white shadow-lg shadow-slate-900/30 transition hover:bg-blue-600 active:scale-95 sm:bottom-6 sm:right-6"
      title="Kembali ke atas" aria-label="Kembali ke atas">
      <ArrowUp className="h-4 w-4" strokeWidth={2.5} />
    </button>
  );
};

    // MAIN APP COMPONENT
    //
    // Dibungkus <ImportJobProvider> di bawah (lihat AppAbsensi). Provider itu
    // yang memegang job import dbabsen, supaya loop pengirimannya tidak ikut
    // mati saat admin meninggalkan layar Import.
// Menu yang boleh dibuka ulang setelah refresh. 'form' sengaja tidak ikut:
// isinya bergantung pada data yang sedang diedit, yang hilang saat refresh.
const VIEW_BISA_DIPULIHKAN = ['dashboard', 'history', 'db_absen', 'admin', 'approval', 'ganti_password',
  'remark', 'input_shift', 'analysis', 'rekap_admin', 'histori_tim', 'gps_audit', 'gps_dashboard', 'log_login', 'dashboard_kerani'];
const viewTersimpan = () => {
  try {
    const v = sessionStorage.getItem('app_view');
    return VIEW_BISA_DIPULIHKAN.includes(v) ? v : 'dashboard';
  } catch (e) { return 'dashboard'; }
};

function AppAbsensiInner() {
  const [user, setUser] = useState(null);
  const [view, setView] = useState('login'); 
  // Menu yang sedang dibuka diingat (sessionStorage, per tab browser), jadi
  // refresh halaman membuka menu yang sama — bukan kembali ke dashboard.
  useEffect(() => {
    if (!user || !view || view === 'login') return;
    try { sessionStorage.setItem('app_view', view); } catch (e) { /* abaikan */ }
  }, [user, view]);
  const [masterData, setMasterData] = useState({ menus: [], roles: [], divisions: [], shifts: [], sheetImport: [] });
  const [editItem, setEditItem] = useState(null);
  const [approvalNotice, setApprovalNotice] = useState(null);
  const logoutTimerRef = useRef(null);
  // Versi frontend dibuat oleh `npm run update:prepare` dari
  // updates/frontend/releases. Jangan menulis angka versi langsung di sini.
  const CLIENT_VERSION = FRONTEND_VERSION;
  const [updateAvailable, setUpdateAvailable] = useState(false);
  // Dibaca sekali saat aplikasi dibuka: berapa kali user sudah menekan
  // "Update Sekarang" tanpa versinya berubah.
  const [updateGagalBerulang] = useState(() => {
    try { return Number(localStorage.getItem('update_percobaan') || 0) >= 3; } catch (e) { return false; }
  });
  const [newVersion, setNewVersion] = useState('');
  // Sisa detik sebelum aplikasi memuat ulang sendiri. null = belum berjalan
  // (mis. karyawan sedang mengisi form absen, jadi update ditunda).
  const [updateHitungMundur, setUpdateHitungMundur] = useState(null);

    //----LOGIKA CEK UPDATE----
    // Login membawa versi server di respons yang sama. check_version juga
    // dijalankan saat link/shortcut mobile dibuka agar bundle lama terdeteksi
    // sebelum user melanjutkan memakai aplikasi.
const cekVersi = useCallback((versiServer) => {
  if (!versiServer || versiServer === CLIENT_VERSION) {
    // Versi sudah cocok: penghitung percobaan tidak boleh tertinggal,
    // kalau tidak update berikutnya langsung dianggap "sudah 2x gagal".
    try { localStorage.removeItem('update_percobaan'); localStorage.removeItem('update_lewati'); } catch (e) { /* abaikan */ }
    return;
  }
  // User sudah memilih melanjutkan dengan versi ini — jangan dikunci lagi
  // setiap kali aplikasi dibuka.
  try { if (localStorage.getItem('update_lewati') === String(versiServer)) return; } catch (e) { /* abaikan */ }
  const angka = (v) => String(v).split('.').map((n) => Number(n) || 0);
  const client = angka(CLIENT_VERSION);
  const server = angka(versiServer);
  let perbandingan = 0;
  for (let i = 0; i < Math.max(client.length, server.length); i += 1) {
    if ((server[i] || 0) !== (client[i] || 0)) {
      perbandingan = (server[i] || 0) - (client[i] || 0);
      break;
    }
  }
  const serverLebihBaru = perbandingan > 0;
  // Backend lama tetap kompatibel dengan bundle baru. Yang wajib di-update
  // hanya ketika server sudah memiliki kontrak/API yang lebih baru.
  if (!serverLebihBaru) return;
  setNewVersion(versiServer);
  setUpdateAvailable(true);
}, [CLIENT_VERSION]);

    //----CEK VERSI FRONTEND (BERKAS DI SERVER YANG SAMA DENGAN BUNDLE)----
    // update-manifest.json ikut ter-upload bersama bundle, jadi isinya
    // selalu mencerminkan versi yang benar-benar ada di server saat ini —
    // tidak bergantung pada deploy Apps Script. Inilah yang membuat
    // "cukup refresh" berlaku untuk semua HP karyawan.
const cekVersiFrontend = useCallback(async () => {
  try {
    const { versi } = await ambilVersiFrontendServer();
    if (bandingkanVersi(versi, CLIENT_VERSION) <= 0) {
      // Sudah paling baru: penghitung percobaan tidak boleh tertinggal.
      try { localStorage.removeItem('update_percobaan'); localStorage.removeItem('update_lewati'); } catch (e) { /* abaikan */ }
      return;
    }
    // Escape hatch hanya berlaku setelah beberapa kali muat ulang gagal
    // (berarti berkas versi baru memang belum terunggah, bukan cache HP).
    try {
      const percobaan = Number(localStorage.getItem('update_percobaan') || 0);
      if (percobaan >= 3 && localStorage.getItem('update_lewati') === String(versi)) return;
    } catch (e) { /* abaikan */ }
    setNewVersion(versi);
    setUpdateAvailable(true);
  } catch (e) {
    // Manifest tidak terbaca (jaringan mati / berkas belum ada):
    // jangan mengunci aplikasi tanpa bukti versi.
  }
}, [CLIENT_VERSION]);

useEffect(() => {
  cekVersiFrontend();
  return pantauPembaruan(cekVersiFrontend);
}, [cekVersiFrontend]);

    // ----------------------------------------------------------------
    // SATU REQUEST LATAR BELAKANG UNTUK MENGISI DASHBOARD (Sep 2026)
    //
    // `bootAwal` menampung jawaban action `buka_aplikasi`: statistik
    // dashboard, pengumuman, daftar periode, status pelacakan GPS, dan
    // angka lonceng approval — semuanya sekaligus, menggantikan empat
    // request terpisah yang dulu ditembakkan begitu dashboard terbuka.
    //
    // KENAPA DI LATAR BELAKANG, BUKAN DITITIPKAN DI RESPONS LOGIN
    // (pelajaran 18 Sep 2026, dibayar dengan satu percobaan yang gagal):
    // menitipkannya di respons login memang membuat jumlah request lebih
    // sedikit lagi, tetapi layar dashboard jadi baru muncul SETELAH
    // seluruh statistik selesai dihitung di server. Yang dirasakan
    // karyawan adalah "login makin lama", padahal total kerjanya
    // berkurang. Sekarang layarnya muncul begitu login dijawab, dan
    // angkanya menyusul di belakang layar — satu request, tidak menahan
    // apa pun.
    //
    // `bootMenunggu` menahan layar-layar di bawah supaya tidak menembak
    // request sendiri untuk data yang sedang dalam perjalanan. Nilai
    // awalnya dibaca langsung dari sessionStorage (bukan lewat efek),
    // karena Dashboard sudah ter-mount pada render yang sama dan akan
    // keburu menembak get_stats kalau menunggu satu putaran efek.
    // ----------------------------------------------------------------
const [bootAwal, setBootAwal] = useState(null);
const [bootMenunggu, setBootMenunggu] = useState(() => {
  try { return !!sessionStorage.getItem('app_user'); } catch (e) { return false; }
});

    // Status pelacakan GPS dari respons login. Dipisah dari `bootAwal`
    // dengan sengaja: nilainya sudah ada sejak detik login, jadi pelacak
    // bisa mulai tanpa menunggu request latar belakang selesai.
const [gpsTrackingAwal, setGpsTrackingAwal] = useState(null);

const bootDijalankanRef = useRef(false);

    // Notifikasi status pengajuan (components/NotifPengajuan.js). Datanya
    // ikut di respons buka_aplikasi — tidak menambah request saat dibuka.
const notifKaryawan = useNotifKaryawan({ user, bootAwal, fetchApi, scriptUrl: SCRIPT_URL });

    //----LOGIKA AUTO LOGIN / RESTORE SESSION----
useEffect(() => { 
  // UBAH: localStorage menjadi sessionStorage
  const u = sessionStorage.getItem('app_user'), m = sessionStorage.getItem('app_master_data'); 
  if (u) { 
    setUser(normalisasiPeran(JSON.parse(u))); 
    if (m) setMasterData(JSON.parse(m)); 
    setView(viewTersimpan()); 
  } 
  // Pengisian dashboard ditangani efek `buka_aplikasi` di bawah, yang
  // berjalan untuk kedua jalur — sesi dipulihkan MAUPUN login baru.
  //
  // Bundle frontend yang basi tetap terdeteksi tanpa request apa pun:
  // cekVersiFrontend() di atas membaca update-manifest.json langsung dari
  // hosting, tanpa menyentuh Apps Script sama sekali.
  if (!u) setBootMenunggu(false);
}, []);

    // ----------------------------------------------------------------
    // REQUEST PENGISI DASHBOARD — SATU KALI PER SESI, DI LATAR BELAKANG
    //
    // Menggantikan empat request yang dulu ditembakkan sendiri-sendiri
    // begitu dashboard terbuka: get_stats, get_latest_announcement,
    // get_gps_tracking_status, dan get_approval_list. Masing-masing dulu
    // menjadi satu eksekusi Apps Script tersendiri — dan yang mahal
    // bukan pekerjaannya, melainkan ongkos tetapnya (redirect 302, boot
    // container, antrean kuota eksekusi serentak).
    //
    // Dikunci pada user.id, bukan objek `user`: objek itu diganti setiap
    // kali sisa cuti diperbarui sesudah absen, dan kalau efek ini ikut
    // dijalankan ulang, request yang sedang berjalan akan dibatalkan
    // tanpa pernah ada yang menggantikannya.
    // ----------------------------------------------------------------
useEffect(() => {
  if (!user || !user.id) return;
  if (bootDijalankanRef.current) return;
  bootDijalankanRef.current = true;

  let batal = false;
  setBootMenunggu(true);
  fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'buka_aplikasi' }) })
    .then((response) => response.json())
    .then((data) => {
      if (batal) return;
      if (data.result === 'success') {
        cekVersi(data.version);
        setBootAwal(data);
        // Peran efektif dari server (master role). Diterapkan hanya bila
        // berbeda, supaya tidak memicu render ulang yang sia-sia.
        if (data.peran && data.peran.tingkat) {
          setUser((lama) => {
            if (!lama) return lama;
            const baru = normalisasiPeran(lama, data.peran);
            const sama = baru.role === lama.role && baru.lokasiAkses === lama.lokasiAkses
              && JSON.stringify(baru.menuAkses || []) === JSON.stringify(lama.menuAkses || []) && baru.roleNama === lama.roleNama;
            if (sama) return lama;
            try { sessionStorage.setItem('app_user', JSON.stringify(baru)); } catch (e) { /* abaikan */ }
            return baru;
          });
        }
      }
    })
    .catch(() => { /* jaringan gagal: layar di bawah mengambil sendiri seperti dulu */ })
    .finally(() => { if (!batal) setBootMenunggu(false); });
  return () => { batal = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [user && user.id, cekVersi]);

    //----FUNGSI EKSEKUSI UPDATE (MEMBERSIHKAN CACHE)----
const performUpdate = async () => {
  // Percobaan dihitung SEBELUM localStorage dibersihkan, lalu ditulis
  // ulang sesudahnya. Tanpa penghitung ini, bundle yang memang belum
  // terunggah membuat layar ini jadi jalan buntu: user menekan Update,
  // halaman dimuat ulang, bundle yang sama muncul lagi, selamanya.
  let percobaan = 0;
  try { percobaan = Number(localStorage.getItem('update_percobaan') || 0) + 1; } catch (e) { percobaan = 1; }

  try { localStorage.clear(); } catch (e) { /* mode privat iOS: abaikan */ }
  // Jangan mengosongkan sessionStorage di sini. Kredensial sesi yang
  // dipulihkan otomatis saat halaman dimuat ulang tersimpan pada
  // `app_user`; menghapusnya membuat pembaruan terasa seperti logout.
  // Cache berkas aplikasi tetap dibersihkan di bawah, jadi reload tetap
  // mengambil bundle terbaru tanpa meminta pengguna login kembali.
  try { localStorage.setItem('update_percobaan', String(percobaan)); } catch (e) { /* abaikan */ }

  if ('serviceWorker' in navigator) {
    try {
      const rs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(rs.map((r) => r.unregister()));
    } catch (e) { /* abaikan */ }
  }

  // INI YANG DULU HILANG. Safari iOS dan Chrome Android menyimpan HTML dan
  // bundle di Cache Storage, dan localStorage.clear() sama sekali tidak
  // menyentuhnya. Selama isinya masih ada, halaman yang dimuat ulang
  // menerima bundle LAMA lagi — layar "Update Tersedia" muncul terus, dan
  // kalau index.html lama menunjuk berkas /static yang sudah tidak ada di
  // server, yang terlihat justru layar putih.
  if (typeof caches !== 'undefined' && caches.keys) {
    try {
      const kunci = await caches.keys();
      await Promise.all(kunci.map((k) => caches.delete(k)));
    } catch (e) { /* abaikan */ }
  }

  // replace(), bukan href: supaya tombol "kembali" tidak mengembalikan
  // user ke halaman versi lama yang baru saja ditinggalkan.
  window.location.replace(window.location.pathname + '?v=' + newVersion + '&t=' + Date.now());
};

// Muat ulang otomatis. Ditunda selama karyawan berada di layar form absen
// supaya foto & titik GPS yang sudah diisi tidak hilang; begitu keluar dari
// form, hitung mundur langsung berjalan dan update tidak bisa dihindari.
const bolehMuatUlangOtomatis = updateAvailable && !updateGagalBerulang && view !== 'form';
useEffect(() => {
  if (!bolehMuatUlangOtomatis) { setUpdateHitungMundur(null); return undefined; }
  let sisa = 5;
  setUpdateHitungMundur(sisa);
  const timer = setInterval(() => {
    sisa -= 1;
    setUpdateHitungMundur(sisa);
    if (sisa <= 0) { clearInterval(timer); performUpdate(); }
  }, 1000);
  return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [bolehMuatUlangOtomatis]);

// Dipanggil dari layar update setelah beberapa percobaan gagal. Aplikasi lama
// tetap bisa dipakai — absensi jauh lebih penting daripada memaksa nomor
// versi cocok, dan backend memang masih melayani klien versi sebelumnya.
const lanjutTanpaUpdate = () => {
  try { localStorage.setItem('update_lewati', newVersion); } catch (e) { /* abaikan */ }
  setUpdateAvailable(false);
};

    //----STATUS PELACAKAN POSISI----
    // Disimpan untuk keperluan debug/diagnosa, tidak digambar di layar.
    // Indikator melayangnya dihapus atas permintaan (8 Sep 2026) karena
    // menutupi baris data paling bawah di layar HP.
    // eslint-disable-next-line no-unused-vars
const [gpsTrackStatus, setGpsTrackStatus] = useState(null);

    //----FUNGSI LOGOUT / KELUAR APLIKASI----
const handleLogout = useCallback(() => { 
  setUser(null); 
  setMasterData({ menus: [], roles: [], divisions: [], shifts: [], sheetImport: [] });
  setApprovalNotice(null);
  setView('login'); 
  // UBAH: localStorage menjadi sessionStorage
  sessionStorage.removeItem('app_user'); 
  sessionStorage.removeItem('app_master_data');
  sessionStorage.removeItem('app_view');
  sessionStorage.removeItem('rekap_tab');
  sessionStorage.removeItem('announcement_shown');
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith('approval_popup_seen_'))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch (e) { /* abaikan */ }
  // Statistik milik user sebelumnya — kalau tidak dibuang, user berikutnya
  // yang login di HP yang sama akan melihat angka orang lain sekejap.
  // Titipan respons login/buka_aplikasi milik user sebelumnya. Kalau
  // tidak dibuang, status pelacakan dan angka approval orang lain bisa
  // ikut terpakai oleh user berikutnya yang login di HP yang sama.
  setBootAwal(null);
  setGpsTrackingAwal(null);
  setBootMenunggu(false);
  bootDijalankanRef.current = false;
  sessionStorage.removeItem('app_stats_awal');
  sessionStorage.removeItem('app_stats_terakhir');
  sessionStorage.removeItem('app_pengumuman_awal');
  if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current); 
}, []);

    //----RESET TIMER OTOMATIS LOGOUT----
//
    // Kalimatnya disusun dari TIMEOUT_LABEL, bukan angka yang diketik di
    // sini. Sebelumnya teksnya berbunyi "10 menit" padahal ambangnya 5
    // menit — karyawan yang mengeluh "baru ditinggal sebentar" ternyata
    // benar, dan pesannya sendiri yang berbohong.
const resetTimer = useCallback(() => { if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current); if (user) logoutTimerRef.current = setTimeout(() => { alert(`Sesi Anda berakhir karena tidak ada aktivitas selama ${TIMEOUT_LABEL}.`); handleLogout(); }, TIMEOUT_DURATION); }, [user, handleLogout]);

    // LISTENER AKTIVITAS USER (AUTO-LOGOUT)
useEffect(() => { if (!user) return; resetTimer(); const ev = ['click', 'mousemove', 'keypress', 'scroll', 'touchstart']; ev.forEach(e => window.addEventListener(e, resetTimer)); return () => { if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current); ev.forEach(e => window.removeEventListener(e, resetTimer)); }; }, [user, resetTimer]);

    // FUNGSI HANDLER LOGIN & PENYIMPANAN SESI
const handleLogin = (userData, rawMasterData, versiServer, statsAwal, pengumumanAwal, pengumumanDisertakan, periodsAwal, ekstra) => {
  cekVersi(versiServer);

  // Status pelacakan GPS ikut di respons login (murni Script Properties,
  // tidak menyentuh sheet). Dengan ini pelacak bisa mulai seketika tanpa
  // menembak get_gps_tracking_status sendiri.
  //
  // `bootAwal` SENGAJA tidak diisi di sini: isinya statistik dan angka
  // approval, yang memang diambil belakangan lewat `buka_aplikasi` agar
  // layar dashboard tidak ditahan menunggu perhitungannya.
  setGpsTrackingAwal((ekstra && ekstra.gpsTracking) || null);
  setBootAwal(null);
  setBootMenunggu(true);
  bootDijalankanRef.current = false;

  const p = { menus: rawMasterData.filter(m => m.kategori === 'Menu'), roles: rawMasterData.filter(m => m.kategori === 'Role'), divisions: rawMasterData.filter(m => m.kategori === 'Divisi'), shifts: rawMasterData.filter(m => m.kategori === 'Shift'), sheetImport: rawMasterData.filter(m => m.kategori === 'SheetImport') };
  setMasterData(p);
  userData = normalisasiPeran(userData);
  // Nama karyawan selalu HURUF KAPITAL di seluruh aplikasi.
  if (userData && typeof userData.nama === 'string') userData = { ...userData, nama: userData.nama.toUpperCase() };
  setUser(userData);
  setApprovalNotice(null);
  setView('dashboard');
  // UBAH: localStorage menjadi sessionStorage
  sessionStorage.setItem('app_user', JSON.stringify(userData));
  sessionStorage.setItem('app_master_data', JSON.stringify(p));

  // STATISTIK IKUT DALAM RESPONS LOGIN (Agu 2026).
  // Dititipkan lewat sessionStorage, bukan props, supaya Dashboard bisa
  // memakainya tanpa mengubah rantai props yang dilewati banyak layar.
  if (statsAwal) {
    try {
      sessionStorage.setItem('app_stats_awal', JSON.stringify(statsAwal));
      sessionStorage.setItem('app_stats_terakhir', JSON.stringify(statsAwal));
    } catch (e) { /* kuota penuh: bukan kegagalan fatal, cuma tidak hemat */ }
  }

  // PENGUMUMAN juga ikut di respons login.
  if (pengumumanDisertakan) {
    try {
      sessionStorage.setItem('app_pengumuman_awal', JSON.stringify({ isi: pengumumanAwal || null }));
    } catch (e) { /* abaikan */ }
  }

  // DAFTAR PERIODE IKUT DI RESPONS LOGIN
  if (periodsAwal && periodsAwal.periodsAktif && periodsAwal.periodsAktif.length) {
    try {
      sessionStorage.setItem('app_periods_awal', JSON.stringify(periodsAwal));
    } catch (e) { /* abaikan */ }
  }
};

  useEffect(() => {
    if (!user || !isApprovalRole(user.role)) {
      setApprovalNotice(null);
      return;
    }

    const kunci = `approval_notice_seen_${user.id}`;
    if (sessionStorage.getItem(kunci) === '1') return;

    // JALUR CEPAT: angka lonceng sudah ikut di respons login maupun
    // buka_aplikasi. Dulu setiap kepala divisi yang membuka aplikasi
    // menembak get_approval_list sendiri — satu eksekusi Apps Script
    // penuh, yang di dalamnya menyisir seluruh sheet Absensi, hanya
    // untuk menampilkan satu angka.
    if (bootAwal) {
      const ap = bootAwal.approval;
      const total = ap ? (Number(ap.total) || 0) : 0;
      if (total > 0) {
        setApprovalNotice({
          total,
          divisiCounts: (ap && ap.divisiCounts) || {},
          nama: user.nama || 'Kepala Divisi'
        });
      }
      return;
    }

    // Jawaban boot masih di perjalanan: jangan menembak request kedua
    // untuk data yang sudah dalam perjalanan.
    if (bootMenunggu) return;

    let batal = false;

    const muatNotifikasiApproval = async () => {
      try {
        const res = await fetchApi(SCRIPT_URL, {
          method: 'POST',
          body: JSON.stringify({
            action: 'get_approval_list',
            userId: user.id,
            divisi: user.divisi,
            role: user.role,
            lokasi: user.lokasi || 'All'
          })
        });
        const data = await res.json();
        if (batal || data.result !== 'success') return;
        const total = Array.isArray(data.list) ? data.list.length : 0;
        if (total > 0) {
          setApprovalNotice({
            total,
            divisiCounts: data.divisiCounts || {},
            nama: user.nama || 'Kepala Divisi'
          });
        }
      } catch (e) {
        // Notifikasi bukan jalur kritis; kalau gagal, dashboard tetap jalan.
      }
    };

    muatNotifikasiApproval();
    return () => { batal = true; };
  }, [user, bootAwal, bootMenunggu]);

    // ----------------------------------------------------------------
    // GERBANG GPS WAJIB (lihat utils/gpsWajib.js)
    //
    // Menu tidak boleh terbuka sebelum lokasi perangkat benar-benar
    // terbaca. Gerbangnya digambar sebagai LAPISAN DI ATAS isi aplikasi,
    // bukan menggantikannya: kalau pemeriksaan ulang di tengah sesi
    // gagal sesaat, layar form yang sedang diisi karyawan tetap hidup di
    // belakang beserta foto dan catatannya. Mengganti isi aplikasi akan
    // melepas komponennya dan membuang isian itu diam-diam.
    // ----------------------------------------------------------------
const [gpsGate, setGpsGate] = useState({ status: GPS_STATUS.MEMERIKSA, pesan: '', lolos: false, memeriksa: true });
const [gpsGatePercobaan, setGpsGatePercobaan] = useState(0);
const gpsOkTerakhirRef = useRef(0);

const periksaGerbangGps = useCallback(async (opsi) => {
  const diam = !!(opsi && opsi.diam);
  // `bebas` = karyawan ini dikecualikan admin dari gerbang GPS (PC kantor
  // tanpa sumber lokasi). Posisinya TETAP dibaca — kalau suatu saat ia
  // membuka aplikasi dari HP, titiknya tetap terkirim ke Dashboard GPS.
  // Yang berbeda cuma satu: kegagalan membaca tidak pernah menahan menu.
  const bebas = !!(opsi && opsi.bebas);
  // Mode diam: menu sudah terbuka, pemeriksaan berjalan di belakang layar.
  // Jangan menampilkan "Memeriksa Lokasi…" — itu justru yang membuat
  // karyawan merasa ditanyai ulang padahal izinnya sudah lama diberikan.
  if (!diam) setGpsGate((g) => ({ ...g, memeriksa: true }));

  const hasil = await periksaGpsWajib({ diam });

  if (hasil.status === GPS_STATUS.OK) {
    gpsOkTerakhirRef.current = Date.now();
    // Dicatat supaya pembukaan aplikasi BERIKUTNYA tidak memunculkan
    // gerbang sama sekali — termasuk di Safari iOS yang tidak punya
    // Permissions API untuk ditanyai.
    tandaiIzinPernahOk();
    setGpsGate({ status: GPS_STATUS.OK, pesan: '', lolos: true, memeriksa: false });
    setGpsGatePercobaan(0);
    return hasil;
  }

  // Izin dicabut: ingatan harus ikut dibuang, kalau tidak aplikasi akan
  // terus mencoba membuka menu lebih dulu dan baru menabrak gerbang.
  if (hasil.status === GPS_STATUS.DITOLAK) lupakanIzin();

  if (bebas) {
    // Tetap lolos. Statusnya disimpan apa adanya supaya tetap terlihat di
    // log kalau suatu saat perlu ditelusuri, tapi menu tidak ditahan.
    setGpsGate({ status: hasil.status, pesan: hasil.pesan, lolos: true, memeriksa: false });
    return hasil;
  }

  setGpsGatePercobaan((n) => n + 1);
  setGpsGate({ status: hasil.status, pesan: hasil.pesan, lolos: false, memeriksa: false });
  return hasil;
}, []);

useEffect(() => {
  if (!user || !user.id) {
    setGpsGate({ status: GPS_STATUS.MEMERIKSA, pesan: '', lolos: false, memeriksa: true });
    setGpsGatePercobaan(0);
    gpsOkTerakhirRef.current = 0;
    return;
  }
  let batal = false;
  (async () => {
    // Karyawan yang izin lokasinya SUDAH diberikan langsung masuk ke menu.
    // Posisinya tetap dibaca — hanya saja di latar belakang, tanpa layar
    // pemeriksaan. Gerbang baru muncul kalau pembacaan itu benar-benar
    // gagal (izin dicabut, atau layanan lokasi perangkat dimatikan).
    // Karyawan yang dikecualikan admin (mis. staf kantor yang bekerja di
    // PC tanpa GPS) tidak pernah melihat gerbang sama sekali. Lihat
    // gpsBebasDaftar di apps-script/Code.gs untuk alasan lengkapnya.
    // PC/laptop juga dibebaskan dari gerbang (24 Sep 2026): komputer tidak
    // punya GPS, jadi gerbangnya hampir pasti gagal di sana. Hanya gerbang
    // menu — form Hadir/Pulang tetap wajib lokasi (lihat adalahPcAtauLaptop).
    const bebasGerbang = user.gpsGerbangBebas === true || adalahPcAtauLaptop();

    const sudahIzin = bebasGerbang || (await izinSudahDiberikan());
    if (batal) return;
    if (sudahIzin) {
      gpsOkTerakhirRef.current = Date.now();
      setGpsGate({ status: GPS_STATUS.OK, pesan: '', lolos: true, memeriksa: false });
    }

    const hasil = await periksaGerbangGps({ diam: sudahIzin, bebas: bebasGerbang });
    if (batal || !hasil || hasil.status !== GPS_STATUS.OK) return;
    // Titik pertama dikirim dari posisi yang BARU SAJA dibaca gerbang —
    // tanpa membaca GPS untuk kedua kalinya. Inilah yang membuat setiap
    // karyawan langsung muncul di Dashboard GPS begitu membuka aplikasi,
    // bukan menunggu siklus pelacak lima menit kemudian.
    kirimTitikGps({ fetchApi, scriptUrl: SCRIPT_URL, user, sumber: 'awal', posisi: hasil.posisi });
  })();
  return () => { batal = true; };
}, [user, periksaGerbangGps]);

    // Pemeriksaan ulang saat aplikasi kembali dibuka.
    //
    // Sengaja DIBATASI 15 menit sekali. Memeriksa setiap kali layar
    // kembali menyala terdengar lebih ketat, tapi hasilnya kebalikannya:
    // pembacaan GPS di dalam ruangan sesekali gagal karena timeout, dan
    // karyawan yang sekadar membalas pesan lalu kembali akan terlempar ke
    // gerbang tanpa melakukan kesalahan apa pun.
useEffect(() => {
  if (!user || !user.id) return;
  const saatKembali = () => {
    if (typeof document !== 'undefined' && document.hidden) return;
    if (!gpsOkTerakhirRef.current) return;
    if (Date.now() - gpsOkTerakhirRef.current < 15 * 60 * 1000) return;
    // Diam: karyawan sedang memakai aplikasi, jangan disela layar
    // pemeriksaan kalau pada akhirnya posisinya memang terbaca.
    periksaGerbangGps({ diam: true, bebas: user.gpsGerbangBebas === true || adalahPcAtauLaptop() });
  };
  document.addEventListener('visibilitychange', saatKembali);
  return () => document.removeEventListener('visibilitychange', saatKembali);
}, [user, periksaGerbangGps]);

    // Admin tidak boleh bisa terkunci dari sistemnya sendiri: setelah tiga
    // kali gagal, jalan keluar darurat dibuka HANYA untuk role admin.
    // Karyawan biasa tidak pernah melihat tombol ini.
const bolehGpsDarurat = String((user && user.role) || '').toLowerCase() === 'admin' && gpsGatePercobaan >= 3;

const lanjutTanpaGps = useCallback(() => {
  setGpsGate({ status: GPS_STATUS.OK, pesan: '', lolos: true, memeriksa: false });
  gpsOkTerakhirRef.current = Date.now();
}, []);

    // ----------------------------------------------------------------
    // PELACAKAN POSISI (lihat utils/gpsTracker.js dan apps-script/GpsTracking.gs)
    //
    // Dipasang di sini, bukan di dalam Dashboard, karena pelacakan harus
    // tetap berjalan saat karyawan berpindah-pindah layar. Dihentikan
    // otomatis saat user berubah/logout — kalau tidak, timernya akan
    // menembak memakai token milik user sebelumnya.
    // ----------------------------------------------------------------
useEffect(() => {
  if (!user || !user.id) { setGpsTrackStatus(null); return; }
  // Menunggu gerbang lolos lebih dulu. Menjalankan pelacak saat gerbang
  // masih menahan hanya menghasilkan ping gagal beruntun — GPS-nya memang
  // belum bisa dibaca, itu justru sebabnya gerbang muncul.
  if (!gpsGate.lolos) return;
  // Status pelacakan datang dari respons login (jalur login) atau dari
  // respons buka_aplikasi (jalur sesi dipulihkan). Selama BELUM ada
  // satu pun dari keduanya dan jawabannya masih di perjalanan, pelacak
  // ditahan — kalau dijalankan sekarang ia akan menembak
  // get_gps_tracking_status sendiri, yaitu persis request yang sedang
  // dihapus dari jalur pembukaan aplikasi.
  const statusPelacak = gpsTrackingAwal || (bootAwal && bootAwal.gpsTracking) || null;
  if (!statusPelacak && bootMenunggu) return;
  const hentikan = mulaiPelacakGps({
    fetchApi,
    scriptUrl: SCRIPT_URL,
    user,
    onStatus: setGpsTrackStatus,
    statusAwal: statusPelacak
  });
  return () => { try { hentikan(); } catch (e) { /* abaikan */ } };
}, [user, gpsGate.lolos, gpsTrackingAwal, bootAwal, bootMenunggu]);

    // LAYOUT CONTAINER / WRAPPER UTAMA APLIKASI
return (<div className={`min-h-screen font-sans text-slate-800 ${view === 'login' ? 'bg-slate-950' : 'bg-slate-100'}`}><div className={view === 'login' ? 'w-full min-h-screen' : 'w-full max-w-md md:max-w-none mx-auto min-h-screen px-3 sm:px-4 md:px-8 lg:px-12 py-3 sm:py-4 md:py-6 relative transition-all duration-300'}>{updateAvailable&&(<div className="fixed inset-0 z-[9999] bg-slate-900/90 backdrop-blur-sm flex flex-col items-center justify-center p-6 text-center animate-in fade-in duration-300"><div className="bg-white p-6 rounded-3xl shadow-2xl max-w-sm w-full"><div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce"><RefreshCcw className="w-10 h-10 text-blue-600"/></div><h2 className="text-2xl font-black text-slate-800 mb-2">Update Tersedia!</h2><p className="text-slate-500 text-sm mb-2">Versi aplikasi Anda usang (v{CLIENT_VERSION}).<br/>Aplikasi akan diperbarui ke <strong>versi {newVersion}</strong>.</p>{!updateGagalBerulang&&(<p className="text-[12px] font-semibold mb-5">{view === 'form' ? (<span className="text-amber-600">Menunggu Anda menyelesaikan form absen. Update berjalan otomatis setelah keluar dari form.</span>) : (<span className="text-blue-600">Memuat ulang otomatis dalam {updateHitungMundur === null ? 5 : updateHitungMundur} detik…</span>)}</p>)}{updateGagalBerulang&&(<div className="mb-5" />)}<button onClick={performUpdate} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-blue-200 active:scale-95 transition-all flex items-center justify-center gap-2"><RefreshCcw className="w-5 h-5 animate-spin"/>Update Sekarang</button>{updateGagalBerulang ? (<><p className="text-[11px] text-amber-600 font-semibold mt-4 leading-relaxed">Sudah beberapa kali dimuat ulang tetapi versinya tetap sama. Kemungkinan besar file aplikasi versi baru belum diunggah ke server — bukan kesalahan HP Anda.</p><button onClick={lanjutTanpaUpdate} className="w-full mt-3 border border-slate-300 text-slate-600 font-bold py-3 rounded-xl active:scale-95 transition-all">Lanjutkan dengan versi ini</button><p className="text-[10px] text-slate-400 mt-3">Absensi tetap dapat dipakai. Laporkan pesan ini ke Admin.</p></>) : (<p className="text-[10px] text-slate-400 mt-4">*Cache aplikasi dibersihkan lalu halaman dimuat ulang otomatis.</p>)}</div></div>)}{/* Bar biru generik. 'form' dikecualikan (Agu 2026): layar itu sekarang
       punya kepala sendiri yang menyebutkan jenis pengajuannya, jadi bar ini
       hanya menghasilkan judul dobel — "Menu Form" di atas "Form Ijin". */}
    {view !== 'login' && view !== 'dashboard' && view !== 'form' && view !== 'ganti_password' && view !== 'rekap_admin' && view !== 'histori_tim' && (
      <header className="bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 text-white border border-slate-800/80 shadow-lg shadow-slate-950/20 backdrop-blur-xl p-3.5 sm:p-4 md:px-6 md:py-4 flex justify-between items-center z-10 relative rounded-2xl mb-4 md:mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 md:h-10 md:w-10 items-center justify-center rounded-xl bg-gradient-to-tr from-blue-600 to-cyan-500 p-2 shadow-md shadow-blue-500/20 ring-1 ring-white/20">
            <Activity className="h-full w-full text-white" />
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-cyan-300/80">Menu Navigasi</p>
            <h2 className="text-base sm:text-lg font-black tracking-tight text-white leading-tight">
              {view === 'history' ? 'Riwayat & Laporan' :
               view === 'admin' ? 'Panel Administrator' :
               view === 'approval' ? 'Approval Pengajuan' :
               view === 'db_absen' ? 'Data Mesin Fingerprint' :
               view === 'analysis' ? 'Analisa Kehadiran' :
               view === 'gps_audit' ? 'Monitoring Integritas GPS' :
               view === 'gps_dashboard' ? 'Dashboard GPS Karyawan' :
               view === 'dashboard_kerani' ? 'Absen Online Kerani Pabrik' :
               view === 'input_shift' ? 'Jadwal Running Shift' :
               view === 'remark' ? 'Respon / Lapor HRD' :
               view === 'ganti_password' ? 'Ubah Kata Sandi' : 'Menu Aplikasi'}
            </h2>
          </div>
        </div>
        <button
          onClick={() => setView('dashboard')}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 border border-white/15 backdrop-blur-md text-xs font-bold text-white transition-all active:scale-95 shadow-sm"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Kembali ke Dashboard</span>
          <span className="sm:hidden">Kembali</span>
        </button>
      </header>
    )}<div className="p-0">{view==='login'&&<LoginScreen onLogin={handleLogin}/>}{view==='dashboard'&&<Dashboard user={user} setUser={setUser} setView={setView} handleLogout={handleLogout} masterData={masterData} approvalNotice={approvalNotice} setApprovalNotice={setApprovalNotice} bootAwal={bootAwal} bootMenunggu={bootMenunggu} notifKaryawan={notifKaryawan.notif} tandaiNotif={notifKaryawan.tandaiDibaca}/>}{view==='form'&&<AttendanceForm user={user} setUser={setUser} setView={setView} editItem={editItem} setEditItem={setEditItem} masterData={masterData}/>}{view==='history'&&<HistoryScreen user={user} setView={setView} setEditItem={setEditItem} masterData={masterData}/>}{view==='db_absen'&&<DbAbsenScreen user={user} setView={setView}/>}{view==='admin'&&<AdminPanel user={user} setView={setView} masterData={masterData} setMasterData={setMasterData}/>}{view==='approval'&&<ApprovalScreen user={user} setView={setView}/>}{view==='ganti_password'&&<ChangePasswordScreen user={user} setView={setView}/>}{view==='remark'&&<RemarkScreen user={user} setView={setView}/>}{view==='input_shift'&&<ShiftScheduleScreen user={user} setView={setView} masterData={masterData}/>}{view==='analysis'&&<AnalysisScreen user={user} setView={setView}/>}{view==='rekap_admin'&&<RekapExcelScreen user={user} setView={setView} fetchApi={fetchApi}/>}{view==='histori_tim'&&<RekapExcelScreen user={user} setView={setView} fetchApi={fetchApi} modeTim/>}{view==='gps_audit'&&<GpsAuditScreen user={user} setView={setView} fetchApi={fetchApi}/>}{view==='gps_dashboard'&&<GpsDashboardScreen user={user} setView={setView} fetchApi={fetchApi}/>}{view==='log_login'&&<LogLoginScreen user={user} setView={setView} fetchApi={fetchApi}/>}{view==='dashboard_kerani'&&<DashboardKerani user={user} setView={setView} fetchApi={fetchApi}/>}</div>{user&&<ImportNotifier/>}{user && !gpsGate.lolos && (
      <GpsGateScreen
        status={gpsGate.status}
        pesan={gpsGate.pesan}
        memeriksa={gpsGate.memeriksa}
        namaUser={user.nama}
        bolehDarurat={bolehGpsDarurat}
        onPeriksaUlang={() => periksaGerbangGps()}
        onKeluar={handleLogout}
        onLanjutDarurat={lanjutTanpaGps}
      />
    )}{user && view !== 'login' && <TombolKembaliKeAtas />}</div></div>);}

    // PEMBUNGKUS APLIKASI
    // Provider dipasang di luar komponen utama, bukan di dalamnya, supaya
    // job import tidak ikut ter-reset setiap kali AppAbsensiInner render ulang.
export default function AppAbsensi() {
  return (
    <ImportJobProvider>
      <AppAbsensiInner />
    </ImportJobProvider>
  );
}

// Satu kartu absen (masuk / pulang).
// Tiga keadaan, dan hanya SATU yang boleh terlihat menonjol pada saat
// yang sama — itu yang membuat orang tidak perlu berpikir tombol mana
// yang harus ditekan sekarang.
//   utama    : aksi yang wajar dilakukan berikutnya -> tombol terisi warna
//   menunggu : belum waktunya (mis. pulang sebelum masuk) -> redup
//   selesai  : sudah tercatat -> menampilkan jam, bukan ajakan
const KartuAbsen = ({ onClick, label, jam, Ikon, warna, keadaan }) => {
  const selesai = keadaan === 'selesai';
  const utama = keadaan === 'utama';

  const gaya = selesai
      ? `bg-white border-slate-200/80 ${warna.teksSelesai}`
      : utama
          ? `${warna.isi} border-transparent text-white ${warna.bayangan}`
          : 'bg-white border-slate-200/80 text-slate-400';

  return (
      <button
          onClick={onClick}
          className={`relative flex-1 min-w-0 rounded-2xl border p-3.5 text-left transition-all duration-200 active:scale-[0.97] ${gaya}`}
      >
          <div className="flex items-center gap-2">
              <span className={`w-7 h-7 shrink-0 rounded-lg flex items-center justify-center
                  ${selesai ? warna.lembut : utama ? 'bg-white/20' : 'bg-slate-100'}`}>
                  <Ikon className="w-[15px] h-[15px]" strokeWidth={2.1} />
              </span>
              <span className={`text-[11px] font-semibold tracking-tight truncate
                  ${utama ? 'text-white/90' : selesai ? 'text-slate-500' : 'text-slate-400'}`}>
                  {label}
              </span>
          </div>

          <p className={`mt-2.5 font-semibold tabular-nums tracking-tight leading-none
              ${jam ? 'text-[26px]' : 'text-[26px] opacity-40'}`}>
              {jam || '--:--'}
          </p>

          <p className={`mt-1.5 text-[10px] font-medium leading-tight
              ${utama ? 'text-white/70' : selesai ? warna.teksSelesai : 'text-slate-300'}`}>
              {selesai
                  ? 'Sudah tercatat'
                  : utama ? 'Ketuk untuk absen' : 'Belum tercatat'}
          </p>

          {selesai && (
              <span className={`absolute top-3 right-3 ${warna.teksSelesai}`}>
                  <CheckCircle className="w-4 h-4" strokeWidth={2.2} />
              </span>
          )}
      </button>
  );
};

// Ikon cuaca dari kode WMO Open-Meteo. Jika data cuaca belum tersedia,
// fallback tetap mengikuti waktu hari agar header tidak pernah kosong.
const IkonCuaca = ({ kode, malam }) => {
  if (kode === null || kode === undefined) return malam ? <Moon className="h-4 w-4 text-indigo-100" strokeWidth={1.8} /> : <CloudSun className="h-4 w-4 text-amber-200" strokeWidth={1.8} />;
  if (kode === 0) return malam ? <Moon className="h-4 w-4 text-indigo-100" strokeWidth={1.8} /> : <Sun className="h-4 w-4 text-amber-200" strokeWidth={1.8} />;
  if ([1, 2, 3, 45, 48].includes(kode)) return <Cloud className="h-4 w-4 text-blue-100" strokeWidth={1.8} />;
  if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(kode)) return <CloudRain className="h-4 w-4 text-blue-100" strokeWidth={1.8} />;
  if ([71, 73, 75, 77].includes(kode)) return <Snowflake className="h-4 w-4 text-blue-100" strokeWidth={1.8} />;
  if ([95, 96, 99].includes(kode)) return <CloudLightning className="h-4 w-4 text-amber-200" strokeWidth={1.8} />;
  return <CloudSun className="h-4 w-4 text-amber-200" strokeWidth={1.8} />;
};

    // DASHBOARD SCREEN (CLICKABLE STATS)
// Label periode dalam format DD/MM/YYYY - DD/MM/YYYY.
//
// Sengaja mengolah STRING 'YYYY-MM-DD' apa adanya, tanpa new Date(): mem-parse
// tanggal polos jadi objek Date membuatnya dibaca sebagai tengah malam UTC,
// lalu ditampilkan ulang menurut zona waktu perangkat — di WIB (UTC+7) itu
// aman, tapi di perangkat yang zonanya di barat GMT tanggalnya mundur satu
// hari. Memecah stringnya menghindarkan seluruh persoalan itu.
function labelPeriodeDDMM(mulai, selesai) {
  const susun = (ymd) => {
    const bagian = String(ymd || '').split('-');
    return bagian.length === 3 ? `${bagian[2]}/${bagian[1]}/${bagian[0]}` : String(ymd || '');
  };
  if (!mulai || !selesai) return '';
  return `${susun(mulai)} - ${susun(selesai)}`;
}

function Dashboard({ user, setUser, setView, handleLogout, masterData, approvalNotice, setApprovalNotice, bootAwal, bootMenunggu, notifKaryawan, tandaiNotif }) {
  const lencanaNotif = hitungLencana(notifKaryawan);
  const [time, setTime] = useState(() => getServerNow());
  const [cuaca, setCuaca] = useState(null);

  // STATISTIK YANG SUDAH IKUT DI RESPONS LOGIN (Agu 2026).
  const [statsAwal] = useState(() => {
    try {
      const raw = sessionStorage.getItem('app_stats_awal');
      if (!raw) return null;
      sessionStorage.removeItem('app_stats_awal');
      return JSON.parse(raw);
    } catch (e) { return null; }
  });

  const [stats, setStats] = useState(() => {
    try {
      const rawAwal = sessionStorage.getItem('app_stats_awal');
      if (rawAwal) return JSON.parse(rawAwal);
      const rawTerakhir = sessionStorage.getItem('app_stats_terakhir');
      if (rawTerakhir) return JSON.parse(rawTerakhir);
    } catch (e) { /* ignore */ }
    return { total_hadir: 0, total_ijin: 0, total_telat_freq: 0, total_telat_menit: 0, total_cuti: 0, total_cuti_bersama: 0, total_sakit: 0, total_alpa: 0, total_no_scan_in: 0, total_no_scan_out: 0, periode_db: '-' };
  });

  const [loadingStats, setLoadingStats] = useState(() => {
    try {
      const rawAwal = sessionStorage.getItem('app_stats_awal');
      const rawTerakhir = sessionStorage.getItem('app_stats_terakhir');
      return !(rawAwal || rawTerakhir);
    } catch (e) { return true; }
  });

  const [showNews, setShowNews] = useState(false);
  const [newsContent, setNewsContent] = useState(null);

  // POPUP APPROVAL (Nov 2026): notifikasi untuk tim approval tampil di
  // DUA tempat — popup seperti "Informasi HRD" + banner di dashboard.
  // Popup punya penanda sesi sendiri, jadi menutup popup TIDAK
  // menghilangkan banner; banner tetap sampai ditutup/dibuka sendiri.
  const kunciPopupApproval = user ? `approval_popup_seen_${user.id}` : '';
  const [popupApprovalDitutup, setPopupApprovalDitutup] = useState(() => {
    try { return !!kunciPopupApproval && sessionStorage.getItem(kunciPopupApproval) === '1'; }
    catch (e) { return false; }
  });
  const tutupPopupApproval = () => {
    try { if (kunciPopupApproval) sessionStorage.setItem(kunciPopupApproval, '1'); } catch (e) { /* abaikan */ }
    setPopupApprovalDitutup(true);
  };
  // Popup approval menunggu giliran: kalau pengumuman HRD sedang tampil,
  // popup approval baru muncul setelah pengumuman HRD ditutup.
  const tampilPopupApproval = !!approvalNotice && !popupApprovalDitutup && !showNews;
  const [statsError, setStatsError] = useState('');
  const [statsRetry, setStatsRetry] = useState(0);

  // PEMILIH PERIODE (Agu 2026).
  const [periodeOpsi, setPeriodeOpsi] = useState(() => {
    try {
      const raw = sessionStorage.getItem('app_periods_awal');
      if (raw) {
        const p = JSON.parse(raw);
        if (Array.isArray(p.periodsAktif) && p.periodsAktif.length) return p.periodsAktif;
      }
    } catch (e) { /* ignore */ }
    return [];
  });

  const [periodeIdPilih, setPeriodeIdPilih] = useState(() => {
    try {
      const raw = sessionStorage.getItem('app_periods_awal');
      if (raw) {
        const p = JSON.parse(raw);
        if (p.periodeDefault && p.periodeDefault.id) return p.periodeDefault.id;
        if (Array.isArray(p.periodsAktif) && p.periodsAktif.length) return p.periodsAktif[0].id;
      }
    } catch (e) { /* ignore */ }
    return '';
  });

  const lewatiRefetchPeriodeAwal = useRef(false);

  // Daftar periode yang ikut di respons buka_aplikasi (sesi dipulihkan).
  useEffect(() => {
    if (!bootAwal) return;
    const aktif = Array.isArray(bootAwal.periodsAktif) ? bootAwal.periodsAktif : [];
    if (aktif.length) setPeriodeOpsi(aktif);
    // Disimpan juga untuk kunci periode lalu di form & Lapor HRD.
    if (aktif.length) {
      try { sessionStorage.setItem('app_periods_awal', JSON.stringify({ periodsAktif: aktif, periodeDefault: bootAwal.periodeDefault || null })); } catch (e) { /* abaikan */ }
    }

    // Periode yang BENAR-BENAR dipakai server saat menghitung stats di
    // respons ini. Pemilih disamakan dengan itu, bukan dengan pilihan
    // lama dari sessionStorage — kalau admin mengganti periode aktif,
    // pilihan lama bisa basi dan label periode di layar akan bercerita
    // beda dari angka yang ditampilkan.
    const idDipakai = (bootAwal.periode && bootAwal.periode.id)
      || (bootAwal.periodeDefault && bootAwal.periodeDefault.id)
      || (aktif.length ? aktif[0].id : '');

    if (idDipakai && idDipakai !== periodeIdPilih) {
      lewatiRefetchPeriodeAwal.current = true;
      setPeriodeIdPilih(idDipakai);
    }
    // periodeIdPilih sengaja tidak masuk daftar: efek ini hanya mengisi
    // nilai AWAL, dan ikut berjalan setiap kali user mengganti periode
    // justru akan menimpanya kembali ke default.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootAwal]);

  useEffect(() => {
    if (periodeOpsi.length > 0) return;
    // Jawaban buka_aplikasi sudah membawa daftar periode; menembak
    // get_absence_period sekarang berarti satu eksekusi Apps Script
    // untuk data yang sedang dalam perjalanan.
    if (bootMenunggu) return;
    let batal = false;
    (async () => {
      try {
        const d = await (await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'get_absence_period' }) })).json();
        if (batal || d.result !== 'success') return;
        const aktif = Array.isArray(d.periodsAktif) ? d.periodsAktif : (d.period ? [d.period] : []);
        setPeriodeOpsi(aktif);
        try { sessionStorage.setItem('app_periods_awal', JSON.stringify({ periodsAktif: aktif, periodeDefault: d.periodeDefault || null })); } catch (e) { /* abaikan */ }
        const idDefault = (d.periodeDefault && d.periodeDefault.id) || (aktif.length ? aktif[0].id : '');
        if (idDefault) { lewatiRefetchPeriodeAwal.current = true; setPeriodeIdPilih(idDefault); }
      } catch (e) { /* pemilih periode tidak muncul; dashboard tetap jalan */ }
    })();
    return () => { batal = true; };
  }, [periodeOpsi.length, bootMenunggu]);

  const lewatiFetchStatsAwal = useRef(!!statsAwal);

  // Angka dashboard dari respons buka_aplikasi sudah dipakai — supaya
  // tidak diterapkan ulang saat user mengganti periode.
  const bootStatsTerpakai = useRef(false);
  // Pengumuman sudah diurus sekali di sesi ini.
  const pengumumanDitangani = useRef(false);

    // LOGIC FETCH PENGUMUMAN / INFO HRD
useEffect(() => { (async () => {
  if (sessionStorage.getItem('announcement_shown')) return;
  if (pengumumanDitangani.current) return;

  // JALUR CEPAT: pengumuman sudah ikut di respons login (lihat handleLogin).
  // Dipakai sekali lalu dihapus, supaya reload halaman mengambil yang terbaru.
  //
  // Yang disimpan objek pembungkus {isi: ...}, bukan nilainya langsung —
  // sehingga "sudah dicek dan memang kosong" bisa dibedakan dari
  // "belum pernah dicek". Kalau tidak dibedakan, setiap user yang tidak
  // punya pengumuman aktif akan tetap menembak request ini sia-sia.
  try {
    const raw = sessionStorage.getItem('app_pengumuman_awal');
    if (raw) {
      sessionStorage.removeItem('app_pengumuman_awal');
      const bungkus = JSON.parse(raw);
      if (bungkus && Object.prototype.hasOwnProperty.call(bungkus, 'isi')) {
        if (bungkus.isi) {
          setNewsContent(bungkus.isi);
          setShowNews(true);
        }
        return;
      }
    }
  } catch (e) { /* cache rusak: lanjut ambil dari server */ }

  // JALUR CEPAT KEDUA: pengumuman ikut di respons buka_aplikasi saat sesi
  // dipulihkan. Tanpa ini, setiap pembukaan aplikasi menembak
  // get_latest_announcement — satu eksekusi Apps Script penuh — bahkan
  // ketika memang tidak ada pengumuman aktif sama sekali, karena penanda
  // 'announcement_shown' baru ditulis saat karyawan MENUTUP kotak
  // pengumuman, dan kotak itu tidak pernah muncul kalau isinya kosong.
  if (bootAwal) {
    pengumumanDitangani.current = true;
    if (bootAwal.pengumumanDisertakan) {
      if (bootAwal.pengumuman) {
        setNewsContent(bootAwal.pengumuman);
        setShowNews(true);
      }
      return;
    }
    // pengumumanDisertakan false = server gagal membacanya. Ambil sendiri
    // seperti dulu, jangan diam-diam menganggap tidak ada pengumuman.
  } else if (bootMenunggu) {
    // Jawabannya masih di perjalanan — jangan menembak request kedua.
    return;
  } else {
    pengumumanDitangani.current = true;
  }

  try { const d = await (await fetchApi(SCRIPT_URL, {method:'POST', body:JSON.stringify({action:'get_latest_announcement'})})).json(); if(d.result==='success'&&d.data){ setNewsContent(d.data); setShowNews(true); } } catch(e){ console.error(e); }
})(); }, [bootAwal, bootMenunggu]);
  
    // LOGIC TIMER / DETAK JAM REAL-TIME
useEffect(() => { const t = setInterval(() => setTime(getServerNow()), 1000); return () => clearInterval(t); }, []);

// Cuaca bersifat tambahan saja: izin lokasi ditolak, API gagal, atau perangkat
// offline tidak boleh menghambat proses login maupun absensi.
useEffect(() => {
  let hidup = true;
  if (!navigator.geolocation) return () => { hidup = false; };
  navigator.geolocation.getCurrentPosition(async ({ coords }) => {
    try {
      const params = new URLSearchParams({
        latitude: String(coords.latitude),
        longitude: String(coords.longitude),
        current: 'temperature_2m,weather_code',
        timezone: 'auto'
      });
      const response = await fetch(`https://api.open-meteo.com/v1/forecast?${params.toString()}`);
      if (!response.ok) return;
      const data = await response.json();
      if (hidup && data.current) setCuaca({ suhu: data.current.temperature_2m, kode: data.current.weather_code });
    } catch (e) { /* cuaca opsional — abaikan gangguan jaringan */ }
  }, () => {}, { enableHighAccuracy: false, timeout: 5000, maximumAge: 900000 });
  return () => { hidup = false; };
}, []);

    // LOGIC FETCH STATISTIK DASHBOARD
useEffect(() => {
  // Menyalin apa adanya + versi huruf kecil semua kunci, karena beberapa
  // kartu dashboard membaca nama kunci dengan kapitalisasi berbeda.
  const terapkanStats = (s) => {
    const n = {};
    Object.keys(s).forEach(k => n[k.toLowerCase()] = s[k]);
    setStats({ ...s, ...n });
    // Disimpan supaya layar DB Absen tidak perlu menembak get_stats lagi
    // hanya untuk mengambil ijin_count.
    try { sessionStorage.setItem('app_stats_terakhir', JSON.stringify(s)); } catch (e) { /* abaikan */ }
  };

  // JALUR CEPAT: angka sudah ikut di respons login, tidak perlu request.
  // Inilah yang menghilangkan satu round trip penuh (POST + 302 redirect
  // + boot container) dari jalur membuka aplikasi.
  if (lewatiFetchStatsAwal.current) {
    lewatiFetchStatsAwal.current = false;
    terapkanStats(statsAwal);
    setLoadingStats(false);
    setStatsError('');
    return;
  }

  // JALUR CEPAT KEDUA: angka sudah ikut di respons buka_aplikasi saat
  // sesi dipulihkan. Inilah yang menghapus satu round trip get_stats —
  // request terberat di jalur membuka aplikasi, karena di dalamnya ada
  // perhitungan seluruh statistik periode.
  //
  // SENGAJA DI ATAS pemeriksaan lewatiRefetchPeriodeAwal di bawah: efek
  // pengisi periode berjalan lebih dulu pada commit yang sama dan bisa
  // menyalakan penanda itu. Kalau urutannya dibalik, angka yang sudah
  // terbawa di respons ini akan dilewati dan tidak pernah dipasang.
  if (!bootStatsTerpakai.current && bootAwal && bootAwal.stats) {
    bootStatsTerpakai.current = true;
    terapkanStats(bootAwal.stats);
    setLoadingStats(false);
    setStatsError('');
    return;
  }

  // Perubahan periode yang berasal dari default server tidak perlu ditembak
  // ulang — angkanya sudah benar sejak respons login.
  if (lewatiRefetchPeriodeAwal.current) { lewatiRefetchPeriodeAwal.current = false; return; }

  // Jawaban buka_aplikasi masih di perjalanan: angkanya sedang dibawa
  // request itu, jadi menembak get_stats sekarang hanya menggandakan
  // pekerjaan yang sama di server.
  if (bootMenunggu) return;

  const f = async () => {
    setLoadingStats(true);
    setStatsError('');
    try {
      const d = await (await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'get_stats', userId: user.id, periodeId: periodeIdPilih }) })).json();
      if (d.result === 'success') {
        terapkanStats(d.stats);
      } else {
        // Dulu cabang ini tidak ada: pesan error dari fetchApi
        // (RESPONS_BUKAN_JSON, FORBIDDEN, dsb) dibuang, dan kartu
        // Statistik tetap menampilkan nol seolah-olah itu datanya.
        setStatsError(d.message || 'Server tidak mengirim data statistik.');
      }
    } catch (e) {
      console.error('Gagal load stats:', e);
      setStatsError('Tidak bisa menghubungi server. Periksa koneksi lalu coba lagi.');
    } finally {
      setLoadingStats(false);
    }
  };
  if (user) f();
  // statsAwal sengaja tidak masuk daftar: nilainya dibekukan sekali oleh
  // useState dan tidak pernah berubah, jadi menambahkannya tidak mengubah
  // kapan efek ini berjalan.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // periodeIdPilih ikut: mengganti periode WAJIB mengambil ulang angkanya.
  // Pengambilan pertama tetap dilewati oleh lewatiFetchStatsAwal (angka sudah
  // ikut di respons login), jadi ini tidak menambah request saat buka aplikasi.
  // bootAwal/bootMenunggu ikut supaya efek ini berjalan lagi begitu jawaban
  // buka_aplikasi tiba — tanpa keduanya, angkanya ikut terbawa tapi tidak
  // pernah dipasang ke layar.
}, [user, statsRetry, periodeIdPilih, bootAwal, bootMenunggu]);

    // FUNGSI KLIK STATISTIK (NAVIGASI FILTER)
const handleStatClick = (c) => { localStorage.setItem('dbAbsenFilter', c); setView('db_absen'); };

    // LOGIKA PERSIAPAN DATA DASHBOARD
if (!user) return null;
const availableMenus = masterData.menus || [];

    // FILTER MENU BERDASARKAN HAK AKSES USER
const menusSesuaiAkses = user.akses && user.akses.length > 0 ? availableMenus.filter(item => user.akses.includes(item.value)) : availableMenus;

    // MODE ABSEN (ModeAbsen.gs): 'gabungan' | 'online' | 'mesin'.
    // Nilai dari buka_aplikasi didahulukan supaya perubahan admin langsung
    // berlaku tanpa logout. MESIN SAJA = tombol Masuk/Pulang online ditutup
    // (server juga menolaknya).
const modeAbsenUser = (bootAwal && bootAwal.modeAbsen) || user.modeAbsen || 'gabungan';
const allowedMenus = modeAbsenUser === 'mesin'
  ? menusSesuaiAkses.filter(item => item.value !== 'Hadir' && item.value !== 'Pulang')
  : menusSesuaiAkses;

    // NORMALISASI ROLE USER
const userRole = (user.role || '').toLowerCase();

    // PENENTUAN HAK AKSES (FLAGS)
const canApprove = isApprovalRole(userRole) && menuDiizinkan(user, 'approval');
// Panel: admin penuh; HRD melihat Panel versi terbatas (Rekap & Info HRD)
// kecuali master role-nya mencabut menu `panel_admin`.
const canAccessPanel = (userRole === 'admin' || userRole === 'hrd') && menuDiizinkan(user, 'panel_admin');
const isHRDOrAdmin = ['admin', 'hrd'].includes(userRole);
// Histori Tim: atasan (role approval selain Admin/HRD) melihat laporan absensi timnya.
const canHistoriTim = isApprovalRole(userRole) && !isHRDOrAdmin;
const jumlahTombolAksi = 3 + (canApprove ? 1 : 0) + (canAccessPanel ? 1 : 0) + (canHistoriTim ? 1 : 0);
const isShiftWorker = String(user.roleKode || userRole).toLowerCase() === 'karyawan_shift';

    // LOGIKA SAPAAN BERDASARKAN WAKTU
const hour = time.getHours();
let greeting = 'Selamat Pagi';

if (hour >= 11 && hour < 15) { 
    greeting = 'Selamat Siang'; 
} else if (hour >= 15 && hour < 18) { 
    greeting = 'Selamat Sore'; 
} else if (hour >= 18) { 
    greeting = 'Selamat Malam'; 
}

    // FORMAT TANGGAL BAHASA INDONESIA
const hariString = time.toLocaleDateString('id-ID', { weekday: 'long' });
const tanggalString = time.toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' });

    // KOMPONEN HELPER LOADING (SKELETON)
const Skeleton = ({ className }) => (
    <div className={`bg-gray-200 animate-pulse rounded ${className}`}></div>
);

  // Sel angka pada grid statistik. Nilai 0 sengaja diredupkan supaya
  // mata langsung tertuju ke angka yang benar-benar ada isinya.
  const StatCell = ({ label, value, tone = 'neutral', onClick }) => {
    const n = Number(value) || 0;
    const warna = n === 0
        ? 'text-slate-300'
        : tone === 'alert' ? 'text-rose-600' : 'text-slate-900';
    return (
        <button
            onClick={onClick}
            className="text-left px-3 py-3 transition-colors hover:bg-slate-50 active:bg-slate-100"
        >
            <p className="text-[10.5px] leading-tight text-slate-500 font-medium min-h-[26px]">{label}</p>
            {loadingStats
                ? <Skeleton className="h-[19px] w-6 mt-1" />
                : <p className={`mt-1 text-[19px] leading-none font-semibold tabular-nums tracking-tight ${warna}`}>{n}</p>}
        </button>
    );
  };

  // ============================================================
  // ABSEN HARI INI — sumber jam masuk & jam pulang
  //
  // Angkanya datang dari hitungStats di Apps Script (field
  // jam_masuk_hari_ini / jam_pulang_hari_ini), jadi TIDAK ada request
  // tambahan: loop sheet Absensi di sana memang sudah membaca kolom
  // waktu inputnya.
  //
  // Cadangan localStorage dipakai hanya untuk jeda beberapa detik antara
  // "form absen baru saja terkirim" dan "stats berikutnya sudah turun",
  // supaya jamnya muncul seketika. Isinya diabaikan kalau tanggalnya
  // bukan hari ini atau user-nya beda.
  const absenLokal = (() => {
    try {
      const raw = localStorage.getItem('absen_hari_ini');
      if (!raw) return {};
      const d = JSON.parse(raw);
      // tglLokal(), bukan toISOString(): toISOString memberi tanggal UTC,
      // jadi absen pukul 06.00 WIB tersimpan sebagai "kemarin" dan
      // jam-nya tidak pernah muncul.
      if (d.tgl !== tglLokal() || String(d.userId) !== String(user.id)) return {};
      return d;
    } catch (e) { return {}; }
  })();

  const jamMasuk  = stats.jam_masuk_hari_ini  || absenLokal.masuk  || '';
  const jamPulang = stats.jam_pulang_hari_ini || absenLokal.pulang || '';

  const menitDariJam = (j) => {
    if (!j) return null;
    const c = String(j).match(/^(\d{1,2}):(\d{2})/);
    return c ? Number(c[1]) * 60 + Number(c[2]) : null;
  };

  // Selisih masuk -> pulang. Kalau negatif, berarti shift yang menyeberang
  // tengah malam, bukan data salah.
  const durasiKerja = (() => {
    const a = menitDariJam(jamMasuk), b = menitDariJam(jamPulang);
    if (a === null || b === null) return '';
    const d = b >= a ? b - a : b + 1440 - a;
    return `${Math.floor(d / 60)} jam ${d % 60} menit`;
  })();

  // Tombol hanya untuk user yang memang punya akses menu-nya (lihat kolom
  // "akses" di sheet Users). Sisa menunya tetap tampil di daftar e-Form.
  const menuMasuk  = allowedMenus.find(m => m.value === 'Hadir');
  const menuPulang = allowedMenus.find(m => m.value === 'Pulang');
  const adaTombolAbsen = !!(menuMasuk || menuPulang);
  const menuEForm = allowedMenus.filter(m => m.value !== 'Hadir' && m.value !== 'Pulang');

  const bukaFormAbsen = (nilai) => {
    localStorage.setItem('absenType', nilai);
    setView('form');
  };

  const jamSekarang = time.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', hour12: false });
  const jamDigital = time.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  const namaPerusahaan = String(user.perusahaan || 'JPT').trim() || 'JPT';
  const panjangNama = String(user.nama || '').trim().length;
  const ukuranNama = panjangNama > 32 ? 'text-[18px]' : panjangNama > 24 ? 'text-[21px]' : 'text-[25px]';

  // --- RENDER UI ---
  const sHadir  = Number(stats.total_hadir) || 0;
  const sTelatX = Number(stats.total_telat_freq) || 0;
  const sTelatM = Number(stats.total_telat_menit) || 0;
  // Basis persentase: seluruh hari yang punya catatan, bukan kalender penuh.
  // HARI TERCATAT — sejak Agu 2026 dihitung server (stats.hari_tercatat),
  // yaitu jumlah tanggal yang benar-benar punya catatan DI DALAM periode
  // terpilih. Penjumlahan kategori di bawah hanya cadangan untuk backend
  // versi lama: cara itu bisa melebihi jumlah hari dalam periodenya sendiri,
  // karena satu hari bisa masuk dua kategori dan pengajuan form tidak selalu
  // punya baris di mesin.
  const sTercatat = Number(stats.hari_tercatat) > 0
    ? Number(stats.hari_tercatat)
    : sHadir
        + (Number(stats.total_ijin) || 0)
        + (Number(stats.total_cuti) || 0)
        + (Number(stats.total_cuti_bersama) || 0)
        + (Number(stats.total_sakit) || 0)
        + (Number(stats.total_alpa) || 0);
  const sPctHadir = sTercatat > 0 ? Math.round((sHadir / sTercatat) * 100) : 0;

  // RINGKASAN PENGAJUAN FORM (Agu 2026), dikirim backend di dalam `stats`.
  // Nilai cadangan penting: backend versi lama tidak mengirim field ini sama
  // sekali, dan tanpa cadangan seluruh dashboard ikut gagal render.
  const sPengajuan = stats.pengajuan || { total: 0, approved: 0, rejected: 0, pending: 0, perTipe: {}, daftar: [] };
  const sPengajuanTipe = Object.keys(sPengajuan.perTipe || {}).sort();


  return (
    <div className="min-h-screen bg-slate-50 md:bg-transparent px-4 pb-24 pt-4 md:px-0 md:pb-8 md:pt-0 font-sans flex flex-col">
      
      {/* --- KARTU PROFIL HEADER (EPIC LUXURY OBSIDIAN & SAPPHIRE THEME) --- */}
      <section className="mb-5 md:mb-6 overflow-hidden rounded-[26px] sm:rounded-[28px] border border-slate-800/80 bg-slate-950 shadow-[0_20px_50px_-15px_rgba(15,23,42,0.4)] relative">
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 px-4 sm:px-6 pt-5 pb-5 text-white">
          
          {/* Ambient Glow Effects */}
          <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-blue-500/20 blur-3xl" />
          <div className="pointer-events-none absolute -left-16 bottom-0 h-48 w-48 rounded-full bg-cyan-500/15 blur-3xl" />
          <div className="pointer-events-none absolute right-1/3 top-1/2 h-36 w-36 rounded-full bg-indigo-500/15 blur-2xl" />

          {/* 1. Baris Atas: Brand Identity & Aksi */}
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center gap-2.5 sm:gap-3">
              <div className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-2xl bg-gradient-to-tr from-blue-600 to-cyan-500 p-2 shadow-lg shadow-blue-500/25 ring-1 ring-white/20">
                <ScanFace className="h-full w-full text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] sm:text-xs font-black tracking-widest text-white uppercase">{namaPerusahaan} GROUP</span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 px-2 py-0.5 text-[8.5px] sm:text-[9px] font-bold text-emerald-300">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> Aktif
                  </span>
                </div>
                <p className="text-[9px] sm:text-[9.5px] font-medium text-slate-400 tracking-wide">Portal Kehadiran Digital</p>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setView('ganti_password')}
                className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 backdrop-blur-md text-white/90 transition-all hover:text-white active:scale-95 shadow-sm"
                title="Ubah password"
                aria-label="Ubah password"
              >
                <KeyRound className="h-4 w-4" strokeWidth={1.8} />
              </button>
              <button
                onClick={handleLogout}
                className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-xl bg-white/10 hover:bg-rose-500/20 border border-white/10 hover:border-rose-400/30 backdrop-blur-md text-white/90 hover:text-rose-200 transition-all active:scale-95 shadow-sm"
                title="Keluar aplikasi"
                aria-label="Keluar aplikasi"
              >
                <LogOut className="h-4 w-4" strokeWidth={1.8} />
              </button>
            </div>
          </div>

          {/* 2. Baris Tengah: Sapaan, Cuaca & Profil Karyawan */}
          <div className="relative z-10 mt-4 pt-3.5 border-t border-white/[0.08]">
            
            {/* Sapaan + Cuaca (Inline Capsule) */}
            <div className="flex items-center justify-start gap-2 mb-2.5">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.08] border border-white/10 px-3 py-1 text-[11px] font-medium text-slate-200 backdrop-blur-md shadow-sm">
                <IkonCuaca kode={cuaca?.kode} malam={hour >= 18 || hour < 5} />
                <span>{greeting}</span>
                {cuaca && (
                  <>
                    <span className="text-white/30">&bull;</span>
                    <span className="font-semibold text-cyan-300 tabular-nums">{Math.round(Number(cuaca.suhu))}°C</span>
                  </>
                )}
              </div>
            </div>

            {/* Identitas Karyawan (Nama + Inisial Avatar + Badge Divisi) */}
            <div className="flex items-center gap-3 mt-1">
              <div className="h-11 w-11 sm:h-12 sm:w-12 shrink-0 rounded-2xl bg-gradient-to-br from-blue-500 via-indigo-600 to-purple-600 flex items-center justify-center text-white font-black text-sm sm:text-base shadow-md shadow-indigo-500/25 ring-2 ring-white/20">
                {(user.nama || 'U').split(' ').map(n => n[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()}
              </div>

              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl md:text-2xl font-black text-white tracking-tight leading-tight truncate md:whitespace-nowrap">
                  {user.nama}
                </h1>

                <div className="flex items-center flex-wrap gap-1.5 mt-1">
                  <span className="inline-flex items-center gap-1 rounded-md bg-blue-500/20 border border-blue-400/30 px-2 py-0.5 text-[9.5px] sm:text-[10px] font-bold text-cyan-200">
                    <Layers className="w-2.5 h-2.5" /> {user.divisi || 'Karyawan'}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-md bg-white/[0.07] border border-white/10 px-2 py-0.5 text-[9.5px] sm:text-[10px] font-medium text-slate-300">
                    <MapPin className="w-2.5 h-2.5 text-rose-400" /> {user.lokasi || 'All'}
                  </span>
                </div>
              </div>
            </div>

            {/* 3. Live Digital Time Capsule Widget */}
            <div className="mt-3.5 p-3 rounded-2xl bg-white/[0.06] border border-white/10 backdrop-blur-md flex items-center justify-between shadow-inner">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-cyan-400/10 text-cyan-300 flex items-center justify-center border border-cyan-400/20 shrink-0">
                  <Clock className="w-4 h-4 animate-pulse" />
                </div>
                <div>
                  <p className="text-[9.5px] text-slate-400 font-semibold uppercase tracking-wider capitalize">{hariString}</p>
                  <p className="text-[11.5px] sm:text-[12px] font-bold text-slate-200 leading-tight">{tanggalString}</p>
                </div>
              </div>

              <div className="text-right">
                <div className="flex items-baseline gap-1 justify-end">
                  <span className="font-mono text-xl sm:text-2xl font-black text-white tabular-nums tracking-wider leading-none">
                    {jamDigital}
                  </span>
                  <span className="text-[9px] sm:text-[10px] font-bold text-cyan-400">WIB</span>
                </div>
                <span className="text-[9px] font-medium text-emerald-400 flex items-center justify-end gap-1 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping"></span> Real-time
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 4. Bagian Bawah: 4 Kartu Metrik Kunci (Glassmorphism Tiles) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-2.5 sm:p-3 bg-slate-900/90 backdrop-blur-xl border-t border-slate-800">
          
          {/* Tile 1: Perusahaan */}
          <div className="flex items-center gap-2.5 p-2 sm:p-2.5 rounded-xl bg-slate-800/60 border border-slate-700/60 hover:bg-slate-800/90 transition-colors">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-blue-500/15 text-blue-400 flex items-center justify-center shrink-0 border border-blue-500/20">
              <Building className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={1.8} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[8.5px] sm:text-[9px] font-bold uppercase tracking-wider text-slate-400">Perusahaan</p>
              <p className="truncate text-[11.5px] sm:text-[12px] font-bold text-white">{user.perusahaan || 'JPT Group'}</p>
            </div>
          </div>

          {/* Tile 2: ID Akun */}
          <div className="flex items-center gap-2.5 p-2 sm:p-2.5 rounded-xl bg-slate-800/60 border border-slate-700/60 hover:bg-slate-800/90 transition-colors">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-indigo-500/15 text-indigo-400 flex items-center justify-center shrink-0 border border-indigo-500/20">
              <CreditCard className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={1.8} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[8.5px] sm:text-[9px] font-bold uppercase tracking-wider text-slate-400">ID Payroll</p>
              <p className="truncate font-mono text-[11.5px] sm:text-[12px] font-bold text-indigo-200">{user.noPayroll || '-'}</p>
            </div>
          </div>

          {/* Tile 3: Status */}
          <div className="flex items-center gap-2.5 p-2 sm:p-2.5 rounded-xl bg-slate-800/60 border border-slate-700/60 hover:bg-slate-800/90 transition-colors">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-emerald-500/15 text-emerald-400 flex items-center justify-center shrink-0 border border-emerald-500/20">
              <User className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={1.8} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[8.5px] sm:text-[9px] font-bold uppercase tracking-wider text-slate-400">Status</p>
              <p className="truncate text-[11.5px] sm:text-[12px] font-bold text-emerald-300">{user.statusKaryawan || '-'}</p>
            </div>
          </div>

          {/* Tile 4: Sisa Cuti */}
          <div className="flex items-center gap-2.5 p-2 sm:p-2.5 rounded-xl bg-slate-800/60 border border-slate-700/60 hover:bg-slate-800/90 transition-colors">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-amber-500/15 text-amber-400 flex items-center justify-center shrink-0 border border-amber-500/20">
              <PieChart className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={1.8} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[8.5px] sm:text-[9px] font-bold uppercase tracking-wider text-slate-400">Cuti Tersedia</p>
              <p className="truncate text-[11.5px] sm:text-[12px] font-bold text-amber-300">{user.sisaCuti ?? 0} hari</p>
            </div>
          </div>

        </div>
      </section>
      
      {/* --- TOP ROW: ABSEN HARI INI & MENU SHORTCUT (Side by side on desktop) --- */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5 mb-5 md:mb-6">
        {/* --- ABSEN HARI INI --- */}
      {/* Dulu "Absen Masuk" dan "Absen Pulang" hanya dua baris di antara
          delapan baris daftar e-Form — padahal keduanya dipakai setiap hari
          oleh setiap orang, sedangkan sisanya sebulan sekali. Sekarang
          keduanya naik ke atas sebagai satu kartu tersendiri, dan jamnya
          langsung terbaca tanpa harus membuka Riwayat. */}
      {adaTombolAbsen && (
        <div className={`overflow-hidden rounded-[22px] border border-slate-200/80 bg-white shadow-[0_10px_30px_-24px_rgba(15,23,42,0.65)] ${canAccessPanel || canApprove ? "md:col-span-6 lg:col-span-5" : "md:col-span-6"}`}>

            <div className="flex items-center justify-between px-4 pb-1 pt-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-[14px] font-semibold tracking-tight text-slate-900">Absensi hari ini</h3>
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.08em] text-emerald-600">Live</span>
                </div>
                <span className="flex items-center gap-1.5 text-[11px] font-medium tabular-nums text-slate-400">
                    <Clock className="w-3 h-3" strokeWidth={2} />
                    {jamSekarang}
                </span>
            </div>

            <div className="flex gap-2.5 p-3 pt-2">
                {menuMasuk && (
                    <KartuAbsen
                        onClick={() => bukaFormAbsen('Hadir')}
                        label={menuMasuk.label || 'Absen Masuk'}
                        jam={jamMasuk}
                        Ikon={LogIn}
                        keadaan={jamMasuk ? 'selesai' : 'utama'}
                        warna={{
                            isi: 'bg-emerald-600',
                            bayangan: 'shadow-lg shadow-emerald-600/20',
                            lembut: 'bg-emerald-50',
                            teksSelesai: 'text-emerald-600',
                        }}
                    />
                )}

                {menuPulang && (
                    <KartuAbsen
                        onClick={() => bukaFormAbsen('Pulang')}
                        label={menuPulang.label || 'Absen Pulang'}
                        jam={jamPulang}
                        Ikon={LogOut}
                        keadaan={jamPulang ? 'selesai' : (jamMasuk || !menuMasuk ? 'utama' : 'menunggu')}
                        warna={{
                            isi: 'bg-rose-600',
                            bayangan: 'shadow-lg shadow-rose-600/20',
                            lembut: 'bg-rose-50',
                            teksSelesai: 'text-rose-600',
                        }}
                    />
                )}
            </div>

            {/* Baris bawah hanya muncul kalau ada yang bisa dikatakan.
                Tanpa ini, kartu punya footer kosong sepanjang pagi. */}
            {(durasiKerja || (jamMasuk && !jamPulang)) && (
                <div className="flex items-center gap-1.5 border-t border-slate-100 bg-slate-50/70 px-4 py-2.5">
                    <Timer className="w-3 h-3 text-slate-400" strokeWidth={2} />
                    <span className="text-[10.5px] font-medium text-slate-500">
                        {durasiKerja
                            ? <>Durasi kerja hari ini <span className="font-semibold text-slate-700 tabular-nums">{durasiKerja}</span></>
                            : <>Masuk pukul <span className="font-semibold text-slate-700 tabular-nums">{jamMasuk}</span> — belum absen pulang</>}
                    </span>
                </div>
            )}
        </div>
      )}
        
        {/* --- MENU SHORTCUT --- */}
      {/* Satu bar tersegmentasi. Versi lama memakai kartu terpisah dengan warna teks
          berbeda-beda + scroll horizontal, jadi scrollbar-nya ikut terlihat di layar kecil.
          Posisinya dinaikkan ke atas kartu Statistik (Agu 2026): ini navigasi, dan
          navigasi tidak boleh berada di bawah blok angka setinggi satu layar. */}
      <div className={`overflow-hidden rounded-[22px] border border-slate-200/80 bg-white shadow-[0_10px_30px_-24px_rgba(15,23,42,0.65)] flex flex-col justify-center ${adaTombolAbsen ? (canAccessPanel || canApprove ? "md:col-span-6 lg:col-span-7" : "md:col-span-6") : "col-span-12"}`}>
        <div className={`grid divide-x divide-slate-100 ${({ 3: 'grid-cols-3', 4: 'grid-cols-4', 5: 'grid-cols-5', 6: 'grid-cols-6' })[jumlahTombolAksi] || 'grid-cols-3'}`}>

            <button onClick={() => setView('history')} className="group relative flex flex-col items-center gap-2 px-1 py-3.5 transition-colors hover:bg-blue-50/50 active:bg-slate-100">
                <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition-colors group-hover:bg-blue-100 group-hover:text-blue-600"><History className="w-[17px] h-[17px]" strokeWidth={1.75} /></span>
                {lencanaNotif.history > 0 && (
                  <span className="absolute right-2 top-2.5 min-w-5 rounded-full bg-rose-600 px-1.5 py-0.5 text-[9px] font-bold leading-none text-white shadow-sm">{lencanaNotif.history}</span>
                )}
                <span className="text-[10.5px] font-medium text-slate-600 leading-tight text-center">Riwayat</span>
            </button>

            <button onClick={() => setView('db_absen')} className="group flex flex-col items-center gap-2 px-1 py-3.5 transition-colors hover:bg-blue-50/50 active:bg-slate-100">
                <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition-colors group-hover:bg-blue-100 group-hover:text-blue-600"><Fingerprint className="w-[17px] h-[17px]" strokeWidth={1.75} /></span>
                <span className="text-[10.5px] font-medium text-slate-600 leading-tight text-center">Data mesin</span>
            </button>

            <button onClick={() => setView('remark')} className="group relative flex flex-col items-center gap-2 px-1 py-3.5 transition-colors hover:bg-blue-50/50 active:bg-slate-100">
                <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition-colors group-hover:bg-blue-100 group-hover:text-blue-600"><MessageSquareText className="w-[17px] h-[17px]" strokeWidth={1.75} /></span>
                {lencanaNotif.remark > 0 && (
                  <span className="absolute right-2 top-2.5 min-w-5 rounded-full bg-rose-600 px-1.5 py-0.5 text-[9px] font-bold leading-none text-white shadow-sm">{lencanaNotif.remark}</span>
                )}
                <span className="text-[10.5px] font-medium text-slate-600 leading-tight text-center">{isHRDOrAdmin ? 'Respon' : 'Lapor HRD'}</span>
            </button>

            {canApprove && (
                <button onClick={() => setView('approval')} className="group relative flex flex-col items-center gap-2 px-1 py-3.5 transition-colors hover:bg-blue-50/50 active:bg-slate-100">
                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition-colors group-hover:bg-blue-100 group-hover:text-blue-600"><UsersRound className="w-[17px] h-[17px]" strokeWidth={1.75} /></span>
                    {approvalNotice && (
                      <span className="absolute right-2 top-2.5 min-w-5 rounded-full bg-rose-600 px-1.5 py-0.5 text-[9px] font-bold leading-none text-white shadow-sm">
                        {approvalNotice.total}
                      </span>
                    )}
                    <span className="text-[10.5px] font-medium text-slate-600 leading-tight text-center">Approval</span>
                </button>
            )}

            {canHistoriTim && (
                <button onClick={() => setView('histori_tim')} className="group flex flex-col items-center gap-2 px-1 py-3.5 transition-colors hover:bg-blue-50/50 active:bg-slate-100">
                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition-colors group-hover:bg-indigo-100 group-hover:text-indigo-600"><ClipboardList className="w-[17px] h-[17px]" strokeWidth={1.75} /></span>
                    <span className="text-[10.5px] font-medium text-slate-600 leading-tight text-center">Histori Tim</span>
                </button>
            )}

            {canAccessPanel && (
                <button onClick={() => setView('admin')} className="group flex flex-col items-center gap-2 px-1 py-3.5 transition-colors hover:bg-blue-50/50 active:bg-slate-100">
                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition-colors group-hover:bg-blue-100 group-hover:text-blue-600"><SlidersHorizontal className="w-[17px] h-[17px]" strokeWidth={1.75} /></span>
                    <span className="text-[10.5px] font-medium text-slate-600 leading-tight text-center">Panel</span>
                </button>
            )}
        </div>
      </div>
      </div>
      
      {approvalNotice && (
        <div className="mb-5 md:mb-6 overflow-hidden rounded-[22px] border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-sky-50 shadow-[0_10px_30px_-24px_rgba(79,70,229,0.45)]">
          <div className="flex items-start gap-3 px-4 py-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-indigo-600 text-white shadow-lg shadow-indigo-600/20">
              <UsersRound className="h-5 w-5" strokeWidth={1.8} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-slate-900">Ada pengajuan menunggu approval</p>
              <p className="mt-0.5 text-[11px] leading-relaxed text-slate-500">
                Halo {approvalNotice.nama}, terdapat <span className="font-semibold text-slate-800">{approvalNotice.total}</span> pengajuan yang bisa Anda setujui langsung di aplikasi.
              </p>
              {approvalNotice.divisiCounts && Object.keys(approvalNotice.divisiCounts).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {Object.entries(approvalNotice.divisiCounts).slice(0, 4).map(([divisi, total]) => (
                    <span key={divisi} className="rounded-full bg-white px-2 py-1 text-[10px] font-semibold text-slate-600 ring-1 ring-indigo-100">
                      {divisi}: {total}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={() => {
                    sessionStorage.setItem(`approval_notice_seen_${user.id}`, '1');
                    tutupPopupApproval();
                    setApprovalNotice(null);
                    setView('approval');
                  }}
                  className="rounded-xl bg-indigo-600 px-3.5 py-2 text-[11px] font-semibold text-white transition hover:bg-indigo-700 active:scale-[0.98]"
                >
                  Buka approval
                </button>
                <button
                  onClick={() => {
                    sessionStorage.setItem(`approval_notice_seen_${user.id}`, '1');
                    tutupPopupApproval();
                    setApprovalNotice(null);
                  }}
                  className="rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-[11px] font-semibold text-slate-600 transition hover:bg-slate-50 active:scale-[0.98]"
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <NotifPengajuanCard notif={notifKaryawan} tandaiDibaca={tandaiNotif} setView={setView} />

      {/* --- RESPONSIVE 2-COLUMN SECTION ON DESKTOP --- */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* LEFT COLUMN: STATISTIK & RINGKASAN PENGAJUAN */}
        <div className="lg:col-span-7 xl:col-span-8 space-y-6">
          {/* --- STATISTIK (CLICKABLE) --- */}
      <div className="mb-5">

        <div className="mb-3 flex items-center justify-between px-1">
            <div className="flex items-center gap-2">
                <h3 className="text-base md:text-lg font-bold tracking-tight text-slate-900">Ringkasan Kehadiran</h3>
                {loadingStats && <Loader2 className="w-3.5 h-3.5 text-slate-400 animate-spin"/>}
            </div>
            {periodeOpsi.length <= 1 && (
              <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-medium text-slate-400 shadow-sm ring-1 ring-slate-200/80">
                  {loadingStats
                    ? "Menyinkronkan…"
                    : statsError
                      ? <span className="text-red-500 font-semibold">gagal dimuat</span>
                      : (periodeOpsi.length === 1
                          ? labelPeriodeDDMM(periodeOpsi[0].mulai, periodeOpsi[0].selesai)
                          : '') || stats.periode_db}
              </span>
            )}
        </div>

        {/* PEMILIH PERIODE — hanya muncul kalau admin memang mengaktifkan lebih
            dari satu. Satu periode aktif berarti tidak ada yang perlu dipilih,
            jadi labelnya cukup ditampilkan seperti sebelumnya (di header atas). */}
        {periodeOpsi.length > 1 && (
          <div className="mb-3 -mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {periodeOpsi.map((p) => {
              const dipilih = p.id === periodeIdPilih;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPeriodeIdPilih(p.id)}
                  aria-pressed={dipilih}
                  className={`shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-[11px] font-medium transition-colors ${
                    dipilih ? 'bg-slate-900 text-white shadow-sm' : 'bg-white text-slate-500 ring-1 ring-slate-200/80 hover:bg-slate-50'
                  }`}
                >
                  {labelPeriodeDDMM(p.mulai, p.selesai) || p.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Gagal ambil statistik — angka nol di bawah BUKAN data asli. */}
        {!loadingStats && statsError && (
            <div className="mb-2.5 bg-red-50 border border-red-200 rounded-xl p-3">
                <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                        <p className="text-[11px] font-bold text-red-700">Statistik gagal dimuat</p>
                        <p className="text-[10px] text-red-600 leading-relaxed mt-0.5">{statsError}</p>
                        <p className="text-[10px] text-red-500 italic mt-0.5">Angka 0 di bawah bukan data Anda — server belum sempat mengirimnya.</p>
                        <button
                            onClick={() => setStatsRetry(r => r + 1)}
                            className="mt-2 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg text-[10px] font-bold flex items-center gap-1.5 transition active:scale-95"
                        >
                            <RefreshCcw className="w-3 h-3" /> Coba Lagi
                        </button>
                    </div>
                </div>
            </div>
        )}

        <div className={`overflow-hidden rounded-[22px] border bg-white shadow-[0_10px_30px_-24px_rgba(15,23,42,0.65)] ${statsError && !loadingStats ? 'border-red-200 opacity-60' : 'border-slate-200/80'}`}>

            {/* BARIS UTAMA — dua angka yang paling sering dilihat */}
            <div className="grid grid-cols-2 divide-x divide-slate-100">

                {/* HADIR -> Filter 'HADIR_ALL' */}
                <button onClick={() => handleStatClick('HADIR_ALL')} className="text-left p-4 transition-colors hover:bg-slate-50 active:bg-slate-100">
                    <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                        <span className="text-[11px] font-medium text-slate-500">Hadir</span>
                    </div>
                    {loadingStats ? <Skeleton className="h-8 w-14 mt-2.5"/> : (
                        <>
                            <p className="mt-2 flex items-baseline gap-1">
                                <span className="text-[32px] leading-none font-semibold text-slate-900 tabular-nums tracking-tighter">{sHadir}</span>
                                <span className="text-[11px] font-medium text-slate-400">hari</span>
                            </p>
                            <div className="mt-3 h-[3px] rounded-full bg-slate-100 overflow-hidden">
                                <div className="h-full rounded-full bg-emerald-500 transition-all duration-700" style={{ width: `${sPctHadir}%` }}></div>
                            </div>
                            <p className="mt-1.5 text-[10px] text-slate-400 tabular-nums">{sPctHadir}% dari {sTercatat} hari tercatat</p>
                        </>
                    )}
                </button>

                {/* TELAT -> Filter 'T' */}
                <button onClick={() => handleStatClick('T')} className="text-left p-4 transition-colors hover:bg-slate-50 active:bg-slate-100">
                    <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                        <span className="text-[11px] font-medium text-slate-500">Telat</span>
                    </div>
                    {loadingStats ? <Skeleton className="h-8 w-14 mt-2.5"/> : (
                        <>
                            <p className="mt-2 flex items-baseline gap-1">
                                <span className={`text-[32px] leading-none font-semibold tabular-nums tracking-tighter ${sTelatX > 0 ? 'text-slate-900' : 'text-slate-300'}`}>{sTelatX}</span>
                                <span className="text-[11px] font-medium text-slate-400">kali</span>
                            </p>
                            <p className="mt-3 flex items-baseline gap-1.5">
                                <span className="text-[13px] font-semibold text-amber-600 tabular-nums leading-none">{sTelatM}</span>
                                <span className="text-[10px] text-slate-400">menit total</span>
                            </p>
                            <p className="mt-1 text-[10px] text-slate-400 tabular-nums">
                                {sTelatX > 0 ? `rata-rata ${Math.round(sTelatM / sTelatX)} menit` : 'selalu tepat waktu'}
                            </p>
                        </>
                    )}
                </button>
            </div>

            {/* KELOMPOK 1 — ketidakhadiran yang sudah berizin */}
            <div className="border-t border-slate-100 bg-slate-50/70 px-4 py-1.5">
                <span className="text-[9.5px] font-semibold uppercase tracking-[0.12em] text-slate-400">Izin &amp; cuti</span>
            </div>
            <div className="grid grid-cols-4 divide-x divide-slate-100 border-t border-slate-100">
                <StatCell label="Ijin"          value={stats.total_ijin}         onClick={() => handleStatClick('I')} />
                <StatCell label="Cuti diambil"  value={stats.total_cuti}         onClick={() => handleStatClick('C')} />
                <StatCell label="Cuti bersama"  value={stats.total_cuti_bersama} onClick={() => handleStatClick('CB')} />
                <StatCell label="Sakit"         value={stats.total_sakit}        onClick={() => handleStatClick('S')} />
            </div>

            {/* KELOMPOK 2 — yang perlu ditindaklanjuti */}
            <div className="border-t border-slate-100 bg-slate-50/70 px-4 py-1.5">
                <span className="text-[9.5px] font-semibold uppercase tracking-[0.12em] text-slate-400">Perlu perhatian</span>
            </div>
            <div className="grid grid-cols-3 divide-x divide-slate-100 border-t border-slate-100">
                <StatCell label="Alpa"            value={stats.total_alpa}         tone="alert" onClick={() => handleStatClick('A')} />
                <StatCell label="Tidak absen-in"  value={stats.total_no_scan_in}   tone="alert" onClick={() => handleStatClick('Si')} />
                <StatCell label="Tidak absen-out" value={stats.total_no_scan_out}  tone="alert" onClick={() => handleStatClick('So')} />
            </div>
        </div>
      </div>
          
          {/* --- RINGKASAN PENGAJUAN FORM --- */}
      <div className="mb-5">
        <div className="mb-3 flex items-center justify-between px-1">
          <h3 className="text-base md:text-lg font-bold tracking-tight text-slate-900">Ringkasan Pengajuan Form</h3>
          <span className="text-xs font-medium text-slate-400">Periode Terpilih</span>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          {/* Yang ditampilkan hanyalah yang menuntut tindakan.
              Ditolak dan Menunggu adalah satu-satunya status yang perlu
              dikerjakan orangnya; saat keduanya nol — kondisi yang paling
              sering terjadi — dua kotak angka nol cuma memakan tempat dan
              membuat mata melewatinya saat suatu hari benar-benar berisi.
              Status dibedakan lewat ikon + label, bukan warna saja. */}
          {!loadingStats && sPengajuan.total > 0 && (
            (sPengajuan.pending > 0 || sPengajuan.rejected > 0) ? (
              <div className="divide-y divide-slate-100 border-b border-slate-200">
                {sPengajuan.pending > 0 && (
                  <button type="button" onClick={() => setView('history')} className="flex w-full items-center gap-2 bg-amber-50/60 px-3 py-2.5 text-left transition-colors hover:bg-amber-50">
                    <Clock className="h-4 w-4 shrink-0 text-amber-600" strokeWidth={2.2} />
                    <span className="flex-1 text-[12px] font-medium text-amber-900">
                      <span className="tabular-nums">{sPengajuan.pending}</span> pengajuan menunggu persetujuan
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-amber-400" />
                  </button>
                )}
                {sPengajuan.rejected > 0 && (
                  <button type="button" onClick={() => setView('history')} className="flex w-full items-center gap-2 bg-rose-50/60 px-3 py-2.5 text-left transition-colors hover:bg-rose-50">
                    <X className="h-4 w-4 shrink-0 text-rose-600" strokeWidth={2.5} />
                    <span className="flex-1 text-[12px] font-medium text-rose-900">
                      <span className="tabular-nums">{sPengajuan.rejected}</span> ditolak &mdash; lihat alasannya
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-rose-400" />
                  </button>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2 border-b border-slate-200 bg-emerald-50/50 px-3 py-2.5">
                <Check className="h-4 w-4 shrink-0 text-emerald-600" strokeWidth={2.5} />
                <span className="text-[12px] font-medium text-emerald-900">
                  <span className="tabular-nums">{sPengajuan.total}</span> pengajuan, semuanya disetujui
                </span>
              </div>
            )
          )}

          {loadingStats && (
            <div className="border-b border-slate-200 px-3 py-3"><span className="block h-4 w-40 animate-pulse rounded bg-slate-100" /></div>
          )}

          {sPengajuanTipe.length > 0 && (
            <table className="w-full table-fixed border-collapse">
              {/* Kolom Jenis melar mengisi sisa ruang; lima kolom angka
                  dipatok selebar SAMA (54px). Angkanya dipilih dari isi
                  terlebar yang mungkin muncul (header "Tunggu" dan nilai sisa
                  dua digit "10 hari"), lalu diperiksa terhadap layar HP
                  tersempit: 5 x 48px menyisakan ~88px untuk kolom Jenis di
                  layar 360px. Memakai 54px terlihat lebih lega di layar besar
                  tapi menyisakan hanya 58px di 360px — "Dinas Luar" terpotong. */}
              <colgroup>
                <col />
                <col className="w-[48px]" />
                <col className="w-[48px]" />
                <col className="w-[48px]" />
                <col className="w-[48px]" />
                <col className="w-[48px]" />
              </colgroup>
              <thead>
                <tr className="bg-white">
                  <th className="border-b border-slate-200 px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">Jenis</th>
                  <th className="border-b border-l border-slate-200 px-1 py-1.5 text-right text-[10px] font-semibold text-slate-500">Jml</th>
                  <th className="border-b border-l border-slate-200 px-1 py-1.5 text-right text-[10px] font-semibold text-slate-500">OK</th>
                  <th className="border-b border-l border-slate-200 px-1 py-1.5 text-right text-[10px] font-semibold text-slate-500">Tolak</th>
                  <th className="border-b border-l border-slate-200 px-1 py-1.5 text-right text-[10px] font-semibold text-slate-500">Tunggu</th>
                  <th className="border-b border-l border-slate-200 px-1 py-1.5 text-right text-[10px] font-semibold text-slate-500">Sisa</th>
                </tr>
              </thead>
              <tbody>
                {sPengajuanTipe.map((t) => {
                  const r = sPengajuan.perTipe[t] || {};
                  const sel = 'border-b border-l border-slate-100 px-1 py-1.5 text-right text-[12px] tabular-nums';

                  // SISA KUOTA. Hanya dua jenis yang punya batas: Ijin
                  // dibatasi 4x per periode, Cuti punya jatah hari per
                  // orang. Jenis lain (Dinas, Sakit, dsb) tidak berjatah,
                  // jadi diberi tanda "-" — bukan angka 0, supaya tidak
                  // terbaca sebagai "kuota habis".
                  let sisa = null;
                  if (t === 'Ijin') sisa = { angka: Math.max(0, 4 - (Number(stats.ijin_count) || 0)), satuan: 'kali' };
                  else if (t === 'Cuti') sisa = { angka: Math.max(0, parseInt(user.sisaCuti) || 0), satuan: 'hari' };

                  return (
                    <tr key={t}>
                      <td className="border-b border-slate-100 px-3 py-1.5 text-[12px] text-slate-700 truncate">{t}</td>
                      <td className={`${sel} font-semibold text-slate-900`}>{r.total || 0}</td>
                      <td className={`${sel} ${r.approved ? 'text-emerald-700' : 'text-slate-300'}`}>{r.approved || 0}</td>
                      <td className={`${sel} ${r.rejected ? 'text-rose-700' : 'text-slate-300'}`}>{r.rejected || 0}</td>
                      <td className={`${sel} ${r.pending ? 'text-amber-700' : 'text-slate-300'}`}>{r.pending || 0}</td>
                      <td className="whitespace-nowrap border-b border-l border-slate-100 px-1 py-1.5 text-right text-[11px] tabular-nums">
                        {sisa
                          ? <span className={sisa.angka === 0 ? 'font-semibold text-rose-700' : 'text-slate-600'}>
                              {sisa.angka}<span className="ml-0.5 text-[9px] text-slate-400">{sisa.satuan}</span>
                            </span>
                          : <span className="text-slate-300">&ndash;</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {!loadingStats && sPengajuan.total === 0 && (
            <p className="px-3 py-6 text-center text-[11px] text-slate-400">Tidak ada pengajuan pada periode ini.</p>
          )}
        </div>
      </div>
          
          {/* --- MENU INPUT SHIFT --- */}
      {isShiftWorker && (
         <div className="mb-5">
            <h3 className="font-bold text-slate-700 mb-2 px-1 flex items-center gap-2 text-sm">
                 <CalendarCheck className="w-4 h-4 text-indigo-500"/> Menu Running Shift
             </h3>
            <button onClick={() => setView('input_shift')} className="w-full bg-indigo-50 border border-indigo-200 p-3 rounded-xl flex items-center justify-between group active:scale-95 transition-all shadow-sm">
                <div className="flex items-center gap-3">
                    <div className="bg-indigo-600 text-white p-2 rounded-lg shadow-sm"><CalendarCheck className="w-5 h-5" /></div>
                    <div className="text-left">
                        <h4 className="font-bold text-indigo-900 text-sm">Input Jadwal Shift</h4>
                         <p className="text-[10px] text-indigo-600">Atur tanggal & jam kerja Shift Anda</p>
                    </div>
                </div>
                 <div className="bg-white p-1 rounded-full text-indigo-400"><ChevronDown className="-rotate-90 w-3 h-3" /></div>
            </button>
         </div>
      )}
        </div>
        
        {/* RIGHT COLUMN: PENGAJUAN E-FORM & FOOTER */}
        <div className="lg:col-span-5 xl:col-span-4 space-y-6">
          {/* --- MENU ABSENSI (DAFTAR) --- */}
      {/* Dulu grid kartu dengan blob dekoratif dan subjudul "Pengajuan Form" yang
          diulang di tiap kartu. Sekarang jadi daftar: kolom kanan dipakai untuk
          sisa kuota, informasi yang sebelumnya baru muncul setelah tombol mati. */}
      {/* Absen Masuk/Pulang sengaja dikeluarkan dari daftar ini — keduanya
          sudah punya kartu sendiri di atas, dan menampilkannya dua kali
          hanya membuat orang ragu mana yang "benar". */}
      {menuEForm.length > 0 && (
      <>
      <div className="mb-3 flex items-center justify-between px-1">
        <h3 className="text-base md:text-lg font-bold tracking-tight text-slate-900">Pengajuan e-Form</h3>
        <span className="text-xs font-medium text-slate-400">Layanan Mandiri</span>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200/70 overflow-hidden divide-y divide-slate-100">
        {menuEForm.map((item) => {
            const Icon = ICON_MAP[item.value] || Star;
            const toneClass = COLOR_MAP[item.value] || 'bg-slate-100 text-slate-500';
            const sisaCuti = parseInt(user.sisaCuti) || 0;
            const ijinTerpakai = stats.ijin_count || 0;
            const isCutiEmpty = item.value === 'Cuti' && sisaCuti < 1;
            const isIjinFull = item.value === 'Ijin' && ijinTerpakai >= 4;
            const isDisabled = isCutiEmpty || isIjinFull;

            let meta = null;
            if (item.value === 'Cuti')      meta = isCutiEmpty ? 'Kuota habis' : `Sisa ${sisaCuti} hari`;
            else if (item.value === 'Ijin') meta = isIjinFull ? 'Limit tercapai' : `${ijinTerpakai} dari 4 terpakai`;

            return (
                <button
                    key={item.value}
                    disabled={isDisabled}
                    onClick={() => {
                        localStorage.setItem('absenType', item.value);
                        setView('form');
                     }}
                    className={`w-full flex items-center gap-3 px-3.5 py-3 text-left transition-colors
                    ${isDisabled ? 'cursor-not-allowed' : 'hover:bg-slate-50 active:bg-slate-100'}`}
                 >
                    <span className={`w-9 h-9 shrink-0 rounded-[10px] flex items-center justify-center ${isDisabled ? 'bg-slate-100 text-slate-300' : toneClass}`}>
                       <Icon className="w-[17px] h-[17px]" strokeWidth={1.75} />
                     </span>

                    <span className={`flex-1 text-[14px] font-medium tracking-tight ${isDisabled ? 'text-slate-400' : 'text-slate-900'}`}>
                        {item.label}
                    </span>

                    {meta && (
                        <span className={`text-[11px] tabular-nums ${isDisabled ? 'font-medium text-rose-600' : 'text-slate-400'}`}>
                            {meta}
                        </span>
                    )}

                    <ChevronRight className={`w-4 h-4 shrink-0 ${isDisabled ? 'text-slate-200' : 'text-slate-300'}`} strokeWidth={2} />
                 </button>
            )
        })}
      </div>
      </>
      )}
          

        </div>
        
      </div>
      
            {/* --- GLOBAL FOOTER (Paling Bawah Halaman - 1 Baris) --- */}
      <footer className="w-full text-center py-5 mt-8 border-t border-slate-200/80">
        <p className="text-[10px] sm:text-xs font-semibold text-slate-400 uppercase tracking-widest whitespace-nowrap overflow-x-auto">
          VERSION {FRONTEND_VERSION} &bull; &copy; {new Date().getFullYear()} JPT GROUP &bull; IT SUPPORT DEPT.
        </p>
      </footer>

      {/* --- POPUP APPROVAL (gaya sama dengan Informasi HRD) --- */}
      {tampilPopupApproval && (
        <div className="fixed inset-0 z-[99] flex items-center justify-center p-4 font-sans" style={{ perspective: '1000px' }}>
          <div
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-[6px] transition-all duration-500"
            onClick={tutupPopupApproval}
          ></div>

          <div className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] border border-white/50 overflow-hidden transform transition-all animate-[softPopApproval_0.6s_cubic-bezier(0.22,1,0.36,1)_forwards]">
             <div className="absolute top-[-50%] left-[-50%] w-[200%] h-[200%] bg-gradient-to-br from-indigo-50 via-white to-sky-50 opacity-60 pointer-events-none z-0"></div>
             <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-100/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>

             <button
                onClick={tutupPopupApproval}
                className="absolute top-5 right-5 z-20 bg-white/80 hover:bg-slate-100 p-2.5 rounded-full text-slate-400 hover:text-rose-500 transition-all shadow-sm border border-slate-100 active:scale-90"
             >
                <X className="w-5 h-5" />
             </button>

             <div className="relative z-10 pt-10 px-8 text-center">
                <div className="relative mx-auto w-20 h-20 bg-white rounded-3xl shadow-xl shadow-indigo-100 flex items-center justify-center mb-5 rotate-3 hover:rotate-0 transition-transform duration-500 border border-slate-50">
                    <UsersRound className="w-9 h-9 text-indigo-500" />
                    <span className="absolute -top-2 -right-2 min-w-6 h-6 px-1.5 rounded-full bg-rose-600 text-white text-[11px] font-bold flex items-center justify-center shadow-md ring-2 ring-white">
                        {approvalNotice.total}
                    </span>
                </div>

                <h3 className="text-2xl font-black text-slate-800 tracking-tight mb-2">Menunggu Approval</h3>

                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-slate-50 border border-slate-100 rounded-full mb-6">
                    <ClipboardList className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                        {approvalNotice.total} Pengajuan
                    </span>
                </div>
             </div>

             <div className="relative z-10 px-8 pb-8">
                <div className="bg-slate-50/80 p-6 rounded-3xl border border-slate-100/80 max-h-[60vh] overflow-y-auto custom-scrollbar shadow-inner text-left">
                    <p className="text-sm md:text-[15px] text-slate-700 font-medium leading-loose">
                        Halo <span className="font-bold text-slate-800">{approvalNotice.nama}</span>, terdapat <span className="font-bold text-indigo-600">{approvalNotice.total}</span> pengajuan yang menunggu persetujuan Anda dan bisa disetujui langsung di aplikasi.
                    </p>
                    {approvalNotice.divisiCounts && Object.keys(approvalNotice.divisiCounts).length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-1.5">
                        {Object.entries(approvalNotice.divisiCounts).map(([divisi, total]) => (
                          <span key={divisi} className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 ring-1 ring-indigo-100">
                            {divisi}: {total}
                          </span>
                        ))}
                      </div>
                    )}
                </div>

                <div className="mt-6 grid grid-cols-1 gap-2.5">
                    <button
                        onClick={() => {
                          tutupPopupApproval();
                          try { sessionStorage.setItem(`approval_notice_seen_${user.id}`, '1'); } catch (e) { /* abaikan */ }
                          setApprovalNotice(null);
                          setView('approval');
                        }}
                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-4 rounded-2xl font-bold shadow-xl shadow-indigo-200 active:scale-[0.98] transition-all flex items-center justify-center gap-3 group"
                    >
                        <span>Buka Approval</span>
                        <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                    <button
                        onClick={tutupPopupApproval}
                        className="w-full bg-slate-800 hover:bg-slate-900 text-white py-4 rounded-2xl font-bold shadow-xl shadow-slate-200 active:scale-[0.98] transition-all flex items-center justify-center gap-3 group"
                    >
                        <span>Nanti Saja</span>
                        <Check className="w-5 h-5 text-emerald-400" />
                    </button>
                </div>
             </div>
          </div>

          <style>{`
            @keyframes softPopApproval {
                0% { opacity: 0; transform: scale(0.95) translateY(10px); }
                100% { opacity: 1; transform: scale(1) translateY(0); }
            }
            .custom-scrollbar::-webkit-scrollbar { width: 4px; }
            .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
            .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
          `}</style>
        </div>
      )}

      {/* --- ANNOUNCEMENT POPUP (SOFT & ELEGANT V2) --- */}
      {showNews && newsContent && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 font-sans" style={{ perspective: '1000px' }}>
          
          {/* 1. Backdrop (Blur Lembut & Gelap Transparan untuk Fokus) */}
          <div 
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-[6px] transition-all duration-500"
            onClick={() => { setShowNews(false); sessionStorage.setItem('announcement_shown', 'true'); }}
          ></div>

          {/* 2. Kartu Utama (Floating Card) */}
          <div className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] border border-white/50 overflow-hidden transform transition-all animate-[softPop_0.6s_cubic-bezier(0.22,1,0.36,1)_forwards]">
             
             {/* Hiasan Latar Belakang (Soft Aurora Glow) */}
             <div className="absolute top-[-50%] left-[-50%] w-[200%] h-[200%] bg-gradient-to-br from-blue-50 via-white to-indigo-50 opacity-60 pointer-events-none z-0"></div>
             <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-100/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>

             {/* Tombol Close (Minimalis) */}
             <button 
                onClick={() => { setShowNews(false); sessionStorage.setItem('announcement_shown', 'true'); }}
                className="absolute top-5 right-5 z-20 bg-white/80 hover:bg-slate-100 p-2.5 rounded-full text-slate-400 hover:text-rose-500 transition-all shadow-sm border border-slate-100 active:scale-90"
             >
                <X className="w-5 h-5" />
             </button>

             {/* Header Section */}
             <div className="relative z-10 pt-10 px-8 text-center">
                {/* Icon Circle (Soft Shadow) */}
                <div className="mx-auto w-20 h-20 bg-white rounded-3xl shadow-xl shadow-indigo-100 flex items-center justify-center mb-5 rotate-3 hover:rotate-0 transition-transform duration-500 border border-slate-50">
                    <Megaphone className="w-9 h-9 text-indigo-500" />
                </div>

                <h3 className="text-2xl font-black text-slate-800 tracking-tight mb-2">Informasi HRD</h3>
                
                {/* Tanggal Badge (Modern Pill) */}
                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-slate-50 border border-slate-100 rounded-full mb-6">
                    <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                        {newsContent.waktu}
                    </span>
                </div>
             </div>

             {/* Content Area (Wide & Readable) */}
             <div className="relative z-10 px-8 pb-8">
                <div className="bg-slate-50/80 p-6 rounded-3xl border border-slate-100/80 max-h-[60vh] overflow-y-auto custom-scrollbar shadow-inner">
                    {/* TYPOGRAPHY FIX: Jarak baris lebar (leading-loose) & Warna Gelap */}
                    <p className="text-sm md:text-[15px] text-slate-700 font-medium leading-loose whitespace-pre-line text-left">
                        {newsContent.isi}
                    </p>
                </div>

                {/* Footer Action */}
                <div className="mt-6">
                    <button 
                        onClick={() => { setShowNews(false); sessionStorage.setItem('announcement_shown', 'true'); }}
                        className="w-full bg-slate-800 hover:bg-slate-900 text-white py-4 rounded-2xl font-bold shadow-xl shadow-slate-200 active:scale-[0.98] transition-all flex items-center justify-center gap-3 group"
                    >
                        <span>Saya Mengerti</span>
                        <Check className="w-5 h-5 text-emerald-400 group-hover:translate-x-1 transition-transform" />
                    </button>
                </div>
             </div>
          </div>

          {/* Animasi Soft Pop (Paste di style atau di file CSS global jika perlu, tapi inline style di sini aman) */}
          <style>{`
            @keyframes softPop {
                0% { opacity: 0; transform: scale(0.95) translateY(10px); }
                100% { opacity: 1; transform: scale(1) translateY(0); }
            }
            /* Custom Scrollbar agar tidak merusak estetika */
            .custom-scrollbar::-webkit-scrollbar { width: 4px; }
            .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
            .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
          `}</style>
        </div>
      )}
      
    </div>
  );
}


// --- BARU: SHIFT SCHEDULE SCREEN (LENGKAP: View Report, Edit, Delete, Validasi 1 Jam) ---
function ShiftScheduleScreen({ user, setView, masterData }) {
    const [date, setDate] = useState('');
    const [selectedShiftValue, setSelectedShiftValue] = useState('');
    const [loading, setLoading] = useState(false);
    // STATE BARU: Untuk Riwayat dan Edit
    const [shiftHistory, setShiftHistory] = useState([]);
    const [editingItem, setEditingItem] = useState(null);
    // Jika sedang mode edit
    const [loadingHistory, setLoadingHistory] = useState(false);

    const availableShifts = masterData?.shifts || [];
    // FUNGSI: Cek apakah masih bisa diedit (Max 1 Jam)
    const isEditable = (waktuInput) => {
        if (!waktuInput) return false;
        try {
            const entryTime = new Date(waktuInput).getTime();
            const now = new Date().getTime();
            const diffInHours = (now - entryTime) / (1000 * 60 * 60);
            return diffInHours <= 1; // True jika kurang dari 1 jam
        } catch (e) { return false; }
    };

    // FUNGSI: Ambil Data Riwayat Shift
    const fetchShiftHistory = useCallback(async () => {
        setLoadingHistory(true);
        try {
            const res = await fetchApi(SCRIPT_URL, {
                method: 'POST',
                body: JSON.stringify({
                    action: 'get_shift_history', 
                    userId: user.id
                })
            });
            const data = await res.json();
            if (data.result === 'success') {
                setShiftHistory(data.data); 
            }
        } catch (e) {
            console.error("Gagal load history shift");
        } finally {
            setLoadingHistory(false);
        }
    }, [user.id]);

    useEffect(() => {
        fetchShiftHistory();
    }, [fetchShiftHistory]);

    // FUNGSI: Handle Klik Edit
    const handleEdit = (item) => {
        let formattedDate = item.tanggal;
        try {
            const d = new Date(item.tanggal);
            if(!isNaN(d.getTime())) {
                formattedDate = d.toISOString().split('T')[0];
            }
        } catch(e) {}

        setDate(formattedDate);
        setSelectedShiftValue(item.shiftValue);
        setEditingItem(item); 
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // FUNGSI: Handle Klik Hapus
    const handleDelete = async (uuid) => {
        // PERBAIKAN: Menambahkan 'window.' sebelum confirm
        if(!window.confirm("Yakin ingin menghapus jadwal shift ini?")) return;
        setLoading(true);
        try {
            const res = await fetchApi(SCRIPT_URL, {
                method: 'POST',
                body: JSON.stringify({
                    action: 'delete_shift_schedule',
                    uuid: uuid
                })
            });
            const data = await res.json();
            if (data.result === 'success') {
                alert("Data berhasil dihapus");
                fetchShiftHistory(); 
            } else {
                alert(data.message);
            }
        } catch (e) {
            alert("Gagal menghapus data.");
        } finally {
            setLoading(false);
        }
    };

    const handleCancelEdit = () => {
        setEditingItem(null);
        setDate('');
        setSelectedShiftValue('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!date || !selectedShiftValue) {
            alert("Mohon lengkapi Tanggal dan Pilihan Shift!");
            return;
        }

        const shiftObj = availableShifts.find(s => s.value === selectedShiftValue);
        const shiftLabel = shiftObj ? shiftObj.label : selectedShiftValue;

        // --- UPDATE VALIDASI: CEK APAKAH TANGGAL SUDAH ADA DAN TERKUNCI ---
        // Jika sedang TIDAK edit (Mode Input Baru), kita cek apakah tanggal sudah pernah diinput
        if (!editingItem) {
            const isLockedDate = shiftHistory.some(item => {
                let itemDate = item.tanggal;
                // Normalisasi format tanggal dari history agar sama dengan input (YYYY-MM-DD)
                try {
                    const d = new Date(item.tanggal);
                    // Adjust Timezone offset agar tidak geser hari
                    const offset = d.getTimezoneOffset() * 60000;
                    itemDate = new Date(d.getTime() - offset).toISOString().split('T')[0];
                } catch (e) {}
                
                // Jika tanggal sama DAN tidak bisa diedit (expired > 1 jam)
                return itemDate === date && !isEditable(item.waktuInput);
            });

            if (isLockedDate) {
                alert("GAGAL: Tanggal ini sudah ada dan tidak bisa di Tambahkan/diubah.");
                return;
            }
        }
        // --- END VALIDASI ---

        setLoading(true);

        // Tentukan Action: Edit atau Baru
        const actionType = editingItem ? 'edit_shift_schedule' : 'submit_shift_schedule';
        const payload = {
            action: actionType,
            userId: user.id,
            nama: user.nama,
            tanggal: date,
            shiftValue: selectedShiftValue,
            shiftLabel: shiftLabel,
            uuid: editingItem ? editingItem.uuid : null 
        };

        try {
            const res = await fetchApi(SCRIPT_URL, {
                method: 'POST',
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.result === 'success') {
                alert(data.message);
                setDate('');
                setSelectedShiftValue('');
                setEditingItem(null); 
                fetchShiftHistory();  
            } else {
                alert(data.message);
            }
        } catch (e) {
            console.error(e);
            alert("Gagal koneksi ke server.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-4 h-full overflow-y-auto pb-20">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold ml-2">Jadwal Shift</h2>
                <BackButton onClick={() => setView('dashboard')} />
            </div>

            {/* --- FORM INPUT --- */}
            <div className={`bg-white p-5 rounded-xl shadow-sm border mb-6 transition-colors ${editingItem ? 'border-yellow-400 ring-1 ring-yellow-400' : 'border-gray-200'}`}>
                {editingItem && (
                    <div className="mb-3 bg-yellow-50 text-yellow-700 p-2 rounded text-xs font-bold flex justify-between items-center">
                        <span>Sedang Mengedit Data...</span>
                        <button onClick={handleCancelEdit} className="bg-white border border-yellow-200 px-2 py-1 rounded hover:bg-yellow-100">Batal</button>
                    </div>
                )}
                
                {!editingItem && (
                    <div className="bg-indigo-50 border border-indigo-200 p-3 rounded-lg mb-4 text-xs text-indigo-800">
                       <p className="font-bold mb-1">Panduan:</p>
                       <p>Silakan input jadwal shift Anda. Data yang sudah diinput bisa diedit/hapus selama 1 jam setelah input.</p>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="text-xs font-bold text-gray-700 block mb-1">Tanggal Shift *</label>
                        <input 
                            type="date" 
                            className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            required
                        />
                    </div>
                    
                    <div>
                        <label className="text-xs font-bold text-gray-700 block mb-1">Pilih Jam Kerja *</label>
                        <select 
                            className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500"
                            value={selectedShiftValue}
                            onChange={(e) => setSelectedShiftValue(e.target.value)}
                            required
                        >
                            <option value="">-- Pilih Shift --</option>
                            {availableShifts.map((s, idx) => (
                                <option key={idx} value={s.value}>
                                    {s.label} ({s.value})
                                </option>
                            ))}
                            {availableShifts.length === 0 && <option disabled>Tidak ada data master shift</option>}
                        </select>
                    </div>

                    <button 
                        type="submit" 
                        disabled={loading} 
                        className={`w-full text-white py-3 rounded-lg font-bold transition flex items-center justify-center gap-2 mt-4 ${editingItem ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                    >
                        {loading ? 'Menyimpan...' : (
                            <>
                                {editingItem ? <Edit className="w-5 h-5"/> : <CheckCircle className="w-5 h-5"/>} 
                                {editingItem ? 'Update Jadwal' : 'Simpan Jadwal'}
                            </>
                        )}
                    </button>
                </form>
            </div>

            {/* --- LIST RIWAYAT SHIFT --- */}
            <div>
                <h3 className="font-bold text-gray-700 mb-3 px-1 flex items-center gap-2">
                    <History className="w-4 h-4"/> Riwayat Input Shift
                </h3>

                {loadingHistory ? <p className="text-center text-gray-400 text-sm">Memuat riwayat...</p> : (
                    <div className="space-y-3">
                        {shiftHistory.length === 0 && (
                            <div className="text-center py-6 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                                <p className="text-gray-400 text-sm">Belum ada data shift yang diinput.</p>
                            </div>
                        )}

                        {shiftHistory.map((item, idx) => {
                            const canEdit = isEditable(item.waktuInput); 
                            return (
                                <div key={idx} className="bg-white p-4 rounded-xl shadow-sm border border-l-4 border-l-indigo-500 relative">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <div className="flex items-center gap-2 text-indigo-700 font-bold mb-1">
                                                <CalendarCheck className="w-4 h-4"/> 
                                                <span>{new Date(item.tanggal).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric'})}</span>
                                            </div>
                                            <p className="text-sm font-semibold text-gray-800">{item.shiftLabel}</p>
                                            <p className="text-xs text-gray-500 mt-0.5">Jam: {item.shiftValue}</p>
                                            <p className="text-[10px] text-gray-400 mt-2">Dibuat: {item.waktuInput ? new Date(item.waktuInput).toLocaleString('id-ID') : '-'}</p>
                                        </div>

                                        {canEdit && (
                                            <div className="flex flex-col gap-2">
                                                <button 
                                                    onClick={() => handleEdit(item)}
                                                    className="bg-yellow-50 text-yellow-600 p-2 rounded-lg border border-yellow-200 hover:bg-yellow-100 transition"
                                                    title="Edit Data"
                                                >
                                                    <Edit className="w-4 h-4"/>
                                                </button>
                                                <button 
                                                    onClick={() => handleDelete(item.uuid)}
                                                    className="bg-red-50 text-red-600 p-2 rounded-lg border border-red-200 hover:bg-red-100 transition"
                                                    title="Hapus Data"
                                                >
                                                    <Trash2 className="w-4 h-4"/>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    {!canEdit && (
                                        <div className="absolute top-2 right-2">
                                            <span className="text-[10px] bg-gray-100 text-gray-400 px-2 py-1 rounded border">Locked</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}


// --- SCREEN ANALISA DATA (UPDATED: REFRESH BTN & MOVED ACTION COLUMN) ---
function AnalysisScreen({ user, setView }) {
    const [dataList, setDataList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const createEmptyColumnFilters = () => ({
        tglPengajuan: [], idAkun: [], nik: [], nama: [], divisi: [],
        periode: [], durasi: [], tglKonflik: [], tipeManual: [],
        simbolMesin: [], waktuScan: [], status: []
    });

    // --- HELPER TANGGAL DEFAULT ---
    // Periode analisa mengikuti siklus absensi: mulai tanggal 21 sampai hari ini.
    // Jika hari ini masih sebelum tanggal 21, ambil tanggal 21 bulan sebelumnya.
    const getDefaultDates = () => {
        const today = new Date();
        const defaultStart = new Date(today);
        if (today.getDate() >= 21) {
            defaultStart.setDate(21);
        } else {
            defaultStart.setMonth(defaultStart.getMonth() - 1);
            defaultStart.setDate(21);
        }
        const formatYMD = (date) => {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
        };
        return { start: formatYMD(defaultStart), end: formatYMD(today) };
    };

    const defaultDates = getDefaultDates();
    const [startDate, setStartDate] = useState(defaultDates.start);
    const [endDate, setEndDate] = useState(defaultDates.end);
    
    // STATE FILTER (Multi Select)
    const [columnFilters, setColumnFilters] = useState(createEmptyColumnFilters);

    // STATE SORTING
    const [sortConfig, setSortConfig] = useState({ key: 'status', direction: 'asc' });
    const [activeFilter, setActiveFilter] = useState(null);
    const [quickStatusFilter, setQuickStatusFilter] = useState('all');

    useEffect(() => {
        if (user.role !== 'admin' && user.role !== 'hrd') {
            alert("Akses Ditolak!");
            setView('dashboard');
        }
    }, [user, setView]);

    // Close dropdown logic
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (activeFilter && !event.target.closest('.filter-dropdown-container')) {
                setActiveFilter(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [activeFilter]);

    // Dipisah dari handleAnalyze supaya bisa dipanggil dengan tanggal yang
    // BELUM tentu sudah masuk ke state (dipakai oleh auto-load periode aktif
    // di bawah, yang tidak bisa menunggu setStartDate selesai re-render dulu).
    const jalankanAnalisa = async (tglMulai, tglSelesai) => {
        setLoading(true);
        setHasSearched(true);

        // Reset Filter & Sort saat search baru (opsional, bisa dihapus jika ingin preserve filter)
        // setColumnFilters({ ... });
        // setSortConfig({ key: null, direction: 'asc' });

        try {
            const res = await fetchApi(SCRIPT_URL, {
                method: 'POST',
                body: JSON.stringify({
                    action: 'get_analysis_data',
                    startDate: tglMulai,
                    endDate: tglSelesai,
                    roleRequester: user.role
                })
            });
            const result = await res.json();
            if (result.result === 'success') {
                const filtered = (result.list || []).filter(item => {
                    const tipe = String(item.tipeManual || item.form || '').trim().toLowerCase();
                    return tipe !== 'standby' && tipe !== 'hadir' && tipe !== 'pulang';
                });
                setDataList(filtered);
            } else {
                alert(result.message);
            }
        } catch (e) {
            alert("Gagal mengambil data analisa.");
        } finally {
            setLoading(false);
        }
    };

    // Handler tombol "Analisa Data" / ikon refresh — dipanggil langsung
    // sebagai event handler (onClick={handleAnalyze}), jadi TIDAK menerima
    // parameter (kalau menerima, argumen pertamanya jadi SyntheticEvent,
    // bukan tanggal). Selalu pakai tanggal dari state.
    const handleAnalyze = () => {
        if (!startDate || !endDate) {
            alert("Mohon pilih Tanggal Mulai dan Sampai.");
            return;
        }
        jalankanAnalisa(startDate, endDate);
    };

    // --- AUTO-LOAD SAAT LAYAR DIBUKA ---
    // 1) "Periode Mulai" default-nya mengikuti tanggal 21 periode berjalan
    //    sampai hari ini.
    // 2) Data langsung dianalisa otomatis begitu layar dibuka, tanpa perlu
    //    klik tombol "Analisa Data" dulu.
    // Tidak perlu lagi mengambil periode aktif dari backend; state awal sudah
    // sesuai kebutuhan default analisa.
    useEffect(() => {
        let batal = false;
        (async () => {
            if (!batal) jalankanAnalisa(startDate, endDate);
        })();
        return () => { batal = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleProcessApproval = async (uuid, decision, namaKaryawan) => {
        const actionText = decision === 'approve' ? 'Menyetujui' : 'Menolak';
        if (!window.confirm(`Apakah Anda yakin ingin ${actionText} pengajuan atas nama ${namaKaryawan}?`)) return;

        setLoading(true);
        try {
            const res = await fetchApi(SCRIPT_URL, {
                method: 'POST',
                body: JSON.stringify({
                    action: 'process_approval',
                    uuid: uuid,
                    decision: decision,
                    approverName: user.nama + ' (Via Analisa)',
                    alasan: 'Diproses melalui Menu Analisa Data'
                })
            });
            const result = await res.json();
            if (result.result === 'success') {
                alert(`Berhasil: ${result.message}`);
                handleAnalyze(); // Refresh data otomatis setelah action
            } else {
                alert(`Gagal: ${result.message}`);
            }
        } catch (e) {
            console.error(e);
            alert("Terjadi kesalahan jaringan.");
        } finally {
            setLoading(false);
        }
    };

    // --- HELPER FILTER & SORT ---
    const getUniqueValues = (field) => {
        const values = dataList.map(item => item[field]).filter(v => v !== null && v !== undefined && v !== '');
        return [...new Set(values)].sort();
    };

    const toggleFilterValue = (field, value) => {
        setColumnFilters(prev => {
            const currentValues = prev[field];
            if (currentValues.includes(value)) return { ...prev, [field]: currentValues.filter(v => v !== value) };
            else return { ...prev, [field]: [...currentValues, value] };
        });
    };

    const toggleSelectAll = (field, visibleOptions) => {
        setColumnFilters(prev => {
            const currentValues = prev[field];
            const allVisibleSelected = visibleOptions.every(val => currentValues.includes(val));
            if (allVisibleSelected) {
                return { ...prev, [field]: currentValues.filter(v => !visibleOptions.includes(v)) };
            } else {
                const newValues = [...currentValues];
                visibleOptions.forEach(v => {
                    if (!newValues.includes(v)) newValues.push(v);
                });
                return { ...prev, [field]: newValues };
            }
        });
    };

    const isPendingStatus = (status) => String(status || '').trim().toLowerCase().includes('pending');

    const getQuickStatusFilterValue = (selectedValues) => {
        if (!selectedValues || selectedValues.length === 0) return 'all';
        if (selectedValues.length !== 1) return 'custom';

        const value = String(selectedValues[0] || '').trim().toLowerCase();
        if (value.includes('pending')) return 'pending';
        if (value.includes('verified') || value.includes('approved')) return 'verified';
        if (value.includes('reject')) return 'rejected';
        return 'custom';
    };

    useEffect(() => {
        setQuickStatusFilter(getQuickStatusFilterValue(columnFilters.status));
    }, [columnFilters.status]);

    const applyQuickStatusFilter = (value) => {
        setQuickStatusFilter(value);
        const statusMap = {
            pending: ['Pending'],
            verified: ['Verified'],
            rejected: ['Rejected']
        };
        setColumnFilters(prev => ({
            ...prev,
            status: statusMap[value] || []
        }));
    };

    const requestSort = (key, direction) => {
        setSortConfig({ key, direction });
    };

    const handleQuickSortChange = (value) => {
        switch (value) {
            case 'status:asc':
                requestSort('status', 'asc');
                break;
            case 'tglPengajuan:desc':
                requestSort('tglPengajuan', 'desc');
                break;
            case 'tglKonflik:desc':
                requestSort('tglKonflik', 'desc');
                break;
            case 'nama:asc':
                requestSort('nama', 'asc');
                break;
            default:
                requestSort('status', 'asc');
                break;
        }
    };

    const resetAnalysisControls = () => {
        setColumnFilters(createEmptyColumnFilters());
        setSortConfig({ key: 'status', direction: 'asc' });
        setQuickStatusFilter('all');
        setActiveFilter(null);
    };

    // FILTERING
    const filteredList = dataList.filter(item => {
        return Object.keys(columnFilters).every(key => {
            const selectedValues = columnFilters[key];
            if (selectedValues.length === 0) return true;
            return selectedValues.includes(String(item[key]));
        });
    });

    const getAnalysisStatusRank = (status) => {
        const normalizedStatus = String(status || '').trim().toLowerCase();
        if (normalizedStatus.includes('pending')) return 0;
        if (normalizedStatus.includes('reject')) return 1;
        if (normalizedStatus.includes('approve') || normalizedStatus.includes('verified')) return 2;
        return 3;
    };

    const pendingTotal = React.useMemo(
        () => dataList.filter(item => isPendingStatus(item.status)).length,
        [dataList]
    );
    const pendingExportList = React.useMemo(
        () => dataList.filter(item => isPendingStatus(item.status)),
        [dataList]
    );

    // SORTING
    const sortedList = React.useMemo(() => {
        let sortableItems = [...filteredList];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let valA = a[sortConfig.key];
                let valB = b[sortConfig.key];
                if (valA === null) valA = '';
                if (valB === null) valB = '';

                if (sortConfig.key === 'status') {
                    const rankA = getAnalysisStatusRank(valA);
                    const rankB = getAnalysisStatusRank(valB);
                    if (rankA !== rankB) {
                        return sortConfig.direction === 'asc' ? rankA - rankB : rankB - rankA;
                    }
                }

                const numA = parseFloat(valA);
                const numB = parseFloat(valB);
                const isNum = !isNaN(numA) && !isNaN(numB) && String(valA).trim() !== '' && String(valB).trim() !== '';

                if (isNum) {
                    return sortConfig.direction === 'asc' ? numA - numB : numB - numA;
                } else {
                    return sortConfig.direction === 'asc' 
                        ? String(valA).localeCompare(String(valB))
                        : String(valB).localeCompare(String(valA));
                }
            });
        }
        return sortableItems;
    }, [filteredList, sortConfig]);

    const visiblePendingTotal = React.useMemo(
        () => sortedList.filter(item => isPendingStatus(item.status)).length,
        [sortedList]
    );

    const quickSortValue = (() => {
        const currentValue = `${sortConfig.key || 'status'}:${sortConfig.direction}`;
        const allowedValues = ['status:asc', 'tglPengajuan:desc', 'tglKonflik:desc', 'nama:asc'];
        return allowedValues.includes(currentValue) ? currentValue : 'status:asc';
    })();

    const getStatusColor = (status) => {
        const s = String(status).toLowerCase();
        if (s.includes('approve') || s.includes('verified')) return 'bg-green-100 text-green-700 border-green-200';
        if (s.includes('reject')) return 'bg-red-100 text-red-700 border-red-200';
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    };

    // --- EXPORT FUNCTIONS (EXCEL & PDF) ---
    const buildAnalysisExcelRows = (rows) => rows.map((item, index) => ({
            "No": index + 1,
            "Tgl Ajuan": item.tglPengajuan,
            "ID Akun": item.idAkun,
            "NIK": item.nik, 
            "Nama Karyawan": item.nama,
            "Departemen": item.divisi,
            "Periode Form": item.periode,
            "Durasi": item.durasi,
            "Tgl Konflik": item.tglKonflik,
            "Data Manual": item.tipeManual,
            "Data Mesin": item.simbolMesin,
            "Waktu Scan": item.waktuScan,
            "Status Approval": item.status 
        }));

    const exportAnalysisExcel = (rows, fileName) => {
        if (rows.length === 0) return alert("Tidak ada data untuk diexport.");
        const dataToExport = buildAnalysisExcelRows(rows);
        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Analisa_Mismatch");
        const wscols = [{wch:5}, {wch:15}, {wch:12}, {wch:12}, {wch:30}, {wch:20}, {wch:25}, {wch:10}, {wch:15}, {wch:15}, {wch:15}, {wch:15}, {wch:15}];
        ws['!cols'] = wscols;
        XLSX.writeFile(wb, fileName);
    };

    const handleExportExcel = () => {
        exportAnalysisExcel(sortedList, `Analisa_Absensi_${startDate}_${endDate}.xlsx`);
    };

    const handleExportPendingExcel = () => {
        exportAnalysisExcel(pendingExportList, `Analisa_Absensi_Pending_${startDate}_${endDate}.xlsx`);
    };

    const handleExportPDF = () => {
        if (sortedList.length === 0) return alert("Tidak ada data untuk dicetak.");
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        doc.setFontSize(14);
        doc.text("Laporan Analisa Ketidakcocokan Absensi", 14, 15);
        doc.setFontSize(10);
        doc.text(`Periode: ${startDate} s/d ${endDate}`, 14, 21);
        doc.text(`Dicetak Oleh: ${user.nama} | Tgl: ${new Date().toLocaleDateString('id-ID')}`, 14, 26);
        const tableColumn = [
            "No", "Tgl Ajuan", "ID Akun", "NIK", "Nama", "Dept", 
            "Periode", "Dur", "Tgl Konflik", "Form", "Mesin", "Scan", "Status"
        ];
        const tableRows = [];
        sortedList.forEach((item, index) => {
            tableRows.push([
                index + 1, item.tglPengajuan, item.idAkun, item.nik, item.nama, item.divisi,
                item.periode, item.durasi, item.tglKonflik, item.tipeManual, item.simbolMesin, item.waktuScan, item.status
            ]);
        });
        autoTable(doc, {
            head: [tableColumn], body: tableRows, startY: 30, theme: 'grid',
            headStyles: { fillColor: [220, 38, 38], textColor: 255, fontStyle: 'bold', halign: 'center', valign: 'middle', lineWidth: 0.1 },
            styles: { fontSize: 6.5, cellPadding: 1.5, valign: 'middle', overflow: 'linebreak', lineWidth: 0.1, lineColor: [200, 200, 200] },
            columnStyles: {
                0: { cellWidth: 8, halign: 'center' }, 1: { cellWidth: 20, halign: 'center' }, 2: { cellWidth: 18, halign: 'center' },
                3: { cellWidth: 18, halign: 'center' }, 4: { cellWidth: 45, halign: 'left' }, 5: { cellWidth: 28, halign: 'left' },
                6: { cellWidth: 35, halign: 'center' }, 7: { cellWidth: 12, halign: 'center' }, 8: { cellWidth: 22, halign: 'center', textColor: [220, 38, 38] },
                9: { cellWidth: 18, halign: 'center' }, 10: { cellWidth: 15, halign: 'center' }, 11: { cellWidth: 18, halign: 'center' }, 12: { cellWidth: 20, halign: 'center' }
            },
            margin: { left: 10, right: 10 },
            didParseCell: function(data) {
                if (data.section === 'body' && data.column.index === 12) {
                    const text = data.cell.raw.toString().toLowerCase();
                    if (text.includes('reject')) data.cell.styles.textColor = [220, 38, 38];
                    else if (text.includes('approve')) data.cell.styles.textColor = [21, 128, 61];
                    else data.cell.styles.textColor = [202, 138, 4];
                }
            }
        });
        doc.save(`Laporan_Analisa_${startDate}_${endDate}.pdf`);
    };

    // --- FILTER HEADER COMPONENT ---
    const FilterHeader = ({ label, field, width, textColor }) => {
        const uniqueOptions = getUniqueValues(field);
        const selectedValues = columnFilters[field];
        const isOpen = activeFilter === field;
        const [searchTerm, setSearchTerm] = useState('');
        useEffect(() => { if (!isOpen) setSearchTerm(''); }, [isOpen]);
        const visibleOptions = uniqueOptions.filter(opt => String(opt).toLowerCase().includes(searchTerm.toLowerCase()));

        return (
            <th className={`p-2 border border-gray-300 align-top ${width || 'w-24'} font-normal text-gray-700 bg-gray-100`}>
                <div className="flex flex-col gap-1 filter-dropdown-container relative">
                    <div className="flex items-center justify-center gap-1">
                        <span className={`text-center font-normal ${textColor || ''}`}>{label}</span>
                        {sortConfig.key === field && (
                            <span className="text-[9px] text-blue-600 font-bold">{sortConfig.direction === 'asc' ? '↓' : '↑'}</span>
                        )}
                    </div>
                    <button onClick={() => setActiveFilter(isOpen ? null : field)} className={`flex items-center justify-between w-full text-[10px] px-2 py-1 border rounded bg-white outline-none focus:border-blue-500 font-normal ${selectedValues.length > 0 ? 'text-blue-600 border-blue-300 bg-blue-50' : 'text-gray-500 border-gray-300'}`}>
                        <span className="truncate">{selectedValues.length === 0 ? "(All)" : `${selectedValues.length} Selected`}</span>
                        <Filter className="w-3 h-3 ml-1" />
                    </button>
                    {isOpen && (
                        <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-300 shadow-xl rounded-md z-50 flex flex-col max-h-80">
                            <div className="p-2 border-b border-gray-200 bg-gray-50 grid grid-cols-2 gap-2">
                                <button onClick={() => requestSort(field, 'asc')} className={`flex items-center justify-center gap-1 px-2 py-1.5 rounded text-[10px] border ${sortConfig.key === field && sortConfig.direction === 'asc' ? 'bg-blue-100 text-blue-700 border-blue-300 font-bold' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'}`}><span>A-Z</span> ↓</button>
                                <button onClick={() => requestSort(field, 'desc')} className={`flex items-center justify-center gap-1 px-2 py-1.5 rounded text-[10px] border ${sortConfig.key === field && sortConfig.direction === 'desc' ? 'bg-blue-100 text-blue-700 border-blue-300 font-bold' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'}`}><span>Z-A</span> ↑</button>
                            </div>
                            <div className="p-2 border-b border-gray-200">
                                <div className="relative">
                                    <Search className="w-3 h-3 absolute left-2 top-2 text-gray-400" />
                                    <input type="text" placeholder="Find..." className="w-full pl-7 pr-2 py-1 text-[11px] border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} autoFocus />
                                </div>
                            </div>
                            <div className="p-2 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
                                <input type="checkbox" className="rounded border-gray-300 w-3.5 h-3.5 cursor-pointer" checked={visibleOptions.length > 0 && visibleOptions.every(v => selectedValues.includes(v))} onChange={() => toggleSelectAll(field, visibleOptions)} />
                                <span className="text-[10px] text-gray-600 font-normal cursor-pointer" onClick={() => toggleSelectAll(field, visibleOptions)}>Select All (Shown)</span>
                            </div>
                            <div className="overflow-y-auto p-1 flex-1 min-h-[100px]">
                                {visibleOptions.map((val, idx) => (
                                    <label key={idx} className="flex items-center gap-2 px-2 py-1.5 hover:bg-blue-50 cursor-pointer rounded">
                                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 w-3.5 h-3.5" checked={selectedValues.includes(val)} onChange={() => toggleFilterValue(field, val)}/>
                                        <span className="text-[11px] text-gray-700 font-normal truncate">{val}</span>
                                    </label>
                                ))}
                                {visibleOptions.length === 0 && <div className="p-2 text-[10px] text-gray-400 text-center font-normal">No results found</div>}
                            </div>
                        </div>
                    )}
                </div>
            </th>
        );
    };

    return (
        <div className="fixed inset-0 z-50 bg-gray-100 flex flex-col font-sans h-screen w-screen overflow-hidden text-xs">
            {/* HEADER */}
            <div className="bg-white border-b border-gray-300 px-4 py-3 shadow-sm flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                    <div className="bg-rose-100 p-2 rounded-lg"><FileSpreadsheet className="w-5 h-5 text-rose-600" /></div>
                    <div><h2 className="text-lg font-normal text-slate-800">Analisa Data Absensi</h2><p className="text-xs text-slate-500 font-normal">Monitoring Ketidakcocokan Data</p></div>
                </div>
                <button onClick={() => setView('dashboard')} className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-normal transition border border-gray-200">
                    <LogOut className="w-3.5 h-3.5 rotate-180" /> Tutup
                </button>
            </div>

            {/* CONTROL BAR */}
            <div className="bg-white border-b border-gray-200 px-4 py-3 flex flex-wrap gap-3 items-end shrink-0">
                <div><label className="text-[10px] font-normal text-gray-400 uppercase block mb-1">Periode Mulai</label><input type="date" className="border border-gray-300 p-1.5 rounded text-xs font-normal shadow-sm" value={startDate} onChange={e => setStartDate(e.target.value)} /></div>
                <div><label className="text-[10px] font-normal text-gray-400 uppercase block mb-1">Sampai Dengan</label><input type="date" className="border border-gray-300 p-1.5 rounded text-xs font-normal shadow-sm" value={endDate} onChange={e => setEndDate(e.target.value)} /></div>
                
                {/* Tombol Analisa Data */}
                <button onClick={handleAnalyze} disabled={loading} className="bg-slate-800 text-white px-5 py-2 rounded font-normal text-xs hover:bg-slate-700 transition flex items-center gap-2 shadow-md">
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Search className="w-3.5 h-3.5"/>} {loading ? 'Proses...' : 'Analisa Data'}
                </button>

                {/* --- [BARU] TOMBOL REFRESH --- */}
                <button 
                    onClick={handleAnalyze} 
                    disabled={loading || !hasSearched} 
                    className="bg-white text-blue-600 border border-blue-200 px-3 py-2 rounded font-normal text-xs hover:bg-blue-50 transition flex items-center gap-2 shadow-sm"
                    title="Refresh Data"
                >
                    <RefreshCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                </button>

                {hasSearched && (
                    <>
                        <div>
                            <label className="text-[10px] font-normal text-gray-400 uppercase block mb-1">Filter Cepat</label>
                            <select
                                value={quickStatusFilter}
                                onChange={e => applyQuickStatusFilter(e.target.value)}
                                className="border border-gray-300 p-1.5 rounded text-xs font-normal shadow-sm min-w-[120px]"
                            >
                                <option value="all">Semua Status</option>
                                <option value="pending">Pending</option>
                                <option value="verified">Verified</option>
                                <option value="rejected">Rejected</option>
                                <option value="custom" disabled>Custom Filter</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] font-normal text-gray-400 uppercase block mb-1">Sort Cepat</label>
                            <select
                                value={quickSortValue}
                                onChange={e => handleQuickSortChange(e.target.value)}
                                className="border border-gray-300 p-1.5 rounded text-xs font-normal shadow-sm min-w-[150px]"
                            >
                                <option value="status:asc">Status Pending Dulu</option>
                                <option value="tglPengajuan:desc">Tgl Ajuan Terbaru</option>
                                <option value="tglKonflik:desc">Tgl Konflik Terbaru</option>
                                <option value="nama:asc">Nama A-Z</option>
                            </select>
                        </div>
                        <button
                            onClick={resetAnalysisControls}
                            className="bg-white text-slate-700 border border-slate-200 px-3 py-2 rounded font-normal text-xs hover:bg-slate-50 transition flex items-center gap-2 shadow-sm"
                            title="Reset Filter & Sort"
                        >
                            <SlidersHorizontal className="w-3.5 h-3.5" />
                            Reset
                        </button>
                    </>
                )}
            </div>

            {/* MAIN CONTENT */}
            <div className="flex-1 flex flex-col overflow-hidden w-full bg-gray-50 p-4">
                {hasSearched && (
                    <div className="flex-1 flex flex-col bg-white border border-gray-300 shadow-sm overflow-hidden">
                        {/* STATS */}
                        <div className="bg-gray-100 px-3 py-1.5 border-b border-gray-200 flex flex-wrap justify-between items-center gap-2 shrink-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-xs font-normal text-gray-600">Total Tampil: {sortedList.length} Data</span>
                                <span className="inline-flex items-center rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[10px] font-normal text-amber-700">
                                    Pending: {pendingTotal} Data
                                </span>
                                {sortedList.length !== dataList.length && (
                                    <span className="inline-flex items-center rounded border border-slate-200 bg-white px-2 py-1 text-[10px] font-normal text-slate-600">
                                        Pending Tampil: {visiblePendingTotal}
                                    </span>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <button onClick={handleExportExcel} className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-[10px] font-normal shadow-sm"><FileSpreadsheet className="w-3 h-3" /> Excel</button>
                                <button onClick={handleExportPendingExcel} disabled={pendingTotal === 0} className="flex items-center gap-1 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-200 disabled:text-amber-700 text-white px-2 py-1 rounded text-[10px] font-normal shadow-sm"><FileSpreadsheet className="w-3 h-3" /> Excel Pending</button>
                                <button onClick={handleExportPDF} className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-[10px] font-normal shadow-sm"><Printer className="w-3 h-3" /> PDF</button>
                            </div>
                        </div>

                        {/* TABLE */}
                        <div className="flex-1 overflow-auto">
                            <table className="w-full text-left border-collapse table-fixed text-xs">
                                <thead className="sticky top-0 z-10 bg-gray-100 text-gray-700 font-normal uppercase border-b-2 border-gray-300">
                                    <tr>
                                        <th className="p-2 border border-gray-300 text-center w-10 align-top font-normal">No</th>
                                        
                                        {/* --- [PINDAH] KOLOM ACTION KE SINI --- */}
                                        <th className="p-2 border border-gray-300 text-center w-20 align-top font-normal">Action</th>

                                        <FilterHeader label="Tgl Ajuan" field="tglPengajuan" width="w-24" />
                                        <FilterHeader label="ID Akun" field="idAkun" width="w-20" />
                                        <FilterHeader label="NIK" field="nik" width="w-20" /> 
                                        <FilterHeader label="Nama Karyawan" field="nama" width="w-40" />
                                        <FilterHeader label="Departemen" field="divisi" width="w-24" />
                                        <FilterHeader label="Periode" field="periode" width="w-28" />
                                        <FilterHeader label="Dur" field="durasi" width="w-12" />
                                        <FilterHeader label="Tgl Konflik" field="tglKonflik" width="w-24" textColor="text-red-600" />
                                        <FilterHeader label="Form" field="tipeManual" width="w-20" />
                                        <FilterHeader label="Mesin" field="simbolMesin" width="w-16" />
                                        <FilterHeader label="Scan" field="waktuScan" width="w-20" />
                                        <FilterHeader label="Status" field="status" width="w-24" />
                                    </tr>
                                </thead>
                                <tbody className="text-gray-800 text-xs bg-white font-normal">
                                    {sortedList.length === 0 ? (
                                        <tr><td colSpan="14" className="p-8 text-center text-gray-400 italic border border-gray-300 font-normal">Tidak ada data.</td></tr>
                                    ) : (
                                        sortedList.map((item, idx) => (
                                            <tr key={idx} className="hover:bg-blue-50 transition-colors">
                                                <td className="p-2 border border-gray-300 text-center font-normal">{idx + 1}</td>
                                                
                                                {/* --- [PINDAH] TOMBOL ACTION DI SINI --- */}
                                                <td className="p-1 border border-gray-300 text-center font-normal">
                                                    {item.status === 'Pending' ? (
                                                        <div className="flex justify-center gap-1">
                                                            <button onClick={() => handleProcessApproval(item.uuid, 'approve', item.nama)} className="bg-green-600 hover:bg-green-700 text-white p-1 rounded shadow-sm" title="Approve"><CheckCircle className="w-3.5 h-3.5" /></button>
                                                            <button onClick={() => handleProcessApproval(item.uuid, 'reject', item.nama)} className="bg-red-600 hover:bg-red-700 text-white p-1 rounded shadow-sm" title="Reject"><X className="w-3.5 h-3.5" /></button>
                                                        </div>
                                                    ) : <span className="text-gray-300">-</span>}
                                                </td>

                                                <td className="p-2 border border-gray-300 text-center font-normal">{item.tglPengajuan}</td>
                                                <td className="p-2 border border-gray-300 font-mono text-gray-600 font-normal">{item.idAkun}</td>
                                                <td className="p-2 border border-gray-300 font-mono text-gray-600 font-normal">{item.nik}</td>
                                                <td className="p-2 border border-gray-300 truncate font-normal" title={item.nama}>{item.nama}</td>
                                                <td className="p-2 border border-gray-300 truncate font-normal">{item.divisi}</td>
                                                <td className="p-2 border border-gray-300 text-center font-normal">{item.periode}</td>
                                                <td className="p-2 border border-gray-300 text-center font-normal">{item.durasi}</td>
                                                <td className="p-2 border border-gray-300 text-center font-normal text-red-600">{item.tglKonflik}</td>
                                                <td className="p-2 border border-gray-300 text-center font-normal">{item.tipeManual}</td>
                                                <td className="p-2 border border-gray-300 text-center font-normal">{item.simbolMesin}</td>
                                                <td className="p-2 border border-gray-300 text-center font-mono font-normal">{item.waktuScan}</td>
                                                <td className="p-2 border border-gray-300 text-center font-normal">
                                                    <span className={`px-1.5 py-0.5 rounded border ${getStatusColor(item.status)}`}>{item.status}</span>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}


// --- DASHBOARD SCREEN (FIX: UI POP-UP DITAMBAHKAN) ---
function DashboardScreen({ user, setView, handleLogout }) {
  // 1. STATE POP-UP
  const [announcement, setAnnouncement] = useState(null);
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const menuItems = [
    { id: 'absen', label: 'Absen', icon: Camera, color: 'bg-blue-600', desc: 'Masuk/Pulang' },
    { id: 'history', label: 'Riwayat', icon: History, color: 'bg-emerald-600', desc: 'Cek Aktivitas' },
    { id: 'approval', label: 'Approval', icon: CheckSquare, color: 'bg-indigo-600', desc: 'Acc Bawahan', hidden: approvalMenuHidden(user.role) },
    { id: 'users', label: 'Karyawan', icon: Users, color: 'bg-violet-600', desc: 'Data Pegawai', hidden: user.role !== 'admin' && user.role !== 'hrd' },
    { id: 'master', label: 'Master', icon: Database, color: 'bg-slate-600', desc: 'Setting Data', hidden: user.role !== 'admin' },
    { id: 'remarks', label: 'Laporan', icon: MessageSquare, color: 'bg-orange-600', desc: 'Koreksi/Isu' },
    { id: 'schedule', label: 'Jadwal', icon: CalendarDays, color: 'bg-cyan-600', desc: 'Shift Kerja' },
    { id: 'announcement', label: 'Info HRD', icon: Megaphone, color: 'bg-rose-600', desc: 'Buat Info', hidden: user.role !== 'admin' && user.role !== 'hrd' },
  ];

  return (
    <div className="flex flex-col h-full bg-slate-50 relative">
      {/* HEADER DASHBOARD */}
      <div className="bg-white p-5 pb-8 rounded-b-[30px] shadow-sm relative z-10">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-xl font-extrabold text-slate-800">Halo, {user.nama.split(' ')[0]} 👋</h1>
            <p className="text-xs text-slate-500 font-medium">{user.divisi} • {user.lokasi || 'Indonesia'}</p>
          </div>
          <button onClick={handleLogout} className="p-2 bg-slate-50 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all shadow-sm border border-slate-100">
            <LogOut className="w-5 h-5" />
          </button>
        </div>

        {/* STATS CARD */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-4 text-white shadow-lg shadow-blue-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[10px] font-medium opacity-80 uppercase tracking-wider">Sisa Cuti Tahunan</p>
              <h2 className="text-3xl font-bold mt-1">{user.sisaCuti} <span className="text-sm font-normal opacity-80">Hari</span></h2>
            </div>
            <div className="bg-white/20 p-2 rounded-lg backdrop-blur-sm">
              <Calendar className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* MENU GRID */}
      <div className="flex-1 overflow-y-auto p-5 -mt-4 z-0 pt-8">
        <h3 className="text-sm font-bold text-slate-700 mb-3 ml-1">Menu Utama</h3>
        <div className="grid grid-cols-2 gap-3">
          {menuItems.filter(i => !i.hidden).map((item) => (
            <button
              key={item.id}
              onClick={() => setView(item.id)}
              className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md hover:border-blue-100 transition-all flex flex-col items-center justify-center gap-3 group active:scale-95"
            >
              <div className={`p-3 rounded-xl ${item.color} text-white shadow-md group-hover:scale-110 transition-transform`}>
                <item.icon className="w-6 h-6" />
              </div>
              <div className="text-center">
                <span className="block text-sm font-bold text-slate-700">{item.label}</span>
                <span className="block text-[10px] text-slate-400 font-medium">{item.desc}</span>
              </div>
            </button>
          ))}
        </div>
        
        <div className="mt-6 text-center">
            <p className="text-[10px] text-slate-300 font-bold">E-ABSENSI APP V1.0</p>
        </div>
      </div>

      {/* --- INI BAGIAN YANG SEBELUMNYA HILANG: UI POP UP MODAL --- */}
      {showAnnouncement && announcement && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white w-full max-w-sm rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 transform transition-all">
            
            {/* Header dengan Icon Megaphone */}
            <div className="bg-gradient-to-br from-rose-500 to-orange-500 p-6 text-center relative">
               <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto backdrop-blur-md shadow-inner mb-2">
                  <Megaphone className="w-8 h-8 text-white animate-bounce-slow" />
               </div>
               <button 
                  onClick={() => setShowAnnouncement(false)} 
                  className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 p-1.5 rounded-full text-white transition-colors"
               >
                  <X className="w-5 h-5" />
               </button>
            </div>
            
            {/* Isi Pengumuman */}
            <div className="p-6 text-center">
               <h3 className="text-lg font-extrabold text-slate-800 mb-1">Informasi HRD</h3>
               <p className="text-[10px] font-bold text-rose-500 uppercase tracking-wider mb-4">
                  {announcement.waktu}
               </p>
               
               <div className="bg-rose-50 p-4 rounded-2xl border border-rose-100 max-h-60 overflow-y-auto custom-scrollbar">
                  <p className="text-sm text-slate-700 leading-relaxed font-medium whitespace-pre-line text-left">
                    {announcement.isi}
                  </p>
               </div>
            </div>

            {/* Tombol Tutup */}
            <div className="p-4 pt-0">
               <button 
                  onClick={() => setShowAnnouncement(false)}
                  className="w-full py-3 bg-slate-800 text-white font-bold rounded-xl shadow-lg shadow-slate-200 hover:bg-slate-700 active:scale-95 transition-all"
               >
                  Saya Mengerti
               </button>
            </div>
          </div>
        </div>
      )}
      {/* ---------------------------------------------------------- */}

    </div>
  );
}

// --- 2. REMARK SCREEN (UPDATED: DATE FORMAT DD-MM-YYYY) ---
function RemarkScreen({ user, setView }) {
    const userRole = user.role ? String(user.role).toLowerCase() : '';
    const isHRDOrAdmin = ['admin', 'hrd'].includes(userRole);

    // Tombol "Lapor HRD" dari Data Mesin dapat membawa konteks tanggal yang
    // sudah lewat batas pengajuan izin. Data ini hanya dipakai sekali agar
    // membuka menu Lapor HRD secara biasa tidak pernah mewarisi laporan lama.
    const [laporanAwal] = useState(() => {
        try {
            const raw = sessionStorage.getItem('laporan_hrd_awal');
            return raw ? JSON.parse(raw) : {};
        } catch (e) { return {}; }
    });
    const [tglKoreksi, setTglKoreksi] = useState(laporanAwal.tgl || '');
    const [modeTanggalKoreksi, setModeTanggalKoreksi] = useState('single');
    const [tglKoreksiSelesai, setTglKoreksiSelesai] = useState('');
    // KUNCI PERIODE LALU (Okt 2026): Lapor HRD tidak bisa untuk tanggal di
    // periode yang sudah ditutup. Server tetap memeriksa ulang.
    const batasTanggalLapor = batasPengajuanMundur(bacaPeriodeAktifTersimpan(), tglLokal());
    const [tanggalPilihan, setTanggalPilihan] = useState([]);
    const [tanggalUntukDitambah, setTanggalUntukDitambah] = useState('');
    const [kategori, setKategori] = useState(laporanAwal.kategori || 'Koreksi Absensi');
    const isLupaDl = kategori === KATEGORI_LUPA_DL;
    const [pesan, setPesan] = useState(laporanAwal.pesan || '');
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [loading, setLoading] = useState(false);
    const [remarks, setRemarks] = useState([]);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [remarkDoneItem, setRemarkDoneItem] = useState(null);
    const [remarkDoneResponse, setRemarkDoneResponse] = useState('Sudah diproses.');
    const [remarkPotongKuota, setRemarkPotongKuota] = useState('none');
    const [remarkDoneLoading, setRemarkDoneLoading] = useState(false);

    // STATUS PENGAMBILAN DATA
    // Sebelum ini, gagal-ambil dan benar-benar-kosong tampil PERSIS SAMA
    // ("Belum ada data laporan"), sehingga request yang gagal terlihat
    // seperti sheet yang kosong. Tiga state ini memisahkan keduanya.
    const [remarksLoading, setRemarksLoading] = useState(true);
    const [remarksError, setRemarksError] = useState('');
    const [remarksTotal, setRemarksTotal] = useState(null); // total di server (bisa > yang dimuat)
    const [muatSemua, setMuatSemua] = useState(false);

    // VIEW MODE STATE
    const [viewMode, setViewMode] = useState('list'); 

    // TABLE FILTER STATE
    const [reportColumnFilters, setReportColumnFilters] = useState({});
    const [reportSortConfig, setReportSortConfig] = useState({ key: null, direction: 'asc' });
    const [activeReportFilter, setActiveReportFilter] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10); // Default 10 baris

    useEffect(() => {
        // Hapus setelah state formulir terbentuk, bukan saat membaca, agar
        // React Strict Mode tidak kehilangan prefill pada render pengembangan.
        sessionStorage.removeItem('laporan_hrd_awal');
    }, []);

    // --- HELPER FORMAT TANGGAL (DD-MM-YYYY) ---
    const formatDateDisplay = (value) => {
        if (!value || value === '-' || value === '') return '-';
        const strVal = String(value);

        // Rentang dan tanggal acak dikirim sebagai teks agar tetap kompatibel
        // dengan satu kolom Remarks yang sudah ada. Format setiap tanggalnya
        // tetap dipoles saat tampil di aplikasi.
        if (strVal.includes(' s/d ')) {
            return strVal.split(' s/d ').map(formatDateDisplay).join(' s/d ');
        }
        if (strVal.includes(', ')) {
            return strVal.split(', ').map(formatDateDisplay).join(', ');
        }

        // 1. Jika format YYYY-MM-DD (dari Input Date/Backend TglKoreksi) -> Ubah ke DD-MM-YYYY
        if (strVal.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [y, m, d] = strVal.split('-');
            return `${d}-${m}-${y}`;
        }
        
        // 2. Jika format dd/mm/yyyy (dari Timestamp Backend) -> Ubah / jadi -
        if (strVal.includes('/')) {
            return strVal.replace(/\//g, '-');
        }

        return strVal;
    };

    const tanggalKoreksiUntukDikirim = () => {
        if (modeTanggalKoreksi === 'single') {
            return { value: tglKoreksi };
        }
        if (modeTanggalKoreksi === 'range') {
            if (!tglKoreksi || !tglKoreksiSelesai) {
                return { error: 'Pilih tanggal mulai dan tanggal selesai.' };
            }
            if (tglKoreksiSelesai < tglKoreksi) {
                return { error: 'Tanggal selesai tidak boleh lebih awal dari tanggal mulai.' };
            }
            return { value: tglKoreksi === tglKoreksiSelesai ? tglKoreksi : `${tglKoreksi} s/d ${tglKoreksiSelesai}` };
        }
        if (tanggalPilihan.length === 0) {
            return { error: 'Tambahkan minimal satu tanggal koreksi.' };
        }
        return { value: [...tanggalPilihan].sort().join(', ') };
    };

    const tambahTanggalPilihan = () => {
        if (!tanggalUntukDitambah) return;
        const pesanKunci = pesanKunciPeriode([tanggalUntukDitambah], batasTanggalLapor);
        if (pesanKunci) { alert(pesanKunci); return; }
        setTanggalPilihan((sebelumnya) => [...new Set([...sebelumnya, tanggalUntukDitambah])].sort());
        setTanggalUntukDitambah('');
    };

    const hapusTanggalPilihan = (tanggal) => {
        setTanggalPilihan((sebelumnya) => sebelumnya.filter((item) => item !== tanggal));
    };

    // Close dropdown logic
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (activeReportFilter && !event.target.closest('.report-filter-container')) {
                setActiveReportFilter(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [activeReportFilter]);

    useEffect(() => {
        let dibatalkan = false;

        const fetchRemarks = async () => {
            setRemarksLoading(true);
            setRemarksError('');
            try {
                const res = await fetchApi(SCRIPT_URL, {
                    method: 'POST',
                    body: JSON.stringify({
                        action: 'get_remarks',
                        userId: user.id,
                        role: userRole,
                        // Backend membatasi jumlah baris yang dikirim (default 200 terbaru).
                        // Seluruh isi sheet Remarks sekaligus berukuran ~300-400 KB dan
                        // itulah request yang paling sering dibalas HTML oleh Google.
                        muatSemua: muatSemua
                    })
                });
                const data = await res.json();
                if (dibatalkan) return;

                if (data.result === 'success') {
                    setRemarks(Array.isArray(data.list) ? data.list : []);
                    setRemarksTotal(typeof data.total === 'number' ? data.total : null);
                    // Sheet tidak ditemukan tetap dibalas 'success' oleh backend lama.
                    // Kalau backend baru mengirim sheetDitemukan:false, tampilkan apa adanya.
                    if (data.sheetDitemukan === false) {
                        setRemarksError('Sheet "Remarks" tidak ditemukan di spreadsheet backend. Periksa nama tab-nya.');
                    }
                } else {
                    // INI yang dulu hilang: pesan error dari fetchApi
                    // (RESPONS_BUKAN_JSON, FORBIDDEN, dsb) dibuang begitu saja.
                    setRemarksError(data.message || 'Gagal mengambil data laporan dari server.');
                }
            } catch (e) {
                if (dibatalkan) return;
                console.error('Gagal load remark:', e);
                setRemarksError('Tidak bisa menghubungi server. Periksa koneksi lalu coba lagi.');
            } finally {
                if (!dibatalkan) setRemarksLoading(false);
            }
        };
        fetchRemarks();

        return () => { dibatalkan = true; };
    }, [user.id, userRole, refreshTrigger, muatSemua]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            if(selectedFile.size > 5 * 1024 * 1024) { alert("Ukuran file maksimal 5MB"); return; }
            setFileName(selectedFile.name);
            const reader = new FileReader();
            reader.onloadend = () => { setFile(reader.result); };
            reader.readAsDataURL(selectedFile);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const hasilTanggal = tanggalKoreksiUntukDikirim();
        if (hasilTanggal.error || !hasilTanggal.value) {
            alert(hasilTanggal.error || 'Pilih tanggal koreksi terlebih dahulu.');
            return;
        }
        {
            const pesanKunci = pesanKunciPeriode(String(hasilTanggal.value).match(/\d{4}-\d{2}-\d{2}/g) || [], batasTanggalLapor);
            if (pesanKunci) { alert(pesanKunci); return; }
        }
        if (isLupaDl) {
            if (!file || !String(file).startsWith('data:image/')) {
                alert('Lampirkan foto kegiatan dinas (gambar) untuk laporan lupa pengajuan DL.');
                return;
            }
            if (pesan.trim().length < 3) {
                alert('Keterangan dinas wajib diisi.');
                return;
            }
        }
        setLoading(true);
        try {
            const res = await fetchApi(SCRIPT_URL, {
                method: 'POST',
                body: JSON.stringify({
                    action: 'send_remark', 
                    userId: user.id, 
                    nama: user.nama, 
                    divisi: user.divisi,
                    tglKoreksi: hasilTanggal.value,
                    kategori, 
                    pesan, 
                    file
                })
            }).then(r => r.json());
            if (res.result === 'success') {
                alert('Laporan berhasil dikirim ke HRD!');
                setPesan(''); setTglKoreksi(''); setTglKoreksiSelesai(''); setTanggalPilihan([]); setTanggalUntukDitambah(''); setFile(null); setFileName('');
                setRefreshTrigger(prev => prev + 1); 
            } else {
                alert('Gagal mengirim laporan: ' + res.message);
            }
        } catch (err) { alert('Gagal koneksi.'); } finally { setLoading(false); }
    };

    const angkaKuota = (value) => {
        const num = Number(value);
        return Number.isFinite(num) ? num : 0;
    };

    const kuotaTidakTersedia = (item) => angkaKuota(item?.sisaIjin) <= 0 && angkaKuota(item?.sisaCuti) <= 0;

    const remarkIsLupaDl = (item) => String(item?.kategori || '').trim() === KATEGORI_LUPA_DL;

    // HAPUS LAPORAN — khusus admin & HRD (server juga memeriksa).
    const [remarkDeleting, setRemarkDeleting] = useState('');
    const handleDeleteRemark = async (item) => {
        if (!isHRDOrAdmin || !item || !item.uuid || remarkDeleting) return;
        let pesan = `Hapus laporan ${item.nama || ''} (${item.kategori || '-'})?\n\n"${String(item.pesan || '').slice(0, 120)}"\n\nTindakan ini tidak bisa dibatalkan.`;
        if (item.potongKuota && item.potongKuota !== 'none') {
            pesan += '\n\nCatatan: potongan kuota dari laporan ini TIDAK ikut terhapus (tetap ada di data absensi).';
        }
        if (!window.confirm(pesan)) return;
        try {
            setRemarkDeleting(item.uuid);
            const res = await fetchApi(SCRIPT_URL, {
                method: 'POST',
                body: JSON.stringify({ action: 'delete_remark', uuid: item.uuid, approverName: user.nama })
            }).then(r => r.json());
            if (res.result === 'success') {
                setRemarks(prev => prev.filter(r => r.uuid !== item.uuid));
                setRefreshTrigger(prev => prev + 1);
            } else alert(res.message || 'Gagal menghapus laporan.');
        } catch (e) { alert('Gagal menghapus laporan (koneksi).'); }
        finally { setRemarkDeleting(''); }
    };

    const openMarkDoneDialog = (item) => {
        setRemarkDoneItem(item);
        if (remarkIsLupaDl(item)) {
            setRemarkDoneResponse('Disetujui, DL dicatat.');
            setRemarkPotongKuota('Dinas');
            return;
        }
        setRemarkDoneResponse(kuotaTidakTersedia(item) ? 'Ditolak karena jatah Ijin dan Cuti tidak tersedia.' : 'Sudah diproses.');
        setRemarkPotongKuota('none');
    };

    const closeMarkDoneDialog = () => {
        if (remarkDoneLoading) return;
        setRemarkDoneItem(null);
        setRemarkDoneResponse('Sudah diproses.');
        setRemarkPotongKuota('none');
    };

    const formatPotongKuota = (item) => {
        const tipe = item?.potongKuota && item.potongKuota !== 'none' ? item.potongKuota : '';
        if (!tipe) return '-';
        const jumlah = Number(item.potongJumlah) || 1;
        const satuan = tipe === 'Ijin' ? 'x' : 'hari';
        return `${tipe === 'Dinas' ? 'DL' : tipe} ${jumlah} ${satuan}`;
    };

    const opsiPotongTidakTersedia = (value, item = remarkDoneItem) => {
        if (value === 'Ijin') return angkaKuota(item?.sisaIjin) <= 0;
        if (value === 'Cuti') return angkaKuota(item?.sisaCuti) <= 0;
        return false;
    };

    const handleMarkDone = async (e) => {
        e.preventDefault();
        if (!remarkDoneItem) return;
        if (opsiPotongTidakTersedia(remarkPotongKuota)) {
            alert(`Jatah ${remarkPotongKuota} sudah 0. Pilih "Tidak memotong" dan simpan respon penolakan HRD.`);
            return;
        }
        const responseText = remarkDoneResponse.trim();
        if (responseText === "") {
            alert("Tanggapan/respon wajib diisi.");
            return;
        }

        try {
            setRemarkDoneLoading(true);
            const res = await fetchApi(SCRIPT_URL, {
                method: 'POST',
                body: JSON.stringify({
                    action: 'update_remark_status',
                    uuid: remarkDoneItem.uuid,
                    response: responseText,
                    potongKuota: remarkPotongKuota,
                    approverName: user.nama
                })
            }).then(r => r.json());
            if (res.result === 'success') {
                alert(res.message || "Status diperbarui & Respon terkirim!");
                setRemarkDoneItem(null);
                setRemarkDoneResponse('Sudah diproses.');
                setRemarkPotongKuota('none');
                setRefreshTrigger(prev => prev + 1);
            } else alert(res.message);
        } catch (e) { alert("Gagal update"); }
        finally { setRemarkDoneLoading(false); }
    };

    // --- FILTER LOGIC ---
    const getReportUniqueValues = (field) => {
        const values = remarks.map(item => item[field]).filter(v => v !== null && v !== undefined && v !== '');
        return [...new Set(values)].sort();
    };

    const toggleReportFilterValue = (field, value) => {
        setReportColumnFilters(prev => {
            const currentValues = prev[field] || [];
            if (currentValues.includes(value)) return { ...prev, [field]: currentValues.filter(v => v !== value) };
            else return { ...prev, [field]: [...currentValues, value] };
        });
    };

    const toggleReportSelectAll = (field, visibleOptions) => {
        setReportColumnFilters(prev => {
            const currentValues = prev[field] || [];
            const allVisibleSelected = visibleOptions.every(val => currentValues.includes(val));
            if (allVisibleSelected) return { ...prev, [field]: currentValues.filter(v => !visibleOptions.includes(v)) };
            else {
                const newValues = [...currentValues];
                visibleOptions.forEach(v => { if (!newValues.includes(v)) newValues.push(v); });
                return { ...prev, [field]: newValues };
            }
        });
    };

    const filteredRemarksTable = remarks.filter(item => {
        return Object.keys(reportColumnFilters).every(key => {
            const selectedValues = reportColumnFilters[key];
            if (!selectedValues || selectedValues.length === 0) return true;
            return selectedValues.includes(String(item[key]));
        });
    });

    const sortedRemarksTable = React.useMemo(() => {
        let sortableItems = [...filteredRemarksTable];
        if (reportSortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let valA = a[reportSortConfig.key] || '';
                let valB = b[reportSortConfig.key] || '';
                return reportSortConfig.direction === 'asc' 
                    ? String(valA).localeCompare(String(valB)) 
                    : String(valB).localeCompare(String(valA));
            });
        }
        return sortableItems;
    }, [filteredRemarksTable, reportSortConfig]);

    const requestReportSort = (key, direction) => {
        setReportSortConfig({ key, direction });
    };

    // Show Columns Logic
    const showResponseColumns = sortedRemarksTable.some(item => item.status === 'Done');

    // --- EXPORT FUNCTION ---
    const generateExcel = () => {
        let tableHead = ["No", "Waktu Lapor", "Tgl Koreksi", "Nama", "Divisi", "Jenis", "Keterangan", "Status", "Potong Kuota"];
        
        if (showResponseColumns) {
            tableHead.push("Respon HRD", "Waktu Respon");
        }

        const tableBody = sortedRemarksTable.map((item, index) => {
            let row = [
                index + 1, 
                formatDateDisplay(item.waktu),       // [UPDATE] Format DD-MM-YYYY
                formatDateDisplay(item.tglKoreksi),  // [UPDATE] Format DD-MM-YYYY
                item.nama, item.divisi, item.kategori, item.pesan, item.status, formatPotongKuota(item)
            ];
            if (showResponseColumns) {
                row.push(item.respon, formatDateDisplay(item.waktuRespon)); // [UPDATE] Format DD-MM-YYYY
            }
            return row;
        });

        const worksheet = XLSX.utils.aoa_to_sheet([tableHead, ...tableBody]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Laporan_Masuk");
        XLSX.writeFile(workbook, `Laporan_Remarks_${new Date().getTime()}.xlsx`);
    };

    const generatePDF = () => {
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        doc.setFontSize(10);
        doc.text("Laporan Respon / Masukan Karyawan", 14, 15);
        doc.setFontSize(8);
        doc.text(`Total Data: ${sortedRemarksTable.length}`, 14, 20);

        let tableColumn = ["No", "Waktu", "Tgl Koreksi", "Nama", "Divisi", "Jenis", "Keterangan", "Status", "Potong"];
        
        if (showResponseColumns) {
            tableColumn.push("Respon");
        }

        const tableRows = sortedRemarksTable.map((item, index) => {
            let row = [
                index + 1, 
                formatDateDisplay(item.waktu),       // [UPDATE] Format DD-MM-YYYY
                formatDateDisplay(item.tglKoreksi),  // [UPDATE] Format DD-MM-YYYY
                item.nama, item.divisi, item.kategori, item.pesan, item.status, formatPotongKuota(item)
            ];
            if (showResponseColumns) {
                row.push(item.respon);
            }
            return row;
        });

        autoTable(doc, {
            head: [tableColumn], body: tableRows, startY: 25, theme: 'grid',
            styles: { fontSize: 7, cellPadding: 1, valign: 'middle' },
            headStyles: { fillColor: [50, 50, 50] }
        });
        doc.save(`Laporan_Remarks_${new Date().getTime()}.pdf`);
    };

    // --- FILTER HEADER COMPONENT ---
    const ReportFilterHeader = ({ label, field, width }) => {
        const uniqueOptions = getReportUniqueValues(field);
        const selectedValues = reportColumnFilters[field] || [];
        const isOpen = activeReportFilter === field;
        const [searchTerm, setSearchTerm] = useState('');
        const visibleOptions = uniqueOptions.filter(opt => String(opt).toLowerCase().includes(searchTerm.toLowerCase()));

        return (
            <th className={`p-0 border border-gray-300 bg-gray-100 align-top ${width || 'w-auto'} relative`}>
                <div className="flex flex-col h-full">
                    <div className="flex items-center justify-between p-2 pb-1">
                        <span className="text-[10px] font-bold text-gray-700 uppercase">{label}</span>
                        {reportSortConfig.key === field && (
                            <span className="text-[9px] text-gray-800 font-bold ml-1">{reportSortConfig.direction === 'asc' ? '↓' : '↑'}</span>
                        )}
                    </div>
                    <div className="px-2 pb-2 report-filter-container">
                        <button onClick={(e) => { e.stopPropagation(); setActiveReportFilter(isOpen ? null : field); }} 
                            className={`flex items-center justify-between w-full text-[9px] px-2 py-1 border rounded bg-white shadow-sm transition-all ${selectedValues.length > 0 ? 'text-blue-700 border-blue-400 bg-blue-50' : 'text-gray-600 border-gray-300 hover:border-gray-400'}`}>
                            <span className="truncate font-medium">{selectedValues.length === 0 ? "(All)" : `${selectedValues.length} Selected`}</span>
                            <Filter className="w-2.5 h-2.5 ml-1 opacity-70" />
                        </button>
                        {isOpen && (
                            <div className="absolute top-[95%] left-0 mt-0 w-48 bg-white border border-gray-400 shadow-xl z-[100] flex flex-col max-h-64 rounded-sm">
                                <div className="p-1.5 border-b bg-gray-50 flex gap-1">
                                    <button onClick={() => requestReportSort(field, 'asc')} className="flex-1 text-[9px] font-bold bg-white border rounded p-1 hover:bg-gray-200">A-Z</button>
                                    <button onClick={() => requestReportSort(field, 'desc')} className="flex-1 text-[9px] font-bold bg-white border rounded p-1 hover:bg-gray-200">Z-A</button>
                                </div>
                                <div className="p-1.5 border-b relative">
                                    <input type="text" placeholder="Cari..." className="w-full text-[9px] border p-1 rounded bg-white outline-none" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} autoFocus />
                                </div>
                                <div className="px-2 py-1.5 bg-gray-50 flex items-center gap-2 border-b hover:bg-gray-100 cursor-pointer" onClick={() => toggleReportSelectAll(field, visibleOptions)}>
                                    <input type="checkbox" readOnly checked={visibleOptions.length > 0 && visibleOptions.every(v => selectedValues.includes(v))} className="w-3 h-3 text-blue-600 rounded border-gray-300"/>
                                    <span className="text-[9px] font-bold text-gray-700">Select All</span>
                                </div>
                                <div className="overflow-y-auto flex-1 p-1 custom-scrollbar">
                                    {visibleOptions.map((val, idx) => (
                                        <label key={idx} className="flex items-center gap-2 px-1.5 py-1 hover:bg-blue-50 cursor-pointer rounded transition-colors">
                                            <input type="checkbox" className="w-3 h-3 text-blue-600 rounded border-gray-300 focus:ring-blue-500" checked={selectedValues.includes(val)} onChange={() => toggleReportFilterValue(field, val)}/>
                                            <span className="text-[9px] text-gray-700 truncate font-medium">{val}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </th>
        );
    };

    // LOGIC SWITCH STYLE
    const isTableMode = viewMode === 'table' && isHRDOrAdmin;
    const containerClass = isTableMode
        ? "fixed inset-0 z-[50] bg-white flex flex-col"
        : "min-h-full overflow-y-auto bg-slate-50 px-4 pb-24 pt-5 sm:px-6 sm:pt-7";

    // --- PANEL STATUS PENGAMBILAN DATA ---
    // Dipakai di kedua mode (list & tabel) supaya sumber kegagalan yang sama
    // tidak lagi muncul sebagai dua "layar kosong" yang berbeda.
    const StatusPanel = () => {
        if (remarksLoading) {
            return (
                <div className="flex items-center justify-center gap-2 py-6 text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-xs font-medium">Memuat data laporan...</span>
                </div>
            );
        }
        if (remarksError) {
            return (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 my-3">
                    <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                        <div className="flex-1">
                            <p className="text-xs font-bold text-red-700 mb-1">Data laporan gagal dimuat</p>
                            <p className="text-[11px] text-red-600 leading-relaxed">{remarksError}</p>
                            <p className="text-[10px] text-red-500 mt-1 italic">
                                Ini BUKAN berarti tidak ada laporan — data di server tidak berhasil diambil.
                            </p>
                            <button
                                onClick={() => setRefreshTrigger(t => t + 1)}
                                className="mt-2 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg text-[10px] font-bold flex items-center gap-1.5 transition"
                            >
                                <RefreshCcw className="w-3 h-3" /> Coba Lagi
                            </button>
                        </div>
                    </div>
                </div>
            );
        }
        // Sukses, tapi server masih menyimpan lebih banyak daripada yang dimuat
        if (!muatSemua && remarksTotal !== null && remarksTotal > remarks.length) {
            return (
                <div className="bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 my-3 flex items-center justify-between gap-2">
                    <span className="text-[10px] text-blue-800 font-medium">
                        Menampilkan {remarks.length} laporan terbaru dari total {remarksTotal}.
                    </span>
                    <button
                        onClick={() => setMuatSemua(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1 rounded-lg text-[10px] font-bold shrink-0 transition"
                    >
                        Muat Semua
                    </button>
                </div>
            );
        }
        return null;
    };

    const dialogLupaDl = remarkIsLupaDl(remarkDoneItem);
    const opsiPotongKuota = dialogLupaDl ? [
        { value: 'Dinas', label: 'Setujui & catat DL', desc: 'Mencatat Dinas Approved pada tanggal laporan, foto lampiran jadi bukti, dan simbol DL ditulis ke payroll.' },
        { value: 'none', label: 'Tolak / tutup tanpa mencatat', desc: 'Laporan ditutup dengan tanggapan HRD, tidak ada DL yang dicatat.' }
    ] : [
        { value: 'none', label: 'Tidak memotong', desc: 'Laporan hanya ditutup dan diberi tanggapan.' },
        { value: 'Ijin', label: 'Potong jatah Ijin', desc: 'Mencatat Ijin Approved agar terhitung dalam batas 4x per periode.' },
        { value: 'Cuti', label: 'Potong jatah Cuti', desc: 'Mencatat Cuti Approved agar ikut sinkron ke MASTER-CUTI.' }
    ];

    return (
        <div className={containerClass}>
            {remarkDoneItem && (
                <div className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/45 p-4 sm:items-center">
                    <form onSubmit={handleMarkDone} className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
                        <div className="border-b border-slate-100 px-5 py-4">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Selesaikan laporan</p>
                                    <h3 className="mt-1 truncate text-[17px] font-semibold text-slate-900">{remarkDoneItem.nama}</h3>
                                    <p className="mt-0.5 text-[12px] text-slate-500">{formatDateDisplay(remarkDoneItem.tglKoreksi)} - {remarkDoneItem.kategori}</p>
                                </div>
                                <button type="button" onClick={closeMarkDoneDialog} disabled={remarkDoneLoading} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50" aria-label="Tutup dialog">
                                    <X className="h-4 w-4" strokeWidth={2}/>
                                </button>
                            </div>
                        </div>

                        <div className="space-y-4 px-5 py-4">
                            {dialogLupaDl && (
                                <div className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2.5 text-[12px] text-sky-900">
                                    <p className="font-semibold">Laporan lupa pengajuan DL</p>
                                    <p className="mt-0.5 leading-relaxed">{remarkDoneItem.pesan || '-'}</p>
                                    {remarkDoneItem.lampiran && remarkDoneItem.lampiran !== '-' && (
                                        <a href={remarkDoneItem.lampiran} target="_blank" rel="noreferrer" className="mt-1 inline-block font-semibold text-sky-700 underline">Lihat foto dinas</a>
                                    )}
                                </div>
                            )}
                            {!dialogLupaDl && (<>
                            <div className="grid grid-cols-2 gap-2">
                                <div className={`rounded-xl border px-3 py-2.5 ${angkaKuota(remarkDoneItem.sisaIjin) <= 0 ? 'border-rose-200 bg-rose-50' : 'border-amber-200 bg-amber-50'}`}>
                                    <p className={`text-[10px] font-semibold uppercase tracking-[0.12em] ${angkaKuota(remarkDoneItem.sisaIjin) <= 0 ? 'text-rose-500' : 'text-amber-600'}`}>Sisa Ijin</p>
                                    <p className={`mt-1 text-[18px] font-bold leading-none ${angkaKuota(remarkDoneItem.sisaIjin) <= 0 ? 'text-rose-700' : 'text-amber-700'}`}>{angkaKuota(remarkDoneItem.sisaIjin)}x</p>
                                    <p className="mt-1 text-[10px] text-slate-500">{angkaKuota(remarkDoneItem.ijinTerpakai)} dari 4 terpakai</p>
                                </div>
                                <div className={`rounded-xl border px-3 py-2.5 ${angkaKuota(remarkDoneItem.sisaCuti) <= 0 ? 'border-rose-200 bg-rose-50' : 'border-teal-200 bg-teal-50'}`}>
                                    <p className={`text-[10px] font-semibold uppercase tracking-[0.12em] ${angkaKuota(remarkDoneItem.sisaCuti) <= 0 ? 'text-rose-500' : 'text-teal-600'}`}>Sisa Cuti</p>
                                    <p className={`mt-1 text-[18px] font-bold leading-none ${angkaKuota(remarkDoneItem.sisaCuti) <= 0 ? 'text-rose-700' : 'text-teal-700'}`}>{angkaKuota(remarkDoneItem.sisaCuti)} hari</p>
                                    <p className="mt-1 text-[10px] text-slate-500">Tersedia di MASTER-CUTI</p>
                                </div>
                            </div>

                            {kuotaTidakTersedia(remarkDoneItem) && (
                                <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5">
                                    <p className="text-[12px] font-semibold text-rose-700">Tidak ada jatah tersedia.</p>
                                    <p className="mt-0.5 text-[11px] leading-relaxed text-rose-600">Opsi pemotongan dikunci. Simpan sebagai respon penolakan HRD.</p>
                                </div>
                            )}
                            </>)}

                            <div>
                                <label className="mb-2 block text-[12px] font-semibold text-slate-700">Tanggapan HRD</label>
                                <textarea
                                    required
                                    rows="3"
                                    value={remarkDoneResponse}
                                    onChange={(e) => setRemarkDoneResponse(e.target.value)}
                                    className="w-full resize-y rounded-xl border border-slate-200 px-3.5 py-3 text-[14px] text-slate-800 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                                />
                            </div>

                            <div>
                                <label className="mb-2 block text-[12px] font-semibold text-slate-700">{dialogLupaDl ? 'Keputusan' : 'Pemotongan kuota'}</label>
                                <div className="grid gap-2">
                                    {opsiPotongKuota.map((opsi) => {
                                        const terkunci = opsiPotongTidakTersedia(opsi.value);
                                        const labelTampil = !dialogLupaDl && opsi.value === 'none' && kuotaTidakTersedia(remarkDoneItem) ? 'Tolak karena jatah habis' : opsi.label;
                                        const deskripsiTampil = !dialogLupaDl && opsi.value === 'none' && kuotaTidakTersedia(remarkDoneItem)
                                            ? 'Simpan sebagai penolakan HRD karena tidak ada jatah Ijin atau Cuti.'
                                            : terkunci
                                                ? `Jatah ${opsi.value} sudah 0, jadi laporan harus ditolak tanpa pemotongan.`
                                                : opsi.desc;
                                        const sisaText = opsi.value === 'Ijin'
                                            ? `Sisa ${angkaKuota(remarkDoneItem.sisaIjin)}x`
                                            : opsi.value === 'Cuti'
                                                ? `Sisa ${angkaKuota(remarkDoneItem.sisaCuti)} hari`
                                                : '';
                                        return (
                                            <label key={opsi.value} className={`flex items-start gap-3 rounded-xl border px-3.5 py-3 transition ${terkunci ? 'cursor-not-allowed border-slate-200 bg-slate-50 opacity-70' : 'cursor-pointer'} ${remarkPotongKuota === opsi.value ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500/10' : !terkunci ? 'border-slate-200 bg-white hover:border-slate-300' : ''}`}>
                                                <input
                                                    type="radio"
                                                    name="remarkPotongKuota"
                                                    value={opsi.value}
                                                    checked={remarkPotongKuota === opsi.value}
                                                    disabled={terkunci}
                                                    onChange={(e) => setRemarkPotongKuota(e.target.value)}
                                                    className="mt-1 h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                                                />
                                                <span className="min-w-0">
                                                    <span className={`block text-[13px] font-semibold ${terkunci ? 'text-slate-500' : 'text-slate-800'}`}>{labelTampil}{sisaText ? ` - ${sisaText}` : ''}</span>
                                                    <span className="mt-0.5 block text-[11px] leading-relaxed text-slate-500">{deskripsiTampil}</span>
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-3">
                            <button type="button" onClick={closeMarkDoneDialog} disabled={remarkDoneLoading} className="rounded-xl px-4 py-2 text-[13px] font-semibold text-slate-600 transition hover:bg-white disabled:opacity-50">Batal</button>
                            <button type="submit" disabled={remarkDoneLoading} className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-xl bg-green-600 px-4 py-2 text-[13px] font-semibold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-slate-400">
                                {remarkDoneLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Memproses</> : <><Check className="h-4 w-4" /> Simpan</>}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* HEADER AREA */}
            <div className={`flex items-center justify-between ${isTableMode ? 'mb-4 px-4 py-3 bg-white border-b shadow-sm' : 'mx-auto mb-5 w-full max-w-2xl'}`}>
                <div>
                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Pusat bantuan</p>
                    <h2 className="text-[22px] font-semibold tracking-tight text-slate-900">{isHRDOrAdmin ? 'Respon Laporan Masuk' : 'Lapor & Riwayat'}</h2>
                    {!isTableMode && <p className="mt-1 text-[13px] text-slate-500">{isHRDOrAdmin ? 'Kelola dan respon laporan karyawan' : 'Kirim koreksi atau pertanyaan ke tim HRD.'}</p>}
                </div>
                <div className="flex gap-2">
                    {isHRDOrAdmin && (
                        <button 
                            onClick={() => setViewMode(viewMode === 'list' ? 'table' : 'list')}
                            className={`${isTableMode ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'} px-3 py-2 rounded-lg text-xs font-bold transition shadow-sm flex items-center gap-2`}
                        >
                            {viewMode === 'list' ? <FileSpreadsheet className="w-4 h-4 text-green-600"/> : <MessageSquare className="w-4 h-4 text-white"/>}
                            {viewMode === 'list' ? 'View Tabel' : 'Kembali ke List'}
                        </button>
                    )}
                    {!isTableMode && <BackButton onClick={() => setView('dashboard')} />}
                    {isTableMode && (
                         <button onClick={() => setViewMode('list')} className="p-2 hover:bg-red-50 text-gray-500 hover:text-red-600 rounded-lg transition" title="Exit Full Screen">
                            <X className="w-5 h-5" />
                         </button>
                    )}
                </div>
            </div>

            {/* FORM BUAT LAPORAN (LIST MODE ONLY) */}
            {(!isHRDOrAdmin || viewMode === 'list') && !isHRDOrAdmin && (
                <section className="mx-auto mb-8 w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_12px_32px_rgba(15,23,42,0.06)]">
                    <div className="border-b border-slate-100 bg-gradient-to-r from-slate-900 to-slate-800 px-5 py-5 sm:px-6">
                        <div className="flex items-start gap-3.5">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/15">
                                <Edit className="h-5 w-5 text-white" strokeWidth={2}/>
                            </div>
                            <div>
                                <h3 className="text-[17px] font-semibold text-white">Buat laporan</h3>
                                <p className="mt-0.5 text-[12px] leading-relaxed text-slate-300">Lengkapi informasi di bawah agar HRD dapat menindaklanjuti dengan tepat.</p>
                            </div>
                        </div>
                    </div>
                    <form onSubmit={handleSubmit} className="space-y-5 p-5 sm:p-6">
                        <div>
                            <label className="mb-2 block text-[13px] font-medium text-slate-700">Tanggal koreksi <span className="text-rose-500">*</span></label>
                            <div className="mb-3 grid grid-cols-3 rounded-xl bg-slate-100 p-1" role="group" aria-label="Jenis pilihan tanggal">
                                {[
                                    ['single', 'Satu tanggal'],
                                    ['range', 'Rentang'],
                                    ['multiple', 'Pilih acak']
                                ].map(([mode, label]) => (
                                    <button
                                        key={mode}
                                        type="button"
                                        onClick={() => setModeTanggalKoreksi(mode)}
                                        aria-pressed={modeTanggalKoreksi === mode}
                                        className={`min-h-[40px] rounded-lg px-2 text-[11px] font-semibold transition sm:text-[12px] ${modeTanggalKoreksi === mode ? 'bg-white text-blue-700 shadow-sm ring-1 ring-slate-200' : 'text-slate-500 hover:text-slate-700'}`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>

                            {modeTanggalKoreksi === 'single' && (
                                <div className="relative">
                                    <CalendarDays className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" strokeWidth={1.8}/>
                                    <input type="date" required min={batasTanggalLapor || undefined} className="min-h-[52px] w-full rounded-xl border border-slate-200 bg-white py-3 pl-11 pr-3 text-[15px] text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10" value={tglKoreksi} onChange={e => setTglKoreksi(e.target.value)} />
                                </div>
                            )}

                            {modeTanggalKoreksi === 'range' && (
                                <div className="grid gap-3 sm:grid-cols-2">
                                    <div>
                                        <label className="mb-1.5 block text-[11px] font-medium text-slate-500">Tanggal mulai</label>
                                        <input type="date" required min={batasTanggalLapor || undefined} className="min-h-[52px] w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-[15px] text-slate-800 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10" value={tglKoreksi} onChange={e => setTglKoreksi(e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="mb-1.5 block text-[11px] font-medium text-slate-500">Tanggal selesai</label>
                                        <input type="date" required min={tglKoreksi || batasTanggalLapor || undefined} className="min-h-[52px] w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-[15px] text-slate-800 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10" value={tglKoreksiSelesai} onChange={e => setTglKoreksiSelesai(e.target.value)} />
                                    </div>
                                </div>
                            )}

                            {modeTanggalKoreksi === 'multiple' && (
                                <div className="space-y-3">
                                    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
                                        <input type="date" min={batasTanggalLapor || undefined} className="min-h-[52px] min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-3 text-[15px] text-slate-800 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10" value={tanggalUntukDitambah} onChange={e => setTanggalUntukDitambah(e.target.value)} />
                                        <button type="button" onClick={tambahTanggalPilihan} disabled={!tanggalUntukDitambah} className="min-h-[52px] rounded-xl bg-slate-900 px-4 text-[13px] font-semibold text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:bg-slate-300">Tambah</button>
                                    </div>
                                    {tanggalPilihan.length > 0 ? (
                                        <div className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                                            {tanggalPilihan.map((tanggal) => (
                                                <span key={tanggal} className="inline-flex min-h-[32px] items-center gap-1.5 rounded-lg bg-white py-1 pl-2.5 pr-1 text-[12px] font-medium text-slate-700 shadow-sm ring-1 ring-slate-200">
                                                    {formatDateDisplay(tanggal)}
                                                    <button type="button" onClick={() => hapusTanggalPilihan(tanggal)} className="flex h-6 w-6 items-center justify-center rounded-md text-slate-400 transition hover:bg-rose-50 hover:text-rose-600" aria-label={`Hapus tanggal ${formatDateDisplay(tanggal)}`}>
                                                        <X className="h-3.5 w-3.5" strokeWidth={2}/>
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-[12px] leading-relaxed text-slate-500">Pilih lalu tambahkan tanggal yang terpisah, misalnya 1, 3, dan 7 September.</p>
                                    )}
                                </div>
                            )}

                            {modeTanggalKoreksi === 'range' && (
                                <p className="mt-2 text-[12px] leading-relaxed text-slate-500">Contoh: pilih 1 September sebagai mulai dan 3 September sebagai selesai.</p>
                            )}
                            {modeTanggalKoreksi === 'single' && (
                                <p className="mt-2 text-[12px] leading-relaxed text-slate-500">Gunakan untuk satu hari koreksi saja.</p>
                            )}
                        </div>

                        <div>
                            <label className="mb-2 block text-[13px] font-medium text-slate-700">Jenis koreksi atau laporan <span className="text-rose-500">*</span></label>
                            <div className="relative">
                            <select className="min-h-[52px] w-full appearance-none rounded-xl border border-slate-200 bg-white px-4 py-3 pr-11 text-[15px] text-slate-800 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10" value={kategori} onChange={e => setKategori(e.target.value)}>
                                <option>Koreksi Profil (Nama/Divisi/Lainnya)</option>
                                <option>Koreksi Absensi (Ijin, Lupa Absen Masuk/Pulang)</option>
                                <option>Koreksi Cuti / Sisa Cuti</option>
                                <option>Koreksi Shift / Jam Kerja</option>
                                <option>{KATEGORI_LUPA_DL}</option>
                                <option>Koreksi Lainnya</option>
                            </select>
                            <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" strokeWidth={1.8}/>
                            </div>
                        </div>
                        <div>
                            <label className="mb-2 block text-[13px] font-medium text-slate-700">Keterangan detail <span className="text-rose-500">*</span></label>
                            <textarea required rows="4" className="w-full resize-y rounded-xl border border-slate-200 px-4 py-3 text-[15px] leading-relaxed text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10" placeholder={isLupaDl ? 'Tujuan, lokasi, keperluan dinas, dan alasan lupa mengajukan…' : 'Jelaskan detail laporan atau koreksi yang diperlukan…'} value={pesan} onChange={e => setPesan(e.target.value)} ></textarea>
                        </div>
                        {isLupaDl && (
                            <div className="rounded-xl border border-sky-200 bg-sky-50 px-3.5 py-3 text-[12px] leading-relaxed text-sky-900">
                                Untuk dinas yang <b>lupa diajukan</b>. Pilih tanggal dinas di atas, lampirkan <b>foto kegiatan dinas</b>, dan tulis keterangannya. HRD akan memverifikasi lalu mencatat DL.
                            </div>
                        )}
                        <div className={`rounded-xl border border-dashed p-3 transition-colors ${isLupaDl && !file ? 'border-rose-300 bg-rose-50/50 hover:border-rose-400' : 'border-blue-300 bg-blue-50/60 hover:border-blue-400 hover:bg-blue-50'}`}>
                            <input type="file" id="fileInput" className="hidden" accept={isLupaDl ? 'image/*' : 'image/*,.pdf,.doc,.docx,.xls,.xlsx'} onChange={handleFileChange}/>
                            <label htmlFor="fileInput" className="flex min-h-[92px] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg text-center">
                                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-blue-600 shadow-sm ring-1 ring-blue-100"><Upload className="h-5 w-5" strokeWidth={2}/></span>
                                <span className="mt-1 text-[14px] font-semibold text-blue-700">{fileName || (isLupaDl ? 'Lampirkan foto dinas' : 'Tambahkan lampiran')}</span>
                                <span className="text-[11px] text-slate-500">{isLupaDl ? 'Wajib · Foto kegiatan dinas · Maks. 5 MB' : 'Opsional · Foto, PDF, Word, atau Excel · Maks. 5 MB'}</span>
                            </label>
                        </div>
                        <button type="submit" disabled={loading} className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-[15px] font-semibold text-white shadow-lg shadow-blue-600/20 transition hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-500/25 disabled:cursor-not-allowed disabled:bg-slate-400 disabled:shadow-none active:scale-[0.99]">
                            {loading ? 'Mengirim laporan…' : <><MessageSquare className="h-[18px] w-[18px]" strokeWidth={2}/> Kirim laporan</>}
                        </button>
                    </form>
                </section>
            )}

            {/* --- MODE TABEL (FULL SCREEN WEB REPORT) --- */}
            {isHRDOrAdmin && viewMode === 'table' ? (
                <div className="flex-1 flex flex-col overflow-hidden bg-white">
                    {/* Status panel juga muncul di mode tabel — dulu mode ini
                        tidak punya indikator apa pun saat request gagal. */}
                    {(remarksLoading || remarksError || (!muatSemua && remarksTotal !== null && remarksTotal > remarks.length)) && (
                        <div className="px-4 border-b border-gray-200"><StatusPanel /></div>
                    )}
                    <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex justify-between items-center">
                        <span className="text-xs font-bold text-gray-600">Total: {sortedRemarksTable.length} Data</span>
                        <div className="flex gap-2">
                            <button onClick={generateExcel} className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-[10px] font-bold shadow-sm transition"><FileSpreadsheet className="w-3.5 h-3.5" /> Excel</button>
                            <button onClick={generatePDF} className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg text-[10px] font-bold shadow-sm transition"><Printer className="w-3.5 h-3.5" /> PDF</button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-auto">
                        <table className="w-full text-left border-collapse table-fixed text-xs">
                            <thead className="sticky top-0 z-10 bg-gray-100 text-gray-700 font-normal uppercase border-b-2 border-gray-300 shadow-sm">
                                <tr>
                                    <th className="p-2 border border-gray-300 text-center w-10 align-top font-bold bg-gray-100">No</th>
                                    <th className="p-2 border border-gray-300 text-center w-24 align-top font-bold bg-gray-100">Action</th>
                                    
                                    <ReportFilterHeader label="Waktu Lapor" field="waktu" width="w-24" />
                                    <ReportFilterHeader label="Tgl Koreksi" field="tglKoreksi" width="w-24" />
                                    <ReportFilterHeader label="Nama" field="nama" width="w-32" />
                                    <ReportFilterHeader label="Divisi" field="divisi" width="w-24" />
                                    <ReportFilterHeader label="Jenis" field="kategori" width="w-28" />
                                    <ReportFilterHeader label="Keterangan" field="pesan" width="w-48" />
                                    <th className="p-2 border border-gray-300 text-center w-16 align-top font-bold bg-gray-100">Lampiran</th>
                                    <ReportFilterHeader label="Status" field="status" width="w-20" />
                                    <th className="p-2 border border-gray-300 text-center w-24 align-top font-bold bg-gray-100">Potong</th>

                                    {/* [DYNAMIC] Header Respon: Hanya muncul jika ada Status DONE */}
                                    {showResponseColumns && (
                                        <>
                                            <ReportFilterHeader label="Respon HRD" field="respon" width="w-32" />
                                            <ReportFilterHeader label="Waktu Respon" field="waktuRespon" width="w-24" />
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="text-gray-800 text-xs bg-white font-normal divide-y divide-gray-200">
                                {sortedRemarksTable.length === 0 ? (
                                    <tr><td colSpan={showResponseColumns ? "13" : "11"} className="p-8 text-center text-gray-400 italic font-normal bg-gray-50">
                                        {remarksLoading ? 'Memuat data laporan...'
                                            : remarksError ? 'Data gagal dimuat — lihat pesan di atas.'
                                            : remarks.length > 0 ? 'Tidak ada baris yang cocok dengan filter.'
                                            : 'Tidak ada data laporan.'}
                                    </td></tr>
                                ) : (
                                    sortedRemarksTable.map((item, idx) => (
                                        <tr key={idx} className="hover:bg-blue-50 transition-colors group">
                                            <td className="p-2 border border-gray-200 text-center font-medium bg-gray-50/50">{idx + 1}</td>
                                            
                                            <td className="p-2 border border-gray-200 text-center">
                                                <div className="flex items-center justify-center gap-1">
                                                    {item.status === 'Open' ? (
                                                        <button onClick={() => openMarkDoneDialog(item)} className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-[10px] font-bold shadow-sm flex items-center justify-center gap-1 flex-1 transition active:scale-95">
                                                            <Check className="w-3 h-3"/> Done
                                                        </button>
                                                    ) : <span className="text-green-600 font-bold flex-1">✔</span>}
                                                    <button onClick={() => handleDeleteRemark(item)} disabled={!!remarkDeleting} title="Hapus laporan" className="shrink-0 rounded p-1 text-red-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-40 transition active:scale-95">
                                                        {remarkDeleting === item.uuid ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Trash2 className="w-3.5 h-3.5"/>}
                                                    </button>
                                                </div>
                                            </td>

                                            <td className="p-2 border border-gray-200 text-center font-mono text-[10px] text-gray-500">{formatDateDisplay(item.waktu)}</td>
                                            <td className="p-2 border border-gray-200 text-center text-blue-600 font-medium">{formatDateDisplay(item.tglKoreksi)}</td>
                                            <td className="p-2 border border-gray-200 font-bold text-gray-700">{item.nama}</td>
                                            <td className="p-2 border border-gray-200 text-gray-600">{item.divisi}</td>
                                            <td className="p-2 border border-gray-200 text-purple-700 font-medium">{item.kategori}</td>
                                            <td className="p-2 border border-gray-200 italic text-gray-600 break-words">{item.pesan}</td>
                                            <td className="p-2 border border-gray-200 text-center">
                                                {item.lampiran && item.lampiran !== '-' ? (
                                                    <a href={item.lampiran} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center p-1.5 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition" title="Lihat Lampiran">
                                                        <FileIcon className="w-3.5 h-3.5"/>
                                                    </a>
                                                ) : <span className="text-gray-300">-</span>}
                                            </td>
                                            <td className="p-2 border border-gray-200 text-center">
                                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${item.status === 'Done' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-yellow-100 text-yellow-700 border-yellow-200'}`}>
                                                    {item.status}
                                                </span>
                                            </td>
                                            <td className="p-2 border border-gray-200 text-center text-[10px] font-semibold text-slate-600">{formatPotongKuota(item)}</td>

                                            {/* [DYNAMIC] Kolom Respon: Hanya muncul jika ada Status DONE */}
                                            {showResponseColumns && (
                                                <>
                                                    <td className="p-2 border border-gray-200 text-blue-800 font-medium">{item.respon}</td>
                                                    <td className="p-2 border border-gray-200 text-center text-[10px] text-gray-500">{formatDateDisplay(item.waktuRespon)}</td>
                                                </>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                /* --- MODE LIST CARD (NORMAL VIEW) --- */
                <div className="mx-auto w-full max-w-2xl space-y-3">
                    <StatusPanel />

                    {/* Pesan kosong hanya boleh muncul kalau pengambilan data
                        benar-benar BERHASIL dan hasilnya memang nol baris. */}
                    {!remarksLoading && !remarksError && filteredRemarksTable.length === 0 && (
                        <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-5 py-10 text-center">
                            <ClipboardList className="mx-auto mb-3 h-8 w-8 text-slate-300" strokeWidth={1.5}/>
                            <p className="text-[14px] font-medium text-slate-500">{remarks.length > 0 ? 'Tidak ada laporan yang cocok dengan filter.' : 'Belum ada data laporan.'}</p>
                        </div>
                    )}

                    {filteredRemarksTable.map((item, idx) => (
                        <div key={idx} className={`bg-white p-4 rounded-xl shadow-sm border-l-4 relative ${item.status === 'Done' ? 'border-l-green-500' : 'border-l-yellow-500'}`}>
                            <div className="flex justify-between items-start mb-1">
                                <div>
                                    <h4 className="font-bold text-gray-800 text-sm">{item.nama} <span className="font-normal text-xs text-gray-500">({item.divisi})</span></h4>
                                    <p className="text-[10px] text-gray-400">{formatDateDisplay(item.waktu)}</p>
                                </div>
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${item.status === 'Done' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-yellow-100 text-yellow-700 border-yellow-200'}`}>
                                    {item.status}
                                </span>
                            </div>
                            
                            <div className="bg-gray-50 p-2 rounded text-xs text-gray-700 mt-2 mb-2 border border-gray-100">
                                {item.tglKoreksi && (
                                    <div className="mb-1 pb-1 border-b border-gray-200 flex items-center gap-1.5">
                                        <CalendarDays className="w-3 h-3 text-blue-500"/>
                                        <span className="font-bold text-gray-600"></span>
                                        {/* [UPDATE] Format DD-MM-YYYY di sini */}
                                        <span className="font-mono text-blue-700">{formatDateDisplay(item.tglKoreksi)}</span>
                                    </div>
                                )}
                                <p className="font-bold text-purple-700 mb-1">{item.kategori}</p>
                                {formatPotongKuota(item) !== '-' && (
                                    <p className="mb-1 inline-flex rounded-md bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700 ring-1 ring-amber-200">Potong: {formatPotongKuota(item)}</p>
                                )}
                                <p className="italic">"{item.pesan}"</p>
                            </div>

                            {/* [UPDATE] KARTU RESPON HANYA MUNCUL JIKA STATUS DONE */}
                            {item.status === 'Done' && item.respon && item.respon !== '' && (
                                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mt-3 relative overflow-hidden">
                                    <div className="absolute top-0 right-0 p-2 opacity-10"><Info className="w-12 h-12 text-blue-500" /></div>
                                    <div className="flex justify-between items-center relative z-10 mb-2">
                                        <div className="flex items-center gap-1.5 font-bold text-blue-800 text-xs">
                                            <div className="bg-blue-200 p-1 rounded-full"><Info className="w-3 h-3 text-blue-700"/></div>
                                            <span>Tanggapan HRD:</span>
                                        </div>
                                        {item.waktuRespon && item.waktuRespon !== '-' && (
                                            <div className="flex items-center gap-1 bg-white/60 px-2 py-1 rounded-full border border-blue-100 shadow-sm">
                                                <Clock className="w-3 h-3 text-blue-400" />
                                                {/* [UPDATE] Format DD-MM-YYYY di sini */}
                                                <span className="text-[10px] text-blue-600 font-bold font-mono">{formatDateDisplay(item.waktuRespon)}</span>
                                            </div>
                                        )}
                                    </div>
                                    <p className="italic text-xs text-blue-900 mt-1 leading-relaxed pl-1 border-l-2 border-blue-300">"{item.respon}"</p>
                                </div>
                            )}

                            <div className="flex justify-between items-center mt-2">
                                {item.lampiran && item.lampiran !== '-' ? (
                                    <a href={item.lampiran} target="_blank" rel="noreferrer" className="text-xs text-blue-600 font-bold underline flex items-center gap-1"><FileIcon className="w-3 h-3"/> Lampiran </a>
                                ) : <span className="text-[10px] text-gray-400">Tidak ada lampiran</span>}

                                {isHRDOrAdmin && (
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => handleDeleteRemark(item)} disabled={!!remarkDeleting} className="flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50 disabled:opacity-40">
                                            {remarkDeleting === item.uuid ? <Loader2 className="w-3 h-3 animate-spin"/> : <Trash2 className="w-3 h-3"/>} Hapus
                                        </button>
                                        {item.status === 'Open' && (
                                            <button onClick={() => openMarkDoneDialog(item)} className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 hover:bg-green-700">
                                                <Check className="w-3 h-3"/> Mark Done & Reply
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// --- KOMPONEN KALENDER RANGE PICKER (UPDATED: HIGHLIGHT TODAY) ---
const DateRangeModal = ({ isOpen, onClose, onApply, initialStart, initialEnd, minDate }) => {
  const [viewDate, setViewDate] = useState(new Date());
  const [tempStart, setTempStart] = useState(initialStart);
  const [tempEnd, setTempEnd] = useState(initialEnd);

  // Helper Lokal
  const formatIndoLocal = (d) => { 
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'}); } 
    catch (e) { return d; } 
  };

  useEffect(() => {
    if (isOpen) {
      // Saat dibuka, pastikan viewDate mengarah ke Bulan Hari Ini atau Tanggal Start yang sudah dipilih
      const d = initialStart ? new Date(initialStart) : new Date();
      setViewDate(d);
      setTempStart(initialStart);
      setTempEnd(initialEnd);
    }
  }, [isOpen, initialStart, initialEnd]);

  if (!isOpen) return null;

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay(); // 0 = Sunday

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const monthNames = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

  // --- LOGIKA HARI INI ---
  const today = new Date();
  const isToday = (day) => {
    return day === today.getDate() && 
           month === today.getMonth() && 
           year === today.getFullYear();
  };

  // KUNCI PERIODE LALU: tanggal sebelum minDate tidak bisa dipilih.
  const terkunci = (day) => !!minDate && `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` < minDate;
  const bulanMin = minDate ? minDate.slice(0, 7) : '';
  const bisaMundurBulan = !bulanMin || `${year}-${String(month + 1).padStart(2, '0')}` > bulanMin;

  const handleDayClick = (day) => {
    if (terkunci(day)) return;
    const selectedStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    if (!tempStart || (tempStart && tempEnd)) {
      setTempStart(selectedStr);
      setTempEnd(null);
    } else {
      if (new Date(selectedStr) < new Date(tempStart)) {
        setTempStart(selectedStr);
      } else {
        setTempEnd(selectedStr);
      }
    }
  };

  const handlePrevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const handleNextMonth = () => setViewDate(new Date(year, month + 1, 1));

  const getDayClass = (day) => {
    const currentStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const todayStatus = isToday(day);

    if (terkunci(day)) {
        return "text-slate-300 line-through cursor-not-allowed rounded-full";
    }

    // 1. Jika Tanggal Start / End (Selected)
    if (currentStr === tempStart || currentStr === tempEnd) {
        return "bg-blue-600 text-white font-bold rounded-full shadow-md";
    }
    
    // 2. Jika Dalam Range
    if (tempStart && tempEnd && new Date(currentStr) > new Date(tempStart) && new Date(currentStr) < new Date(tempEnd)) {
        return "bg-blue-100 text-blue-800 rounded-none font-medium";
    }

    // 3. Jika HARI INI (Tapi tidak sedang dipilih)
    if (todayStatus) {
        return "border-2 border-blue-600 text-blue-700 font-extrabold rounded-full";
    }

    // 4. Tanggal Biasa
    return "text-slate-700 hover:bg-gray-100 rounded-full font-medium";
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200 border border-gray-200">
        
        {/* Header Kalender */}
        <div className="bg-blue-600 p-4 text-white">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-lg flex items-center gap-2">
                <CalendarDays className="w-5 h-5"/> Pilih Tanggal
            </h3>
            <button onClick={onClose} className="hover:bg-white/20 p-1 rounded-full transition"><X className="w-5 h-5"/></button>
          </div>
          <div className="flex justify-between items-center bg-blue-700/50 p-1 rounded-lg">
            <button onClick={handlePrevMonth} disabled={!bisaMundurBulan} className="p-1 hover:bg-white/20 rounded disabled:opacity-30 disabled:cursor-not-allowed"><ChevronDown className="w-5 h-5 rotate-90"/></button>
            <span className="font-bold text-sm tracking-wide">{monthNames[month]} {year}</span>
            <button onClick={handleNextMonth} className="p-1 hover:bg-white/20 rounded"><ChevronDown className="w-5 h-5 -rotate-90"/></button>
          </div>
        </div>

        {/* Grid Kalender */}
        <div className="p-4">
          <div className="grid grid-cols-7 mb-2 text-center border-b border-gray-100 pb-2">
            {['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'].map(d => (
              <span key={d} className={`text-[10px] font-bold uppercase ${d === 'Min' ? 'text-red-500' : 'text-slate-400'}`}>{d}</span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1 text-center">
            {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              return (
                <button
                  key={day}
                  disabled={terkunci(day)}
                  onClick={() => handleDayClick(day)}
                  className={`h-9 w-9 mx-auto flex items-center justify-center text-xs transition-all ${getDayClass(day)}`}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer Info & Action */}
        <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
          <div className="text-[10px] flex flex-col gap-0.5">
            <div className="flex items-center gap-1 text-slate-500">
                <span className="w-2 h-2 rounded-full border border-blue-600 bg-white"></span> Hari Ini
            </div>
            <div className="flex items-center gap-1 text-slate-500">
                <span className="w-2 h-2 rounded-full bg-blue-600"></span> Dipilih
            </div>
            {minDate && (
              <div className="flex items-center gap-1 text-slate-400">
                <span className="w-2 h-2 rounded-full bg-slate-200"></span> Sebelum {tampilYmd(minDate)}: periode ditutup
              </div>
            )}
          </div>
          <button 
            disabled={!tempStart}
            onClick={() => onApply(tempStart, tempEnd || tempStart)} 
            className="px-5 py-2.5 bg-blue-600 text-white text-xs font-bold rounded-xl hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all shadow-md active:scale-95"
          >
            Terapkan
          </button>
        </div>
      </div>
    </div>
  );
};

// --- 3. ATTENDANCE FORM (FIXED: DATE RANGE + OPTIONAL TIME TOGGLE) ---
// ============================================================
// STEMPEL LOKASI PADA FOTO ABSEN
//
// Dulu stempel foto menuliskan koordinat mentah apa adanya
// (`${location.lat}, ${location.lng}`), sehingga muncul angka
// sepanjang "-7.247680086678265, 112.73673079382687" — tidak terbaca
// siapa pun, dan itulah yang tetap terlihat di foto meski tampilan
// form sudah menampilkan nama alamat.
//
// Nama jalan jauh lebih panjang daripada koordinat, jadi teks harus
// dibungkus dan dikecilkan supaya tidak terpotong di tepi kiri foto.
// ============================================================
function pecahBarisTeks(ctx, teks, maxWidth) {
  const kata = String(teks).split(/\s+/).filter(Boolean);
  const baris = [];
  let sekarang = '';
  kata.forEach((k) => {
    const coba = sekarang ? sekarang + ' ' + k : k;
    // `!sekarang` menjaga satu kata yang lebih lebar dari kanvas tetap
    // ditulis, bukan menghasilkan baris kosong tanpa akhir.
    if (ctx.measureText(coba).width <= maxWidth || !sekarang) sekarang = coba;
    else { baris.push(sekarang); sekarang = k; }
  });
  if (sekarang) baris.push(sekarang);
  return baris;
}

// Pemotong pengaman. Satu kata yang lebih lebar dari foto (nama jalan
// tanpa spasi) tidak bisa dibungkus, jadi harus dipotong — kalau tidak,
// teksnya meluber keluar bingkai dan bagian depannya hilang sama sekali.
function potongAgarMuat(ctx, teks, maxWidth) {
  if (ctx.measureText(teks).width <= maxWidth) return teks;
  let potong = teks;
  while (potong.length > 1 && ctx.measureText(potong + '…').width > maxWidth) {
    potong = potong.slice(0, -1);
  }
  return potong + '…';
}

function gambarStempelKanan(ctx, teks, xKanan, yBawah, fontSizeAwal, maxWidth, maksBaris) {
  const batas = maksBaris || 2;
  let fontSize = fontSizeAwal;
  let baris = [];
  // Kecilkan huruf bertahap sampai muat dalam batas baris DAN tidak ada
  // baris yang melebihi lebar foto. Lebih baik huruf sedikit lebih kecil
  // daripada nama jalan terpotong.
  for (let percobaan = 0; percobaan < 6; percobaan++) {
    ctx.font = `bold ${Math.round(fontSize)}px sans-serif`;
    baris = pecahBarisTeks(ctx, teks, maxWidth);
    const adaYangLuber = baris.some((b) => ctx.measureText(b).width > maxWidth);
    if (baris.length <= batas && !adaYangLuber) break;
    fontSize *= 0.85;
  }
  ctx.font = `bold ${Math.round(fontSize)}px sans-serif`;
  baris = baris.slice(0, batas).map((b) => potongAgarMuat(ctx, b, maxWidth));
  const tinggiBaris = Math.round(fontSize) * 1.2;
  baris.forEach((b, i) => {
    const y = yBawah - (baris.length - 1 - i) * tinggiBaris;
    ctx.strokeText(b, xKanan, y);
    ctx.fillText(b, xKanan, y);
  });
  return baris.length * tinggiBaris;
}

// ============================================================
// UKURAN FOTO YANG DIKIRIM KE SERVER
//
// Kamera depan HP sekarang menghasilkan frame 1080p ke atas. Dikirim apa
// adanya (JPEG 0,8 -> base64), satu foto absen berukuran ~400-900 KB.
// Di jaringan seluler jam masuk, ITULAH bagian terlama dari menekan
// "Kirim" — bukan pemrosesan di server; dan di sisi Apps Script berkas
// sebesar itu harus di-decode lebih dulu sebelum disimpan ke Drive.
//
// 1280 px pada sisi terpanjang masih jauh di atas kebutuhan: foto absen
// hanya dilihat sebagai bukti wajah + stempel waktu, di layar HP atau
// laptop. Penghematannya biasanya 3-5 kali lipat.
//
// PENTING: pengecilan dikerjakan SETELAH seluruh pemeriksaan wajah.
// Deteksi dan deskriptor wajah tetap dihitung dari frame resolusi penuh,
// jadi ketelitian pencocokan tidak berubah sedikit pun.
// ============================================================
const FOTO_MAKS_SISI = 1280;
const FOTO_MUTU_KIRIM = 0.8;
const FOTO_MUTU_MENTAH = 0.9;

function ukuranTerbatas(lebar, tinggi, maks) {
  const terpanjang = Math.max(lebar, tinggi);
  if (!terpanjang || terpanjang <= maks) return { lebar: lebar, tinggi: tinggi };
  const rasio = maks / terpanjang;
  return { lebar: Math.round(lebar * rasio), tinggi: Math.round(tinggi * rasio) };
}

// Ubah sebuah canvas menjadi dataURL JPEG, dikecilkan bila perlu.
function kecilkanCanvas(sumber, mutu) {
  try {
    const uk = ukuranTerbatas(sumber.width, sumber.height, FOTO_MAKS_SISI);
    if (uk.lebar === sumber.width && uk.tinggi === sumber.height) {
      return sumber.toDataURL('image/jpeg', mutu);
    }
    const c = document.createElement('canvas');
    c.width = uk.lebar;
    c.height = uk.tinggi;
    c.getContext('2d').drawImage(sumber, 0, 0, uk.lebar, uk.tinggi);
    return c.toDataURL('image/jpeg', mutu);
  } catch (e) {
    return sumber.toDataURL('image/jpeg', mutu);
  }
}

// Menggambar seluruh stempel (waktu + lokasi) pada sebuah context canvas.
// Dipakai dua kali: saat foto dijepret, dan saat stempel digambar ULANG
// setelah nama alamat tiba.
function gambarStempelFoto(ctx, lebar, tinggi, timestampText, lokasiText) {
  const fontSize = Math.floor(lebar / 25);
  const paddingX = 20;
  const paddingY = 20;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';
  ctx.lineWidth = 3;
  ctx.strokeStyle = 'black';
  ctx.fillStyle = 'white';
  ctx.font = `bold ${fontSize}px sans-serif`;
  ctx.strokeText(timestampText, lebar - paddingX, tinggi - paddingY);
  ctx.fillText(timestampText, lebar - paddingX, tinggi - paddingY);
  gambarStempelKanan(ctx, lokasiText, lebar - paddingX, tinggi - paddingY - (fontSize * 1.2), fontSize, lebar - (paddingX * 2), 2);
}

// Menggambar ulang stempel di atas foto MENTAH (yang belum berstempel).
//
// Kenapa perlu: seksi Foto berada di ATAS seksi Lokasi pada form, jadi
// karyawan hampir selalu menjepret SEBELUM nama alamat selesai diambil
// dari server. Kalau stempel hanya digambar sekali saat menjepret, foto
// akan hampir selalu berisi koordinat — persis keluhan yang muncul.
function buatFotoBerstempel(dataUrlMentah, timestampText, lokasiText) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const uk = ukuranTerbatas(img.naturalWidth, img.naturalHeight, FOTO_MAKS_SISI);
        const c = document.createElement('canvas');
        c.width = uk.lebar;
        c.height = uk.tinggi;
        const ctx = c.getContext('2d');
        ctx.drawImage(img, 0, 0, uk.lebar, uk.tinggi);
        // Stempel digambar pada ukuran akhir, bukan diperkecil bersama
        // gambarnya: fontSize dihitung dari lebar canvas, jadi teksnya
        // tetap seproporsional aslinya dan tetap terbaca.
        gambarStempelFoto(ctx, c.width, c.height, timestampText, lokasiText);
        resolve(c.toDataURL('image/jpeg', FOTO_MUTU_KIRIM));
      } catch (e) {
        resolve(null); // gagal menggambar ulang: foto lama tetap dipakai
      }
    };
    img.onerror = () => resolve(null);
    img.src = dataUrlMentah;
  });
}

function AttendanceForm({ user, setUser, setView, editItem, setEditItem, masterData }) {
  const type = localStorage.getItem('absenType') || 'Hadir';
  const isEditMode = !!editItem;

  // KONFIGURASI TIPE ABSEN
  // 'Standby' ikut sejak 1.0.20: barisnya dipakai sebagai penanda
  // kehadiran pada rekap (sekolom dengan Masuk/Pulang), jadi bukti
  // fotonya harus setara — lihat wajibKameraDepan / wajibWajah di bawah.
  const PHOTO_REQUIRED_TYPES = ['Hadir', 'Pulang', 'Standby', 'Dinas', 'Dinas Luar', 'Sakit'];
  const NO_GPS_TYPES = ['Ijin', 'Cuti', 'Sakit', 'Cuti EO', 'Tukar Shift'];
  // const NO_TIME_TYPES = ['Cuti', 'Dinas Luar', 'Sakit', 'Cuti EO']; // (Digantikan logic manual)
  const H3_REQUIRED_TYPES = ['Ijin', 'Tukar Shift'];
  const UPLOAD_ALLOWED_TYPES = ['Cuti', 'Cuti EO', 'Ijin']; 

  const isPhotoRequired = PHOTO_REQUIRED_TYPES.includes(type);
  // Karyawan yang dikecualikan admin dari gerbang GPS (staf kantor di PC
  // tanpa sumber lokasi) juga tidak boleh tertahan di tombol Kirim —
  // kalau tidak, gerbangnya lolos tapi formnya tetap buntu.
  // Server ikut tahu: handleAbsen melewati gerbang Fake GPS HANYA untuk
  // karyawan ini dan HANYA ketika koordinatnya memang kosong, lalu
  // menandai absennya 'DITERIMA-TANPA-LOKASI' di audit.
  const bebasGerbangGps = user && user.gpsGerbangBebas === true;
  const isGpsRequired = !NO_GPS_TYPES.includes(type) && !bebasGerbangGps;
  const isGeofenceRequired = !isEditMode && ['Hadir', 'Pulang'].includes(type) && user.geofenceRequired === true;
  const isH3Required = H3_REQUIRED_TYPES.includes(type);
  const isUploadAllowed = UPLOAD_ALLOWED_TYPES.includes(type);
  const isDlOnline = TIPE_DL_ONLINE.includes(type);
  const isIntervalType = !['Hadir', 'Pulang'].includes(type) && !isDlOnline;
  // KUNCI PERIODE LALU (Okt 2026): tanggal di periode yang sudah ditutup
  // tidak bisa dipilih. '' = daftar periode belum diketahui -> server yang menilai.
  const batasTanggalForm = batasPengajuanMundur(bacaPeriodeAktifTersimpan(), tglLokal());
  const isShiftWorker = String(user.roleKode || user.role || '').toLowerCase() === 'karyawan_shift'; 
  const isClockIn = type === 'Hadir';
  // DUA ATURAN YANG BERBEDA, SENGAJA DIPISAH.
  //
  // Standby masuk KEDUA daftar (1.0.20). Alasannya sama dengan Hadir /
  // Pulang, bukan dengan Dinas: Standby dicatat sebagai kehadiran orang
  // itu sendiri di rekap, jadi fotonya harus berisi tepat satu wajah
  // milik pemegang akun. Daftar yang sama WAJIB dicerminkan oleh
  // FACE_TIPE_WAJIB di apps-script/FaceProfile.gs.
  //
  // Sampai 1.0.19 keduanya menempel pada satu flag, sehingga "kunci kamera
  // depan" otomatis berarti "hanya boleh satu wajah". Untuk Dinas itu
  // salah: foto dinas memang sering berisi beberapa orang sekaligus,
  // tetapi tetap tidak boleh diambil dari kamera belakang.
  //
  //   wajibKameraDepan -> kamera belakang ditolak, tombol ganti kamera hilang
  //   wajibWajah       -> verifikasi wajah penuh + liveness + pencocokan
  //                       identitas, dan karena itu HANYA SATU wajah
  const wajibKameraDepan = ['Hadir', 'Pulang', 'Standby', 'Dinas', 'Dinas Luar'].includes(type);
  const wajibWajah = ['Hadir', 'Pulang', 'Standby'].includes(type);

  const [selectedShift, setSelectedShift] = useState('');
  const availableShifts = masterData?.shifts || [];

  // CAMERA & FILES
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const trackerRef = useRef(null);
  // Menyimpan foto SEBELUM diberi stempel, supaya stempelnya bisa digambar
  // ulang begitu nama alamat tiba tanpa perlu menjepret ulang.
  const fotoMentahRef = useRef(null);
  const [photo, setPhoto] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [facingMode, setFacingMode] = useState(type === 'Sakit' ? 'environment' : 'user');
  const [fileLampiran, setFileLampiran] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileMime, setFileMime] = useState('');
  const [livenessStatus, setLivenessStatus] = useState({ status: 'idle', isLive: false, faceCount: 0, message: '', badgeColor: '' });
  const [kameraInfo, setKameraInfo] = useState({ facing: '', label: '', dikenali: false, ok: false });
  const [errorKamera, setErrorKamera] = useState('');
  const [fotoTerverifikasi, setFotoTerverifikasi] = useState(false);
  const [sedangVerifikasi, setSedangVerifikasi] = useState(false);
  // Pencocokan IDENTITAS wajah (Hadir/Pulang). Berbeda dari livenessStatus
  // di atas, yang hanya menilai "ini wajah manusia hidup".
  //   status: 'idle' | 'model' | 'proses' | 'cocok' | 'gagal'
  const [wajahIdentitas, setWajahIdentitas] = useState({ status: 'idle', pesan: '' });
  // Deskriptor 128 angka dari foto yang diterima. Disimpan di ref, bukan
  // state: ia tidak pernah dipakai untuk menggambar apa pun, dan menaruhnya
  // di state memicu render ulang seluruh form pada momen paling sibuk.
  const deskriptorWajahRef = useRef(null);
  
  // FORM DATA
  const [location, setLocation] = useState(null);
  const [gpsValidation, setGpsValidation] = useState({ isValid: true, isMock: false, warning: '', accuracy: null, bukti: null });
  // Nama tempat hasil reverse geocoding di server. 'mencari' -> masih
  // menunggu, '' -> sudah dicoba tapi tidak dapat (koordinat jadi cadangan).
  const [alamat, setAlamat] = useState('');
  const [alamatStatus, setAlamatStatus] = useState('idle');
  const [catatan, setCatatan] = useState('');
  // DL: jam jepret selfie, dipakai sebagai teks terkunci di atas keterangan.
  const [waktuJepret, setWaktuJepret] = useState('');
  const [intervalData, setIntervalData] = useState({ tglMulai: '', tglSelesai: '', jamMulai: '', jamSelesai: '' });
  
  // UI STATES
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [showCalendar, setShowCalendar] = useState(false);
  
  // [NEW] STATE UNTUK OPSI JAM
  const [useTime, setUseTime] = useState(false);

  // --- INIT EFFECT ---
  useEffect(() => {
    let isMounted = true;
    const initForm = async () => {
      setIsInitializing(true);
      await new Promise(r => setTimeout(r, 500));

      // Default Use Time logic (Misal: Lembur/Tukar Shift default ON, Cuti default OFF)
      if (!isEditMode) {
          const timeDefaultRequired = ['Lembur', 'Tukar Shift'].includes(type);
          setUseTime(timeDefaultRequired);
      }

      // Prefill Logic
      const prefillJson = localStorage.getItem('absen_prefill');
      if (prefillJson && !editItem) {
        try {
          const data = JSON.parse(prefillJson);
          setIntervalData(prev => ({
            ...prev,
            tglMulai: data.tgl || prev.tglMulai,
            tglSelesai: data.tgl || prev.tglSelesai,
            jamMulai: data.jamMulai || '',    
            jamSelesai: data.jamSelesai || '' 
          }));
          if (data.jamMulai) setUseTime(true); // Auto enable jika ada data jam
          localStorage.removeItem('absen_prefill');
        } catch (e) { console.error("Gagal parse prefill", e); }
      }

      // Edit Mode Logic
      if (isEditMode) {
        setCatatan(editItem.catatan);
        const formatDate = (d) => d && d !== '-' ? new Date(d).toISOString().split('T')[0] : '';
        const hasTime = (editItem.jamMulai && editItem.jamMulai !== '-' && editItem.jamMulai !== '');
        
        setIntervalData({ 
          tglMulai: formatDate(editItem.tglMulai), 
          tglSelesai: formatDate(editItem.tglSelesai), 
          jamMulai: hasTime ? editItem.jamMulai : '', 
          jamSelesai: hasTime ? editItem.jamSelesai : '' 
        });
        setUseTime(hasTime); // Set toggle sesuai data yang diedit
        setPhoto(editItem.foto); 
      }

      // GPS Logic with Anti-Fake GPS verification
      if (!isEditMode && isGpsRequired && 'geolocation' in navigator) {
        try {
          // getVerifiedGeolocation mengumpulkan sampai 3 fix GPS yang
          // BERBEDA (dibedakan dari timestamp-nya), maksimal 3,5 detik.
          // Koordinat yang tidak bergeser sedikit pun di antara fix-fix
          // itu menyalakan badge "Mock GPS" dan menolak kiriman di
          // handleSubmit. Perangkat yang hanya DIAM tidak kena: chip-nya
          // mengulang fix yang sama, jitternya null, bukan nol.
          const { position, accuracy, isMockSuspicious, warning, bukti } = await getVerifiedGeolocation({ timeout: 10000 });
          if (isMounted) {
            setLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
            setGpsValidation({ isValid: !isMockSuspicious, isMock: isMockSuspicious, warning: warning || '', accuracy, bukti: bukti || null });
          }
        } catch (err) {
          if (isMounted) {
            console.error('GPS error:', err);
            alert(err.message || 'Gagal memperoleh koordinat GPS. Pastikan GPS aktif dan izin lokasi diberikan.');
          }
        }
      }
      if (isMounted) setIsInitializing(false);
    };
    initForm();
    return () => { isMounted = false; };
  }, [isEditMode, editItem, isGpsRequired, type]);

  // --- NAMA LOKASI ---
  // Reverse geocoding dikerjakan server (Geocode.gs) karena di sanalah
  // hasilnya bisa di-cache lintas karyawan — satu titik hanya pernah
  // memakai kuota sekali, bukan sekali per orang per absen.
  //
  // Sengaja TIDAK memblokir tombol kirim. Nama lokasi itu untuk dibaca
  // manusia; yang menentukan sah tidaknya absen tetap koordinat dan
  // geofence di server. Kalau layanan alamat mati, absen harus tetap jalan.
  useEffect(() => {
    if (!location) return;
    let hidup = true;
    setAlamatStatus('mencari');
    (async () => {
      try {
        const res = await fetchApi(SCRIPT_URL, {
          method: 'POST',
          body: JSON.stringify({ action: 'get_alamat', lokasi: `${location.lat}, ${location.lng}` })
        });
        const d = await res.json();
        if (!hidup) return;
        if (d.result === 'success' && d.tersedia) {
          setAlamat(d.alamat);
          setAlamatStatus('ada');
        } else {
          // Dibedakan supaya bisa didiagnosa. Sebelum ini semua kegagalan
          // terlihat sama di layar (koordinat muncul), sehingga "backend
          // belum di-deploy" tidak bisa dibedakan dari "titik ini memang
          // tidak punya alamat".
          if (d.result !== 'success') {
            console.warn('[alamat] server menolak:', d.code || '-', d.message || '');
          }
          setAlamatStatus('kosong');
        }
      } catch (e) {
        console.warn('[alamat] gagal menghubungi server:', e && e.message);
        if (hidup) setAlamatStatus('kosong');
      }
    })();
    return () => { hidup = false; };
  }, [location]);

  // --- GAMBAR ULANG STEMPEL SETELAH ALAMAT TIBA ---
  // Ini yang membuat nama alamat benar-benar sampai ke foto. Tanpa efek
  // ini, stempel hanya ditulis sekali saat menjepret — dan karena seksi
  // Foto ada di atas seksi Lokasi, saat itu alamat hampir tidak pernah
  // sudah siap, sehingga foto selalu berisi koordinat.
  useEffect(() => {
    if (alamatStatus !== 'ada' || !alamat) return;
    const mentah = fotoMentahRef.current;
    if (!mentah || mentah.pakaiAlamat) return; // belum ada foto, atau sudah beralamat
    let hidup = true;
    buatFotoBerstempel(mentah.dataUrl, mentah.timestampText, alamat).then((baru) => {
      if (!hidup || !baru) return;
      fotoMentahRef.current = { ...mentah, pakaiAlamat: true };
      setPhoto(baru);
    });
    return () => { hidup = false; };
  }, [alamatStatus, alamat]);

  // Clean up camera & face tracker on unmount
  useEffect(() => {
    return () => {
      if (trackerRef.current) {
        trackerRef.current.stop();
      }
      if (videoRef.current && videoRef.current.srcObject) {
        videoRef.current.srcObject.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  // --- HANDLERS ---
  const handleCalendarApply = (start, end) => {
      setIntervalData({ ...intervalData, tglMulai: start, tglSelesai: end });
      setShowCalendar(false);
  };

  const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) {
          if (file.size > 5 * 1024 * 1024) { alert("Ukuran file terlalu besar (Max 5MB)"); return; }
          setFileName(file.name); setFileMime(file.type);
          const reader = new FileReader(); reader.onloadend = () => { setFileLampiran(reader.result); }; reader.readAsDataURL(file);
      }
  };

  const startCamera = async () => {
      if (trackerRef.current) { trackerRef.current.stop(); trackerRef.current = null; }
      if (videoRef.current && videoRef.current.srcObject) {
          hentikanStream(videoRef.current.srcObject);
          videoRef.current.srcObject = null;
      }
      setCameraActive(false);
      setErrorKamera('');
      setLivenessStatus({ status: 'idle', isLive: false, faceCount: 0, message: '', badgeColor: '' });
      try {
          let stream;
          if (wajibKameraDepan) {
            // Hadir / Pulang / Dinas: kamera depan DIKUNCI. bukaKameraDepan()
            // menolak stream yang ternyata berasal dari kamera belakang.
            const hasil = await bukaKameraDepan({ width: 640, height: 480 });
            stream = hasil.stream;
            setKameraInfo({ facing: hasil.facing || 'user', label: hasil.label, dikenali: hasil.dikenali, ok: true });
          } else {
            stream = await navigator.mediaDevices.getUserMedia({
              video: { facingMode: facingMode, width: { ideal: 640 }, height: { ideal: 480 } }
            });
            setKameraInfo({ facing: facingMode, label: '', dikenali: true, ok: true });
          }
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            setCameraActive(true);
            if (wajibWajah) {
              trackerRef.current = startFaceLivenessTracker(videoRef.current, setLivenessStatus);
              // Model pengenal wajah (~6,2 MB) mulai diunduh SEKARANG, di
              // latar, tanpa ditunggu. Karyawan butuh beberapa detik untuk
              // memosisikan wajahnya; detik-detik itu yang dipakai. Kalau
              // baru diunduh saat tombol jepret ditekan, ia menunggu dua
              // kali — sekali untuk posisi, sekali untuk berkasnya.
              deskriptorWajahRef.current = null;
              setWajahIdentitas({ status: 'model', pesan: '' });
              muatPengenalWajah()
                .then(() => setWajahIdentitas((p) => (p.status === 'model' ? { status: 'idle', pesan: '' } : p)))
                .catch(() => setWajahIdentitas({
                  status: 'gagal',
                  pesan: 'Model pengenal wajah gagal dimuat. Periksa koneksi, lalu buka ulang kamera.'
                }));
            }
          } else {
            hentikanStream(stream);
          }
      } catch (err) {
          setKameraInfo({ facing: '', label: '', dikenali: false, ok: false });
          const pesan = (err && err.message) ? err.message : 'Gagal akses kamera.';
          setErrorKamera(pesan);
          if (!wajibKameraDepan) alert(pesan);
      }
  };
  // Ganti kamera hanya berlaku untuk jenis non-presensi (Dinas / Sakit).
  useEffect(() => { if (cameraActive && !wajibKameraDepan) { startCamera(); } }, [facingMode]);

  const toggleCamera = () => {
    if (wajibKameraDepan) return; // kamera depan terkunci untuk Hadir / Pulang / Dinas
    if (trackerRef.current) {
      trackerRef.current.stop();
      trackerRef.current = null;
    }
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  const takePhoto = async () => {
      if (sedangVerifikasi) return;
      // Gerbang 1: status realtime harus "wajah penuh + aktif".
      if (wajibWajah && !livenessStatus.isLive) {
        alert(livenessStatus.message || 'Wajah belum terverifikasi. Posisikan seluruh wajah menghadap kamera depan.');
        return;
      }
      const video = videoRef.current; const canvas = canvasRef.current;
      if (!video || !canvas) return;
      canvas.width = video.videoWidth; canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d'); ctx.drawImage(video, 0, 0);

      // Gerbang 2: periksa ulang WAJAH PENUH pada frame yang benar-benar
      // dijepret, supaya kamera tidak bisa dialihkan sedetik sebelum jepret.
      if (wajibWajah) {
        setSedangVerifikasi(true);
        let cek;
        try { cek = await periksaWajahPenuh(canvas); } finally { setSedangVerifikasi(false); }
        if (!cek || !cek.ok) {
          alert((cek && cek.pesan) || 'Wajah tidak terverifikasi pada foto. Ulangi dengan wajah penuh menghadap kamera.');
          return;
        }

        // --- GERBANG IDENTITAS: wajah SIAPA ---------------------------
        // Sampai di sini kita hanya tahu ini wajah manusia hidup dan utuh.
        // Sekarang dicocokkan dengan wajah acuan pemilik akun.
        //
        // Pembandingnya dikerjakan SERVER. Yang dikirim dari sini hanya
        // 128 angka hasil jepretan; acuan milik karyawan tidak pernah
        // sampai ke HP (lihat apps-script/FaceProfile.gs).
        setSedangVerifikasi(true);
        setWajahIdentitas({ status: 'proses', pesan: 'Mencocokkan wajah…' });
        let lolosIdentitas = false;
        try {
          const desk = await ambilDeskriptorWajah(canvas);

          if (!desk.ok && !desk.tersedia) {
            // Modelnya yang gagal, BUKAN wajahnya yang salah. Foto tetap
            // diteruskan: server masih punya gerbangnya sendiri, dan
            // menolak absen karena jaringan buruk adalah kegagalan yang
            // salah alamat.
            deskriptorWajahRef.current = null;
            setWajahIdentitas({ status: 'gagal', pesan: desk.pesan });
            lolosIdentitas = true;
          } else if (!desk.ok) {
            alert(desk.pesan);
            setWajahIdentitas({ status: 'gagal', pesan: desk.pesan });
          } else {
            const res = await fetchApi(SCRIPT_URL, {
              method: 'POST',
              body: JSON.stringify({ action: 'verifikasi_wajah', wajahDescriptor: desk.deskriptor })
            });
            const hasil = await res.json();

            if (hasil.result !== 'success') {
              // Server tidak menjawab (Google membalas HTML, dsb). Foto
              // diteruskan; gerbang sesungguhnya tetap jalan saat Kirim.
              deskriptorWajahRef.current = desk.deskriptor;

              // Selama jendela transisi rilis, backend lama belum mengenal
              // action ini. Itu keadaan yang sepenuhnya normal dan bukan
              // urusan karyawan — jangan tampilkan apa-apa.
              const backendBelumSiap = /tidak dikenal/i.test(String(hasil.message || ''));
              setWajahIdentitas(backendBelumSiap
                ? { status: 'idle', pesan: '' }
                : { status: 'gagal', pesan: hasil.message || 'Pencocokan wajah tidak dapat dijalankan sekarang.' });
              lolosIdentitas = true;
            } else if (hasil.bolehLanjut) {
              deskriptorWajahRef.current = desk.deskriptor;
              setWajahIdentitas({
                status: hasil.cocok ? 'cocok' : 'idle',
                pesan: hasil.cocok
                  ? 'Wajah cocok dengan data karyawan ✓'
                  : (hasil.status === 'belum_terdaftar'
                      ? 'Wajah acuan Anda belum didaftarkan admin — presensi tetap dapat dikirim.'
                      : '')
              });
              lolosIdentitas = true;
            } else {
              // Inilah penolakan yang sebenarnya: wajahnya bukan pemilik akun.
              deskriptorWajahRef.current = null;
              setWajahIdentitas({ status: 'gagal', pesan: hasil.pesan || 'Wajah tidak cocok dengan data karyawan ini.' });
              alert(
                (hasil.pesan || 'Wajah tidak cocok dengan data karyawan ini.')
                + '\n\nPresensi hanya boleh diambil oleh pemilik akun. '
                + 'Bila Anda yakin ini keliru, coba di tempat yang lebih terang tanpa masker/topi, '
                + 'lalu hubungi admin bila tetap gagal.'
              );
            }
          }
        } catch (e) {
          deskriptorWajahRef.current = null;
          setWajahIdentitas({ status: 'gagal', pesan: 'Gagal menghubungi server untuk pencocokan wajah.' });
          lolosIdentitas = true; // gerbang server saat Kirim tetap menjaga
        } finally {
          setSedangVerifikasi(false);
        }

        if (!lolosIdentitas) return; // foto dibuang, kamera tetap menyala
      }

      const now = getServerNow(); const timestampText = `${now.toLocaleDateString('id-ID')} ${now.toLocaleTimeString('id-ID', { hour12: false })}`;
      // Simpan gambar POLOS dulu. Waktu jepret ikut disimpan supaya
      // penggambaran ulang nanti tetap memakai jam saat foto diambil,
      // bukan jam saat alamat tiba.
      const pakaiAlamat = !!(alamatStatus === 'ada' && alamat);
      fotoMentahRef.current = {
        dataUrl: kecilkanCanvas(canvas, FOTO_MUTU_MENTAH),
        timestampText,
        pakaiAlamat
      };
      // Nama tempat kalau sudah ada; kalau belum, koordinat dipakai
      // sementara dan akan diganti otomatis begitu alamat tiba.
      const lokasiText = pakaiAlamat
        ? alamat
        : (location ? `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}` : "No GPS");
      gambarStempelFoto(ctx, canvas.width, canvas.height, timestampText, lokasiText);
      setWaktuJepret(timestampText);
      setPhoto(kecilkanCanvas(canvas, FOTO_MUTU_KIRIM));
      setFotoTerverifikasi(true);
      if (trackerRef.current) {
        trackerRef.current.stop();
        trackerRef.current = null;
      }
      if (video.srcObject) { hentikanStream(video.srcObject); video.srcObject = null; }
      setCameraActive(false);
  };

  const handleSubmit = async () => {
    // Validasi
    if (type === 'Cuti') {
        const sisa = parseInt(user.sisaCuti) || 0;
        if (sisa < 1) { alert('Sisa Cuti Habis.'); return; }
    }
    if (isH3Required && intervalData.tglMulai) {
        const dMulai = new Date(intervalData.tglMulai); const dBatas = new Date();
        dBatas.setDate(dBatas.getDate() - 3); dMulai.setHours(0,0,0,0); dBatas.setHours(0,0,0,0);
        if (dMulai < dBatas) { alert(`Pengajuan ${type} maks H-3.`); return; }
    }
    if (isIntervalType) {
        if (!intervalData.tglMulai || !intervalData.tglSelesai) { alert('Pilih Tanggal!'); return; }
        const pesanKunci = pesanKunciPeriode([intervalData.tglMulai, intervalData.tglSelesai], batasTanggalForm);
        if (pesanKunci) { alert(pesanKunci); return; }
        // Validasi Jam hanya jika useTime aktif
        if (useTime && (!intervalData.jamMulai || !intervalData.jamSelesai)) { alert('Jam Mulai dan Selesai wajib diisi!'); return; }
    }
    if (isShiftWorker && isClockIn && !isEditMode && !selectedShift) { alert('Pilih Shift!'); return; }
    if (isPhotoRequired && !isEditMode && !photo) { alert('Foto Wajib.'); return; }
    if (isDlOnline && !isEditMode && catatan.trim().length < 3) { alert('Keterangan dinas wajib diisi (min. 3 huruf).'); return; }
    if (wajibWajah && !isEditMode && !fotoTerverifikasi) {
        alert('Foto presensi harus diambil langsung dari kamera depan dengan wajah penuh terverifikasi. Silakan ambil ulang foto.');
        return;
    }
    if (isGpsRequired && !isEditMode) {
      if (!location) { alert('Lokasi belum ditemukan.'); return; }
      if (gpsValidation.isMock) {
        alert(`Lokasi ditolak: Terdeteksi Mock Location / Fake GPS (${gpsValidation.warning}). Harap matikan aplikasi Fake GPS untuk melakukan presensi.`);
        return;
      }
    }

    setIsSubmitting(true);
    try {
      let shiftJamMulai = '', shiftJamSelesai = '';
      if (selectedShift) {
           const splitJam = selectedShift.split('-');
           if(splitJam.length === 2) { shiftJamMulai = splitJam[0].trim(); shiftJamSelesai = splitJam[1].trim(); }
      }

      // Logic Jam Final
      const finalJamMulai = isShiftWorker && isClockIn ? shiftJamMulai : (useTime ? intervalData.jamMulai : '-');
      const finalJamSelesai = isShiftWorker && isClockIn ? shiftJamSelesai : (useTime ? intervalData.jamSelesai : '-');

      const payload = { 
          action: isEditMode ? 'edit_absen' : 'absen', 
          uuid: isEditMode ? editItem.uuid : null, 
          userId: user.id, nama: user.nama, tipe: type, 
          lokasi: location ? `${location.lat}, ${location.lng}` : '-', 
          catatan: dlKeteranganTerbuka ? `${dlPrefix}\n${catatan.trim()}` : catatan, foto: photo, 
          fileLampiran: isUploading ? fileLampiran : null, 
          fileName: isUploading ? fileName : '', fileMime: isUploading ? fileMime : '',
          isMockGps: gpsValidation.isMock,
          gpsAccuracy: gpsValidation.accuracy,
          // Bukti mentah diteruskan apa adanya. Server memeriksanya ulang
          // bersama riwayat absensi karyawan — keputusan menolak absen ada
          // di server, bukan di sini, karena kode ini bisa diubah pengguna.
          gpsBukti: gpsValidation.bukti || null,

          // Identitas perangkat: server membandingkannya dengan perangkat
          // yang dipakai saat login. Bedanya ditandai, bukan ditolak
          // (lihat apps-script/Devices.gs).
          ...infoPerangkat(),

          // 128 angka wajah dari foto yang barusan dijepret. Server
          // mencocokkannya lagi di handleAbsen — pemeriksaan saat jepret
          // tadi hanya untuk memberi tahu karyawan lebih awal, bukan
          // pengganti gerbang ini.
          wajahDescriptor: (wajibWajah && !isEditMode) ? deskriptorWajahRef.current : null,

          ...intervalData,
          jamMulai: finalJamMulai,
          jamSelesai: finalJamSelesai
      };

      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify(payload) });
      const data = await res.json();
      if (data.result === 'success') {
        alert(data.message);

        // Titik absen ikut masuk ke pelacakan (GpsTracking.gs). Dikirim
        // TANPA await dan tanpa penanganan error: absen sudah tersimpan,
        // dan kegagalan mengirim satu titik pemantauan tidak boleh
        // mengubah apa pun yang dilihat karyawan.
        if (location && (type === 'Hadir' || type === 'Pulang' || type === 'Dinas Luar' || type === 'Dinas')) {
          kirimTitikGps({
            fetchApi, scriptUrl: SCRIPT_URL, user, sumber: 'absen',
            posisi: { lat: location.lat, lng: location.lng, akurasi: gpsValidation.accuracy }
          });
        }

        // Catat jam masuk/pulang hari ini mengikuti JAM SERVER
        // Nilai diambil langsung dari data.serverTime / data.jam respons server
        if (!isEditMode && (type === 'Hadir' || type === 'Pulang')) {
          try {
            const serverJam = data.serverTime || data.jam || getServerNow().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', hour12: false });
            const serverTgl = data.serverDate || data.tanggal || tglLokal(getServerNow());

            let simpan = {};
            try {
              const lama = JSON.parse(localStorage.getItem('absen_hari_ini') || '{}');
              if (lama.tgl === serverTgl && String(lama.userId) === String(user.id)) simpan = lama;
            } catch (e2) { /* isi rusak: mulai dari kosong */ }

            simpan.tgl = serverTgl;
            simpan.userId = user.id;
            simpan[type === 'Hadir' ? 'masuk' : 'pulang'] = serverJam;

            localStorage.setItem('absen_hari_ini', JSON.stringify(simpan));
          } catch (e2) { /* localStorage penuh/diblokir: abaikan saja */ }
        }

        if (data.newSisaCuti !== undefined) {
           const updatedUser = { ...user, sisaCuti: data.newSisaCuti };
           setUser(updatedUser); 
           // UBAH: localStorage menjadi sessionStorage
           sessionStorage.setItem('app_user', JSON.stringify(updatedUser));
        }
        setEditItem(null); setView(isEditMode ? 'history' : 'dashboard');
      } else if (data.code === 'WAJAH_TIDAK_COCOK') {
        // Gerbang server. Fotonya dibuang supaya tidak ada jalan mengirim
        // ulang isi yang sama tanpa menghadap kamera lagi.
        fotoMentahRef.current = null;
        deskriptorWajahRef.current = null;
        setPhoto(null);
        setFotoTerverifikasi(false);
        setWajahIdentitas({ status: 'gagal', pesan: data.message });
        alert(data.message + '\n\nSilakan ambil ulang foto presensi.');
      } else { alert(data.message); }
    } catch (e) { alert('Gagal kirim.'); } finally { setIsSubmitting(false); }
  };
  
  // ============================================================
  // TAMPILAN
  //
  // Susunannya: kepala berwarna (identitas pengajuan) -> kartu-kartu
  // seksi di atas latar netral -> bilah aksi yang menempel di bawah.
  //
  // Kenapa bilah aksi ditempel: di versi lama tombol "Kirim" ikut
  // menggulung ke bawah bersama isi form. Pada form yang panjang (foto +
  // peta + alasan) tombolnya keluar layar, dan orang harus menggulir
  // balik untuk mengirim.
  // ============================================================
  const tema = temaFor(type);
  const IkonTipe = ICON_MAP[type] || FileText;

  // Daftar syarat yang BELUM terpenuhi. Ini murni untuk ditampilkan —
  // validasi sesungguhnya tetap di handleSubmit dan tidak diubah, supaya
  // tidak ada jalur kirim yang diam-diam ikut terblokir oleh logika baru.
  // Nilainya juga bukan sekadar hiasan: sebelum ini satu-satunya cara tahu
  // ada yang kurang adalah menekan tombol lalu membaca alert.
  const NOTE_WAJIB = ['Ijin', 'Cuti', 'Sakit', 'Dinas Luar', 'Dinas', 'Cuti EO', 'Tukar Shift', 'Off', 'Lembur'];
  // DL: keterangan baru dibuka setelah selfie diambil. Di atasnya baris
  // terkunci berisi waktu jepret + lokasi (alamat, atau koordinat sampai
  // alamat tiba) — sama dengan stempel di foto, dan ikut tersimpan di
  // depan keterangan supaya HRD membacanya tanpa membuka foto.
  const dlKeteranganTerbuka = isDlOnline && !isEditMode && !!photo;
  const dlLokasiTeks = (alamatStatus === 'ada' && alamat)
      ? alamat
      : (location ? `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}` : 'Lokasi belum terbaca');
  const dlPrefix = `Waktu: ${waktuJepret || '-'} | Lokasi: ${dlLokasiTeks}`;
  const kurang = [];
  if (isShiftWorker && isClockIn && !isEditMode && !selectedShift) kurang.push('pilih shift');
  if (isIntervalType && (!intervalData.tglMulai || !intervalData.tglSelesai)) kurang.push('tanggal');
  if (isIntervalType && useTime && (!intervalData.jamMulai || !intervalData.jamSelesai)) kurang.push('jam mulai & selesai');
  if (isPhotoRequired && !isEditMode && !photo) kurang.push('foto');
  if (isGpsRequired && !isEditMode && !location) kurang.push('lokasi GPS');
  if (NOTE_WAJIB.includes(type) && catatan.trim().length < 3) kurang.push(isDlOnline ? 'keterangan dinas' : 'alasan');

  const adaTanggal = !!intervalData.tglMulai;
  const teksTanggal = adaTanggal
      ? (intervalData.tglMulai === intervalData.tglSelesai
          ? formatDateIndo(intervalData.tglMulai)
          : `${formatDateShort(intervalData.tglMulai)} — ${formatDateShort(intervalData.tglSelesai)}`)
      : 'Pilih tanggal…';

  return (
    <div className="min-h-screen bg-slate-50 pb-32 md:pb-12 md:max-w-2xl lg:max-w-3xl md:mx-auto md:my-4 md:rounded-3xl md:overflow-hidden md:shadow-lg md:border md:border-slate-200">

      {/* ================= KEPALA ================= */}
      <div className={`relative overflow-hidden bg-gradient-to-br ${tema.grad} px-5 pt-5 pb-14`}>
        {/* Dua lingkaran samar. Gradien polos terlihat rata seperti blok cat;
            ini memberi kedalaman tanpa menambah elemen yang harus dibaca. */}
        <div className="absolute -top-16 -right-10 w-52 h-52 rounded-full bg-white/10"></div>
        <div className="absolute -bottom-24 -left-16 w-56 h-56 rounded-full bg-black/5"></div>

        <div className="relative z-10 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-11 h-11 shrink-0 rounded-2xl bg-white/15 border border-white/25 backdrop-blur-sm flex items-center justify-center">
              <IkonTipe className="w-[19px] h-[19px] text-white" strokeWidth={2} />
            </span>
            <div className="min-w-0">
              <p className="text-[9.5px] font-semibold uppercase tracking-[0.14em] text-white/65 leading-none">
                {isEditMode ? 'Ubah pengajuan' : 'Pengajuan e-Form'}
              </p>
              <h2 className="mt-1 text-[20px] font-semibold text-white tracking-tight leading-none truncate">{type}</h2>
            </div>
          </div>

          <button
            onClick={() => { setEditItem(null); setView('dashboard'); }}
            className="shrink-0 w-9 h-9 rounded-xl bg-white/15 border border-white/25 backdrop-blur-sm flex items-center justify-center text-white transition-colors hover:bg-white/25 active:scale-95"
            title="Kembali"
          >
            <ChevronLeft className="w-[18px] h-[18px]" strokeWidth={2.2} />
          </button>
        </div>

        {/* Identitas pemohon. Di form HRIS ini selalu terlihat supaya orang
            yakin sedang mengajukan atas nama dirinya sendiri. */}
        <div className="relative z-10 mt-4 flex flex-wrap items-center gap-1.5">
          <span className="inline-flex items-center gap-1.5 bg-white/15 border border-white/20 backdrop-blur-sm rounded-lg px-2.5 py-1 text-[10.5px] font-medium text-white">
            <User className="w-3 h-3 text-white/70" strokeWidth={2} /> {user.nama}
          </span>
          <span className="inline-flex items-center gap-1.5 bg-white/15 border border-white/20 backdrop-blur-sm rounded-lg px-2.5 py-1 text-[10.5px] font-medium text-white">
            <Layers className="w-3 h-3 text-white/70" strokeWidth={2} /> {user.divisi}
          </span>
          {user.noPayroll && (
            <span className="inline-flex items-center gap-1.5 bg-white/15 border border-white/20 backdrop-blur-sm rounded-lg px-2.5 py-1 text-[10.5px] font-medium text-white font-mono">
              {user.noPayroll}
            </span>
          )}
        </div>
      </div>

      {isInitializing ? (
        <div className="px-4 -mt-8 relative z-10">
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm py-16 flex flex-col items-center justify-center">
            <Loader2 className="w-7 h-7 text-slate-300 animate-spin mb-3" />
            <p className="text-[12px] font-medium text-slate-400">Menyiapkan formulir…</p>
          </div>
        </div>
      ) : (
        <div className="px-4 -mt-8 relative z-10 space-y-3">

          {/* ---- PERINGATAN H-3 ---- */}
          {isH3Required && (
            <div className="bg-amber-50 border border-amber-200/80 rounded-2xl p-3.5 flex gap-2.5">
              <CircleAlert className="w-[17px] h-[17px] text-amber-600 shrink-0 mt-px" strokeWidth={2} />
              <div>
                <p className="text-[12px] font-semibold text-amber-900 leading-tight">Pengajuan wajib H-3</p>
                <p className="text-[11px] text-amber-700/90 leading-relaxed mt-0.5">
                  Tanggal mulai minimal 3 hari dari hari ini. Pengajuan lebih mepet akan ditolak sistem.
                </p>
              </div>
            </div>
          )}

          {/* ---- PILIH SHIFT ---- */}
          {isShiftWorker && isClockIn && !isEditMode && (
            <SeksiForm ikon={Clock} judul="Shift hari ini" catatan="Wajib dipilih sebelum absen masuk" warnaIkon={tema.chip}>
              <div className="relative">
                <select
                  className={`${INPUT_FORM} ${tema.fokus} appearance-none pr-10 cursor-pointer`}
                  value={selectedShift}
                  onChange={(e) => setSelectedShift(e.target.value)}
                >
                  <option value="">— Pilih shift —</option>
                  {availableShifts.map((s, idx) => (<option key={idx} value={s.value}>{s.label} ({s.value})</option>))}
                </select>
                <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </SeksiForm>
          )}

          {/* ---- TANGGAL & JAM ---- */}
          {isIntervalType && (
            <SeksiForm
              ikon={CalendarRange}
              judul="Periode pengajuan"
              catatan={useTime ? 'Beserta jam mulai & selesai' : 'Sehari penuh'}
              warnaIkon={tema.chip}
              aksi={
                <div className="flex items-center gap-2 shrink-0">
                  {/* Tanpa breakpoint 'xs:' — itu bukan breakpoint bawaan
                      Tailwind, jadi kelasnya tidak pernah ter-generate dan
                      labelnya akan hilang selamanya. */}
                  <span className="text-[10px] font-medium text-slate-400">Jam</span>
                  <Sakelar aktif={useTime} onToggle={() => setUseTime(!useTime)} label="Sertakan jam" />
                </div>
              }
            >
              <LabelKecil>Tanggal mulai — selesai</LabelKecil>
              <button
                onClick={() => setShowCalendar(true)}
                className={`w-full flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-xl border bg-white text-left transition-all active:scale-[0.995]
                  ${adaTanggal ? 'border-slate-200' : 'border-dashed border-slate-300'}`}
              >
                <span className="flex items-center gap-2.5 min-w-0">
                  <CalendarDays className={`w-4 h-4 shrink-0 ${adaTanggal ? 'text-slate-400' : 'text-slate-300'}`} strokeWidth={2} />
                  <span className={`text-[13.5px] truncate ${adaTanggal ? 'font-medium text-slate-900' : 'text-slate-400'}`}>
                    {teksTanggal}
                  </span>
                </span>
                <ChevronDown className="w-4 h-4 text-slate-300 shrink-0" />
              </button>

              <DateRangeModal
                isOpen={showCalendar}
                onClose={() => setShowCalendar(false)}
                onApply={handleCalendarApply}
                initialStart={intervalData.tglMulai}
                initialEnd={intervalData.tglSelesai}
                minDate={batasTanggalForm}
              />

              {useTime && (
                <div className="grid grid-cols-2 gap-2.5 mt-3.5 pt-3.5 border-t border-slate-100">
                  <div>
                    <LabelKecil>Jam mulai</LabelKecil>
                    <input type="time" className={`${INPUT_FORM} ${tema.fokus} tabular-nums`}
                      value={intervalData.jamMulai}
                      onChange={e => setIntervalData({ ...intervalData, jamMulai: e.target.value })} />
                  </div>
                  <div>
                    <LabelKecil>Jam selesai</LabelKecil>
                    <input type="time" className={`${INPUT_FORM} ${tema.fokus} tabular-nums`}
                      value={intervalData.jamSelesai}
                      onChange={e => setIntervalData({ ...intervalData, jamSelesai: e.target.value })} />
                  </div>
                </div>
              )}
            </SeksiForm>
          )}

          {/* ---- LAMPIRAN ---- */}
          {isUploadAllowed && (
            <SeksiForm
              ikon={Paperclip}
              judul="Lampiran"
              catatan={fileName ? fileName : 'Opsional — gambar atau PDF'}
              warnaIkon={tema.chip}
              padat
              aksi={
                <Sakelar
                  aktif={isUploading}
                  label="Sertakan lampiran"
                  onToggle={() => { setIsUploading(!isUploading); if (isUploading) { setFileLampiran(null); setFileName(''); } }}
                />
              }
            >
              {isUploading && (
                <>
                  <input type="file" id="lampiranInput" accept="image/*,.pdf" className="hidden" onChange={handleFileChange} />
                  <label
                    htmlFor="lampiranInput"
                    className={`cursor-pointer w-full flex flex-col items-center justify-center gap-1.5 py-6 rounded-xl border-2 border-dashed transition-colors
                      ${fileName ? 'border-slate-200 bg-slate-50/70' : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'}`}
                  >
                    {fileName
                      ? <CheckCircle className="w-6 h-6 text-emerald-500" strokeWidth={1.8} />
                      : <Upload className="w-6 h-6 text-slate-400" strokeWidth={1.8} />}
                    <span className="text-[12px] font-medium text-slate-700 text-center px-4 truncate max-w-full">
                      {fileName || 'Ketuk untuk memilih berkas'}
                    </span>
                    {fileName && <span className="text-[10px] text-slate-400">Ketuk lagi untuk mengganti</span>}
                  </label>
                </>
              )}
            </SeksiForm>
          )}

          {/* ---- FOTO ---- */}
          {isPhotoRequired && !isEditMode && (
            <SeksiForm
              ikon={Camera}
              judul="Foto kehadiran"
              catatan={wajibWajah
                ? "Wajib — kamera depan, wajah penuh terlihat"
                : wajibKameraDepan
                  ? "Wajib — kamera depan, boleh lebih dari satu orang"
                  : "Wajib — diambil langsung dari kamera"}
              warnaIkon={tema.chip}
              padat
              aksi={photo
                ? <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg shrink-0"><CheckCircle className="w-3 h-3" strokeWidth={2.4} /> Siap</span>
                : <span className="text-[10.5px] font-semibold text-slate-300 shrink-0">Belum</span>}
            >
              <div className="relative rounded-xl overflow-hidden bg-slate-900 aspect-[4/5]">
                {!photo && !cameraActive && (
                  <button onClick={startCamera} className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-100 hover:bg-slate-200/70 transition-colors">
                    <span className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center">
                      <Camera className="w-6 h-6 text-slate-500" strokeWidth={1.8} />
                    </span>
                    <span className="text-[12.5px] font-semibold text-slate-600">Buka kamera</span>
                  </button>
                )}

                <video ref={videoRef} autoPlay playsInline
                  className={`absolute inset-0 w-full h-full object-cover ${cameraActive && !photo ? 'block' : 'hidden'}`} />
                <canvas ref={canvasRef} className="hidden" />
                {photo && <img src={photo} alt="Pratinjau" className="absolute inset-0 w-full h-full object-cover" />}

                {cameraActive && !photo && (
                  <>
                    {/* Viewfinder Oval Face Guide & Liveness Indicator */}
                    {wajibWajah ? (
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none p-4">
                        <div className={`w-48 h-64 sm:w-56 sm:h-72 rounded-[50%] border-4 transition-all duration-300 ${
                          livenessStatus.isLive 
                            ? 'border-emerald-400 shadow-[0_0_25px_rgba(52,211,153,0.5)]' 
                            : livenessStatus.status === 'verifying'
                              ? 'border-amber-400 shadow-[0_0_20px_rgba(251,191,36,0.4)]'
                              : 'border-white/40'
                        }`} />
                        {livenessStatus.message && (
                          <div className="mt-3 px-3 py-1 rounded-full bg-black/65 backdrop-blur-sm border border-white/20">
                            <p className="text-[11px] font-medium text-white text-center">
                              {livenessStatus.message}
                            </p>
                          </div>
                        )}
                        {/* Baris kedua khusus pencocokan IDENTITAS. Sengaja
                            terpisah dari pesan liveness di atas: keduanya
                            menjawab pertanyaan berbeda dan bisa berbeda status
                            pada saat yang sama. */}
                        {(wajahIdentitas.status === 'model' || wajahIdentitas.status === 'proses') && (
                          <div className="mt-2 px-3 py-1 rounded-full bg-blue-600/80 backdrop-blur-sm border border-white/25">
                            <p className="text-[10.5px] font-semibold text-white text-center">
                              {wajahIdentitas.status === 'model'
                                ? 'Menyiapkan pencocokan wajah…'
                                : 'Mencocokkan dengan data karyawan…'}
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="absolute inset-6 rounded-2xl border-2 border-white/25 pointer-events-none"></div>
                    )}

                    <div className="absolute inset-x-0 bottom-0 pt-10 pb-4 bg-gradient-to-t from-black/60 to-transparent flex items-center justify-center gap-8">
                      {wajibKameraDepan ? (
                        <span className="w-10"></span>
                      ) : (
                        <button onClick={toggleCamera} className="w-10 h-10 rounded-full bg-white/15 backdrop-blur-sm border border-white/25 flex items-center justify-center text-white active:scale-90 transition-transform" title="Ganti kamera">
                          <SwitchCamera className="w-[18px] h-[18px]" strokeWidth={2} />
                        </button>
                      )}
                      <button
                        onClick={takePhoto}
                        disabled={wajibWajah && (!livenessStatus.isLive || sedangVerifikasi)}
                        className={`w-[62px] h-[62px] rounded-full backdrop-blur-sm flex items-center justify-center active:scale-90 transition-transform disabled:cursor-not-allowed ${
                          (wajibWajah && (!livenessStatus.isLive || sedangVerifikasi))
                            ? 'bg-amber-500/30'
                            : 'bg-white/25'
                        }`} 
                        title="Ambil foto"
                      >
                        <span className={`w-[50px] h-[50px] rounded-full border-[3px] transition-colors ${
                          (wajibWajah && (!livenessStatus.isLive || sedangVerifikasi))
                            ? 'bg-amber-100 border-amber-400'
                            : 'bg-white border-white/60'
                        }`}></span>
                      </button>
                      <span className="w-10"></span>
                    </div>
                  </>
                )}
              </div>

              {!photo && errorKamera && (
                <div className="mt-2.5 rounded-xl bg-rose-50 border border-rose-200 p-3">
                  <p className="text-[11.5px] font-semibold text-rose-700 leading-relaxed">{errorKamera}</p>
                  <button
                    onClick={startCamera}
                    className="mt-2 w-full py-2 rounded-lg bg-rose-600 text-white text-[12px] font-semibold active:scale-[0.99]"
                  >
                    Coba lagi
                  </button>
                </div>
              )}

              {wajibWajah && !isEditMode && wajahIdentitas.pesan && (
                <div className={`mt-2.5 rounded-xl border p-3 ${
                  wajahIdentitas.status === 'cocok'
                    ? 'bg-emerald-50 border-emerald-200'
                    : wajahIdentitas.status === 'gagal'
                      ? 'bg-rose-50 border-rose-200'
                      : 'bg-amber-50 border-amber-200'
                }`}>
                  <p className={`text-[11.5px] font-semibold leading-relaxed ${
                    wajahIdentitas.status === 'cocok'
                      ? 'text-emerald-700'
                      : wajahIdentitas.status === 'gagal'
                        ? 'text-rose-700'
                        : 'text-amber-700'
                  }`}>
                    {wajahIdentitas.pesan}
                  </p>
                </div>
              )}

              {wajibKameraDepan && cameraActive && !photo && !kameraInfo.dikenali && (
                <p className="mt-2 text-[10.5px] text-slate-500 leading-relaxed">
                  {wajibWajah
                    ? 'Jenis kamera tidak dilaporkan perangkat ini — verifikasi wajah penuh tetap wajib lolos sebelum foto dapat diambil.'
                    : 'Jenis kamera tidak dilaporkan perangkat ini. Pastikan Anda memakai kamera depan.'}
                </p>
              )}

              {photo && (
                <button
                  onClick={() => { /* Wajib dikosongkan: tanpa ini efek penggambaran ulang stempel bisa memunculkan kembali foto yang baru saja dibuang. */ fotoMentahRef.current = null; deskriptorWajahRef.current = null; setPhoto(null); setFotoTerverifikasi(false); setWajahIdentitas({ status: 'idle', pesan: '' }); startCamera(); }}
                  className="mt-2.5 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-[12.5px] font-semibold text-slate-700 transition-colors active:scale-[0.99]"
                >
                  <RotateCcw className="w-3.5 h-3.5" strokeWidth={2.2} /> Ambil ulang
                </button>
              )}
            </SeksiForm>
          )}

          {/* ---- LOKASI ---- */}
          {!isEditMode && isGpsRequired && (
            <SeksiForm
              ikon={LocateFixed}
              judul="Lokasi"
              catatan={location ? (gpsValidation.isMock ? 'Peringatan: Terdeteksi Fake GPS / Mock' : isGeofenceRequired ? 'GPS terkunci · wajib di area kantor' : 'Titik GPS terkunci') : (isGeofenceRequired ? 'Mencari GPS dan memeriksa area kantor…' : 'Mencari sinyal GPS…')}
              warnaIkon={tema.chip}
              padat
              aksi={location
                ? ((isDlOnline && !gpsValidation.isMock) ? null : <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-1 rounded-lg shrink-0 ${gpsValidation.isMock ? 'text-rose-600 bg-rose-50' : 'text-emerald-600 bg-emerald-50'}`}><CheckCircle className="w-3 h-3" strokeWidth={2.4} /> {gpsValidation.isMock ? 'Mock GPS' : 'Terkunci'}</span>)
                : <Loader2 className="w-3.5 h-3.5 text-slate-300 animate-spin shrink-0" />}
            >
              <div className={`rounded-xl overflow-hidden ${isDlOnline ? 'h-28' : 'h-44'} bg-slate-100 relative`}>
                {location ? (
                  <iframe title="Lokasi" width="100%" height="100%" frameBorder="0" style={{ border: 0 }}
                    src={`https://maps.google.com/maps?q=${location.lat},${location.lng}&z=17&output=embed`}></iframe>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full gap-2">
                    <MapPin className="w-7 h-7 text-slate-300 animate-pulse" strokeWidth={1.8} />
                    <span className="text-[11px] font-medium text-slate-400">Menunggu GPS…</span>
                  </div>
                )}
              </div>
              {location && (
                <div className="mt-2 text-center">
                  {/* Nama tempat lebih dulu; koordinat hanya muncul sebagai
                      cadangan kalau alamat tidak berhasil didapat. */}
                  {alamatStatus === 'mencari' && (
                    <p className="inline-flex items-center gap-1.5 text-[11px] text-slate-400">
                      <Loader2 className="w-3 h-3 animate-spin" /> Mencari nama lokasi…
                    </p>
                  )}
                  {alamatStatus === 'ada' && (
                    <p className="text-[12px] font-semibold leading-snug text-slate-700 px-2">
                      <MapPin className="inline w-3.5 h-3.5 -mt-0.5 mr-1 text-slate-400" strokeWidth={2} />
                      {alamat}
                    </p>
                  )}
                  {alamatStatus === 'kosong' && (
                    <p className="text-[10px] text-slate-400 font-mono tabular-nums">
                      {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
                    </p>
                  )}
                  {gpsValidation.accuracy !== null && (
                    <p className="mt-0.5 text-[10px] text-slate-400 tabular-nums">
                      Akurasi ±{Math.round(gpsValidation.accuracy)} m
                    </p>
                  )}
                  {gpsValidation.isMock && (
                    <p className="mt-1 text-[11px] font-medium text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-2.5 py-1.5">
                      ⚠️ {gpsValidation.warning || 'Mock Location / Fake GPS terdeteksi. Matikan aplikasi Fake GPS.'}
                    </p>
                  )}
                </div>
              )}
              {isGeofenceRequired && (
                <p className="mt-2 text-[10.5px] leading-relaxed text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-2 text-center">
                  Absen hanya diterima jika berada di salah satu area kantor yang ditetapkan Admin.
                </p>
              )}
            </SeksiForm>
          )}

          {/* ---- ALASAN ---- */}
          {isDlOnline && !isEditMode ? (
            dlKeteranganTerbuka ? (
              <section className="bg-white rounded-2xl border border-slate-200/80 shadow-sm shadow-slate-900/[0.03] overflow-hidden px-4 py-3.5">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 overflow-hidden" aria-readonly="true">
                    <div className="px-3 py-1.5 border-b border-slate-200/80">
                      <span className="text-[9.5px] font-semibold uppercase tracking-[0.1em] text-slate-400">Waktu &amp; Lokasi Dinas</span>
                    </div>
                    <dl className="grid grid-cols-[18px_52px_1fr] items-start gap-x-1.5 gap-y-2 px-3 py-2.5 text-[12px] leading-snug select-none">
                      <Clock className="w-3.5 h-3.5 mt-px text-slate-400" strokeWidth={2} />
                      <dt className="text-slate-400">Waktu</dt>
                      <dd className="font-semibold text-slate-700 tabular-nums">{waktuJepret || '-'}</dd>
                      <MapPin className="w-3.5 h-3.5 mt-px text-slate-400" strokeWidth={2} />
                      <dt className="text-slate-400">Lokasi</dt>
                      <dd className={`font-semibold break-words ${alamatStatus === 'mencari' ? 'text-slate-400 animate-pulse' : 'text-slate-700'}`}>
                        {alamatStatus === 'mencari' && !alamat ? 'Mencari nama lokasi…' : dlLokasiTeks}
                      </dd>
                    </dl>
                  </div>
                  <LabelKecil><span className="mt-3.5 block">Keterangan dinas</span></LabelKecil>
                  <textarea
                    className={`${INPUT_FORM} ${tema.fokus} resize-none leading-relaxed`}
                    placeholder="Tujuan, lokasi, dan keperluan dinas…"
                    rows="3"
                    autoFocus
                    value={catatan}
                    onChange={e => setCatatan(e.target.value)}
                  />
                  <div className="mt-1.5 flex justify-end">
                    <span className={`text-[10px] tabular-nums ${catatan.trim().length < 3 ? 'text-slate-300' : 'text-slate-400'}`}>
                      {catatan.length} huruf
                    </span>
                  </div>
              </section>
            ) : null
          ) : (
          <SeksiForm
            ikon={NotebookPen}
            judul={isDlOnline ? 'Keterangan dinas' : 'Keterangan'}
            catatan={NOTE_WAJIB.includes(type) ? 'Wajib diisi — minimal 3 huruf' : 'Opsional'}
            warnaIkon={tema.chip}
          >
            <textarea
              className={`${INPUT_FORM} ${tema.fokus} resize-none leading-relaxed`}
              placeholder={isDlOnline ? 'Tujuan, lokasi, dan keperluan dinas…' : `Tulis alasan pengajuan ${type} Anda…`}
              rows="3"
              value={catatan}
              onChange={e => setCatatan(e.target.value)}
            />
            <div className="mt-1.5 flex justify-end">
              <span className={`text-[10px] tabular-nums ${catatan.trim().length < 3 && NOTE_WAJIB.includes(type) ? 'text-slate-300' : 'text-slate-400'}`}>
                {catatan.length} huruf
              </span>
            </div>
          </SeksiForm>
          )}
          {isDlOnline && !isEditMode && (
            <p className="px-2 text-center text-[10.5px] leading-relaxed text-slate-400">
              Dinas dicatat untuk hari ini dan langsung terverifikasi. Lupa mengajukan DL di hari lain? Gunakan <span className="font-semibold text-slate-500">Lapor HRD</span>.
            </p>
          )}

          <p className="flex items-center justify-center gap-1.5 pt-1 pb-2 text-[10px] text-slate-400">
            <ShieldCheck className="w-3 h-3" strokeWidth={2} />
            Data pengajuan tercatat atas nama {user.nama}
          </p>
        </div>
      )}

      {/* ================= BILAH AKSI ================= */}
      {!isInitializing && (
        <div className="fixed bottom-0 left-0 right-0 z-40 pointer-events-none">
          <div className="max-w-md mx-auto pointer-events-auto bg-white/90 backdrop-blur-md border-t border-slate-200 px-4 pt-3 pb-4">

            {/* Daftar yang masih kurang. Sebelumnya informasi ini baru muncul
                sebagai alert SETELAH tombol ditekan, satu per satu. */}
            {kurang.length > 0 && (
              <div className="mb-2.5 flex items-start gap-2 text-[11px] text-slate-500">
                <CircleAlert className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-px" strokeWidth={2.2} />
                <span className="leading-snug">
                  Belum lengkap: <span className="font-semibold text-slate-700">{kurang.join(', ')}</span>
                </span>
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-white text-[14px] font-semibold tracking-tight shadow-lg transition-all active:scale-[0.98]
                ${isSubmitting ? 'bg-slate-300 shadow-none cursor-wait' : tema.tombol}`}
            >
              {isSubmitting
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Mengirim…</>
                : <><Send className="w-4 h-4" strokeWidth={2.2} /> {isEditMode ? 'Simpan perubahan' : 'Kirim pengajuan'}</>}
            </button>
          </div>
        </div>
      )}
      <ProcessingModal
        isOpen={isSubmitting}
        title={isEditMode ? "Menyimpan Perubahan..." : "Mengirim Pengajuan..."}
        subtitle="Sistem sedang memverifikasi koordinat GPS, validasi biometrik, dan sinkronisasi ke server."
      />
    </div>
  );
}

// --- 4. APPROVAL SCREEN (MODERN REDESIGN) ---
// [SOURCE REFERENCE: 1724]
function ApprovalScreen({ user, setView }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('All');

  // Tab kedua: riwayat SEMUA pengajuan tim (bukan cuma yang Pending), supaya
  // approver bisa lihat tipe form apa saja yang sudah diajukan timnya lengkap
  // dengan keterangan/alasan, tanggal, dan lampiran.
  const [activeApprovalTab, setActiveApprovalTab] = useState('pending');
  const [historyList, setHistoryList] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyStatusFilter, setHistoryStatusFilter] = useState('All');
  const [historyTruncated, setHistoryTruncated] = useState(false);
  const [historyPeriod, setHistoryPeriod] = useState(null);
  const [expandedHistoryUuid, setExpandedHistoryUuid] = useState(null);

  // PERIODE (4 Nov 2026): daftar Pending & Riwayat Tim hanya menampilkan
  // pengajuan yang tanggalnya jatuh di periode terpilih (bawaan: periode
  // berjalan). '' = semua tanggal.
  const [periodeOpsi, setPeriodeOpsi] = useState([]);
  const [periodePilih, setPeriodePilih] = useState(null);   // { mulai, selesai } | '' (semua)
  useEffect(() => {
    let batal = false;
    fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'get_absence_period' }) })
      .then(r => r.json())
      .then(d => {
        if (batal || d.result !== 'success') return;
        const daftar = (Array.isArray(d.periodsAktif) && d.periodsAktif.length ? d.periodsAktif : (Array.isArray(d.periods) ? d.periods : []))
          .filter(p => p && p.mulai && p.selesai)
          .sort((a, b) => String(b.mulai).localeCompare(String(a.mulai)));
        setPeriodeOpsi(daftar);
        const bawaan = d.periodeDefault || d.period || daftar[0] || null;
        setPeriodePilih(prev => (prev === null ? (bawaan && bawaan.mulai ? { mulai: bawaan.mulai, selesai: bawaan.selesai } : '') : prev));
      })
      .catch(() => { if (!batal) setPeriodePilih(prev => (prev === null ? '' : prev)); });
    return () => { batal = true; };
  }, []);
  // Tanggal acara pengajuan (YYYY-MM-DD); jatuh ke tanggal input bila tidak ada.
  const keYMD = (v) => {
    if (!v || v === '-') return '';
    const t = String(v);
    let m = t.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      // ISO dengan jam (mis. 2026-10-31T17:00:00.000Z) -> tanggal lokal.
      if (t.length > 10) { const d = new Date(t); if (!isNaN(d)) return d.toLocaleDateString('en-CA'); }
      return `${m[1]}-${m[2]}-${m[3]}`;
    }
    m = t.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
    if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
    const d = new Date(t);
    return isNaN(d) ? '' : d.toLocaleDateString('en-CA');
  };
  const dalamPeriode = (item) => {
    if (!periodePilih) return true;
    const mulai = keYMD(item.tglMulai) || keYMD(item.waktu);
    const selesai = keYMD(item.tglSelesai) || mulai;
    if (!mulai) return true;
    return selesai >= periodePilih.mulai && mulai <= periodePilih.selesai;
  };

  // --- LOGIC FETCH (Sama seperti sebelumnya) ---
  const fetchApprovalList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchApi(SCRIPT_URL, { 
        method: 'POST', 
        body: JSON.stringify({ 
            action: 'get_approval_list', 
            userId: user.id, 
            divisi: user.divisi, 
            role: user.role,
            lokasi: user.lokasi || 'All' 
        }) 
      });
      const data = await res.json();
      if (data.result === 'success') setList(data.list);
    } catch (e) { alert('Gagal memuat data approval'); } finally { setLoading(false); }
  }, [user.id, user.divisi, user.role, user.lokasi]);

  useEffect(() => { fetchApprovalList(); }, [fetchApprovalList]);

  const fetchTeamHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await fetchApi(SCRIPT_URL, {
        method: 'POST',
        body: JSON.stringify({
          action: 'get_team_history',
          userId: user.id,
          divisi: user.divisi,
          role: user.role,
          lokasi: user.lokasi || 'All',
          ...(periodePilih ? { filterStart: periodePilih.mulai, filterEnd: periodePilih.selesai } : { filterStart: '2000-01-01', filterEnd: '2100-12-31' })
        })
      });
      const data = await res.json();
      if (data.result === 'success') {
        setHistoryList(data.list || []);
        setHistoryTruncated(!!data.truncated);
        setHistoryPeriod(data.period || null);
        setHistoryLoaded(true);
      } else alert(data.message || 'Gagal memuat riwayat tim');
    } catch (e) { alert('Gagal memuat riwayat tim'); } finally { setLoadingHistory(false); }
  }, [user.id, user.divisi, user.role, user.lokasi, periodePilih]);

  // Periode diganti -> riwayat tim dimuat ulang untuk periode itu.
  useEffect(() => { setHistoryLoaded(false); }, [periodePilih]);
  useEffect(() => {
    if (periodePilih === null) return;
    if (activeApprovalTab === 'riwayat' && !historyLoaded) fetchTeamHistory();
  }, [activeApprovalTab, historyLoaded, fetchTeamHistory, periodePilih]);

  // --- LOGIC APPROVE/REJECT ---
const handleDecision = async (uuid, decision, namaUser) => {
    const isReject = decision === 'reject';
    const actionText = isReject ? 'Menolak' : 'Menyetujui';
    
    // 1. Minta Input Alasan/Catatan
    const pesanPrompt = isReject 
        ? `Alasan PENOLAKAN untuk ${namaUser} (Wajib diisi):` 
        : `Catatan PERSETUJUAN untuk ${namaUser} (Opsional):`;
    
    const alasanInput = window.prompt(pesanPrompt, "");
    
    // Jika user klik Cancel di prompt
    if (alasanInput === null) return;

    // 2. Validasi: Reject Wajib Ada Alasan
    if (isReject && alasanInput.trim() === "") {
        alert("Gagal! Anda wajib memberikan alasan jika menolak pengajuan.");
        return;
    }

    // 3. Konfirmasi Terakhir
    if (!window.confirm(`Yakin ingin ${actionText} pengajuan ini?`)) return;
    
    // 4. Optimistic Update UI (Hapus baris dari layar biar cepat)
    const newList = list.filter(item => item.uuid !== uuid);
    setList(newList);

    try {
        // 5. Kirim ke Server
        const res = await fetchApi(SCRIPT_URL, { 
            method: 'POST', 
            body: JSON.stringify({ 
                action: 'process_approval', 
                uuid: uuid, 
                decision: decision, 
                approverName: user.nama, // Mengambil nama user yang sedang login
                alasan: alasanInput.trim() 
            }) 
        }).then(r => r.json());
        
        if (res.result === 'success') {
            alert(res.message);
            // Tidak perlu fetch ulang jika sukses, karena data sudah dihapus dari layar (Optimistic UI).
            // Tandai riwayat tim sebagai basi supaya di-refetch saat tab Riwayat dibuka lagi.
            setHistoryLoaded(false);
        } else {
            // Jika Gagal, kembalikan data (Revert)
            alert(res.message);
            fetchApprovalList(); 
        }
    } catch (e) {
        alert('Terjadi kesalahan koneksi');
        fetchApprovalList(); // Revert jika error koneksi
    }
};

  const formatDateIndo = (dateString) => { 
    if (!dateString || dateString === '-') return '-';
    try { 
      const date = new Date(dateString);
      return date.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric'});
    } catch (e) { return dateString; } 
  };

  const currentList = activeApprovalTab === 'riwayat' ? historyList : list;
  const uniqueTypes = ['All', ...new Set(currentList.map(item => item.tipe))];
  const statusFilteredHistory = activeApprovalTab === 'riwayat'
    ? historyList.filter(item => historyStatusFilter === 'All' || item.status === historyStatusFilter)
    : historyList;
  const listPeriode = list.filter(dalamPeriode);
  const tersembunyiPeriode = list.length - listPeriode.length;
  const filteredList = activeApprovalTab === 'riwayat'
    ? statusFilteredHistory.filter(item => filterType === 'All' || item.tipe === filterType)
    : listPeriode.filter(item => filterType === 'All' || item.tipe === filterType);

  // --- COLORS HELPER ---
  const getTypeColor = (tipe) => {
      switch(tipe) {
          case 'Cuti': return 'bg-pink-100 text-pink-700 border-pink-200';
          case 'Sakit': return 'bg-orange-100 text-orange-700 border-orange-200';
          case 'Ijin': return 'bg-blue-100 text-blue-700 border-blue-200';
          case 'Dinas Luar': return 'bg-indigo-100 text-indigo-700 border-indigo-200';
          default: return 'bg-gray-100 text-gray-700 border-gray-200';
      }
  };

  const getStatusColor = (status) => {
      switch (status) {
          case 'Approved': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
          case 'Verified': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
          case 'Rejected': return 'bg-rose-100 text-rose-700 border-rose-200';
          case 'Pending': return 'bg-amber-100 text-amber-700 border-amber-200';
          default: return 'bg-gray-100 text-gray-700 border-gray-200';
      }
  };

  return (
      <div className="p-4 h-full overflow-y-auto pb-24 bg-gray-50/50">
        <div className="flex items-center justify-between mb-4">
          <div>
              <h2 className="text-xl font-extrabold text-slate-800 tracking-tight">Approval</h2>
              <p className="text-[10px] text-slate-500 font-medium">{activeApprovalTab === 'pending' ? 'Menunggu persetujuan HRD' : 'Riwayat semua pengajuan tim'}</p>
          </div>
          <BackButton onClick={() => setView('dashboard')} />
        </div>

        {/* --- TAB PENDING / RIWAYAT TIM --- */}
        <div className="flex gap-1.5 bg-slate-100 p-1 rounded-xl mb-4">
            <button
                onClick={() => setActiveApprovalTab('pending')}
                className={`flex-1 py-2 rounded-lg text-xs font-bold transition-colors ${activeApprovalTab === 'pending' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}
            >
                Pending
            </button>
            <button
                onClick={() => setActiveApprovalTab('riwayat')}
                className={`flex-1 py-2 rounded-lg text-xs font-bold transition-colors ${activeApprovalTab === 'riwayat' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}
            >
                Riwayat Tim
            </button>
        </div>

        {/* --- PERIODE --- */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
                <CalendarRange className="w-4 h-4 text-indigo-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <select
                    value={periodePilih ? `${periodePilih.mulai}|${periodePilih.selesai}` : ''}
                    onChange={(e) => { const v = e.target.value; if (!v) { setPeriodePilih(''); return; } const [m, sl] = v.split('|'); setPeriodePilih({ mulai: m, selesai: sl }); }}
                    className="w-full appearance-none pl-9 pr-8 py-2 rounded-xl border border-indigo-100 bg-indigo-50 text-xs font-bold text-indigo-800 outline-none cursor-pointer">
                    {periodePilih === null && <option value="">Memuat periode…</option>}
                    {periodeOpsi.map(p => (
                        <option key={`${p.mulai}|${p.selesai}`} value={`${p.mulai}|${p.selesai}`}>
                            Periode {formatDateShort(p.mulai)} – {formatDateShort(p.selesai)}
                        </option>
                    ))}
                    {periodePilih !== null && <option value="">Semua tanggal</option>}
                </select>
                <ChevronDown className="w-3.5 h-3.5 text-indigo-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
            {activeApprovalTab === 'pending' && tersembunyiPeriode > 0 && (
                <button type="button" onClick={() => setPeriodePilih('')}
                    className="text-[11px] font-medium text-slate-500 underline decoration-dotted hover:text-indigo-700">
                    {tersembunyiPeriode} pengajuan di luar periode ini disembunyikan
                </button>
            )}
        </div>

        {/* --- FILTER SECTION --- */}
        <div className="flex flex-wrap justify-between items-center gap-2 mb-5 sticky top-0 bg-gray-50/95 py-2 z-10 backdrop-blur-sm">
            <div className="flex items-center gap-2">
                 <span className="bg-slate-800 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-sm shadow-slate-300">
                    {filteredList.length} {activeApprovalTab === 'pending' ? 'Pending' : 'Pengajuan'}
                 </span>
            </div>

            <div className="flex items-center gap-2">
                {activeApprovalTab === 'riwayat' && (
                    <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full shadow-sm border border-gray-200">
                        <select
                            value={historyStatusFilter}
                            onChange={(e) => setHistoryStatusFilter(e.target.value)}
                            className="text-xs bg-transparent border-none outline-none font-bold text-slate-600 cursor-pointer"
                        >
                            <option value="All">Semua Status</option>
                            <option value="Pending">Pending</option>
                            <option value="Approved">Approved</option>
                            <option value="Rejected">Rejected</option>
                            <option value="Verified">Verified</option>
                        </select>
                    </div>
                )}
                <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full shadow-sm border border-gray-200">
                    <Filter className="w-3.5 h-3.5 text-slate-400" />
                    <select
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        className="text-xs bg-transparent border-none outline-none font-bold text-slate-600 cursor-pointer"
                    >
                        <option value="All">Semua Tipe</option>
                        {uniqueTypes.filter(t => t !== 'All').map((type, idx) => (
                           <option key={idx} value={type}>{type}</option>
                        ))}
                    </select>
                </div>
            </div>
        </div>

        {activeApprovalTab === 'riwayat' && historyPeriod && (
            <p className="text-[10px] text-slate-400 font-medium mb-2 px-1">
                Periode aktif: {formatDateIndo(historyPeriod.mulai)} – {formatDateIndo(historyPeriod.selesai)}
            </p>
        )}

        {activeApprovalTab === 'riwayat' && historyTruncated && (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                Menampilkan {historyList.length} pengajuan terbaru saja — riwayat lebih lama dari tim ini tidak ikut ditampilkan.
            </div>
        )}

        {(activeApprovalTab === 'pending' ? loading : loadingHistory) ? (
            <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mb-2"/>
                <span className="text-xs font-bold text-slate-400">Memuat {activeApprovalTab === 'pending' ? 'Pengajuan' : 'Riwayat'}...</span>
            </div>
        ) : filteredList.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-gray-300">
                 <CheckCircle className="w-12 h-12 text-gray-200 mx-auto mb-2" />
                 <p className="text-slate-400 font-bold text-sm">{activeApprovalTab === 'pending' ? 'Semua beres!' : 'Belum ada riwayat'}</p>
                 <p className="text-[10px] text-slate-300">{activeApprovalTab === 'pending' ? 'Tidak ada pengajuan yang perlu diproses.' : 'Tidak ada pengajuan tim pada periode aktif ini.'}</p>
            </div>
        ) : activeApprovalTab === 'riwayat' ? (
          /* --- TAMPILAN TABEL (RINGKAS, MIRIP EXCEL) UNTUK RIWAYAT TIM --- */
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Nama</th>
                    <th className="px-3 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Departemen</th>
                    <th className="px-3 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Tipe</th>
                    <th className="px-3 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Periode</th>
                    <th className="px-3 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Status</th>
                    <th className="px-3 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Approver</th>
                    <th className="px-2 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredList.map((item, idx) => {
                    const isOpen = expandedHistoryUuid === item.uuid;
                    const adaLampiran = (item.foto && item.foto.length > 10 && item.foto !== 'Error Upload') || (item.lampiran && item.lampiran.length > 10 && item.lampiran !== '-');
                    return (
                      <React.Fragment key={item.uuid || idx}>
                        <tr
                          onClick={() => setExpandedHistoryUuid(isOpen ? null : item.uuid)}
                          className={`cursor-pointer hover:bg-slate-50 transition-colors ${isOpen ? 'bg-indigo-50/60' : ''}`}
                        >
                          <td className="px-3 py-2 text-xs font-bold text-slate-800 whitespace-nowrap">{item.nama}</td>
                          <td className="px-3 py-2 text-[11px] text-slate-500 whitespace-nowrap">{item.divisi}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${getTypeColor(item.tipe)}`}>{item.tipe}</span>
                          </td>
                          <td className="px-3 py-2 text-[11px] text-slate-600 whitespace-nowrap">
                            {item.tglMulai && item.tglMulai !== '-' ? `${formatDateIndo(item.tglMulai)}${item.tglSelesai && item.tglSelesai !== item.tglMulai ? ` – ${formatDateIndo(item.tglSelesai)}` : ''}` : formatDateIndo(item.waktu)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${getStatusColor(item.status)}`}>{item.status}</span>
                          </td>
                          <td className="px-3 py-2 text-[11px] text-slate-500 whitespace-nowrap">{item.status !== 'Pending' ? (item.approver || '-') : '-'}</td>
                          <td className="px-2 py-2 text-center">
                            <ChevronDown className={`w-3.5 h-3.5 inline transition-transform ${isOpen ? 'rotate-180 text-indigo-500' : 'text-slate-300'}`} />
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-slate-50/70">
                            <td colSpan={7} className="px-4 py-3">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Keterangan / Alasan</p>
                              <p className="text-xs text-slate-600 italic mb-2 leading-relaxed">"{item.catatan || '-'}"</p>

                              {item.status !== 'Pending' && (
                                <p className="text-[11px] text-slate-500 mb-2">
                                  {item.status === 'Rejected' ? 'Ditolak' : 'Disetujui'} oleh <span className="font-bold text-slate-700">{item.approver || '-'}</span>{item.approvalTime && item.approvalTime !== '-' ? ` · ${formatDateIndo(item.approvalTime)}` : ''}
                                </p>
                              )}
                              {item.status === 'Rejected' && item.alasan && item.alasan !== '-' && (
                                <p className="text-xs text-rose-600 italic mb-2">"{item.alasan}"</p>
                              )}

                              {adaLampiran && (
                                <div className="flex gap-2 mt-1">
                                  {item.foto && item.foto.length > 10 && item.foto !== 'Error Upload' && (
                                    <a href={item.foto} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 bg-blue-50 text-blue-600 px-3 py-1.5 rounded-lg text-[10px] font-bold border border-blue-200 hover:bg-blue-100 transition shadow-sm no-underline">
                                        <Camera className="w-3 h-3"/> Foto Bukti
                                    </a>
                                  )}
                                  {item.lampiran && item.lampiran.length > 10 && item.lampiran !== '-' && (
                                    <a href={item.lampiran} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 bg-amber-50 text-amber-600 px-3 py-1.5 rounded-lg text-[10px] font-bold border border-amber-200 hover:bg-amber-100 transition shadow-sm no-underline">
                                        <FileIcon className="w-3 h-3"/> Dokumen
                                    </a>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* --- CARD DESIGN BARU (TAB PENDING) --- */}
            {filteredList.map((item, idx) => (
              <div key={idx} className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-lg transition-all duration-300 group">

                {/* HEADER STRIP */}
                <div className="h-1.5 w-full bg-gradient-to-r from-blue-500 to-indigo-600"></div>

                <div className="p-4">
                    {/* TOP: USER INFO & TYPE */}
                    <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500 font-black text-sm shadow-sm">
                                {item.nama.charAt(0)}
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-800 text-sm leading-tight">{item.nama}</h4>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className="text-[10px] font-medium text-slate-500 bg-slate-50 px-1.5 rounded border border-slate-100">{item.divisi}</span>
                                    <span className="text-[10px] text-slate-300">•</span>
                                    <span className="text-[10px] font-medium text-slate-400">{item.lokasi}</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col items-end gap-1.5">
                            <span className={`text-[10px] font-extrabold px-3 py-1 rounded-full border shadow-sm ${getTypeColor(item.tipe)}`}>
                                  {item.tipe.toUpperCase()}
                            </span>
                        </div>
                    </div>

                    {/* MIDDLE: INFO GRID */}
                    <div className="bg-slate-50 rounded-xl p-3 border border-slate-100 mb-4">
                        <div className="grid grid-cols-2 gap-y-3 gap-x-2">
                             {/* Tanggal */}
                             <div className="col-span-2 flex items-start gap-2">
                                <Calendar className="w-4 h-4 text-slate-400 mt-0.5"/>
                                <div>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Periode Pengajuan</p>
                                    <p className="text-sm font-bold text-slate-700">
                                        {item.tglMulai && item.tglMulai !== '-' ? `${formatDateIndo(item.tglMulai)} - ${formatDateIndo(item.tglSelesai)}` : formatDateIndo(item.waktu)}
                                    </p>
                                </div>
                             </div>

                             {/* Catatan / Alasan */}
                             <div className="col-span-2 border-t border-dashed border-slate-200 pt-2 mt-1">
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Keterangan / Alasan</p>
                                <p className="text-xs text-slate-600 italic leading-relaxed">"{item.catatan || '-'}"</p>
                             </div>
                        </div>
                    </div>

                    {/* ATTACHMENTS (Jika Ada) */}
                    <div className="flex gap-2 mb-4">
                        {item.foto && item.foto.length > 10 && item.foto !== 'Error Upload' && (
                            <a href={item.foto} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 bg-blue-50 text-blue-600 px-3 py-1.5 rounded-lg text-[10px] font-bold border border-blue-200 hover:bg-blue-100 transition shadow-sm no-underline">
                                <Camera className="w-3 h-3"/> Foto Bukti
                            </a>
                        )}
                        {item.lampiran && item.lampiran.length > 10 && item.lampiran !== '-' && (
                            <a href={item.lampiran} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 bg-amber-50 text-amber-600 px-3 py-1.5 rounded-lg text-[10px] font-bold border border-amber-200 hover:bg-amber-100 transition shadow-sm no-underline">
                                <FileIcon className="w-3 h-3"/> Dokumen
                            </a>
                        )}
                    </div>

                    {/* ACTION BUTTONS */}
                    <div className="grid grid-cols-2 gap-3">
                        <button
                            onClick={() => handleDecision(item.uuid, 'reject', item.nama)}
                            className="w-full py-2.5 rounded-xl border border-rose-200 text-rose-600 font-bold text-xs hover:bg-rose-50 active:scale-[0.98] transition flex items-center justify-center gap-2 group/btn"
                        >
                           <X className="w-4 h-4 group-hover/btn:scale-110 transition-transform"/> Tolak
                        </button>
                        <button
                            onClick={() => handleDecision(item.uuid, 'approve', item.nama)}
                            className="w-full py-2.5 rounded-xl bg-indigo-600 text-white font-bold text-xs hover:bg-indigo-700 shadow-md shadow-indigo-200 active:scale-[0.98] transition flex items-center justify-center gap-2 group/btn"
                        >
                           <CheckCircle className="w-4 h-4 group-hover/btn:scale-110 transition-transform"/> Setujui
                        </button>
                    </div>

                </div>
              </div>
            ))}
          </div>
        )}
      </div>
  );
}

// --- 5. HISTORY SCREEN (FIXED: ADDED APPROVE/REJECT BUTTONS IN REPORT) ---
function HistoryScreen({ user, setView, setEditItem, masterData }) {
  const [history, setHistory] = useState([]);
  const [historyPeriod, setHistoryPeriod] = useState(null);
  const [shiftReport, setShiftReport] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // MAIN FILTER STATE
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All'); 
  
  // REPORT MODAL STATE
  const [showWebReport, setShowWebReport] = useState(false);
  const isFirstReportModalRender = useRef(true);
  const [reportStatusFilter, setReportStatusFilter] = useState('All');
  const [reportCategory, setReportCategory] = useState('General'); 
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [reportFormFilter, setReportFormFilter] = useState('All');
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState('');
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  // Pilihan periode (Admin Panel > Periode absensi) & filter lokasi karyawan.
  const [reportPeriodeOpsi, setReportPeriodeOpsi] = useState([]);
  const [reportLokasiFilter, setReportLokasiFilter] = useState([]);
  const [bukaLokasiReport, setBukaLokasiReport] = useState(false);
  const refLokasiReport = useRef(null);

  // REPORT ADVANCED FILTERS
  const [reportUserIds, setReportUserIds] = useState([]);
  const [reportDivisiFilters, setReportDivisiFilters] = useState([]); 
  const [isReportFilterExpanded, setIsReportFilterExpanded] = useState(false); 
  const [isPosisiFilterExpanded, setIsPosisiFilterExpanded] = useState(false); 
  const [searchReportUser, setSearchReportUser] = useState('');
  
  // REPORT TABLE FILTERS
  const [reportColumnFilters, setReportColumnFilters] = useState({});
  const [reportSortConfig, setReportSortConfig] = useState({ key: null, direction: 'asc' });
  const [activeReportFilter, setActiveReportFilter] = useState(null);

  // Close dropdown logic
  useEffect(() => {
      const handleClickOutside = (event) => {
          if (activeReportFilter && !event.target.closest('.report-filter-container')) {
              setActiveReportFilter(null);
          }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeReportFilter]);

  const userRole = user.role ? String(user.role).toLowerCase() : '';
  const canViewAll = ['admin', 'hrd'].includes(userRole);
  const isSuperAdmin = userRole === 'admin' && (user.lokasi === 'All' || !user.lokasi);
  
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]); 
  
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [locationFilter, setLocationFilter] = useState('All');
  const [searchUser, setSearchUser] = useState('');
  const APPROVAL_TYPES = ['Cuti', 'Sakit', 'Lembur', 'Tukar Shift', 'Off', 'Cuti EO'];

  // --- EFFECT & FETCH DATA ---
  useEffect(() => {
      if (showWebReport) {
          if(filterStart) setReportStartDate(filterStart);
          if(filterEnd) setReportEndDate(filterEnd);
          setIsReportLoading(true);
          if (canViewAll && allUsers.length === 0) fetchUsers();
          setTimeout(() => setIsReportLoading(false), 800);
          setReportColumnFilters({});
          setReportSortConfig({ key: null, direction: 'asc' });
      } else if (isFirstReportModalRender.current) {
          isFirstReportModalRender.current = false;
      } else {
          // Modal Laporan ditutup: `history` mungkin masih berisi data untuk
          // Periode yang dipilih di modal (bisa beda dari filterStart/filterEnd
          // layar Riwayat). Ambil ulang sesuai filter Riwayat supaya layar di
          // baliknya tidak ikut menampilkan rentang tanggal milik Laporan.
          fetchHistory();
      }
  }, [showWebReport]);

  useEffect(() => {
      if (!showWebReport || reportPeriodeOpsi.length) return;
      fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'get_absence_period' }) })
        .then(r => r.json())
        .then(d => {
          if (d.result !== 'success') return;
          const daftar = Array.isArray(d.periods) ? d.periods : (d.period ? [{ ...d.period, aktif: true }] : []);
          setReportPeriodeOpsi(daftar.filter(p => p && p.mulai && p.selesai)
            .sort((a, b) => String(b.mulai).localeCompare(String(a.mulai))));
        })
        .catch(() => {});
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWebReport]);
  useEffect(() => {
      if (!bukaLokasiReport) return undefined;
      const tutup = (e) => { if (refLokasiReport.current && !refLokasiReport.current.contains(e.target)) setBukaLokasiReport(false); };
      document.addEventListener('mousedown', tutup);
      return () => document.removeEventListener('mousedown', tutup);
  }, [bukaLokasiReport]);

  const fetchUsers = async () => {
    try {
        const res = await fetchApi(SCRIPT_URL, { 
            method: 'POST', 
            body: JSON.stringify({ action: 'get_user_list_simple', lokasi: user.lokasi || 'All', filterLokasi: locationFilter }) 
        });
        const data = await res.json();
        if(data.result === 'success') { setAllUsers(data.list); setSelectedUserIds([]); }
    } catch(e) { console.error("Gagal load users"); }
  }

  const fetchHistory = async (overrideStart, overrideEnd) => {
    setLoading(true);
    try {
      const payload = {
        action: 'get_history',
        userId: user.id,
        canViewAll: canViewAll,
        requestorLokasi: isSuperAdmin ? locationFilter : (user.lokasi || 'All'),
        targetUserIds: canViewAll ? selectedUserIds : [],
        // Laporan (Periode di modal report) mengirim tanggalnya sendiri lewat
        // overrideStart/overrideEnd, supaya server benar-benar mengambil data
        // periode itu -- bukan cuma menyaring "history" yang sudah kepalang
        // dibatasi ke periode aktif default (filterStart/filterEnd Riwayat).
        filterStart: overrideStart !== undefined ? overrideStart : filterStart,
        filterEnd: overrideEnd !== undefined ? overrideEnd : filterEnd
      };
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify(payload) });
      const data = await res.json();
      if (data.result === 'success') {
        setHistory(data.history || []);
        setHistoryPeriod(data.period || null);
      }
    } catch (e) { alert('Gagal ambil data history'); } finally { setLoading(false); }
  };

  const fetchShiftReport = async () => {
    if (reportCategory !== 'RunningShift') return;
    setIsReportLoading(true);
    try {
       const res = await fetchApi(SCRIPT_URL, {
          method: 'POST',
          body: JSON.stringify({ action: 'get_shift_history', userId: user.id, role: user.role })
       });
       const data = await res.json();
       if (data.result === 'success') { setShiftReport(data.data); }
    } catch (e) { console.error("Gagal load shift report"); } 
    finally { setIsReportLoading(false); }
  };

  useEffect(() => { if(canViewAll) fetchUsers(); }, [locationFilter]);
  useEffect(() => { fetchHistory(); }, [selectedUserIds, filterStart, filterEnd]);
  useEffect(() => {
      if (showWebReport && reportCategory === 'RunningShift') {
          fetchShiftReport();
      } else if (showWebReport) {
          setIsReportLoading(true);
          setTimeout(() => setIsReportLoading(false), 500);
      }
      setReportColumnFilters({});
  }, [showWebReport, reportCategory]);

  // Laporan ("General"/"Tally") memakai data yang sama (`history`) dengan
  // layar Riwayat, tapi punya date-picker "Periode" sendiri di dalam modal.
  // Tanpa efek ini, mengganti Periode di modal cuma menyaring ulang `history`
  // yang sudah kepalang dibatasi server ke periode aktif (lihat fetchHistory)
  // -- kalau tanggal yang dipilih di luar periode aktif, hasilnya selalu
  // kosong walau datanya ada di sheet. Refetch ke server dengan tanggal yang
  // benar-benar dipilih di sini.
  useEffect(() => {
      if (!showWebReport) return;
      if (reportCategory !== 'General' && reportCategory !== 'Tally') return;
      fetchHistory(reportStartDate, reportEndDate);
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWebReport, reportCategory, reportStartDate, reportEndDate]);

  // --- HELPER FORMAT ---
  const formatDateIndo = (d) => { if (!d || d === '-') return '-'; try { return new Date(d).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'}); } catch (e) { return d; } };
  const formatDateShort = (d) => { if (!d || d === '-') return '-'; try { return new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric'}); } catch (e) { return d; } };
  const formatTimeOnly = (val) => { if (!val || val === '-') return '-'; if (typeof val === 'string' && (val.includes('T') || val.length > 8)) { try { const d = new Date(val); if (!isNaN(d.getTime())) { return d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', hour12: false }).replace('.', ':'); } } catch (e) { return val.substring(0, 5); } } return val.length >= 5 ? val.substring(0, 5) : val; };
  const formatDateTimeFull = (val) => { if (!val || val === '-') return '-'; try { const d = new Date(val); if(isNaN(d.getTime())) return val; const dd = String(d.getDate()).padStart(2, '0'); const mm = String(d.getMonth() + 1).padStart(2, '0'); const yy = String(d.getFullYear()).slice(-2); const hh = String(d.getHours()).padStart(2, '0'); const min = String(d.getMinutes()).padStart(2, '0'); return `${dd}-${mm}-${yy} ${hh}:${min}`; } catch(e) { return val; } };
  const getDurasiHari = (start, end) => { if (!start || start === '-' || !end || end === '-') return '-'; try { const d1 = new Date(start); const d2 = new Date(end); d1.setHours(0,0,0,0); d2.setHours(0,0,0,0); const diffTime = Math.abs(d2 - d1); const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; return `${diffDays} Hari`; } catch (e) { return '-'; } };
  
  const formatDateDDMMYYYY = (d) => { try { const dateObj = new Date(d); if (isNaN(dateObj.getTime())) return d; const day = String(dateObj.getDate()).padStart(2, '0'); const month = String(dateObj.getMonth() + 1).padStart(2, '0'); const year = dateObj.getFullYear(); return `${day}-${month}-${year}`; } catch (e) { return '-'; } };

  const uniqueForms = ['All', ...new Set(history.map(item => item.tipe))];

  // --- HANDLE APPROVAL ACTION (Laporan & Cetak Data, admin/hrd) ---
  // mode: 'approve' | 'reject' (dari Pending) | 'cancel' (Approved -> Rejected)
  const [statusEdit, setStatusEdit] = useState(null); // { item, mode }
  const [statusEditKet, setStatusEditKet] = useState('');
  const [statusEditSaving, setStatusEditSaving] = useState(false);

  const openStatusEdit = (item, mode) => { setStatusEdit({ item, mode }); setStatusEditKet(''); };
  const closeStatusEdit = () => { if (!statusEditSaving) { setStatusEdit(null); setStatusEditKet(''); } };

  const submitStatusEdit = async () => {
    if (!statusEdit) return;
    const { item, mode } = statusEdit;
    const newStatus = mode === 'approve' ? 'Approved' : 'Rejected';
    const ket = statusEditKet.trim();
    if (mode !== 'approve' && ket.length < 3) {
        alert(mode === 'cancel' ? 'Keterangan pembatalan wajib diisi (min. 3 karakter).' : 'Alasan penolakan wajib diisi (min. 3 karakter).');
        return;
    }
    setStatusEditSaving(true);
    try {
        const res = await fetchApi(SCRIPT_URL, {
            method: 'POST',
            body: JSON.stringify({
                action: 'update_status_absen',
                uuid: item.uuid,
                status: newStatus,
                approverName: user.nama,
                keterangan: ket
            })
        });
        const data = await res.json();
        if (data.result === 'success') {
            alert(data.message || `Berhasil di-${newStatus}`);
            setStatusEdit(null);
            setStatusEditKet('');
            setIsReportLoading(true);
            try { await fetchHistory(reportStartDate, reportEndDate); } finally { setIsReportLoading(false); }
        } else {
            alert("Gagal update: " + data.message);
        }
    } catch (e) {
        console.error(e);
        alert("Terjadi kesalahan koneksi.");
    } finally {
        setStatusEditSaving(false);
    }
  };

  // --- HAPUS PENGAJUAN (Laporan & Cetak Data, khusus admin) ---
  // Untuk membersihkan pengajuan dobel (tanggal sama). Approved/Verified
  // ditolak server — batalkan dulu lewat Edit.
  const [deletingUuid, setDeletingUuid] = useState(null);
  const handleDeletePengajuan = async (item) => {
    if (!item || !item.uuid || deletingUuid) return;
    const ok = window.confirm(
      `Hapus pengajuan ini secara permanen?\n\n` +
      `Nama    : ${item.nama}\n` +
      `Form    : ${item.tipe}\n` +
      `Periode : ${item.col_periode}\n` +
      `Input   : ${item.col_date}\n` +
      `Status  : ${item.status}\n\n` +
      `Data yang dihapus tidak bisa dikembalikan.`
    );
    if (!ok) return;
    setDeletingUuid(item.uuid);
    try {
      const res = await fetchApi(SCRIPT_URL, {
        method: 'POST',
        body: JSON.stringify({ action: 'delete_absensi', uuid: item.uuid, approverName: user.nama })
      });
      const data = await res.json();
      if (data.result === 'success') {
        alert(data.message || 'Pengajuan dihapus.');
        setIsReportLoading(true);
        try { await fetchHistory(reportStartDate, reportEndDate); } finally { setIsReportLoading(false); }
      } else {
        alert('Gagal hapus: ' + data.message);
      }
    } catch (e) {
      console.error(e);
      alert('Terjadi kesalahan koneksi.');
    } finally {
      setDeletingUuid(null);
    }
  };

  // --- KIRIM ULANG EMAIL APPROVAL (Laporan & Cetak Data, Pending saja) ---
  const [resendingUuid, setResendingUuid] = useState(null);
  const handleResendPengajuan = async (item) => {
    if (!item || !item.uuid || resendingUuid) return;
    if (!window.confirm(
      `Kirim ulang email approval ke atasan?\n\n` +
      `Nama    : ${item.nama}\n` +
      `Form    : ${item.tipe}\n` +
      `Periode : ${item.col_periode}`
    )) return;
    setResendingUuid(item.uuid);
    try {
      const res = await fetchApi(SCRIPT_URL, {
        method: 'POST',
        body: JSON.stringify({ action: 'request_approval_email', uuid: item.uuid, scriptUrl: SCRIPT_URL })
      });
      const data = await res.json();
      if (data.result === 'success') alert('Terkirim! ' + data.message);
      else alert('Gagal kirim ulang: ' + data.message);
    } catch (e) {
      console.error(e);
      alert('Terjadi kesalahan koneksi.');
    } finally {
      setResendingUuid(null);
    }
  };

  // Tombol ikon kolom ACTION laporan: selalu tampil; aktif = berwarna &
  // bisa diklik, nonaktif = abu-abu & terkunci (alasan di tooltip).
  const ACT_TONE = {
    green: 'bg-emerald-100 hover:bg-emerald-200 text-emerald-700 border-emerald-300',
    red: 'bg-red-100 hover:bg-red-200 text-red-700 border-red-300',
    amber: 'bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-300',
    blue: 'bg-blue-50 hover:bg-blue-100 text-blue-600 border-blue-300',
    trash: 'bg-white hover:bg-red-50 text-red-600 border-red-300'
  };
  const renderActBtn = ({ key, enabled, onClick, Icon, tone, title, offTitle, loading }) => (
    <button
      key={key}
      type="button"
      onClick={enabled && !loading ? onClick : undefined}
      disabled={!enabled || loading}
      aria-disabled={!enabled}
      className={`w-6 h-6 shrink-0 flex items-center justify-center rounded-full border transition-colors ${
        enabled ? ACT_TONE[tone] : 'bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed'
      } ${loading ? 'opacity-60' : ''}`}
      title={enabled ? title : (offTitle || title)}
    >
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
    </button>
  );

  // --- REPORT PROCESSING LOGIC ---
  const processReportData = () => {
      try {
          let finalData = [];
          let availableUsers = [];
          let availableDivisions = [];
          let availableLokasi = [];
          const start = reportStartDate ? new Date(reportStartDate).setHours(0, 0, 0, 0) : null;
          const end = reportEndDate ? new Date(reportEndDate).setHours(23, 59, 59, 999) : null;

          const getAcaraDateSource = (item) => {
              if (item && item.tglMulai && item.tglMulai !== '-' && !isNaN(new Date(item.tglMulai).getTime())) {
                  return String(item.tglMulai);
              }
              return String(item.waktu);
          };
          
          if (reportCategory === 'Tally') {
              const tallyTypes = ['Hadir', 'Pulang', 'Standby', 'Off'];
              const filteredRaw = history.filter(item => {
                 const itemDate = new Date(getAcaraDateSource(item)).setHours(0,0,0,0);
                 const matchDate = (!start && !end) || (itemDate >= start && itemDate <= end);
                 return matchDate && tallyTypes.includes(item.tipe);
              });
              const groupedMap = {};
              filteredRaw.forEach(item => {
                  const acaraDateSrc = getAcaraDateSource(item);
                  const dateKey = new Date(acaraDateSrc).toLocaleDateString('en-CA'); 
                  const groupKey = `${item.userId}_${dateKey}`;
                  if (!groupedMap[groupKey]) {
                      groupedMap[groupKey] = {
                          id: item.userId, 
                          idAkun: item.idAkun || '-', 
                          nama: item.nama, 
                          lokasiKaryawan: item.lokasiKaryawan, 
                          noPayroll: item.noPayroll, 
                          divisi: item.divisi || '-',
                          dateObj: new Date(acaraDateSrc), tanggal: acaraDateSrc, foto: item.foto || '-', 
                          catatanList: item.catatan ? [item.catatan] : [], masuk: '-', pulang: '-', standby: '-'
                      };
                  } else {
                      if (item.catatan) groupedMap[groupKey].catatanList.push(item.catatan);
                      if ((!groupedMap[groupKey].foto || groupedMap[groupKey].foto === '-') && item.foto) groupedMap[groupKey].foto = item.foto;
                  }
                  const timeStr = formatTimeOnly(item.waktu); 
                  if (item.tipe === 'Hadir') groupedMap[groupKey].masuk = timeStr;
                  else if (item.tipe === 'Pulang') groupedMap[groupKey].pulang = timeStr;
                  else if (item.tipe === 'Standby' || item.tipe === 'Off') groupedMap[groupKey].standby = timeStr; 
              });
              finalData = Object.values(groupedMap).map(g => ({ 
                  ...g, 
                  catatan: g.catatanList.join('; '), 
                  col_date: formatDateDDMMYYYY(g.tanggal),
                  col_userId: g.idAkun, 
                  col_payroll: g.noPayroll,
                  
              }));
          } else {
              let sourceData = (reportCategory === 'RunningShift') ? shiftReport : history;
              finalData = sourceData.filter(item => {
                  let dateRef;
                  if (reportCategory === 'RunningShift') {
                      dateRef = item.tanggal;
                  } else {
                      dateRef = getAcaraDateSource(item);
                  }
                  const itemDate = new Date(dateRef).setHours(0, 0, 0, 0);
                  const inRange = (t) => !isNaN(t) && (!start || t >= start) && (!end || t <= end);
                  // Laporan Absensi: tampil juga bila TANGGAL INPUT ada di periode,
                  // supaya pengajuan untuk tanggal mendatang (cuti 24 Okt diajukan
                  // 19 Okt) sudah muncul di tanggal pengajuannya.
                  const inputDate = reportCategory === 'RunningShift' ? NaN : new Date(item.waktu).setHours(0, 0, 0, 0);
                  const matchDate = (!start && !end) || inRange(itemDate) || inRange(inputDate);
                  let matchStatus = true; let matchForm = true;
                  if (reportCategory === 'General') {
                     matchStatus = (reportStatusFilter === 'All' || item.status === reportStatusFilter);
                     matchForm = (reportFormFilter === 'All' || item.tipe === reportFormFilter);
                  }
                  return matchDate && matchStatus && matchForm;
              });

              finalData = finalData.map(item => {
                  if (reportCategory === 'RunningShift') {
                      const matchedUser = allUsers.find(u => u.nama === item.nama);
                      const realUsername = matchedUser ? matchedUser.id : (item.userId || '-');
                      return { 
                          ...item, 
                          col_date: formatDateDDMMYYYY(item.tanggal), 
                          col_time: formatDateTimeFull(item.waktuInput),
                          col_userId: realUsername,
                          col_payroll: item.idAkun || '-'
                      };
                  } else { 
                      const idAkunFromSheet = item.idAkun || item.userId || '-'; 
                      const pRoll = item.noPayroll || '-';
                      let periode = item.tglMulai && item.tglMulai !== '-' ? `${formatDateShort(item.tglMulai)} - ${formatDateShort(item.tglSelesai)}` : (item.jamMulai && item.jamMulai !== '-' ? `${formatTimeOnly(item.jamMulai)} - ${formatTimeOnly(item.jamSelesai)}` : '-');
                      let durasi = getDurasiHari(item.tglMulai, item.tglSelesai);
                      return { 
                          ...item, 
                          col_date: formatDateTimeFull(item.waktu),
                          col_periode: periode,
                          col_durasi: durasi,
                          col_approval: item.approvalTime && item.approvalTime !== '-' ? formatDateTimeFull(item.approvalTime) : '-',
                          col_keterangan: item.alasan && String(item.alasan).trim() && item.alasan !== '-' ? String(item.alasan) : '-',
                          col_userId: idAkunFromSheet, 
                          col_payroll: pRoll 
                      };
                  }
              });
          }

          availableUsers = finalData.reduce((acc, current) => {
              const uid = String(current.id || current.userId); 
              if (!acc.find(u => u.id === uid)) acc.push({ id: uid, nama: current.nama });
              return acc;
          }, []).sort((a, b) => a.nama.localeCompare(b.nama));
          availableDivisions = [...new Set(finalData.map(item => item.divisi || '-'))].sort();

          if (reportUserIds.length > 0) finalData = finalData.filter(item => reportUserIds.includes(String(item.id || item.userId)));
          if (reportDivisiFilters.length > 0) finalData = finalData.filter(item => reportDivisiFilters.includes(item.divisi || '-'));
          // Daftar lokasi dihitung SEBELUM filter lokasi supaya pilihan tidak hilang.
          const hitungLokasi = {};
          finalData.forEach(item => { const l = item.lokasiKaryawan || '(kosong)'; hitungLokasi[l] = (hitungLokasi[l] || 0) + 1; });
          availableLokasi = Object.keys(hitungLokasi).sort((a, b) => hitungLokasi[b] - hitungLokasi[a]).map(l => ({ lokasi: l, jumlah: hitungLokasi[l] }));
          if (reportLokasiFilter.length > 0) finalData = finalData.filter(item => reportLokasiFilter.includes(item.lokasiKaryawan || '(kosong)'));

          return { finalData, availableUsers, availableDivisions, availableLokasi };
      } catch (err) {
          console.error("Error processing report:", err);
          return { finalData: [], availableUsers: [], availableDivisions: [], availableLokasi: [] };
      }
  };
  const { finalData: currentReportData, availableUsers, availableDivisions, availableLokasi } = processReportData();
  // Ringkasan status pada periode & filter yang sedang dipilih (Laporan Absensi).
  const ringkasanStatus = (() => {
      const h = {};
      currentReportData.forEach(it => { const st = it.status || 'Pending'; h[st] = (h[st] || 0) + 1; });
      return h;
  })();
  const periodeTerpilih = reportPeriodeOpsi.find(p => p.mulai === reportStartDate && p.selesai === reportEndDate);

  // Kunci pengajuan dobel: karyawan + form + periode yang sama.
  const reportDuplicateKeys = (() => {
      const hitung = {};
      if (reportCategory !== 'General') return hitung;
      currentReportData.forEach(it => {
          if (!it.col_periode || it.col_periode === '-') return;
          const k = `${it.col_userId}|${it.tipe}|${it.col_periode}`;
          hitung[k] = (hitung[k] || 0) + 1;
      });
      return hitung;
  })();
  const isReportDuplicate = (it) => it && it.col_periode && it.col_periode !== '-' && (reportDuplicateKeys[`${it.col_userId}|${it.tipe}|${it.col_periode}`] || 0) > 1;

  // Label aksi per baris untuk filter kolom ACTION (bisa lebih dari satu).
  const ACTION_LABEL = {
    approve: 'Perlu Approval',
    edit: 'Bisa Edit / Batal',
    hapus: 'Bisa Dihapus',
    dup: 'Duplikat',
    none: 'Tidak Ada Aksi'
  };
  const reportRows = reportCategory !== 'General' ? currentReportData : currentReportData.map(it => {
      const isPending = it.status === 'Pending';
      const isFinal = it.status === 'Approved' || it.status === 'Verified';
      const canCancel = it.status === 'Approved' || (it.status === 'Verified' && TIPE_DL_ONLINE.includes(it.tipe));
      const canDelete = userRole === 'admin' && !isFinal;
      const labels = [];
      if (isPending) labels.push(ACTION_LABEL.approve);
      if (canCancel) labels.push(ACTION_LABEL.edit);
      if (canDelete) labels.push(ACTION_LABEL.hapus);
      if (isReportDuplicate(it)) labels.push(ACTION_LABEL.dup);
      if (labels.length === 0) labels.push(ACTION_LABEL.none);
      // Jumlah ikon yang menyala (✓ ✗ ✏️ ✉️ 🗑️) — dipakai urutan default.
      const activeCount = (isPending ? 3 : 0) + (canCancel ? 1 : 0) + (canDelete ? 1 : 0);
      return { ...it, col_action: labels, _actCount: activeCount };
  });

  // --- FILTERING & SORTING ---
  const getReportUniqueValues = (field) => {
      const values = reportRows.flatMap(item => Array.isArray(item[field]) ? item[field] : [item[field]]).filter(v => v !== null && v !== undefined && v !== '');
      return [...new Set(values)].sort();
  };
  const toggleReportFilterValue = (field, value) => {
      setReportColumnFilters(prev => {
          const currentValues = prev[field] || [];
          if (currentValues.includes(value)) return { ...prev, [field]: currentValues.filter(v => v !== value) };
          else return { ...prev, [field]: [...currentValues, value] };
      });
  };
  const toggleReportSelectAll = (field, visibleOptions) => {
      setReportColumnFilters(prev => {
          const currentValues = prev[field] || [];
          const allVisibleSelected = visibleOptions.every(val => currentValues.includes(val));
          if (allVisibleSelected) return { ...prev, [field]: currentValues.filter(v => !visibleOptions.includes(v)) };
          else {
              const newValues = [...currentValues];
              visibleOptions.forEach(v => { if (!newValues.includes(v)) newValues.push(v); });
              return { ...prev, [field]: newValues };
          }
      });
  };
  const requestReportSort = (key, direction) => { setReportSortConfig({ key, direction }); };
  const filteredReportTable = reportRows.filter(item => {
      return Object.keys(reportColumnFilters).every(key => {
          const selectedValues = reportColumnFilters[key];
          if (!selectedValues || selectedValues.length === 0) return true;
          if (Array.isArray(item[key])) return item[key].some(v => selectedValues.includes(String(v)));
          return selectedValues.includes(String(item[key]));
      });
  });
  const sortedReportTable = React.useMemo(() => {
      let sortableItems = [...filteredReportTable];
      // Laporan Absensi: status Verified SELALU di paling bawah. Tanpa sort
      // kolom pilihan user, sisanya diurutkan dari ikon ACTION yang paling
      // banyak menyala (Pending dulu, lalu yang bisa Edit/Hapus, lalu yang
      // tanpa aksi). Sort bawaan JS stabil, jadi urutan asli (terbaru di
      // atas) tetap terjaga di dalam kelompok yang sama.
      const isGeneral = reportCategory === 'General';
      const verifiedRank = (it) => (isGeneral && it.status === 'Verified') ? 1 : 0;
      sortableItems.sort((a, b) => {
          const v = verifiedRank(a) - verifiedRank(b);
          if (v !== 0) return v;
          if (reportSortConfig.key !== null) {
              let valA = a[reportSortConfig.key] || '';
              let valB = b[reportSortConfig.key] || '';
              return reportSortConfig.direction === 'asc' ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
          }
          if (isGeneral) return (b._actCount || 0) - (a._actCount || 0);
          return 0;
      });
      return sortableItems;
  }, [filteredReportTable, reportSortConfig, reportCategory]);

  // --- ACTIONS HANDLERS ---
  const toggleReportUserSelection = (id) => { if(reportUserIds.includes(id)) setReportUserIds(reportUserIds.filter(x => x !== id)); else setReportUserIds([...reportUserIds, id]); };
  const toggleReportDivisiSelection = (div) => { if(reportDivisiFilters.includes(div)) setReportDivisiFilters(reportDivisiFilters.filter(x => x !== div)); else setReportDivisiFilters([...reportDivisiFilters, div]); };
  const selectAllReportUsers = () => { const visibleIds = availableUsers.filter(u => u.nama.toLowerCase().includes(searchReportUser.toLowerCase())).map(u => u.id); if(visibleIds.every(id => reportUserIds.includes(id))) { setReportUserIds(reportUserIds.filter(id => !visibleIds.includes(id))); } else { setReportUserIds([...new Set([...reportUserIds, ...visibleIds])]); } };
  const selectAllDivisions = () => { if(availableDivisions.every(d => reportDivisiFilters.includes(d))) { setReportDivisiFilters([]); } else { setReportDivisiFilters(availableDivisions); } };

  // --- EXPORT FUNCTIONS ---
  const generateExcel = () => {
    let tableHead = [], tableBody = [];
    const dataToExport = sortedReportTable; 
    if (reportCategory === 'RunningShift') {
        tableHead = ["No", "UserID", "PAYROLL", "Nama", "Posisi", "Tanggal Shift", "Jam Kerja", "Waktu Input"];
        tableBody = dataToExport.map((item, index) => [ index + 1, item.col_userId, item.col_payroll, item.nama, item.divisi || '-', item.col_date, item.shiftLabel, item.col_time ]);
    } else if (reportCategory === 'Tally') {
        tableHead = ["No", "UserID", "PAYROLL", "Nama", "Tanggal", "Posisi", "Masuk", "Pulang", "Standby", "Foto URL", "Catatan"];
        tableBody = dataToExport.map((item, index) => [ index + 1, item.col_userId, item.col_payroll, item.nama, item.col_date, item.divisi || '-', item.masuk, item.pulang, item.standby, item.foto || '-', item.catatan || '-' ]);
    } else {
        tableHead = ["No", "UserID", "PAYROLL", "Nama", "Form", "Waktu Input", "Periode", "Durasi", "Catatan", "Status", "Approval", "Keterangan"];
        tableBody = dataToExport.map((item, index) => [ index + 1, item.col_userId, item.col_payroll, item.nama, item.tipe, item.col_date, item.col_periode, item.col_durasi, item.catatan || '-', item.status, item.col_approval, item.col_keterangan || '-' ]);
    }
    const worksheet = XLSX.utils.aoa_to_sheet([tableHead, ...tableBody]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Laporan");
    XLSX.writeFile(workbook, `Laporan_${reportCategory}_${reportStartDate || 'All'}.xlsx`);
  };

  const generatePDF = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const marginLeft = 10; const marginTop = 10;
    doc.setFontSize(10); doc.setFont("helvetica", "bold");
    let judul = "LAPORAN DATA ABSENSI";
    if (reportCategory === 'RunningShift') judul = "LAPORAN RUNNING SHIFT (JADWAL)";
    if (reportCategory === 'Tally') judul = "LAPORAN ABSEN ONLINE TALLY";
    doc.text(judul, marginLeft, marginTop);
    doc.setFontSize(8); doc.setFont("helvetica", "normal");
    const infoFilter = `Periode: ${reportStartDate || 'Awal'} s/d ${reportEndDate || 'Akhir'} | Total: ${sortedReportTable.length}`;
    doc.text(infoFilter, marginLeft, marginTop + 4);
    let tableColumn = [], tableRows = [];
    
    if (reportCategory === 'RunningShift') {
        tableColumn = ["No", "UserID", "PAYROLL", "Nama", "Posisi", "Tanggal Shift", "Jam Kerja", "Waktu Input"];
        sortedReportTable.forEach((item, index) => { tableRows.push([index + 1, item.col_userId, item.col_payroll, item.nama, item.divisi || '-', item.col_date, item.shiftLabel, item.col_time]); });
    } else if (reportCategory === 'Tally') {
        tableColumn = ["No", "UserID", "PAYROLL", "Nama", "Tanggal", "Posisi", "Masuk", "Pulang", "Standby", "Foto URL", "Catatan"];
        sortedReportTable.forEach((item, index) => { tableRows.push([ index + 1, item.col_userId, item.col_payroll, item.nama, item.col_date, item.divisi, item.masuk, item.pulang, item.standby, item.foto || '-', item.catatan || '-' ]); });
    } else {
        tableColumn = ["No", "UserID", "PAYROLL", "Nama", "Form", "Waktu Input", "Periode", "Durasi", "Catatan", "Status", "Approval", "Keterangan"];
        sortedReportTable.forEach((item, index) => { tableRows.push([index + 1, item.col_userId, item.col_payroll, item.nama, item.tipe, item.col_date, item.col_periode, item.col_durasi, item.catatan || '-', item.status, item.col_approval, item.col_keterangan || '-']); });
    }
    autoTable(doc, { head: [tableColumn], body: tableRows, startY: 18, theme: 'grid', styles: { fontSize: 6, cellPadding: 1, valign: 'middle' }, headStyles: { fillColor: [50, 50, 50] } });
    doc.save(`Laporan_${reportCategory}_${reportStartDate}.pdf`);
  };

  // --- ACTIONS --- (Non-Report)
  const handleRequestApproval = async (item) => {
    const detailTanggal = item.tglMulai && item.tglMulai !== '-' ? `${formatDateIndo(item.tglMulai)} s/d ${formatDateIndo(item.tglSelesai)}` : formatDateIndo(item.waktu);
    if (!window.confirm(`Kirim ulang email approval untuk ${item.tipe} (${detailTanggal})?`)) return;
    setSendingEmail(true);
    try {
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'request_approval_email', uuid: item.uuid, scriptUrl: SCRIPT_URL }) });
      const data = await res.json(); 
      if (data.result === 'success') alert("Sukses! " + data.message); else alert("Gagal: " + data.message);
    } catch (e) { alert("Gagal kirim email: " + e.message); } finally { setSendingEmail(false); }
  };
  const handleDelete = async (uuid) => { if (!window.confirm('Yakin hapus data ini?')) return;
    try { const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'delete_absen', uuid }) });
    const data = await res.json(); if (data.result === 'success') { alert('Berhasil dihapus'); fetchHistory(); } else { alert(data.message); } } catch (e) { alert('Gagal hapus'); } };
  const handleEdit = (item) => { setEditItem(item); localStorage.setItem('absenType', item.tipe); setView('form'); };
  const isEditable = (waktuStr, status) => { if (status === 'Approved' || status === 'Rejected') return false;
    if (!waktuStr || waktuStr === '-') return false; try { return (new Date().getTime() - new Date(waktuStr).getTime()) / (1000 * 60 * 60) <= 1; } catch (e) { return false; } };
  
  const getFilteredHistory = () => {
    return history.filter(item => {
      const itemStart = new Date(item.tglMulai && item.tglMulai !== '-' ? item.tglMulai : item.waktu).setHours(0, 0, 0, 0);
      const itemEnd = new Date(item.tglSelesai && item.tglSelesai !== '-' ? item.tglSelesai : item.waktu).setHours(23, 59, 59, 999);
      const start = filterStart ? new Date(filterStart).setHours(0, 0, 0, 0) : null;
      const end = filterEnd ? new Date(filterEnd).setHours(23, 59, 59, 999) : null;
      const itemInput = new Date(item.waktu).setHours(0, 0, 0, 0);
      // Tampil bila tanggal acara beririsan dengan filter ATAU tanggal input
      // ada di filter (pengajuan untuk tanggal mendatang tetap terlihat).
      const matchesDate = (!start && !end)
        || (!isNaN(itemStart) && !isNaN(itemEnd) && itemEnd >= (start || -Infinity) && itemStart <= (end || Infinity))
        || (!isNaN(itemInput) && itemInput >= (start || -Infinity) && itemInput <= (end || Infinity));
      return matchesDate && (filterType === 'All' || item.tipe === filterType) && (filterStatus === 'All' || item.status === filterStatus);
    });
  };
  
  const getStatusColor = (status) => { if (status === 'Approved' || status === 'Verified') return 'bg-emerald-100 text-emerald-700 border-emerald-200'; if (status === 'Rejected') return 'bg-rose-100 text-rose-700 border-rose-200'; return 'bg-amber-100 text-amber-700 border-amber-200'; };
  const toggleUserSelection = (id) => { if(selectedUserIds.includes(id)) { setSelectedUserIds(selectedUserIds.filter(x => x !== id)); } else { setSelectedUserIds([...selectedUserIds, id]); } };
  const selectAllUsers = () => { const visibleIds = allUsers.filter(u => u.nama.toLowerCase().includes(searchUser.toLowerCase())).map(u => u.id); if(visibleIds.every(id => selectedUserIds.includes(id))) { setSelectedUserIds(selectedUserIds.filter(id => !visibleIds.includes(id))); } else { setSelectedUserIds([...new Set([...selectedUserIds, ...visibleIds])]); } };
  
  const uniqueTypes = ['All', ...new Set(history.map(item => item.tipe))];
  const displayData = getFilteredHistory().filter(item => {
      if (canViewAll) {
          const HIDDEN_TYPES = ['Hadir', 'Pulang', 'Standby', 'Off', 'Tukar Shift'];
          if (HIDDEN_TYPES.includes(item.tipe)) return false;
      }
      return true;
  });

  // --- HEADER COMPONENT ---
  const ReportFilterHeader = ({ label, field, width }) => {
      const uniqueOptions = getReportUniqueValues(field);
      const selectedValues = reportColumnFilters[field] || [];
      const isOpen = activeReportFilter === field;
      const [searchTerm, setSearchTerm] = useState('');
      const visibleOptions = uniqueOptions.filter(opt => String(opt).toLowerCase().includes(searchTerm.toLowerCase()));

      return (
          <th className={`p-0 border border-gray-300 bg-gray-100 align-top ${width || 'w-auto'} relative`}>
              <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between p-2 pb-1">
                      <span className="text-[10px] font-bold text-gray-700 uppercase">{label}</span>
                      {reportSortConfig.key === field && (
                          <span className="text-[9px] text-gray-800 font-bold ml-1">{reportSortConfig.direction === 'asc' ? '↓' : '↑'}</span>
                      )}
                  </div>
                  <div className="px-2 pb-2 report-filter-container">
                    <button onClick={(e) => { e.stopPropagation(); setActiveReportFilter(isOpen ? null : field); }} 
                        className={`flex items-center justify-between w-full text-[9px] px-2 py-1 border rounded bg-white shadow-sm transition-all ${selectedValues.length > 0 ? 'text-blue-700 border-blue-400 bg-blue-50' : 'text-gray-600 border-gray-300 hover:border-gray-400'}`}>
                        <span className="truncate font-medium">{selectedValues.length === 0 ? "(All)" : `${selectedValues.length} Selected`}</span>
                        <Filter className="w-2.5 h-2.5 ml-1 opacity-70" />
                    </button>
                    {isOpen && (
                        <div className="absolute top-[95%] left-0 mt-0 w-48 bg-white border border-gray-400 shadow-xl z-[100] flex flex-col max-h-64 rounded-sm">
                            <div className="p-1.5 border-b bg-gray-50 flex gap-1">
                                <button onClick={() => requestReportSort(field, 'asc')} className="flex-1 text-[9px] font-bold bg-white border rounded p-1 hover:bg-gray-200">A-Z</button>
                                <button onClick={() => requestReportSort(field, 'desc')} className="flex-1 text-[9px] font-bold bg-white border rounded p-1 hover:bg-gray-200">Z-A</button>
                            </div>
                            <div className="p-1.5 border-b relative">
                                <input type="text" placeholder="Cari..." className="w-full text-[9px] border p-1 rounded bg-white outline-none" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} autoFocus />
                            </div>
                            <div className="px-2 py-1.5 bg-gray-50 flex items-center gap-2 border-b hover:bg-gray-100 cursor-pointer" onClick={() => toggleReportSelectAll(field, visibleOptions)}>
                                <input type="checkbox" readOnly checked={visibleOptions.length > 0 && visibleOptions.every(v => selectedValues.includes(v))} className="w-3 h-3 text-blue-600 rounded border-gray-300"/>
                                <span className="text-[9px] font-bold text-gray-700">Select All</span>
                            </div>
                            <div className="overflow-y-auto flex-1 p-1 custom-scrollbar">
                                {visibleOptions.map((val, idx) => (
                                    <label key={idx} className="flex items-center gap-2 px-1.5 py-1 hover:bg-blue-50 cursor-pointer rounded transition-colors">
                                        <input type="checkbox" className="w-3 h-3 text-blue-600 rounded border-gray-300 focus:ring-blue-500" checked={selectedValues.includes(val)} onChange={() => toggleReportFilterValue(field, val)}/>
                                        <span className="text-[9px] text-gray-700 truncate font-medium">{val}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                  </div>
              </div>
          </th>
      );
  };

  return (
    <div className="p-4 h-full overflow-y-auto pb-20 bg-gray-50/50">
      {showWebReport && (
        <div className="fixed inset-0 bg-slate-200/50 backdrop-blur-sm z-[60] flex flex-col font-sans animate-in fade-in duration-200">
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white px-5 py-3 flex justify-between items-center shadow-lg shadow-slate-300/50 z-30 flex-none h-16 rounded-b-2xl mx-2 mt-2">
              <div className="flex items-center gap-3">
                  <div className="bg-white/10 p-2 rounded-xl backdrop-blur-sm"><FileIcon className="w-5 h-5 text-yellow-400"/></div>
                  <div>
                    <h3 className="font-bold text-base tracking-wide text-white">Laporan & Cetak Data</h3>
                    <p className="text-[10px] text-slate-300 font-medium">Download PDF/Excel atau lihat preview tabel.</p>
                  </div>
              </div>
              <button onClick={() => setShowWebReport(false)} className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all duration-200 border border-white/5"><X className="w-5 h-5 text-white"/></button>
          </div>

          <div className="bg-white/90 backdrop-blur-md shadow-sm z-20 border border-gray-100 mx-2 mt-2 rounded-2xl flex-none flex flex-col overflow-visible">
              <div className="p-4 flex flex-wrap gap-4 items-center justify-between">
                  <div className="flex flex-wrap gap-3 items-center">
                      {reportPeriodeOpsi.length > 0 && (
                        <div className="relative">
                          <CalendarRange className="w-3.5 h-3.5 text-indigo-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                          <select
                            value={periodeTerpilih ? `${periodeTerpilih.mulai}|${periodeTerpilih.selesai}` : ''}
                            onChange={(e) => { if (!e.target.value) return; const [m, sl] = e.target.value.split('|'); setReportStartDate(m); setReportEndDate(sl); }}
                            className="appearance-none pl-8 pr-8 py-1.5 text-xs font-bold text-indigo-700 bg-indigo-50 border border-indigo-100 hover:bg-indigo-100 rounded-full cursor-pointer outline-none transition-all shadow-sm max-w-[260px]">
                            <option value="">{reportStartDate || reportEndDate ? 'Tanggal manual' : 'Pilih periode…'}</option>
                            {reportPeriodeOpsi.map(p => (
                              <option key={`${p.mulai}|${p.selesai}`} value={`${p.mulai}|${p.selesai}`}>
                                {formatDateShort(p.mulai)} – {formatDateShort(p.selesai)}{p.aktif ? ' (aktif)' : ''}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="w-3 h-3 text-indigo-400 absolute right-3 top-2 pointer-events-none"/>
                        </div>
                      )}
                      <div className="flex items-center bg-slate-50 border border-slate-200 rounded-full px-4 py-1.5 shadow-sm">
                          <span className="text-[10px] text-slate-500 font-bold mr-2 uppercase tracking-wider">Periode</span>
                          <input type="date" value={reportStartDate} onChange={(e) => setReportStartDate(e.target.value)} className="bg-transparent text-xs font-bold text-slate-700 outline-none w-24 cursor-pointer" />
                          <span className="text-slate-300 mx-1">|</span>
                          <input type="date" value={reportEndDate} onChange={(e) => setReportEndDate(e.target.value)} className="bg-transparent text-xs font-bold text-slate-700 outline-none w-24 cursor-pointer" />
                      </div>
                      <div className="relative">
                        <select value={reportCategory} onChange={(e) => setReportCategory(e.target.value)} className="appearance-none pl-4 pr-8 py-1.5 text-xs font-bold text-indigo-700 bg-indigo-50 border border-indigo-100 hover:bg-indigo-100 rounded-full cursor-pointer outline-none transition-all shadow-sm">
                            <option value="General">Laporan Absensi</option>
                            <option value="RunningShift">Running Shift</option>
                            <option value="Tally">Absen Online</option>
                        </select>
                        <ChevronDown className="w-3 h-3 text-indigo-400 absolute right-3 top-2 pointer-events-none"/>
                      </div>
                      {reportCategory === 'General' && (
                          <div className="relative">
                            <select value={reportFormFilter} onChange={(e) => setReportFormFilter(e.target.value)} className="appearance-none pl-4 pr-8 py-1.5 text-xs font-bold text-slate-600 bg-white border border-slate-200 hover:border-slate-300 rounded-full cursor-pointer outline-none transition-all shadow-sm">
                                <option value="All">Semua Form</option>
                                {uniqueForms.filter(t => t !== 'All').map((type, idx) => (<option key={idx} value={type}>{type}</option>))}
                             </select>
                            <ChevronDown className="w-3 h-3 text-slate-400 absolute right-3 top-2 pointer-events-none"/>
                          </div>
                      )}
                      {canViewAll && reportCategory !== 'RunningShift' && availableLokasi.length > 0 && !(availableLokasi.length === 1 && availableLokasi[0].lokasi === '(kosong)' && !reportLokasiFilter.length) && (
                        <div className="relative" ref={refLokasiReport}>
                          <button type="button" onClick={() => setBukaLokasiReport(v => !v)}
                            className={`flex items-center gap-1.5 pl-3 pr-3 py-1.5 rounded-full text-xs font-bold border shadow-sm transition-all ${reportLokasiFilter.length ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}>
                            <MapPin className="w-3.5 h-3.5" />
                            {reportLokasiFilter.length === 0 ? 'Semua Lokasi' : reportLokasiFilter.length <= 2 ? reportLokasiFilter.join(', ') : `${reportLokasiFilter.length} lokasi`}
                            <ChevronDown className={`w-3 h-3 transition ${bukaLokasiReport ? 'rotate-180' : ''}`} />
                          </button>
                          {bukaLokasiReport && (
                            <div className="absolute left-0 top-full mt-1.5 z-50 w-60 rounded-xl border border-slate-200 bg-white shadow-xl p-2">
                              <div className="flex items-center justify-between px-2 pb-2 mb-1 border-b border-slate-100">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Lokasi karyawan</span>
                                <div className="flex gap-2 text-[11px] font-semibold">
                                  <button type="button" onClick={() => setReportLokasiFilter(availableLokasi.map(x => x.lokasi))} className="text-blue-600 hover:underline">Semua</button>
                                  <button type="button" onClick={() => setReportLokasiFilter([])} className="text-slate-500 hover:underline">Reset</button>
                                </div>
                              </div>
                              <div className="max-h-64 overflow-y-auto">
                                {availableLokasi.map(({ lokasi, jumlah }) => (
                                  <label key={lokasi} className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer text-xs text-slate-700">
                                    <input type="checkbox" checked={reportLokasiFilter.includes(lokasi)}
                                      onChange={() => setReportLokasiFilter(prev => prev.includes(lokasi) ? prev.filter(x => x !== lokasi) : [...prev, lokasi])}
                                      className="w-4 h-4 rounded border-slate-300 text-emerald-600" />
                                    <span className="flex-1 font-medium">{lokasi}</span>
                                    <span className="text-[10px] text-slate-400 tabular-nums">{jumlah}</span>
                                  </label>
                                ))}
                              </div>
                              <p className="px-2 pt-2 mt-1 border-t border-slate-100 text-[10px] text-slate-400">Tidak ada yang dicentang = semua lokasi.</p>
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                  <div className="flex items-center gap-3">
                      <button onClick={async () => { setIsReportLoading(true); try { if (reportCategory === 'RunningShift') await fetchShiftReport(); else await fetchHistory(reportStartDate, reportEndDate); } catch (e) { console.error(e); } finally { setTimeout(() => setIsReportLoading(false), 500); } }} className="flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold transition-all border shadow-sm bg-white text-slate-600 border-slate-200 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50 active:scale-95 group">
                          <RefreshCcw className={`w-3.5 h-3.5 transition-transform group-hover:rotate-180 ${isReportLoading ? 'animate-spin text-blue-500' : ''}`} />
                          <span className="hidden sm:inline">Refresh</span>
                      </button>
                      {canViewAll && (
                          <button onClick={() => setShowAdvancedFilter(!showAdvancedFilter)} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold transition-all border shadow-sm ${showAdvancedFilter ? 'bg-blue-50 text-blue-600 border-blue-200 ring-2 ring-blue-100' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
                              <Filter className="w-3.5 h-3.5" /> Filter
                          </button>
                      )}
                      <div className="h-6 w-px bg-slate-200 mx-1"></div>
                      <div className="flex gap-2">
                          <button onClick={generatePDF} className="flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold text-white bg-rose-500 hover:bg-rose-600 shadow-md shadow-rose-200 transition-all active:scale-95"><Printer className="w-3.5 h-3.5" /> PDF</button>
                          {(reportCategory === 'RunningShift' || reportCategory === 'Tally' || (reportCategory === 'General' && canViewAll)) && (
                              <button onClick={generateExcel} className="flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold text-white bg-emerald-500 hover:bg-emerald-600 shadow-md shadow-emerald-200 transition-all active:scale-95"><FileSpreadsheet className="w-3.5 h-3.5" /> Excel</button>
                          )}
                      </div>
                  </div>
              </div>
              {/* RINGKASAN STATUS — jumlah pengajuan per status pada periode & filter terpilih.
                  Klik chip untuk menyaring kolom STATUS; klik lagi untuk melepas. */}
              {reportCategory === 'General' && (
                <div className="px-4 pb-3 -mt-1 flex flex-wrap items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mr-1">
                    {periodeTerpilih ? `Periode ${formatDateShort(periodeTerpilih.mulai)} – ${formatDateShort(periodeTerpilih.selesai)}` : 'Ringkasan'}
                  </span>
                  {(() => {
                    const aktif = reportColumnFilters.status || [];
                    const urutan = ['Pending', 'Approved', 'Verified', 'Rejected'];
                    const daftar = [...urutan.filter(st => ringkasanStatus[st]), ...Object.keys(ringkasanStatus).filter(st => !urutan.includes(st))];
                    const warna = { Pending: 'amber', Approved: 'emerald', Verified: 'sky', Rejected: 'rose' };
                    const kelas = {
                      amber: 'bg-amber-50 text-amber-700 border-amber-200', emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                      sky: 'bg-sky-50 text-sky-700 border-sky-200', rose: 'bg-rose-50 text-rose-700 border-rose-200', slate: 'bg-slate-50 text-slate-700 border-slate-200'
                    };
                    return (
                      <>
                        <button type="button" onClick={() => setReportColumnFilters(prev => ({ ...prev, status: [] }))}
                          className={`px-3 py-1 rounded-full border text-xs font-bold transition ${aktif.length === 0 ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
                          Total <span className="tabular-nums ml-1">{currentReportData.length}</span>
                        </button>
                        {daftar.map(st => {
                          const on = aktif.includes(st);
                          return (
                            <button key={st} type="button"
                              onClick={() => setReportColumnFilters(prev => ({ ...prev, status: on ? [] : [st] }))}
                              className={`px-3 py-1 rounded-full border text-xs font-bold transition ${kelas[warna[st] || 'slate']} ${on ? 'ring-2 ring-offset-1 ring-slate-400' : 'hover:brightness-95'}`}>
                              {st} <span className="tabular-nums ml-1">{ringkasanStatus[st]}</span>
                            </button>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
              )}
          </div>

          <div className="flex-1 overflow-hidden relative flex flex-col mx-2 mb-2 rounded-b-2xl">
                {isReportLoading && (
                    <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-50 flex flex-col items-center justify-center rounded-2xl">
                        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mb-2"/>
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Loading...</span>
                    </div>
                )}
                
                <div className="flex-1 overflow-auto bg-white border border-gray-300 shadow-sm custom-scrollbar">
                        <table className="w-full whitespace-nowrap border-collapse">
                            <thead className="sticky top-0 z-10 shadow-sm">
                                <tr>
                                    <th className="px-2 py-3 text-xs font-bold text-center text-gray-700 uppercase bg-gray-100 border border-gray-300 w-10">No</th>
                                    {reportCategory === 'RunningShift' ? (
                                        <>
                                            <ReportFilterHeader label="UserID" field="col_userId" />
                                            <ReportFilterHeader label="PAYROLL" field="col_payroll" />
                                            <ReportFilterHeader label="NAMA" field="nama" width="min-w-[140px]"/>
                                            <ReportFilterHeader label="Posisi" field="divisi" />
                                            <ReportFilterHeader label="Tgl Shift" field="col_date" />
                                            <ReportFilterHeader label="Jam Kerja" field="shiftLabel" />
                                            <ReportFilterHeader label="Waktu Input" field="col_time" />
                                        </>
                                    ) : reportCategory === 'Tally' ? (
                                        <>
                                            <ReportFilterHeader label="UserID" field="col_userId" />
                                            <ReportFilterHeader label="PAYROLL" field="col_payroll" />
                                            <ReportFilterHeader label="NAMA" field="nama" width="min-w-[140px]"/>
                                            <ReportFilterHeader label="Tanggal" field="col_date" />
                                            <ReportFilterHeader label="Posisi" field="divisi" />
                                            <ReportFilterHeader label="Masuk" field="masuk" />
                                            <ReportFilterHeader label="Pulang" field="pulang" />
                                            <ReportFilterHeader label="Standby" field="standby" />
                                            <th className="px-2 py-3 text-xs font-bold text-left text-gray-700 uppercase bg-gray-100 border border-gray-300">Foto</th>
                                            <ReportFilterHeader label="Catatan" field="catatan" width="min-w-[200px]"/>
                                        </>
                                    ) : (
                                        <>
                                            {/* [NEW] KOLOM ACTION (Hanya untuk General Report) */}
                                            {canViewAll && <ReportFilterHeader label="Action" field="col_action" width="min-w-[150px]" />}
                                            <ReportFilterHeader label="UserID" field="col_userId" />
                                            <ReportFilterHeader label="PAYROLL" field="col_payroll" />
                                            <ReportFilterHeader label="NAMA" field="nama" width="min-w-[140px]"/>
                                            <ReportFilterHeader label="Form" field="tipe" />
                                            <ReportFilterHeader label="Waktu Input" field="col_date" />
                                            <ReportFilterHeader label="Periode" field="col_periode" />
                                            <ReportFilterHeader label="Durasi" field="col_durasi" />
                                            <ReportFilterHeader label="Catatan" field="catatan" width="min-w-[250px]"/>
                                            <ReportFilterHeader label="Status" field="status" />
                                            <ReportFilterHeader label="Approval" field="col_approval" />
                                            <ReportFilterHeader label="Keterangan" field="col_keterangan" width="min-w-[220px]"/>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="bg-white">
                               {sortedReportTable.length === 0 ? (
                                   <tr><td colSpan="14" className="p-12 text-center text-slate-400 italic border border-gray-300">Tidak ada data sesuai filter.</td></tr>
                              ) : (
                                    sortedReportTable.map((item, index) => (
                                    <tr key={index} className="hover:bg-blue-50 transition-colors">
                                        <td className="px-2 py-1.5 text-[11px] text-center text-gray-600 border border-gray-300 bg-gray-50/50 align-top">{index + 1}</td>
                                        {reportCategory === 'RunningShift' ? (
                                            <>
                                                <td className="px-2 py-1.5 text-[11px] font-mono text-gray-700 border border-gray-300 align-top">{item.col_userId}</td>
                                                <td className="px-2 py-1.5 text-[11px] font-mono text-gray-700 border border-gray-300 align-top">{item.col_payroll}</td>
                                                <td className="px-2 py-1.5 text-[11px] font-bold text-gray-800 border border-gray-300 align-top">{item.nama}</td>
                                                <td className="px-2 py-1.5 text-[11px] text-gray-600 border border-gray-300 align-top">{item.divisi}</td>
                                                <td className="px-2 py-1.5 text-[11px] text-center text-gray-700 border border-gray-300 align-top">{item.col_date}</td>
                                                <td className="px-2 py-1.5 text-[11px] text-center border border-gray-300 align-top">{item.shiftLabel}</td>
                                                <td className="px-2 py-1.5 text-[11px] text-center text-gray-500 border border-gray-300 align-top">{item.col_time}</td>
                                            </>
                                        ) : reportCategory === 'Tally' ? (
                                            <>
                                                <td className="px-2 py-1.5 text-[11px] font-mono border border-gray-300 align-top">{item.col_userId}</td>
                                                <td className="px-2 py-1.5 text-[11px] font-mono border border-gray-300 align-top">{item.col_payroll}</td>
                                                <td className="px-2 py-1.5 text-[11px] font-bold border border-gray-300 align-top">{item.nama}</td>
                                                <td className="px-2 py-1.5 text-[11px] text-center border border-gray-300 align-top">{item.col_date}</td>
                                                <td className="px-2 py-1.5 text-[11px] border border-gray-300 align-top">{item.divisi}</td>
                                                <td className="px-2 py-1.5 text-[11px] font-bold text-center text-emerald-700 bg-emerald-50/50 border border-gray-300 align-top">{item.masuk}</td>
                                                <td className="px-2 py-1.5 text-[11px] font-bold text-center text-red-700 bg-red-50/50 border border-gray-300 align-top">{item.pulang}</td>
                                                <td className="px-2 py-1.5 text-[11px] font-bold text-center text-amber-700 bg-amber-50/50 border border-gray-300 align-top">{item.standby}</td>
                                                <td className="px-2 py-1.5 text-[11px] border border-gray-300 text-center align-top">{(item.foto && item.foto !== '-') ? (<a href={item.foto} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline font-bold text-[10px]">Lihat</a>) : '-'}</td>
                                                <td className="px-2 py-1.5 text-[11px] text-gray-600 italic border border-gray-300 whitespace-normal min-w-[200px] align-top">{item.catatan}</td>
                                            </>
                                        ) : (
                                            <>
                                                {/* [NEW] TOMBOL ACTION APPROVE/REJECT */}
                                                {canViewAll && (
                                                    <td className={`px-2 py-1.5 text-center border border-gray-300 align-top ${isReportDuplicate(item) ? 'bg-orange-50' : ''}`}>
                                                        {/* 5 ikon selalu tampil, urutan tetap: ✓ ✗ ✏️ ✉️ 🗑️ */}
                                                        <div className="flex flex-col items-center gap-1">
                                                        <div className="flex gap-1 items-center justify-center">
                                                            {(() => {
                                                                const isPending = item.status === 'Pending';
                                                                const isFinal = item.status === 'Approved' || item.status === 'Verified';
                                                                const canCancel = item.status === 'Approved' || (item.status === 'Verified' && TIPE_DL_ONLINE.includes(item.tipe));
                                                                const canDelete = userRole === 'admin' && !isFinal;
                                                                return [
                                                                    renderActBtn({ key: 'ok', enabled: isPending, onClick: () => openStatusEdit(item, 'approve'), Icon: Check, tone: 'green', title: 'Approve', offTitle: 'Approve (hanya untuk Pending)' }),
                                                                    renderActBtn({ key: 'no', enabled: isPending, onClick: () => openStatusEdit(item, 'reject'), Icon: X, tone: 'red', title: 'Reject', offTitle: 'Reject (hanya untuk Pending)' }),
                                                                    renderActBtn({ key: 'ed', enabled: canCancel, onClick: () => openStatusEdit(item, 'cancel'), Icon: Edit, tone: 'amber', title: 'Edit / batalkan approval', offTitle: 'Edit (hanya untuk Approved)' }),
                                                                    renderActBtn({ key: 'ml', enabled: isPending, onClick: () => handleResendPengajuan(item), Icon: Mail, tone: 'blue', title: 'Kirim ulang email approval', offTitle: 'Kirim ulang (hanya untuk Pending)', loading: resendingUuid === item.uuid }),
                                                                    renderActBtn({ key: 'rm', enabled: canDelete, onClick: () => handleDeletePengajuan(item), Icon: Trash2, tone: 'trash', title: 'Hapus pengajuan',
                                                                        offTitle: userRole !== 'admin' ? 'Hapus (khusus admin)' : 'Batalkan approval dulu lewat Edit, baru bisa dihapus', loading: deletingUuid === item.uuid })
                                                                ];
                                                            })()}
                                                        </div>
                                                        {isReportDuplicate(item) && (
                                                            <span className="text-[9px] font-bold text-orange-700 bg-orange-100 border border-orange-300 rounded px-1 leading-tight" title="Ada pengajuan lain dengan karyawan, form & periode yang sama">Duplikat</span>
                                                        )}
                                                        </div>
                                                    </td>
                                                )}
                                                
                                                <td className="px-2 py-1.5 text-[11px] font-mono text-gray-600 border border-gray-300 align-top">{item.col_userId}</td>
                                                <td className="px-2 py-1.5 text-[11px] font-mono text-gray-600 border border-gray-300 align-top">{item.col_payroll}</td>
                                                <td className="px-2 py-1.5 text-[11px] font-bold text-gray-800 border border-gray-300 align-top">{item.nama}</td>
                                                <td className="px-2 py-1.5 text-[11px] border border-gray-300 align-top">{item.tipe}</td>
                                                <td className="px-2 py-1.5 text-[11px] text-center text-gray-600 border border-gray-300 align-top">{item.col_date}</td>
                                                <td className="px-2 py-1.5 text-[11px] text-center text-gray-700 border border-gray-300 align-top">{item.col_periode}</td>
                                                <td className="px-2 py-1.5 text-[11px] text-center text-gray-800 font-bold border border-gray-300 align-top">{item.col_durasi}</td>
                                                <td className="px-2 py-1.5 text-[11px] text-gray-600 italic border border-gray-300 whitespace-normal min-w-[200px] align-top">{item.catatan}</td>
                                                <td className="px-2 py-1.5 text-[11px] text-center border border-gray-300 align-top"><span className={`font-bold ${item.status === 'Approved' ? 'text-green-700' : (item.status === 'Rejected' ? 'text-red-700' : 'text-amber-700')}`}>{item.status}</span></td>
                                                <td className="px-2 py-1.5 text-[11px] text-center text-gray-500 border border-gray-300 align-top">{item.col_approval}</td>
                                                <td className={`px-2 py-1.5 text-[11px] border border-gray-300 whitespace-normal min-w-[220px] align-top ${String(item.col_keterangan || '').startsWith('[Batal Approval]') ? 'text-red-700 font-medium' : 'text-gray-600 italic'}`}>{item.col_keterangan || '-'}</td>
                                            </>
                                        )}
                                    </tr>
                                   ))
                              )}
                            </tbody>
                        </table>
                </div>
                <div className="bg-gray-100 px-4 py-2 border border-gray-300 border-t-0 flex justify-between items-center rounded-b-2xl mt-0">
                     <span className="text-[10px] text-gray-500 font-bold">Total Data: {sortedReportTable.length}</span>
                     <div className="text-[10px] text-gray-400 italic">E-Absensi System</div>
                </div>
          </div>
        </div>
      )}
      
      {showWebReport && statusEdit && (
        <div className="fixed inset-0 z-[70] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeStatusEdit}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className={`px-5 py-3 flex items-center justify-between text-white ${statusEdit.mode === 'approve' ? 'bg-emerald-600' : statusEdit.mode === 'reject' ? 'bg-rose-600' : 'bg-amber-600'}`}>
              <h4 className="font-bold text-sm">
                {statusEdit.mode === 'approve' ? 'Approve Pengajuan' : statusEdit.mode === 'reject' ? 'Tolak Pengajuan' : 'Batalkan Approval'}
              </h4>
              <button onClick={closeStatusEdit} className="p-1 rounded-full hover:bg-white/20"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3 text-xs text-slate-700">
              <div className="grid grid-cols-[90px_1fr] gap-y-1">
                <span className="text-slate-400">Nama</span><span className="font-bold">{statusEdit.item.nama}</span>
                <span className="text-slate-400">Form</span><span>{statusEdit.item.tipe}</span>
                <span className="text-slate-400">Periode</span><span>{statusEdit.item.col_periode}</span>
                <span className="text-slate-400">Status</span>
                <span>
                  <b className={['Approved', 'Verified'].includes(statusEdit.item.status) ? 'text-green-700' : 'text-amber-700'}>{statusEdit.item.status}</b>
                  {' → '}
                  <b className={statusEdit.mode === 'approve' ? 'text-green-700' : 'text-red-700'}>{statusEdit.mode === 'approve' ? 'Approved' : 'Rejected'}</b>
                </span>
                {statusEdit.mode === 'cancel' && (<><span className="text-slate-400">{statusEdit.item.status === 'Verified' ? 'Diverifikasi' : 'Di-approve'}</span><span>{statusEdit.item.approver || '-'} · {statusEdit.item.col_approval}</span></>)}
              </div>
              {statusEdit.mode === 'cancel' && (
                <p className="flex gap-1.5 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-2 text-[11px]">
                  <AlertTriangle className="w-3.5 h-3.5 flex-none mt-0.5" />
                  Approval akan dibatalkan dan status menjadi Rejected. Simbol pengajuan ini di sheet payroll eksternal ikut dikosongkan.
                </p>
              )}
              <label className="block">
                <span className="font-bold text-[11px]">
                  Keterangan {statusEdit.mode === 'approve' ? <span className="font-normal text-slate-400">(opsional)</span> : <span className="text-red-600">*</span>}
                </span>
                <textarea
                  autoFocus
                  rows={3}
                  value={statusEditKet}
                  onChange={(e) => setStatusEditKet(e.target.value)}
                  placeholder={statusEdit.mode === 'cancel' ? 'Alasan pembatalan approval…' : statusEdit.mode === 'reject' ? 'Alasan penolakan…' : 'Catatan persetujuan…'}
                  className="mt-1 w-full border border-slate-300 rounded-lg p-2 text-xs outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 resize-none"
                />
              </label>
            </div>
            <div className="px-5 pb-4 flex justify-end gap-2">
              <button onClick={closeStatusEdit} disabled={statusEditSaving} className="px-4 py-1.5 rounded-full text-xs font-bold border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50">Batal</button>
              <button onClick={submitStatusEdit} disabled={statusEditSaving}
                className={`px-4 py-1.5 rounded-full text-xs font-bold text-white flex items-center gap-1.5 disabled:opacity-60 ${statusEdit.mode === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : statusEdit.mode === 'reject' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-amber-600 hover:bg-amber-700'}`}>
                {statusEditSaving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {statusEdit.mode === 'approve' ? 'Approve' : statusEdit.mode === 'reject' ? 'Tolak' : 'Simpan → Rejected'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- MAIN PAGE CONTENT (RIWAYAT CARD VIEW) --- */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
           <h2 className="text-xl font-bold ml-2 text-slate-800">Riwayat & Laporan</h2>
           {loading && <span className="text-[10px] text-slate-400 font-normal animate-pulse">Updating...</span>}
        </div>

        <div className="flex items-center gap-2">
            <button onClick={() => { fetchHistory(); if(canViewAll) fetchUsers(); }} disabled={loading} className="p-2.5 bg-white text-slate-700 hover:text-blue-600 rounded-xl border border-slate-200 hover:border-blue-200 hover:bg-blue-50/50 active:scale-95 transition-all shadow-sm flex items-center justify-center">
                <RefreshCcw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <BackButton onClick={() => setView('dashboard')} />
        </div>
      </div>

      {/* --- KARTU FILTER --- */}
      <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-200 mb-3">
        <div className="flex items-center gap-2 mb-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider"><Filter className="w-3.5 h-3.5 text-slate-400" /> Filter Data</div>
        {canViewAll && (
            <div className="grid grid-cols-2 gap-2 mb-2">
                {isSuperAdmin && (
                    <div>
                        <label className="text-[9px] font-bold text-slate-400 block mb-0.5">Lokasi</label>
                        <div className="relative">
                            <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)} className="w-full border border-gray-200 rounded-lg p-1.5 text-xs font-semibold text-slate-700 bg-white appearance-none focus:ring-1 focus:ring-blue-100 outline-none"><option value="All">Semua Lokasi</option><option value="Surabaya">Surabaya</option><option value="Jakarta">Jakarta</option></select>
                            <ChevronDown className="w-3 h-3 text-gray-400 absolute right-2 top-2 pointer-events-none"/>
                        </div>
                    </div>
                )}
                <div className={isSuperAdmin ? "" : "col-span-2"}>
                    <label className="text-[9px] font-bold text-slate-400 block mb-0.5">Karyawan ({selectedUserIds.length})</label>
                    <div className="relative">
                        <button onClick={() => setShowAdvancedFilter(!showAdvancedFilter)} className="w-full border border-gray-200 rounded-lg p-1.5 text-xs font-semibold text-slate-700 bg-white flex justify-between items-center focus:ring-1 focus:ring-blue-100"><span className="truncate">{selectedUserIds.length > 0 ? `${selectedUserIds.length} Dipilih` : 'Semua Karyawan'}</span><ChevronDown className="w-3 h-3 text-gray-400"/></button>
                        {showAdvancedFilter && (
                            <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 shadow-xl rounded-lg z-20 p-2">
                                <input type="text" placeholder="Cari nama..." value={searchUser} onChange={(e) => setSearchUser(e.target.value)} className="w-full p-1.5 text-xs border border-gray-200 rounded mb-2 outline-none focus:border-blue-300"/>
                                <div className="max-h-40 overflow-y-auto space-y-1 custom-scrollbar">
                                    <div onClick={selectAllUsers} className="p-1.5 hover:bg-blue-50 rounded cursor-pointer text-xs font-bold text-blue-600">{selectedUserIds.length > 0 ? 'Reset Pilihan' : 'Pilih Semua'}</div>
                                    {allUsers.filter(u => u.nama.toLowerCase().includes(searchUser.toLowerCase())).map(u => (
                                        <div key={u.id} onClick={() => toggleUserSelection(u.id)} className="flex items-center gap-2 p-1.5 hover:bg-gray-50 rounded cursor-pointer"><input type="checkbox" checked={selectedUserIds.includes(u.id)} readOnly className="w-3 h-3 rounded text-blue-600"/><span className="text-xs text-slate-600 truncate">{u.nama}</span></div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )}
        <div className="grid grid-cols-2 gap-2 mb-2"> 
          <div><label className="text-[9px] font-bold text-slate-400 block mb-0.5">Dari Tanggal</label><input type="date" className="w-full border border-gray-200 rounded-lg p-1.5 text-xs font-semibold text-slate-700 focus:ring-1 focus:ring-blue-100 outline-none" value={filterStart} onChange={e => setFilterStart(e.target.value)} /></div> 
          <div><label className="text-[9px] font-bold text-slate-400 block mb-0.5">Sampai Tanggal</label><input type="date" className="w-full border border-gray-200 rounded-lg p-1.5 text-xs font-semibold text-slate-700 focus:ring-1 focus:ring-blue-100 outline-none" value={filterEnd} onChange={e => setFilterEnd(e.target.value)} /></div> 
        </div>
        {historyPeriod?.aktif && !filterStart && !filterEnd && (
          <p className="mb-2 text-[10px] font-medium text-slate-400">
            Periode aktif: {formatDateShort(historyPeriod.aktif.mulai)} – {formatDateShort(historyPeriod.aktif.selesai)}
          </p>
        )}
        <div className="grid grid-cols-2 gap-2"> 
             <div><label className="text-[9px] font-bold text-slate-400 block mb-0.5">Tipe Absen</label><div className="relative"><select className="w-full border border-gray-200 rounded-lg p-1.5 text-xs font-semibold text-slate-700 bg-white appearance-none focus:ring-1 focus:ring-blue-100 outline-none" value={filterType} onChange={(e) => setFilterType(e.target.value)}>{uniqueTypes.map((t, i) => ( <option key={i} value={t}>{t === 'All' ? 'Semua Form' : t}</option> ))}</select><ChevronDown className="w-3 h-3 text-gray-400 absolute right-2 top-2 pointer-events-none"/></div></div>
             <div><label className="text-[9px] font-bold text-slate-400 block mb-0.5">Status Approval</label><div className="relative"><select className="w-full border border-gray-200 rounded-lg p-1.5 text-xs font-semibold text-slate-700 bg-white appearance-none focus:ring-1 focus:ring-blue-100 outline-none" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}><option value="All">Semua Status</option><option value="Pending">Pending</option><option value="Approved">Approved</option><option value="Rejected">Rejected</option><option value="Verified">Verified</option></select><ChevronDown className="w-3 h-3 text-gray-400 absolute right-2 top-2 pointer-events-none"/></div></div>
        </div>
      </div>

      <button onClick={() => setShowWebReport(true)} className="w-full mb-4 flex items-center justify-center gap-2 p-3.5 bg-slate-900 hover:bg-blue-600 text-white rounded-2xl transition-all text-xs font-bold shadow-lg shadow-slate-900/15 active:scale-[0.98]">
          <Eye className="w-4 h-4" /> MENU LAPORAN
      </button>

      {/* --- HISTORY LIST (CARD VIEW) --- */}
      {loading ? (
          <div className="flex flex-col items-center justify-center py-10"><Loader2 className="w-8 h-8 text-slate-300 animate-spin mb-2"/><p className="text-xs font-bold text-slate-400">Memuat Riwayat...</p></div>
      ) : (
        <div className="space-y-2">
          {displayData.map((item, idx) => {
            const canEdit = isEditable(item.waktu, item.status);
            const isRegularAbsen = item.tipe === 'Hadir' || item.tipe === 'Pulang';
            const bisaLihatMaps = ['Hadir', 'Pulang', 'Dinas', 'Dinas Luar', 'Standby'].includes(item.tipe);
            const showResendButton = APPROVAL_TYPES.includes(item.tipe) && item.status === 'Pending' && !canViewAll;
            return (
              <div key={idx} className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm relative group transition-all hover:border-blue-200">
                <div className="flex justify-between items-start">
                    <div className="flex flex-col">
                        {canViewAll ? (
                            <><h4 className="font-bold text-slate-800 text-sm leading-tight">{item.nama}</h4><div className="flex items-center gap-1.5 mt-1"><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded text-white ${item.tipe === 'Sakit' ? 'bg-orange-400' : (item.tipe === 'Cuti' ? 'bg-pink-400' : 'bg-blue-500')}`}>{item.tipe}</span><span className="text-[10px] text-slate-400 font-medium">• {formatDateShort(item.waktu)}</span></div></>
                        ) : (
                            <><h4 className="font-bold text-slate-800 text-sm flex items-center gap-2">{item.tipe}</h4><div className="flex items-center gap-1 mt-0.5 text-[10px] text-slate-400 font-medium"><Clock className="w-3 h-3"/> {formatDateIndo(item.waktu)}</div></>
                        )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                        {!isRegularAbsen && <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${getStatusColor(item.status)}`}>{item.status || 'Pending'}</span>}
                        {isRegularAbsen && <span className="text-xs font-bold text-slate-600 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-full font-mono">{formatTimeOnly(item.waktu)}</span>}
                        {canEdit && !canViewAll && (
                            <div className="flex gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => handleEdit(item)} className="p-1 bg-yellow-50 text-yellow-600 rounded border border-yellow-100 hover:bg-yellow-100" title="Edit"><Edit className="w-3 h-3"/></button>
                                <button onClick={() => handleDelete(item.uuid)} className="p-1 bg-red-50 text-red-600 rounded border border-red-100 hover:bg-red-100" title="Hapus"><Trash2 className="w-3 h-3"/></button>
                            </div>
                        )}
                    </div>
                </div>
                {(item.catatan || (item.alasan && item.status === 'Rejected') || item.tglMulai) && (
                    <div className="mt-2 pt-2 border-t border-dashed border-gray-100 text-xs">
                        {item.status === 'Rejected' && item.alasan && <p className="text-red-600 italic mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3"/> {item.alasan}</p>}
                        {(item.tglMulai && item.tglMulai !== '-') && <div className="flex items-center gap-1 text-[10px] font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded w-fit mb-1"><CalendarDays className="w-3 h-3"/> {formatDateShort(item.tglMulai)} - {formatDateShort(item.tglSelesai)}</div>}
                        {item.catatan && <p className="text-slate-500 italic">"{item.catatan}"</p>}
                    </div>
                )}
                <div className="flex gap-2 mt-2">
                    {item.foto && item.foto.length > 10 && item.foto !== 'Error Upload' && <a href={item.foto} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[9px] font-bold text-blue-600 hover:underline"><Camera className="w-3 h-3"/> Foto</a>}
                    {item.lampiran && item.lampiran.length > 10 && item.lampiran !== '-' && <a href={item.lampiran} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[9px] font-bold text-orange-600 hover:underline"><FileIcon className="w-3 h-3"/> Lampiran</a>}
                    {bisaLihatMaps && <TombolLokasiMaps lokasi={item.lokasi} alamat={item.alamat} />}
                </div>
                {showResendButton && <button onClick={() => handleRequestApproval(item)} disabled={sendingEmail} className="w-full mt-2 bg-purple-50 text-purple-700 py-1.5 rounded-lg text-[10px] font-bold flex items-center justify-center gap-1 hover:bg-purple-100 border border-purple-200 transition-colors">{sendingEmail ? 'Mengirim...' : <><CheckSquare className="w-3 h-3"/> Kirim Ulang Email</>}</button>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// --- 6. ADMIN PANEL (FIX: MENAMBAHKAN MENU ANALISA DATA) ---
function AdminPanel({ user, setView, masterData, setMasterData }) {
  // Admin mulai di Master User; HRD (tanpa menu itu) mulai di Info HRD.
  const [subMasterUser, setSubMasterUser] = useState('akses');
  const [activeTab, setActiveTab] = useState(user && user.role !== 'admin' ? 'news' : 'master_user'); 
  const [loading, setLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false); 
  
  // State Reset Password
  const [geofenceUsers, setGeofenceUsers] = useState([]);
  const [geofenceConfigs, setGeofenceConfigs] = useState({});
  const [geofenceUserId, setGeofenceUserId] = useState('');
  const [geofenceRequired, setGeofenceRequired] = useState(false);
  // Daftar UserID yang dikecualikan dari gerbang GPS (lihat gpsBebasDaftar
  // di apps-script/Code.gs). Disimpan sebagai array dari server, dipakai
  // untuk mencentang kotaknya saat karyawan dipilih.
  const [gpsBebasList, setGpsBebasList] = useState([]);
  const [gpsBebasUser, setGpsBebasUser] = useState(false);
  // Mode absen per karyawan (ModeAbsen.gs): peta { userId: 'online'|'mesin' },
  // karyawan yang tidak ada di peta = 'gabungan' (online + mesin).
  const [modeAbsenMap, setModeAbsenMap] = useState({});
  const [modeAbsenPilih, setModeAbsenPilih] = useState('gabungan');
  const [modeDivisi, setModeDivisi] = useState('');
  const [modeDivisiNilai, setModeDivisiNilai] = useState('gabungan');
  const [geofenceAreas, setGeofenceAreas] = useState([]);
  // Master lokasi geofence (sheet MasterGeofence) + pilihan lokasi per karyawan.
  const [geoMaster, setGeoMaster] = useState([]);
  const [geoMasterDraft, setGeoMasterDraft] = useState([]);
  const [geofenceMasterIds, setGeofenceMasterIds] = useState([]);
  const [geofenceDivisi, setGeofenceDivisi] = useState('');
  const [divisiMasterIds, setDivisiMasterIds] = useState([]);
  const [loadingGeofence, setLoadingGeofence] = useState(false);
  // PERIODE ABSENSI — sejak Agu 2026 berupa DAFTAR, bukan satu periode.
  // Tiap entri: { id, mulai, selesai, aktif }. Boleh lebih dari satu yang
  // aktif, asalkan tidak tumpang tindih — divalidasi backend karena kuota
  // ijin 4x per periode menuntut tiap tanggal jatuh ke tepat satu periode
  // aktif (lihat AbsencePeriod.gs).
  const [periodeList, setPeriodeList] = useState([]);
  // Baris kosong di paling atas daftar. Begitu KEDUA tanggalnya terisi dan
  // masuk akal, isinya pindah jadi periode baru dan baris ini kosong lagi —
  // jadi menambah periode tidak perlu menekan tombol apa pun dulu.
  const [periodeDraft, setPeriodeDraft] = useState({ mulai: '', selesai: '', aktif: true });
  const [loadingPeriod, setLoadingPeriod] = useState(false);
  const [savingPeriod, setSavingPeriod] = useState(false);

  // State Tim Approval (mapping kepala divisi -> anggota tim)
  const [approvalTeamUsers, setApprovalTeamUsers] = useState([]);
  const [approvalTeamAssignments, setApprovalTeamAssignments] = useState([]);
  const [loadingApprovalTeam, setLoadingApprovalTeam] = useState(false);
  const [atKepalaId, setAtKepalaId] = useState('');
  const [atDepartemen, setAtDepartemen] = useState('');
  const [atMemberIds, setAtMemberIds] = useState([]);
  const [atSearch, setAtSearch] = useState('');
  const [atKepalaSearch, setAtKepalaSearch] = useState('');
  const [atNewRole, setAtNewRole] = useState('kepala_divisi');
  const [atJabatan, setAtJabatan] = useState('');
  const [savingApprovalRole, setSavingApprovalRole] = useState(false);
  const [showNewDepartemenInput, setShowNewDepartemenInput] = useState(false);
  const [newDepartemenName, setNewDepartemenName] = useState('');
  const [addingDepartemen, setAddingDepartemen] = useState(false);

  // State Lainnya
  const [newsInput, setNewsInput] = useState('');
  const [masterInput, setMasterInput] = useState({ kategori: 'Menu', value: '', label: '' });

  // --- PENGUNCIAN PERANGKAT (lihat apps-script/Devices.gs) ---
  const [deviceList, setDeviceList] = useState([]);
  const [deviceSesi, setDeviceSesi] = useState([]);
  const [deviceMode, setDeviceMode] = useState('tandai');
  const [deviceAudit, setDeviceAudit] = useState([]);
  const [loadingDevice, setLoadingDevice] = useState(false);
  const [deviceCari, setDeviceCari] = useState('');
  const [deviceKuotaDraft, setDeviceKuotaDraft] = useState({});
  const [deviceTampilLog, setDeviceTampilLog] = useState(false);
  // Dipisah per fitur, bukan satu penanda bersama: sangat mungkin admin
  // sudah menempel Devices.gs tapi lupa FaceProfile.gs, dan pesan yang
  // menyebut keduanya sekaligus justru menyesatkan.
  const [deviceBelumAda, setDeviceBelumAda] = useState(false);

  // --- WAJAH ACUAN (lihat apps-script/FaceProfile.gs) ---
  const [wajahList, setWajahList] = useState([]);
  const [wajahKonfig, setWajahKonfig] = useState({ mode: 'ketat', ambang: 0.52, wajibTerdaftar: false });
  const [loadingWajah, setLoadingWajah] = useState(false);
  const [wajahCari, setWajahCari] = useState('');
  const [wajahTargetId, setWajahTargetId] = useState('');
  const [wajahFileInfo, setWajahFileInfo] = useState([]);
  const [wajahProses, setWajahProses] = useState('');
  const [wajahHanyaBelum, setWajahHanyaBelum] = useState(false);
  const [wajahBelumAda, setWajahBelumAda] = useState(false);

  const LIST_LOKASI = ['Surabaya', 'Jakarta', 'Semarang', 'Banyuwangi', 'Cilegon', 'Citeureup', 'Makassar', 'Balikpapan', 'Medan', 'All'];



  useEffect(() => {
    if (activeTab === 'geofence' || activeTab === 'geomaster') fetchGeofenceConfig();
    if (activeTab === 'period') fetchAbsencePeriod();
    if (activeTab === 'approval_team') fetchApprovalTeamConfig();
    if (activeTab === 'perangkat') fetchDeviceList();
    if (activeTab === 'wajah') fetchWajahList();
  }, [activeTab]);

  const fetchAbsencePeriod = async () => {
    setLoadingPeriod(true);
    try {
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'get_absence_period' }) });
      const data = await res.json();
      if (data.result === 'success') {
        // Backend baru mengirim `periods` (daftar lengkap). Kalau yang
        // menjawab masih backend lama, `periods` tidak ada — jatuh ke
        // `period` tunggal supaya layar ini tetap berguna, bukan kosong.
        if (Array.isArray(data.periods)) {
          setPeriodeList(data.periods.map(normalisasiPeriode));
        } else if (data.period) {
          setPeriodeList([normalisasiPeriode({ ...data.period, aktif: true })]);
        } else {
          setPeriodeList([]);
        }
      } else alert(data.message || 'Gagal memuat periode absensi.');
    } catch (e) { alert('Gagal koneksi saat memuat periode absensi.'); }
    finally { setLoadingPeriod(false); }
  };

  // Logic Tim Approval — mapping kepala divisi ke anggota tim yang berhak
  // approve pengajuannya. Kalau kepala belum punya mapping di sini, backend
  // tetap fallback ke pencocokan divisi lama (lihat handleGetApprovalList).
  const fetchApprovalTeamConfig = async () => {
    setLoadingApprovalTeam(true);
    try {
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'get_approval_team_config', roleRequester: user.role }) });
      const data = await res.json();
      if (data.result === 'success') {
        setApprovalTeamUsers(data.users || []);
        setApprovalTeamAssignments(data.assignments || []);
      } else alert(data.message || 'Gagal memuat tim approval.');
    } catch (e) { alert('Gagal koneksi saat memuat tim approval.'); }
    finally { setLoadingApprovalTeam(false); }
  };

  const handlePilihKepalaTeam = (id) => {
    setAtKepalaId(id);
    setAtDepartemen('');
    setAtMemberIds([]);
    setAtSearch('');
    setAtJabatan('');
    setShowNewDepartemenInput(false);
    setNewDepartemenName('');
  };

  const handleEditGrupApproval = (departemen, anggotaRows) => {
    setAtDepartemen(departemen);
    setAtMemberIds(anggotaRows.map(a => a.anggotaId));
    setAtJabatan((anggotaRows[0] && anggotaRows[0].jabatan) || '');
  };

  const handleToggleAnggotaTeam = (id) => {
    setAtMemberIds(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);
  };

  const handleSaveApprovalTeam = async () => {
    if (!atKepalaId) return alert('Pilih kepala divisi terlebih dahulu.');
    if (!atDepartemen) return alert('Pilih departemen terlebih dahulu.');
    setLoading(true);
    try {
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({
        action: 'save_approval_team_config', roleRequester: user.role,
        kepalaId: atKepalaId, departemen: atDepartemen, memberIds: atMemberIds, jabatan: atJabatan
      }) });
      const data = await res.json();
      if (data.result === 'success') {
        alert(data.message || 'Tim approval berhasil disimpan.');
        setApprovalTeamAssignments(data.assignments || []);
      } else alert(data.message || 'Gagal menyimpan tim approval.');
    } catch (e) { alert('Gagal koneksi saat menyimpan tim approval.'); }
    finally { setLoading(false); }
  };

  const handleHapusGrupApproval = async (departemen) => {
    if (!window.confirm(`Hapus grup approval "${departemen}" untuk kepala ini?`)) return;
    setLoading(true);
    try {
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({
        action: 'save_approval_team_config', roleRequester: user.role,
        kepalaId: atKepalaId, departemen: departemen, memberIds: []
      }) });
      const data = await res.json();
      if (data.result === 'success') {
        setApprovalTeamAssignments(data.assignments || []);
        if (atDepartemen === departemen) { setAtDepartemen(''); setAtMemberIds([]); }
      } else alert(data.message || 'Gagal menghapus grup approval.');
    } catch (e) { alert('Gagal koneksi saat menghapus grup approval.'); }
    finally { setLoading(false); }
  };

  // Jadikan seorang user (yang rolenya belum "approval") sebagai kepala
  // divisi, LANGSUNG sekalian simpan tim (departemen + anggota yang sudah
  // dicentang + jabatan tampilan) dalam satu aksi — supaya admin tidak perlu
  // dua langkah terpisah (ubah role, lalu buka form lagi untuk isi tim).
  const handleJadikanKepalaDivisi = async () => {
    if (!atKepalaId) return;
    if (!atDepartemen) return alert('Pilih departemen yang akan dipimpin terlebih dahulu.');
    setSavingApprovalRole(true);
    try {
      const resRole = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({
        action: 'set_approval_role', roleRequester: user.role,
        targetUuid: atKepalaId, newRole: String(atNewRole || '').trim().toLowerCase()
      }) });
      const dataRole = await resRole.json();
      if (dataRole.result !== 'success') {
        alert(dataRole.message || 'Gagal menjadikan user sebagai kepala divisi.');
        return;
      }

      if (atMemberIds.length > 0) {
        const resTeam = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({
          action: 'save_approval_team_config', roleRequester: user.role,
          kepalaId: atKepalaId, departemen: atDepartemen, memberIds: atMemberIds, jabatan: atJabatan
        }) });
        const dataTeam = await resTeam.json();
        if (dataTeam.result !== 'success') {
          alert('Role berhasil diubah, tapi tim gagal disimpan: ' + (dataTeam.message || 'error tidak diketahui'));
        } else {
          alert(`${dataTeam.message || 'Berhasil.'}`);
        }
      } else {
        alert('Role berhasil diubah. Centang anggota tim di bawah lalu Simpan untuk melengkapi timnya.');
      }
      await fetchApprovalTeamConfig();
    } catch (e) { alert('Gagal koneksi saat mengubah role.'); }
    finally { setSavingApprovalRole(false); }
  };

  // Tambah departemen baru ke Master Data (kategori "Divisi") langsung dari
  // kartu Tim Approval, tanpa admin harus pindah ke tab Master Data.
  const handleTambahDepartemenBaru = async () => {
    const nama = newDepartemenName.trim();
    if (!nama) return;
    setAddingDepartemen(true);
    try {
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({
        action: 'tambah_master', roleRequester: user.role, kategori: 'Divisi', value: nama, label: nama
      }) });
      const data = await res.json();
      if (data.result === 'success') {
        const sudahAda = (masterData.divisions || []).some(d => String(d.value).toLowerCase() === nama.toLowerCase());
        const updatedDivisions = sudahAda ? masterData.divisions : [...(masterData.divisions || []), { kategori: 'Divisi', value: nama, label: nama }];
        const updatedMasterData = { ...masterData, divisions: updatedDivisions };
        if (typeof setMasterData === 'function') setMasterData(updatedMasterData);
        try { sessionStorage.setItem('app_master_data', JSON.stringify(updatedMasterData)); } catch (e) { /* abaikan */ }
        setAtDepartemen(nama);
        setNewDepartemenName('');
        setShowNewDepartemenInput(false);
      } else alert(data.message || 'Gagal menambah departemen.');
    } catch (e) { alert('Gagal koneksi saat menambah departemen.'); }
    finally { setAddingDepartemen(false); }
  };

  // Menyusun satu baris periode ke bentuk yang dipakai layar ini.
  // `key` dipakai React untuk membedakan baris; sengaja TIDAK memakai id
  // dari backend, karena id backend berubah begitu tanggalnya diedit dan
  // React akan membuang state input yang sedang diketik.
  const normalisasiPeriode = (p) => ({
    key: p.key || `${p.mulai || ''}_${p.selesai || ''}_${Math.random().toString(36).slice(2, 8)}`,
    mulai: p.mulai || '',
    selesai: p.selesai || '',
    aktif: p.aktif !== false
  });

  const ubahPeriode = (key, patch) =>
    setPeriodeList(list => list.map(p => (p.key === key ? { ...p, ...patch } : p)));

  const hapusPeriode = (key) =>
    setPeriodeList(list => list.filter(p => p.key !== key));

  // Menggeser satu tanggal maju sejumlah bulan, dengan pengaman akhir bulan
  // (31 Jan + 1 bulan -> 28/29 Feb, bukan meluber ke Maret).
  const geserBulan = (ymd, n) => {
    const [y, mo, d] = ymd.split('-').map(Number);
    const hariTerakhir = new Date(Date.UTC(y, mo - 1 + n + 1, 0)).getUTCDate();
    return new Date(Date.UTC(y, mo - 1 + n, Math.min(d, hariTerakhir))).toISOString().slice(0, 10);
  };

  // Mengisi baris draft dengan periode BERIKUTNYA, digeser satu BULAN dari
  // periode terbaru yang sudah ada.
  //
  // Sengaja tidak memakai "panjang periode dalam hari": untuk siklus 21-20
  // panjangnya berbeda tiap bulan (30, 31, 28), sehingga cara itu meleset
  // makin jauh setiap kali ditambah — 21 Jan-20 Feb akan menghasilkan
  // 21 Feb-23 Mar, bukan 21 Feb-20 Mar. Aritmetika bulan selalu tepat.
  const isiOtomatisDraft = () => {
    const urut = [...periodeList].filter(p => p.mulai && p.selesai).sort((a, b) => (a.mulai < b.mulai ? 1 : -1));
    if (!urut.length) return;
    ubahDraft({ mulai: geserBulan(urut[0].mulai, 1), selesai: geserBulan(urut[0].selesai, 1) });
  };

  // Setiap perubahan pada baris draft diperiksa: kalau sudah lengkap dan
  // tanggalnya masuk akal, baris itu langsung menjadi periode baru di
  // daftar dan draft dikosongkan kembali.
  //
  // Pemeriksaan `mulai <= selesai` penting di sini: tanpa itu, mengetik
  // tanggal selesai lebih dulu akan langsung membuat periode terbalik yang
  // tidak sempat diperbaiki karena barisnya sudah telanjur pindah.
  //
  // CATATAN: penambahan ke daftar TIDAK boleh dilakukan di dalam updater
  // setPeriodeDraft(draft => ...). React memanggil updater dua kali di
  // StrictMode (mode pengembangan), sehingga satu pengisian akan membuat
  // DUA periode kembar. Updater wajib murni — efeknya ditaruh di luar.
  const ubahDraft = (patch) => {
    const baru = { ...periodeDraft, ...patch };
    if (baru.mulai && baru.selesai && baru.mulai <= baru.selesai) {
      setPeriodeList(list => [normalisasiPeriode(baru), ...list]);
      setPeriodeDraft({ mulai: '', selesai: '', aktif: true });
    } else {
      setPeriodeDraft(baru);
    }
  };

  // Deteksi tumpang tindih antar periode AKTIF, ditampilkan langsung di
  // layar. Backend juga menolaknya, tapi memberi tahu di sini lebih baik
  // daripada membiarkan admin menekan Simpan lalu ditolak.
  const periodeBertabrakan = (() => {
    const aktif = periodeList
      .filter(p => p.aktif && p.mulai && p.selesai && p.mulai <= p.selesai)
      .sort((a, b) => (a.mulai < b.mulai ? -1 : 1));
    const bentrok = new Set();
    for (let i = 1; i < aktif.length; i++) {
      if (aktif[i].mulai <= aktif[i - 1].selesai) {
        bentrok.add(aktif[i].key);
        bentrok.add(aktif[i - 1].key);
      }
    }
    return bentrok;
  })();

  const handleSaveAbsencePeriod = async (e) => {
    e.preventDefault();

    for (const p of periodeList) {
      if (!p.mulai || !p.selesai) return alert('Masih ada periode yang tanggalnya belum lengkap.');
      if (p.mulai > p.selesai) return alert(`Periode ${p.mulai}: tanggal mulai tidak boleh setelah tanggal selesai.`);
    }
    if (periodeBertabrakan.size) {
      return alert('Ada periode aktif yang tanggalnya tumpang tindih. Nonaktifkan salah satunya atau perbaiki tanggalnya.');
    }
    if (!periodeList.some(p => p.aktif)) {
      if (!window.confirm('Tidak ada periode yang aktif. Dashboard karyawan akan memakai siklus standar tanggal 21–20. Lanjutkan?')) return;
    }

    setSavingPeriod(true);
    try {
      const res = await fetchApi(SCRIPT_URL, {
        method: 'POST',
        body: JSON.stringify({
          action: 'save_absence_periods',
          periods: periodeList.map(p => ({ mulai: p.mulai, selesai: p.selesai, aktif: p.aktif }))
        })
      });
      const data = await res.json();
      if (data.result === 'success') {
        if (Array.isArray(data.periods)) setPeriodeList(data.periods.map(normalisasiPeriode));
        alert(data.message || 'Daftar periode berhasil disimpan.');
      } else alert(data.message || 'Gagal menyimpan periode absensi.');
    } catch (e) { alert('Gagal koneksi saat menyimpan periode absensi.'); }
    finally { setSavingPeriod(false); }
  };

  const applyGeofenceUser = (userId, configs = geofenceConfigs, bebasList = gpsBebasList, modeMap = modeAbsenMap) => {
    const cfg = configs[String(userId)] || { required: false, areas: [] };
    setGeofenceUserId(String(userId || ''));
    setModeAbsenPilih((modeMap || {})[String(userId || '')] || 'gabungan');
    setGeofenceRequired(!!cfg.required);
    setGpsBebasUser((bebasList || []).indexOf(String(userId || '')) !== -1);
    // Area bermasterID ditampilkan sebagai centang; sisanya area manual lama.
    setGeofenceMasterIds((cfg.areas || []).filter(a => a.masterId).map(a => String(a.masterId)));
    setGeofenceAreas((cfg.areas || []).filter(a => !a.masterId).map(area => ({
      nama: area.nama || 'Area kantor', lat: area.lat, lng: area.lng,
      radius: area.radius || 150, aktif: area.aktif !== false
    })));
  };

  const fetchGeofenceConfig = async () => {
    setLoadingGeofence(true);
    try {
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'get_geofence_config', roleRequester: user.role }) });
      const data = await res.json();
      if (data.result === 'success') {
        const users = data.users || [];
        const configs = data.configs || {};
        const bebas = (data.gpsBebas || []).map(String);
        const modeMap = data.modeAbsen || {};
        setGeofenceUsers(users); setGeofenceConfigs(configs); setGpsBebasList(bebas); setModeAbsenMap(modeMap);
        const master = data.master || [];
        setGeoMaster(master);
        setGeoMasterDraft(master.map(m => ({ ...m })));
        if (users.length) applyGeofenceUser(geofenceUserId || users[0].id, configs, bebas, modeMap);
      } else alert(data.message || 'Gagal memuat konfigurasi geofence.');
    } catch (e) { alert('Gagal koneksi saat memuat geofence.'); }
    finally { setLoadingGeofence(false); }
  };

  const handleGeofenceUserChange = (e) => applyGeofenceUser(e.target.value);
  const handleAddGeofenceArea = () => setGeofenceAreas(prev => [...prev, { nama: '', lat: '', lng: '', radius: 150, aktif: true }]);
  const handleUpdateGeofenceArea = (index, field, value) => setGeofenceAreas(prev => prev.map((area, i) => i === index ? { ...area, [field]: value } : area));
  const handleRemoveGeofenceArea = (index) => setGeofenceAreas(prev => prev.filter((_, i) => i !== index));
  const toggleGeofenceMaster = (id) => setGeofenceMasterIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const handleAddGeoMaster = () => setGeoMasterDraft(prev => [...prev, { id: '', nama: '', link: '', lat: '', lng: '', radius: 150, aktif: true }]);
  const handleUpdateGeoMaster = (index, field, value) => setGeoMasterDraft(prev => prev.map((m, i) => i === index ? { ...m, [field]: value } : m));
  const handleRemoveGeoMaster = (index) => {
    const m = geoMasterDraft[index];
    if (m.id && !window.confirm(`Hapus lokasi "${m.nama || 'ini'}"? Karyawan yang memakainya kehilangan lokasi ini setelah disimpan.`)) return;
    setGeoMasterDraft(prev => prev.filter((_, i) => i !== index));
  };
  const handleSaveGeoMaster = async () => {
    setLoading(true);
    try {
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'save_geofence_master', roleRequester: user.role, locations: geoMasterDraft }) });
      const data = await res.json();
      if (data.result === 'success') { alert(data.message); fetchGeofenceConfig(); }
      else alert(data.message || 'Gagal menyimpan master geofence.');
    } catch (e) { alert('Gagal koneksi saat menyimpan master geofence.'); }
    finally { setLoading(false); }
  };
  const handleApplyGeofenceDivisi = async () => {
    if (!geofenceDivisi) return alert('Pilih divisi terlebih dahulu.');
    if (divisiMasterIds.length === 0) return alert('Centang minimal satu lokasi di atas.');
    if (!window.confirm(`Terapkan ${divisiMasterIds.length} lokasi terpilih (wajib geofence) ke SEMUA karyawan divisi "${geofenceDivisi}"? Pilihan lokasi master mereka sebelumnya diganti.`)) return;
    setLoading(true);
    try {
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'apply_geofence_divisi', roleRequester: user.role, divisi: geofenceDivisi, required: true, masterIds: divisiMasterIds }) });
      const data = await res.json();
      if (data.result === 'success') { alert(data.message); fetchGeofenceConfig(); }
      else alert(data.message || 'Gagal menerapkan geofence.');
    } catch (e) { alert('Gagal koneksi saat menerapkan geofence.'); }
    finally { setLoading(false); }
  };
  const handleApplyModeDivisi = async () => {
    if (!modeDivisi) return alert('Pilih divisi terlebih dahulu.');
    const label = (OPSI_MODE_ABSEN.find(o => o.value === modeDivisiNilai) || {}).label || modeDivisiNilai;
    if (!window.confirm(`Atur mode absen "${label}" untuk SEMUA karyawan divisi "${modeDivisi}"?`)) return;
    setLoading(true);
    try {
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'apply_mode_absen_divisi', roleRequester: user.role, divisi: modeDivisi, modeAbsen: modeDivisiNilai }) });
      const data = await res.json();
      if (data.result === 'success') { alert(data.message); fetchGeofenceConfig(); }
      else alert(data.message || 'Gagal menerapkan mode absen.');
    } catch (e) { alert('Gagal koneksi saat menerapkan mode absen.'); }
    finally { setLoading(false); }
  };
  const handleSaveGeofence = async () => {
    if (!geofenceUserId) return alert('Pilih karyawan terlebih dahulu.');
    if (geofenceRequired && geofenceAreas.length === 0 && geofenceMasterIds.length === 0) return alert('Pilih minimal satu lokasi geofence.');
    if (gpsBebasUser && geofenceRequired) return alert('Tidak bisa keduanya: karyawan yang dikecualikan dari gerbang GPS tidak punya lokasi untuk dicocokkan dengan area kantor.');
    if (gpsBebasUser && !window.confirm('Karyawan ini akan bisa absen TANPA lokasi sama sekali.\n\nPakai ini hanya untuk staf yang memang bekerja di PC tanpa GPS. Setiap absennya akan ditandai "tanpa lokasi" di Audit GPS.\n\nLanjutkan?')) return;
    setLoading(true);
    try {
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({
        action: 'save_geofence_config', roleRequester: user.role, userId: geofenceUserId,
        required: geofenceRequired, areas: geofenceAreas, masterIds: geofenceMasterIds, gpsGerbangBebas: gpsBebasUser,
        modeAbsen: modeAbsenPilih
      }) });
      const data = await res.json();
      if (data.result === 'success') { alert(data.message); fetchGeofenceConfig(); }
      else alert(data.message || 'Gagal menyimpan geofence.');
    } catch (e) { alert('Gagal koneksi saat menyimpan geofence.'); }
    finally { setLoading(false); }
  };

  const handleResetPassword = async (uuid, namaUser) => {
    if(!window.confirm(`Reset password "${namaUser}" jadi "123"?`)) return;
    setLoading(true);
    try {
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'reset_password_user', roleRequester: user.role, targetUuid: uuid }) });
      const data = await res.json();
      alert(data.message);
    } catch (e) { alert("Gagal koneksi."); } finally { setLoading(false); }
  };

  const handleAddMaster = async (e) => { 
    e.preventDefault(); setLoading(true);
    try { const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'tambah_master', roleRequester: user.role, ...masterInput }) }).then(r=>r.json());
      if(res.result === 'success') { alert('Data Ditambah!'); setMasterInput({ kategori: 'Menu', value: '', label: '' }); } else alert(res.message); 
    } catch(e) { alert('Error'); } finally { setLoading(false); } 
  };
  const handleAddAnnouncement = async () => {
    if (!newsInput.trim()) return alert("Isi kosong!");
    setLoading(true);
    try {
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'tambah_announcement', roleRequester: user.role, isi: newsInput }) }).then(r => r.json());
      if (res.result === 'success') { alert("Terbit!"); setNewsInput(''); } else { alert(res.message); }
    } catch (e) { alert("Gagal koneksi."); } finally { setLoading(false); }
  };


  // Turunan data Tim Approval — kepala divisi boleh dipilih dari SEMUA user
  // (bukan cuma yang rolenya sudah approval), karena admin bisa langsung
  // menjadikan siapapun kepala divisi dari layar ini.
  const approvalTeamKepalaOptions = approvalTeamUsers.filter(u =>
    (u.nama || '').toLowerCase().includes(atKepalaSearch.toLowerCase())
  );
  const kepalaTeamTerpilih = approvalTeamUsers.find(u => String(u.uuid) === String(atKepalaId)) || null;
  const kepalaSudahApproval = !!kepalaTeamTerpilih && isApprovalRole(kepalaTeamTerpilih.role);
  // Sengaja TIDAK memakai masterData.roles di sini — value di Master Data itu
  // bebas diisi admin (mis. label saja tanpa slug baku), jadi tidak terjamin
  // cocok dengan value yang divalidasi backend. Daftar di bawah ini persis
  // sama dengan APPROVAL_HEAD_ROLE_VALUES di Code.gs.
  const approvalHeadRoleChoices = APPROVAL_HEAD_ROLES.map(v => ({ value: v, label: ROLE_LABEL_FALLBACK[v] || v }));
  const grupApprovalUntukKepala = approvalTeamAssignments
    .filter(a => String(a.kepalaId) === String(atKepalaId))
    .reduce((acc, a) => {
      if (!acc[a.departemen]) acc[a.departemen] = [];
      acc[a.departemen].push(a);
      return acc;
    }, {});
  const atAnggotaOptions = approvalTeamUsers.filter(u =>
    String(u.uuid) !== String(atKepalaId) &&
    (u.nama || '').toLowerCase().includes(atSearch.toLowerCase())
  );

  // ============================================================
  // PERANGKAT — 1 perangkat 1 akun, kuota lebih untuk HP milik PIC
  // ============================================================
  const fetchDeviceList = async () => {
    setLoadingDevice(true);
    try {
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'get_device_list' }) });
      const data = await res.json();
      if (data.result === 'success') {
        setDeviceBelumAda(false);
        setDeviceList(data.devices || []);
        setDeviceSesi(data.sesi || []);
        setDeviceMode(data.mode || 'tandai');
      } else if (aksiBelumAdaDiBackend(data)) {
        // Ditampilkan sebagai kartu di dalam tab, bukan alert: admin perlu
        // membaca langkah-langkahnya sambil melihat layarnya.
        setDeviceBelumAda(true);
        setDeviceList([]);
        setDeviceSesi([]);
      } else alert(data.message || 'Gagal memuat daftar perangkat.');
    } catch (e) { alert('Gagal koneksi saat memuat daftar perangkat.'); }
    finally { setLoadingDevice(false); }
  };

  const fetchDeviceAudit = async () => {
    try {
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'get_device_audit', limit: 150 }) });
      const data = await res.json();
      if (data.result === 'success') setDeviceAudit(data.log || []);
    } catch (e) { /* riwayat gagal dimuat tidak boleh mengunci layar */ }
  };

  const handleUbahDeviceMode = async (modeBaru) => {
    if (modeBaru === 'ketat' && !window.confirm(
      'Mode KETAT menolak login dari perangkat yang kuotanya sudah penuh.\n\n'
      + 'Karyawan yang berganti HP tidak akan bisa masuk sampai admin melepas ikatan perangkat lamanya. '
      + 'Nyalakan ini hanya setelah daftar perangkat di bawah terlihat wajar.\n\nLanjutkan?'
    )) return;
    setLoading(true);
    try {
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'set_device_mode', mode: modeBaru }) });
      const data = await res.json();
      if (data.result === 'success') { setDeviceMode(data.mode); alert(data.message); }
      else if (aksiBelumAdaDiBackend(data)) alert(PESAN_BACKEND_LAMA);
      else alert(data.message || 'Gagal mengubah mode.');
    } catch (e) { alert('Gagal koneksi saat mengubah mode perangkat.'); }
    finally { setLoading(false); }
  };

  const handleSimpanKuotaDevice = async (deviceId) => {
    const kuota = deviceKuotaDraft[deviceId];
    if (kuota === undefined || kuota === '') return;
    setLoading(true);
    try {
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'save_device_config', deviceId, kuota }) });
      const data = await res.json();
      if (data.result === 'success') {
        setDeviceList(prev => prev.map(d => d.deviceId === deviceId ? { ...d, kuota: parseInt(kuota, 10) } : d));
        setDeviceKuotaDraft(prev => { const n = { ...prev }; delete n[deviceId]; return n; });
      } else alert(data.message || 'Gagal menyimpan kuota.');
    } catch (e) { alert('Gagal koneksi saat menyimpan kuota.'); }
    finally { setLoading(false); }
  };

  const handleUbahStatusDevice = async (deviceId, statusBaru) => {
    setLoading(true);
    try {
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'save_device_config', deviceId, status: statusBaru }) });
      const data = await res.json();
      if (data.result === 'success') {
        setDeviceList(prev => prev.map(d => d.deviceId === deviceId ? { ...d, status: statusBaru } : d));
      } else alert(data.message || 'Gagal mengubah status perangkat.');
    } catch (e) { alert('Gagal koneksi saat mengubah status perangkat.'); }
    finally { setLoading(false); }
  };

  const handleLepasIkatan = async (deviceId, targetUserId, nama) => {
    if (!window.confirm(`Lepas ikatan ${nama} dari perangkat ini?\n\nSesi aktifnya ikut dicabut, jadi ia harus login ulang.`)) return;
    setLoading(true);
    try {
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'lepas_device', deviceId, targetUserId }) });
      const data = await res.json();
      alert(data.message || (data.result === 'success' ? 'Ikatan dilepas.' : 'Gagal melepas ikatan.'));
      if (data.result === 'success') fetchDeviceList();
    } catch (e) { alert('Gagal koneksi saat melepas ikatan.'); }
    finally { setLoading(false); }
  };

  const handleCabutSesi = async (targetUserId, nama) => {
    if (!window.confirm(`Paksa logout ${nama}? Ikatan perangkatnya tidak dihapus.`)) return;
    setLoading(true);
    try {
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'cabut_sesi_user', targetUserId }) });
      const data = await res.json();
      alert(data.message || 'Selesai.');
      if (data.result === 'success') fetchDeviceList();
    } catch (e) { alert('Gagal koneksi saat mencabut sesi.'); }
    finally { setLoading(false); }
  };

  // ============================================================
  // WAJAH ACUAN KARYAWAN
  // ============================================================
  const fetchWajahList = async () => {
    setLoadingWajah(true);
    try {
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'get_wajah_list' }) });
      const data = await res.json();
      if (data.result === 'success') {
        setWajahBelumAda(false);
        setWajahList(data.list || []);
        setWajahKonfig({
          mode: data.mode || 'ketat',
          ambang: data.ambang || 0.52,
          wajibTerdaftar: !!data.wajibTerdaftar
        });
      } else if (aksiBelumAdaDiBackend(data)) {
        setWajahBelumAda(true);
        setWajahList([]);
      } else alert(data.message || 'Gagal memuat data wajah.');
    } catch (e) { alert('Gagal koneksi saat memuat data wajah.'); }
    finally { setLoadingWajah(false); }
  };

  const handleSimpanKonfigWajah = async (perubahan) => {
    setLoading(true);
    try {
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'set_face_config', ...perubahan }) });
      const data = await res.json();
      if (data.result === 'success') {
        setWajahKonfig({ mode: data.mode, ambang: data.ambang, wajibTerdaftar: !!data.wajibTerdaftar });
      } else if (aksiBelumAdaDiBackend(data)) alert(PESAN_BACKEND_LAMA);
      else alert(data.message || 'Gagal menyimpan pengaturan.');
    } catch (e) { alert('Gagal koneksi saat menyimpan pengaturan wajah.'); }
    finally { setLoading(false); }
  };

  // Berkas foto -> elemen <img> -> deskriptor. Analisisnya dikerjakan di
  // BROWSER ADMIN, bukan di server: Apps Script tidak bisa menjalankan
  // model neural, dan mengirim foto mentah ke sana hanya akan menumpuk
  // berkas besar tanpa gunanya.
  const bacaBerkasKeGambar = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Berkas tidak terbaca.'));
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve({ img, dataUrl: reader.result });
      img.onerror = () => reject(new Error('Berkas ini bukan gambar yang bisa dibuka.'));
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

  const handlePilihFotoWajah = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3);
    e.target.value = ''; // supaya memilih berkas yang sama lagi tetap memicu onChange
    if (!files.length) return;
    if (!wajahTargetId) { alert('Pilih karyawan terlebih dahulu.'); return; }

    setWajahProses('Menganalisis foto…');
    const hasil = [];
    for (let i = 0; i < files.length; i++) {
      try {
        setWajahProses(`Menganalisis foto ${i + 1} dari ${files.length}…`);
        const { img, dataUrl } = await bacaBerkasKeGambar(files[i]);
        const d = await deskriptorDariGambar(img);
        hasil.push({ nama: files[i].name, ok: d.ok, pesan: d.pesan, deskriptor: d.deskriptor, dataUrl: d.ok ? dataUrl : null });
      } catch (err) {
        hasil.push({ nama: files[i].name, ok: false, pesan: err.message, deskriptor: null, dataUrl: null });
      }
    }
    setWajahFileInfo(hasil);
    setWajahProses('');
  };

  const handleSimpanWajah = async () => {
    const valid = wajahFileInfo.filter(f => f.ok && f.deskriptor);
    if (!wajahTargetId) { alert('Pilih karyawan terlebih dahulu.'); return; }
    if (!valid.length) { alert('Belum ada foto yang berhasil dianalisis.'); return; }

    const target = wajahList.find(u => u.userId === wajahTargetId);
    setLoading(true);
    setWajahProses('Menyimpan…');
    try {
      const res = await fetchApi(SCRIPT_URL, {
        method: 'POST',
        body: JSON.stringify({
          action: 'daftar_wajah',
          targetUserId: wajahTargetId,
          targetNama: target ? target.nama : '',
          descriptors: valid.map(f => f.deskriptor),
          // Hanya foto pertama yang disimpan, sekadar agar admin bisa
          // melihat wajah siapa yang terdaftar. Bukan ini yang dipakai
          // mencocokkan — yang dipakai adalah deskriptornya.
          fotoAcuan: valid[0].dataUrl || ''
        })
      });
      const data = await res.json();
      alert(data.message || (data.result === 'success' ? 'Tersimpan.' : 'Gagal menyimpan.'));
      if (data.result === 'success') { setWajahFileInfo([]); fetchWajahList(); }
    } catch (e) { alert('Gagal koneksi saat menyimpan wajah acuan.'); }
    finally { setLoading(false); setWajahProses(''); }
  };

  const handleHapusWajah = async (userId, nama) => {
    if (!window.confirm(`Hapus wajah acuan ${nama}?\n\nIa tidak akan bisa presensi Masuk/Pulang bila pengaturan "wajib terdaftar" sedang aktif.`)) return;
    setLoading(true);
    try {
      const res = await fetchApi(SCRIPT_URL, { method: 'POST', body: JSON.stringify({ action: 'hapus_wajah', targetUserId: userId }) });
      const data = await res.json();
      alert(data.message || 'Selesai.');
      if (data.result === 'success') fetchWajahList();
    } catch (e) { alert('Gagal koneksi saat menghapus wajah acuan.'); }
    finally { setLoading(false); }
  };

  const switchTab = (tabName) => { setActiveTab(tabName); setIsMenuOpen(false); };

  // Satu gaya input untuk seluruh panel — sebelumnya tiap tab memakai
  // padding, radius, dan warna border yang berbeda-beda.
  const inputCls = "w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-[13px] text-slate-900 placeholder:text-slate-400 outline-none transition-shadow focus:border-slate-400 focus:ring-2 focus:ring-slate-900/5";

  // ---------------------------------------------------------------
  // DAFTAR MENU
  //
  // Dulu tiap item ditulis sebagai satu blok <button> tersendiri —
  // empat belas blok yang isinya nyaris identik, dan menambah satu menu
  // berarti menyalin selusin baris kelas Tailwind beserta peluang salah
  // ketiknya. Sekarang bentuknya data: menambah menu cukup satu baris.
  //
  //   tab  -> berpindah tab DI DALAM panel ini
  //   view -> keluar dari panel ini ke layar lain
  //
  // Kelompoknya mengikuti urusan, bukan urutan ditambahkannya fitur.
  // "Laporan & Monitoring" sengaja dipisah dari "Data": ketiganya
  // meninggalkan panel ini, sedangkan isi "Data" tetap di dalamnya.
  // ---------------------------------------------------------------
  const menuKelompok = [
    {
      judul: 'Pengguna',
      item: [
        // Satu menu: User & Akses (termasuk Tambah User) + Role & Lokasi.
        // Tampil bila salah satu dari tiga kunci menu lamanya diizinkan.
        { tab: 'master_user', label: 'Master User', ikon: UserRoundPlus, adminSaja: true, menuSalahSatu: ['master_user', 'user', 'master_role'] },
      ]
    },
    {
      judul: 'Data',
      adminSaja: true,
      item: [
        { view: 'analysis', label: 'Analisa data', ikon: ChartColumn, pusat: true },
        { tab: 'master', label: 'Master data', ikon: Database, pusat: true },
        { tab: 'import_db', label: 'Import data mesin absen', ikon: FileUp, pusat: true },
        { tab: 'bebas_absen', label: 'Bebas absen & karyawan harian', ikon: UserCheck, pusat: true },
        { tab: 'geomaster', label: 'Master lokasi geofence', ikon: MapPin, pusat: true },
        { tab: 'geofence', label: 'Akses absen & geofence', ikon: LocateFixed, pusat: true },
        { tab: 'period', label: 'Periode absensi', ikon: CalendarRange, pusat: true },
        { tab: 'approval_team', label: 'Master Tim Approval', ikon: UsersRound, pusat: true },
        { tab: 'perangkat', label: 'Perangkat & sesi login', ikon: Smartphone, pusat: true },
        { tab: 'wajah', label: 'Wajah karyawan', ikon: ScanFace, pusat: true },
        { tab: 'board', label: 'Board absensi', ikon: FileSpreadsheet, pusat: true },
      ]
    },
    {
      judul: 'Laporan & Monitoring',
      item: [
        // `hrd: <kunci menu>` = boleh tampil untuk HRD bila menu itu diizinkan master role.
        { view: 'rekap_admin', label: 'Rekap, Koreksi & Export Excel', ikon: FileSpreadsheet, warna: 'emerald', hrd: 'rekap', menu: 'rekap' },
        { view: 'gps_dashboard', label: 'Dashboard GPS Karyawan', ikon: MapPin, warna: 'sky', adminSaja: true, menu: 'gps' },
        { view: 'gps_audit', label: 'Monitoring Integritas GPS', ikon: Shield, warna: 'amber', adminSaja: true },
        { view: 'dashboard_kerani', label: 'Absen Online Kerani Pabrik', ikon: ClipboardList, warna: 'emerald', adminSaja: true, pusat: true },
        { view: 'log_login', label: 'Log Login Pengguna', ikon: KeyRound, warna: 'sky', adminSaja: true, pusat: true },
      ]
    },
    {
      judul: 'Komunikasi',
      item: [
        { tab: 'news', label: 'Info HRD', ikon: Megaphone, hrd: 'info_hrd', menu: 'info_hrd' },
      ]
    },
  ];

  // Admin melihat semuanya; HRD hanya item bertanda `hrd` yang menunya
  // diizinkan master role (lihat normalisasiPeran / menuDiizinkan).
  // Admin bercakupan lokasi (mis. Admin Jakarta): item `pusat` disembunyikan.
  const panelAdmin = user.role === 'admin';
  const adminLokasi = adminTerbatas(user);
  // MENU PER USER (Master user): kunci menu item = `menu`, atau tab/view-nya.
  const kunciBoleh = (it) => (it.menuSalahSatu
    ? it.menuSalahSatu.some((k) => menuDiizinkan(user, k))
    : menuDiizinkan(user, it.menu || it.tab || it.view));
  // Item `pusat` tetap tampil untuk admin lokasi bila Administrator
  // mencentangnya di Master User > Akses menu (menuAkses memuat kuncinya).
  // Pengecualian: bebas_absen & master_role ditulis lewat Edge Function
  // Supabase yang khusus Admin pusat, jadi tetap tersembunyi.
  const dicentangManual = (it) => {
    const k = it.menu || it.tab || it.view;
    return k !== 'bebas_absen' && k !== 'master_role' && Array.isArray(user.menuAkses) && user.menuAkses.includes(k);
  };
  const itemBolehTampil = (it) => (panelAdmin && !(adminLokasi && it.pusat && !dicentangManual(it)) && kunciBoleh(it))
    || (!it.adminSaja && !!it.hrd && menuDiizinkan(user, it.hrd));
  // Tab pembuka (mis. "Tambah user") bisa saja tidak diizinkan untuk user
  // ini -> pindah ke tab pertama yang boleh tampil.
  const tabBoleh = menuKelompok
    .filter((k) => !(k.adminSaja && !panelAdmin))
    .flatMap((k) => k.item.filter(itemBolehTampil))
    .filter((it) => it.tab).map((it) => it.tab);
  const tabBolehKunci = tabBoleh.join('|');
  useEffect(() => {
    const daftar = tabBolehKunci ? tabBolehKunci.split('|') : [];
    if (daftar.length && !daftar.includes(activeTab)) setActiveTab(daftar[0]);
  }, [tabBolehKunci, activeTab]);

  // Fungsi biasa, BUKAN komponen React. Komponen yang didefinisikan di
  // dalam render akan di-mount ulang setiap kali panel ini render, dan
  // itu membuang state anaknya tanpa alasan.
  const renderItemMenu = (it, idx) => {
    const aktif = it.tab && activeTab === it.tab;
    const Ikon = it.ikon;
    const warnaIkon = aktif ? 'text-white'
      : it.warna === 'emerald' ? 'text-emerald-600'
      : it.warna === 'sky' ? 'text-sky-600'
      : it.warna === 'amber' ? 'text-amber-600'
      : 'text-slate-400';

    return (
      <button
        key={it.tab || it.view || idx}
        onClick={() => { if (it.tab) { switchTab(it.tab); } else { setView(it.view); } setIsMenuOpen(false); }}
        className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] text-left transition-colors
          ${aktif ? 'bg-slate-900 text-white font-medium' : 'text-slate-600 hover:bg-slate-100'}`}
      >
        <Ikon className={`w-[17px] h-[17px] shrink-0 ${warnaIkon}`} strokeWidth={1.75} />
        <span className="flex-1 leading-tight">{it.label}</span>
        {it.view && <ChevronRight className={`w-3.5 h-3.5 shrink-0 ${aktif ? 'text-white/70' : 'text-slate-300'}`} />}
      </button>
    );
  };

  const getPageTitle = () => {
      switch(activeTab) {
          case 'master_user': return 'Master User';
          case 'master': return 'Master Data';
          case 'import_db': return 'Import Data Mesin Absen';
          case 'news': return 'Broadcast Info HRD';
          case 'geofence': return 'Akses Absen & Geofence';
          case 'geomaster': return 'Master Lokasi Geofence';
          case 'period': return 'Periode Absensi';
          case 'approval_team': return 'Master Tim Approval';
          case 'board': return 'Board Absensi';
          case 'perangkat': return 'Perangkat & Sesi Login';
          case 'wajah': return 'Wajah Karyawan';
          case 'bebas_absen': return 'Bebas Absen Mesin & Karyawan Harian';
          default: return 'Admin Panel';
      }
  };

  return (
    <div className="min-h-screen bg-gray-50 lg:flex">

      {/* ================= SIDEBAR =================
          Di layar lebar ia menetap di kiri (lg:static). Di layar sempit
          ia menjadi laci yang menggeser masuk — karena panel ini juga
          dibuka dari HP, dan sidebar tetap selebar 264px di sana akan
          memakan separuh layar. */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-[264px] bg-white border-r border-slate-200 flex flex-col
          transform transition-transform duration-200 lg:static lg:translate-x-0 lg:shrink-0
          ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>

        <div className="flex items-center gap-2.5 px-4 h-[60px] border-b border-slate-100 shrink-0">
          <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center shrink-0">
            <SlidersHorizontal className="w-4 h-4 text-white" strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold text-slate-900 leading-tight truncate">Admin Panel</p>
            <p className="text-[11px] text-slate-400 leading-tight truncate">{user.nama || user.username || 'Administrator'}</p>
          </div>
          <button onClick={() => setIsMenuOpen(false)} className="lg:hidden p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 transition-colors" aria-label="Tutup menu">
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-2.5 py-3 space-y-4">
          {menuKelompok.map((k) => {
            if (k.adminSaja && !panelAdmin) return null;
            const item = k.item.filter(itemBolehTampil);
            if (!item.length) return null;
            return (
              <div key={k.judul}>
                <p className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">{k.judul}</p>
                <div className="space-y-0.5">{item.map(renderItemMenu)}</div>
              </div>
            );
          })}
        </nav>

        <div className="px-2.5 py-3 border-t border-slate-100 shrink-0">
          <button onClick={() => setView('dashboard')} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] text-slate-600 hover:bg-slate-100 transition-colors">
            <ChevronLeft className="w-[17px] h-[17px] shrink-0 text-slate-400" strokeWidth={1.75} />
            <span className="flex-1 text-left leading-tight">Kembali ke dashboard</span>
          </button>
        </div>
      </aside>

      {/* Latar gelap hanya di layar sempit; di layar lebar sidebar memang
          selalu terlihat sehingga tidak ada yang perlu ditutup. */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-40 bg-slate-900/40 lg:hidden" onClick={() => setIsMenuOpen(false)} />
      )}

      {/* ================= KONTEN ================= */}
      <main className="flex-1 min-w-0 p-4 pb-20 lg:p-6">

        <div className="flex items-center gap-2.5 mb-5">
          <button onClick={() => setIsMenuOpen(true)} className="lg:hidden shrink-0 p-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors" aria-label="Buka menu">
            <Menu className="w-4 h-4" strokeWidth={2} />
          </button>
          <div className="min-w-0">
            <p className="text-[11px] font-medium text-slate-400 tracking-tight">Admin panel</p>
            <h2 className="text-[19px] font-semibold text-slate-900 tracking-tight leading-tight truncate">{getPageTitle()}</h2>
          </div>
        </div>

      {/* KONTEN TAB: MASTER USER */}
      {activeTab === 'master_user' && user.role === 'admin' && (() => {
        // MASTER USER = User & Akses (+ Tambah User) dan Role & Lokasi.
        const bolehAkses = menuDiizinkan(user, 'master_user') || menuDiizinkan(user, 'user');
        const bolehRole = menuDiizinkan(user, 'master_role') && !adminTerbatas(user);
        const sub = !bolehAkses ? 'role' : (!bolehRole ? 'akses' : subMasterUser);
        return (
          <div className="space-y-4">
            {bolehAkses && bolehRole && (
              <div className="inline-flex p-1 rounded-xl bg-slate-200/60">
                {[['akses', 'User & Akses', UsersRound], ['role', 'Role & Lokasi', ShieldCheck]].map(([k, l, Ikon]) => (
                  <button key={k} onClick={() => setSubMasterUser(k)}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium transition ${sub === k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                    <Ikon className="w-4 h-4" strokeWidth={1.75} />{l}
                  </button>
                ))}
              </div>
            )}
            {sub === 'akses' && (
              <MasterUserPanel user={user} fetchApi={fetchApi} masterData={masterData}
                listLokasi={LIST_LOKASI.filter(l => !adminTerbatas(user) || l === user.lokasiAkses)}
                terbatas={adminTerbatas(user)} onResetPassword={handleResetPassword}
                bolehTambah={menuDiizinkan(user, 'user')} bolehEdit={menuDiizinkan(user, 'master_user')} />
            )}
            {sub === 'role' && <MasterRolePanel user={user} fetchApi={fetchApi} />}
          </div>
        );
      })()}


      {/* KONTEN TAB: MASTER DATA */}
      {activeTab === 'master' && user.role === 'admin' && (
        <div className="animate-in fade-in duration-300">
          <form onSubmit={handleAddMaster} className="bg-white rounded-2xl border border-slate-200/70 overflow-hidden">
            <div className="p-4 space-y-3.5">
                <div>
                    <label className="block text-[11px] font-medium text-slate-500 mb-1.5">Kategori</label>
                    <select className={inputCls} value={masterInput.kategori} onChange={e => setMasterInput({...masterInput, kategori: e.target.value})}>
                        <option value="Menu">Menu absensi</option><option value="Role">Role user</option><option value="Divisi">Divisi</option><option value="Shift">Jam shift</option><option value="SheetImport">Sheet tujuan import mesin absen</option>
                    </select>
                </div>
                <div>
                    <label className="block text-[11px] font-medium text-slate-500 mb-1.5">Value</label>
                    <input required type="text" className={inputCls} value={masterInput.value} onChange={e => setMasterInput({...masterInput, value: e.target.value})} placeholder={masterInput.kategori === 'SheetImport' ? 'Nama sheet Google, mis. shift' : 'Nilai yang disimpan sistem'} />
                </div>
                <div>
                    <label className="block text-[11px] font-medium text-slate-500 mb-1.5">Label</label>
                    <input required type="text" className={inputCls} value={masterInput.label} onChange={e => setMasterInput({...masterInput, label: e.target.value})} placeholder="Teks yang dilihat pengguna" />
                </div>
                {masterInput.kategori === 'SheetImport' && (
                    <p className="text-[11px] text-slate-400 leading-relaxed -mt-1">
                        Value dipakai sebagai nama sheet Google (dibuat otomatis kalau belum ada) DAN
                        sebagai kata kunci pencocokan nama tab di file mesin absen — harus PERSIS sama
                        (tanpa spasi/strip) dengan nama tab sumbernya, mis. tab "SHIFT" di file → value "shift".
                        Sheet "dbabsen" selalu jadi tujuan bawaan, tidak perlu didaftarkan di sini.
                    </p>
                )}
            </div>
            <div className="p-4 pt-0">
                <button type="submit" disabled={loading} className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white py-3 rounded-xl text-[14px] font-medium hover:bg-slate-800 disabled:opacity-60 transition-colors">
                    {loading && <Loader2 className="w-4 h-4 animate-spin"/>}
                    {loading ? 'Menyimpan…' : 'Tambah data'}
                </button>
            </div>
          </form>
        </div>
      )}

      {/* KONTEN TAB: IMPORT dbabsen */}
      {activeTab === 'import_db' && user.role === 'admin' && (
        <ImportDbAbsen user={user} masterData={masterData} />
      )}

      {/* KONTEN TAB: GEOFENCE ABSEN ONLINE */}
      {activeTab === 'geofence' && user.role === 'admin' && (
        <div className="animate-in fade-in duration-300 space-y-3">
          {loadingGeofence ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-slate-400 animate-spin" /></div>
          ) : (
            <>
              <div className="bg-white rounded-2xl border border-slate-200/70 p-4 space-y-3">
                <div>
                  <label className="block text-[11px] font-medium text-slate-500 mb-1.5">Karyawan</label>
                  <select className={inputCls} value={geofenceUserId} onChange={handleGeofenceUserChange}>
                    {geofenceUsers.map(u => <option key={u.id} value={u.id}>{u.nama} · {u.username}{modeAbsenMap[String(u.id)] ? ` · ${(OPSI_MODE_ABSEN.find(o => o.value === modeAbsenMap[String(u.id)]) || {}).label}` : ''}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-slate-500 mb-1.5">Akses absen</label>
                  <div className="space-y-2">
                    {OPSI_MODE_ABSEN.map(o => (
                      <label key={o.value} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer ${modeAbsenPilih === o.value ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-100'}`}>
                        <input type="radio" name="modeAbsen" value={o.value} checked={modeAbsenPilih === o.value} onChange={() => setModeAbsenPilih(o.value)} className="mt-0.5 w-4 h-4 border-slate-300 text-blue-600 focus:ring-blue-500/20" />
                        <span><span className="block text-[13px] font-semibold text-slate-800">{o.label}</span><span className="block mt-0.5 text-[11px] leading-relaxed text-slate-500">{o.ket}</span></span>
                      </label>
                    ))}
                  </div>
                </div>
                <label className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100 cursor-pointer">
                  <input type="checkbox" checked={geofenceRequired} onChange={e => setGeofenceRequired(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900/20" />
                  <span><span className="block text-[13px] font-semibold text-slate-800">Wajib berada di area geofence</span><span className="block mt-0.5 text-[11px] leading-relaxed text-slate-500">Hanya berlaku untuk absen online Hadir dan Pulang. User tanpa centang tetap dapat absen dari lokasi mana saja.</span></span>
                </label>
                <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer ${gpsBebasUser ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-100'}`}>
                  <input type="checkbox" checked={gpsBebasUser} onChange={e => setGpsBebasUser(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500/20" />
                  <span>
                    <span className="block text-[13px] font-semibold text-slate-800">Kecualikan dari gerbang GPS (staf PC)</span>
                    <span className="block mt-0.5 text-[11px] leading-relaxed text-slate-500">
                      Untuk karyawan yang bekerja di komputer tanpa sumber lokasi — PC kabel tanpa WiFi selalu gagal membaca GPS.
                      Menu terbuka tanpa menunggu lokasi, dan tombol Kirim tidak lagi menahan. Absennya tercatat tanpa koordinat
                      dan <strong>ditandai "tanpa lokasi" di Audit GPS</strong>. Jangan dipakai untuk karyawan lapangan.
                    </span>
                  </span>
                </label>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200/70 overflow-hidden">
                <div className="p-4 border-b border-slate-100">
                  <p className="text-[13px] font-semibold text-slate-800">Lokasi yang diizinkan</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">Pilih dari master lokasi. Absen Hadir/Pulang hanya sah di lokasi yang dicentang; Standby tidak dibatasi.</p>
                </div>
                <div className="p-4 space-y-2">
                  {geoMaster.filter(m => m.aktif).length === 0 && <p className="py-4 text-center text-[12px] text-slate-400">Belum ada master lokasi. Tambahkan dulu di menu "Master lokasi geofence".</p>}
                  {geoMaster.filter(m => m.aktif).map(m => (
                    <label key={m.id} className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 cursor-pointer">
                      <input type="checkbox" checked={geofenceMasterIds.includes(String(m.id))} onChange={() => toggleGeofenceMaster(String(m.id))} className="mt-0.5 w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900/20" />
                      <span><span className="block text-[13px] font-semibold text-slate-800">{m.nama}</span><span className="block text-[11px] text-slate-400">Radius {m.radius} m</span></span>
                    </label>
                  ))}
                  {geofenceAreas.length > 0 && (
                    <div className="pt-2 space-y-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Area manual lama</p>
                      {geofenceAreas.map((area, index) => (
                        <div key={index} className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
                          <span className="text-[12px] text-slate-700">{area.nama} · {area.radius} m</span>
                          <button type="button" onClick={() => handleRemoveGeofenceArea(index)} className="text-[11px] font-medium text-rose-500 hover:text-rose-700">Hapus</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="p-4 pt-0"><button type="button" onClick={handleSaveGeofence} disabled={loading || !geofenceUserId} className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white py-3 rounded-xl text-[14px] font-medium hover:bg-slate-800 disabled:opacity-50">{loading && <Loader2 className="w-4 h-4 animate-spin" />} {loading ? 'Menyimpan…' : 'Simpan konfigurasi geofence'}</button></div>
              </div>
            </>
          )}
        </div>
      )}

      {/* KONTEN TAB: MASTER LOKASI GEOFENCE */}
      {activeTab === 'geomaster' && user.role === 'admin' && (
        <div className="animate-in fade-in duration-300 space-y-3">
          {loadingGeofence ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-slate-400 animate-spin" /></div>
          ) : (
            <>
              <div className="bg-white rounded-2xl border border-slate-200/70 overflow-hidden">
                <div className="p-4 flex items-center justify-between gap-3 border-b border-slate-100">
                  <div><p className="text-[13px] font-semibold text-slate-800">Daftar lokasi</p><p className="text-[11px] text-slate-400 mt-0.5">Isi nama lokasi dan tempel link Google Maps (Share &gt; Salin link). Koordinat dibaca otomatis saat disimpan.</p></div>
                  <button type="button" onClick={handleAddGeoMaster} className="shrink-0 px-3 py-2 rounded-lg bg-slate-900 text-white text-[12px] font-medium hover:bg-slate-800">+ Tambah lokasi</button>
                </div>
                {geoMasterDraft.length === 0 && <p className="p-6 text-center text-[12px] text-slate-400">Belum ada lokasi.</p>}
                <div className="p-4 space-y-3">
                  {geoMasterDraft.map((m, index) => (
                    <div key={m.id || `baru-${index}`} className="rounded-xl border border-slate-200 p-3 space-y-2.5">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 text-[11px] font-medium text-slate-500"><input type="checkbox" checked={m.aktif !== false} onChange={e => handleUpdateGeoMaster(index, 'aktif', e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-slate-900" /> Aktif</label>
                        <button type="button" onClick={() => handleRemoveGeoMaster(index)} className="text-[11px] font-medium text-rose-500 hover:text-rose-700">Hapus</button>
                      </div>
                      <input className={inputCls} value={m.nama} onChange={e => handleUpdateGeoMaster(index, 'nama', e.target.value)} placeholder="Nama lokasi, mis. Kebun Sei Rokan" />
                      <input className={inputCls} value={m.link || ''} onChange={e => handleUpdateGeoMaster(index, 'link', e.target.value)} placeholder="Link Google Maps ATAU koordinat, mis. -7.2575, 112.7521" />
                      <div className="grid grid-cols-2 gap-2">
                        <input className={inputCls} type="number" step="any" value={m.lat} onChange={e => handleUpdateGeoMaster(index, 'lat', e.target.value)} placeholder="Latitude (opsional)" />
                        <input className={inputCls} type="number" step="any" value={m.lng} onChange={e => handleUpdateGeoMaster(index, 'lng', e.target.value)} placeholder="Longitude (opsional)" />
                      </div>
                      <div className="flex items-center gap-2"><input className={inputCls} type="number" min="1" max="100000" value={m.radius} onChange={e => handleUpdateGeoMaster(index, 'radius', e.target.value)} placeholder="Radius meter" /><span className="shrink-0 text-[11px] text-slate-400">meter</span></div>
                      {m.link && m.lat !== '' && Number.isFinite(Number(m.lat)) && <a href={`https://www.google.com/maps?q=${m.lat},${m.lng}`} target="_blank" rel="noreferrer" className="inline-block text-[11px] text-sky-600 hover:underline">Cek titik di peta ({Number(m.lat).toFixed(5)}, {Number(m.lng).toFixed(5)})</a>}
                    </div>
                  ))}
                </div>
                <div className="p-4 pt-0"><button type="button" onClick={handleSaveGeoMaster} disabled={loading} className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white py-3 rounded-xl text-[14px] font-medium hover:bg-slate-800 disabled:opacity-50">{loading && <Loader2 className="w-4 h-4 animate-spin" />} {loading ? 'Menyimpan…' : 'Simpan master lokasi'}</button></div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200/70 p-4 space-y-3">
                <div><p className="text-[13px] font-semibold text-slate-800">Akses absen untuk satu divisi</p><p className="text-[11px] text-slate-400 mt-0.5">Atur mode absen semua karyawan dalam satu divisi sekaligus. Pengaturan per karyawan di atas tetap bisa diubah sesudahnya.</p></div>
                <select className={inputCls} value={modeDivisiNilai} onChange={e => setModeDivisiNilai(e.target.value)}>
                  {OPSI_MODE_ABSEN.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <select className={inputCls} value={modeDivisi} onChange={e => setModeDivisi(e.target.value)}>
                  <option value="">Pilih divisi…</option>
                  {[...new Set(geofenceUsers.map(u => String(u.divisi || '').trim()).filter(Boolean))].sort().map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <button type="button" onClick={handleApplyModeDivisi} disabled={loading || !modeDivisi} className="w-full bg-slate-900 text-white py-3 rounded-xl text-[14px] font-medium hover:bg-slate-800 disabled:opacity-50">Terapkan mode absen ke divisi</button>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200/70 p-4 space-y-3">
                <div><p className="text-[13px] font-semibold text-slate-800">Terapkan ke satu divisi</p><p className="text-[11px] text-slate-400 mt-0.5">Centang lokasi, pilih divisi (mis. Kerani). Semua karyawan divisi itu jadi wajib geofence pada lokasi tercentang. Standby tetap bebas.</p></div>
                <div className="space-y-2">
                  {geoMaster.filter(m => m.aktif).map(m => (
                    <label key={m.id} className="flex items-center gap-3 text-[13px] text-slate-700"><input type="checkbox" checked={divisiMasterIds.includes(String(m.id))} onChange={() => setDivisiMasterIds(prev => prev.includes(String(m.id)) ? prev.filter(x => x !== String(m.id)) : [...prev, String(m.id)])} className="w-4 h-4 rounded border-slate-300 text-slate-900" /> {m.nama}</label>
                  ))}
                </div>
                <select className={inputCls} value={geofenceDivisi} onChange={e => setGeofenceDivisi(e.target.value)}>
                  <option value="">Pilih divisi…</option>
                  {[...new Set(geofenceUsers.map(u => String(u.divisi || '').trim()).filter(Boolean))].sort().map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <button type="button" onClick={handleApplyGeofenceDivisi} disabled={loading || !geofenceDivisi} className="w-full bg-slate-900 text-white py-3 rounded-xl text-[14px] font-medium hover:bg-slate-800 disabled:opacity-50">Terapkan ke semua karyawan divisi</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* KONTEN TAB: PERIODE ABSENSI */}
      {activeTab === 'period' && user.role === 'admin' && (
        <div className="animate-in fade-in duration-300">
          <form onSubmit={handleSaveAbsencePeriod} className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white">
            <div className="border-b border-slate-100 p-4">
              <div className="mb-1 flex items-center gap-2">
                <CalendarRange className="h-4 w-4 text-blue-600" strokeWidth={1.8} />
                <p className="text-[14px] font-semibold text-slate-900">Periode absensi aktif</p>
              </div>
              <p className="text-[11px] leading-relaxed text-slate-500">Periode yang <strong>aktif</strong> muncul sebagai pilihan di dashboard karyawan. Kuota pengajuan Ijin maksimal 4 kali dihitung ulang di setiap periode.</p>
            </div>
            {loadingPeriod ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
                  <p className="text-[11px] text-slate-500">
                    <span className="font-semibold text-slate-700">{periodeList.filter(p => p.aktif).length}</span> aktif dari {periodeList.length} periode
                  </p>
                  {periodeList.length > 0 && (
                    <button type="button" onClick={isiOtomatisDraft} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-medium text-slate-700 transition-colors hover:bg-slate-50">
                      <CalendarCheck className="h-3.5 w-3.5" strokeWidth={1.8} /> Isi periode berikutnya
                    </button>
                  )}
                </div>

                <div className="divide-y divide-slate-100">
                  {/* BARIS DRAFT — selalu di paling atas.
                      Begitu kedua tanggalnya terisi, isinya pindah jadi
                      periode baru di bawah dan baris ini kosong lagi. */}
                  <div className="bg-slate-50/70 p-4">
                    <div className="flex items-end gap-2">
                      <button
                        type="button"
                        onClick={() => setPeriodeDraft(d => ({ ...d, aktif: !d.aktif }))}
                        aria-pressed={periodeDraft.aktif}
                        title={periodeDraft.aktif ? 'Periode baru langsung aktif' : 'Periode baru dibuat nonaktif'}
                        className="mb-2 shrink-0"
                      >
                        <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${periodeDraft.aktif ? 'bg-blue-600' : 'bg-slate-300'}`}>
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${periodeDraft.aktif ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                        </span>
                      </button>

                      <div className="min-w-0 flex-1">
                        <label className="mb-1.5 block text-[11px] font-medium text-slate-500">Tanggal mulai</label>
                        <input type="date" className={`${inputCls} tabular-nums`} value={periodeDraft.mulai} onChange={(e) => ubahDraft({ mulai: e.target.value })} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <label className="mb-1.5 block text-[11px] font-medium text-slate-500">Tanggal selesai</label>
                        <input type="date" className={`${inputCls} tabular-nums`} value={periodeDraft.selesai} onChange={(e) => ubahDraft({ selesai: e.target.value })} />
                      </div>

                      {/* Penyeimbang lebar tombol hapus, supaya kolom tanggal
                          baris draft lurus dengan baris-baris di bawahnya. */}
                      <span className="mb-2 h-7 w-7 shrink-0" aria-hidden="true" />
                    </div>
                    <p className="mt-2 text-[11px] text-slate-400">
                      Isi kedua tanggal untuk menambah periode baru.
                    </p>
                  </div>

                  {periodeList.map((p) => {
                    const bentrok = periodeBertabrakan.has(p.key);
                    const terbalik = p.mulai && p.selesai && p.mulai > p.selesai;
                    return (
                      <div key={p.key} className={`p-4 transition-colors ${bentrok || terbalik ? 'bg-red-50/60' : p.aktif ? 'bg-blue-50/30' : 'bg-white'}`}>
                        <div className="flex items-end gap-2">
                          {/* Saklar aktif/nonaktif — sejajar dengan kotak tanggal */}
                          <button
                            type="button"
                            onClick={() => ubahPeriode(p.key, { aktif: !p.aktif })}
                            aria-pressed={p.aktif}
                            aria-label={p.aktif ? 'Nonaktifkan periode' : 'Aktifkan periode'}
                            title={p.aktif ? 'Aktif — muncul di dashboard karyawan' : 'Nonaktif — hanya untuk histori'}
                            className="mb-2 shrink-0"
                          >
                            <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${p.aktif ? 'bg-blue-600' : 'bg-slate-300'}`}>
                              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${p.aktif ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                            </span>
                          </button>

                          <div className="min-w-0 flex-1">
                            <input type="date" aria-label="Tanggal mulai" className={`${inputCls} tabular-nums`} value={p.mulai} onChange={(e) => ubahPeriode(p.key, { mulai: e.target.value })} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <input type="date" aria-label="Tanggal selesai" className={`${inputCls} tabular-nums`} value={p.selesai} onChange={(e) => ubahPeriode(p.key, { selesai: e.target.value })} />
                          </div>

                          <button
                            type="button"
                            onClick={() => { if (window.confirm('Hapus periode ini dari daftar?')) hapusPeriode(p.key); }}
                            className="mb-1.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                            aria-label="Hapus periode"
                          >
                            <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                          </button>
                        </div>

                        {terbalik && (
                          <p className="mt-2 flex items-start gap-1.5 text-[11px] font-medium text-red-600">
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                            Tanggal mulai tidak boleh setelah tanggal selesai.
                          </p>
                        )}
                        {bentrok && !terbalik && (
                          <p className="mt-2 flex items-start gap-1.5 text-[11px] font-medium text-red-600">
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                            Tumpang tindih dengan periode aktif lain. Nonaktifkan salah satunya atau perbaiki tanggalnya.
                          </p>
                        )}
                      </div>
                    );
                  })}

                  {periodeList.length === 0 && (
                    <div className="px-4 py-8 text-center text-[12px] text-slate-400">
                      Belum ada periode tersimpan.
                    </div>
                  )}
                </div>

                <div className="px-4 pb-4 pt-4">
                  <div className="rounded-xl border border-blue-100 bg-blue-50/70 p-3 text-[11px] leading-relaxed text-blue-800">
                    Boleh mengaktifkan <strong>lebih dari satu</strong> periode — karyawan tinggal memilihnya di dashboard.
                    Syaratnya tanggalnya tidak boleh saling tumpang tindih, karena kuota Ijin 4× dihitung per periode
                    dan setiap tanggal harus jatuh ke tepat satu periode.
                    <br /><br />
                    Periode <strong>nonaktif</strong> tidak hilang — tetap bisa dipilih semua orang di tab Riwayat untuk melihat data lama.
                  </div>
                </div>

                <div className="bg-slate-50/60 p-4">
                  <button type="submit" disabled={savingPeriod || periodeBertabrakan.size > 0} className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-3 text-[14px] font-medium text-white transition-colors hover:bg-slate-800 disabled:opacity-60">
                    {savingPeriod && <Loader2 className="h-4 w-4 animate-spin" />}
                    {savingPeriod ? 'Menyimpan…' : 'Simpan daftar periode'}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      )}

      {/* KONTEN TAB: BOARD ABSENSI (link ke Google Sheet, admin saja)

          Pengecekan `user.role === 'admin'` di sini menentukan TAMPILAN, bukan
          keamanan: siapa pun yang tahu URL-nya tetap bisa membukanya langsung.
          Yang benar-benar menjaga isinya adalah izin berbagi di Google Drive —
          spreadsheet itu harus dibagikan hanya ke akun admin, bukan
          "siapa saja yang memiliki link". */}
      {activeTab === 'bebas_absen' && user.role === 'admin' && (
        <BebasAbsenPanel user={user} fetchApi={fetchApi} />
      )}


      {activeTab === 'board' && user.role === 'admin' && (
        <div className="animate-in fade-in duration-300">
          <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white">
            <div className="border-b border-slate-100 p-4">
              <div className="mb-1 flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4 text-emerald-600" strokeWidth={1.8} />
                <p className="text-[14px] font-semibold text-slate-900">Board Absensi 2026</p>
              </div>
              <p className="text-[11px] leading-relaxed text-slate-500">
                Rekap absensi bulanan dalam format lembar kerja, mengikuti data DB_FIX.
                Diperbarui otomatis; hanya bisa dibuka oleh akun yang diberi akses di Google Drive.
              </p>
            </div>
            <div className="p-4">
              <a
                href={BOARD_ABSENSI_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-3 text-[14px] font-medium text-white transition-colors hover:bg-slate-800"
              >
                <FileSpreadsheet className="h-4 w-4" strokeWidth={1.9} /> Buka Board Absensi
              </a>
              <p className="mt-2.5 text-center text-[10px] text-slate-400">Terbuka di tab baru</p>
            </div>
          </div>
        </div>
      )}

      {/* KONTEN TAB: TIM APPROVAL */}
      {activeTab === 'approval_team' && user.role === 'admin' && (
        <div className="animate-in fade-in duration-300 space-y-3">
          <div className="rounded-xl border border-blue-100 bg-blue-50/70 p-3 text-[11px] leading-relaxed text-blue-800">
            Atur anggota tim yang boleh di-approve oleh tiap kepala divisi. Kalau kepala divisi belum
            punya grup di sini, approval untuk bawahannya tetap jalan seperti biasa berdasarkan
            kesamaan divisi — data lama tidak akan tiba-tiba putus.
          </div>

          {loadingApprovalTeam ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-slate-400 animate-spin" /></div>
          ) : (
            <>
              <MasterTimApproval users={approvalTeamUsers} assignments={approvalTeamAssignments}
                onKelola={(id, departemen, rows) => {
                  handlePilihKepalaTeam(id);
                  handleEditGrupApproval(departemen, rows);
                  setTimeout(() => { const el = document.getElementById('editor-tim-approval'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 50);
                }}
                onTambah={() => {
                  handlePilihKepalaTeam('');
                  setTimeout(() => { const el = document.getElementById('editor-tim-approval'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 50);
                }} />

              <div id="editor-tim-approval" className="bg-white rounded-2xl border border-slate-200/70 p-4 space-y-3 scroll-mt-4">
                <p className="text-[13px] font-semibold text-slate-900">{atKepalaId ? 'Kelola tim atasan' : 'Tambah / kelola tim atasan'}</p>
                <div>
                  <label className="block text-[11px] font-medium text-slate-500 mb-1.5">Kepala divisi / atasan</label>
                  <p className="text-[11px] text-slate-400 mb-2 leading-relaxed">Pilih siapapun — kalau rolenya belum "approval", akan ada opsi untuk langsung menjadikannya kepala divisi di bawah.</p>
                  <div className="relative mb-2">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={1.75} />
                    <input type="text" placeholder="Cari nama user" value={atKepalaSearch} onChange={e => setAtKepalaSearch(e.target.value)} className={`${inputCls} pl-9`} />
                  </div>
                  <select className={inputCls} value={atKepalaId} onChange={e => handlePilihKepalaTeam(e.target.value)}>
                    <option value="">Pilih kepala divisi…</option>
                    {approvalTeamKepalaOptions.map(u => (
                      <option key={u.uuid} value={u.uuid}>{u.nama} · {u.divisi || '-'} · {isApprovalRole(u.role) ? (u.role || '-') : 'belum approval'}</option>
                    ))}
                  </select>
                </div>

                {kepalaTeamTerpilih && !kepalaSudahApproval && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-3">
                    <p className="text-[12px] leading-relaxed text-amber-800">
                      <span className="font-semibold">{kepalaTeamTerpilih.nama}</span> belum berperan sebagai approval
                      (role saat ini: {kepalaTeamTerpilih.role || '-'}). Lengkapi departemen, jabatan, dan anggota tim di
                      bawah — dia hanya akan bisa approve anggota tim yang dipilihkan untuknya di departemen ini, bukan
                      pengajuan dari departemen lain.
                    </p>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-[11px] font-medium text-amber-700">Departemen yang dipimpin</label>
                        <button type="button" onClick={() => { setShowNewDepartemenInput(v => !v); setNewDepartemenName(''); }} className="text-[11px] font-medium text-amber-700 underline decoration-dotted">
                          {showNewDepartemenInput ? 'Batal' : '+ Departemen baru'}
                        </button>
                      </div>
                      {showNewDepartemenInput ? (
                        <div className="flex gap-2">
                          <input type="text" className={`${inputCls} bg-white`} placeholder="Nama departemen baru" value={newDepartemenName} onChange={e => setNewDepartemenName(e.target.value)} />
                          <button type="button" onClick={handleTambahDepartemenBaru} disabled={addingDepartemen || !newDepartemenName.trim()} className="shrink-0 px-3 py-2.5 rounded-lg bg-amber-600 text-white text-[12px] font-medium hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1.5">
                            {addingDepartemen && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                            Tambah
                          </button>
                        </div>
                      ) : (
                        <select className={`${inputCls} bg-white`} value={atDepartemen} onChange={e => setAtDepartemen(e.target.value)}>
                          <option value="">Pilih departemen…</option>
                          {masterData.divisions.map((d, i) => <option key={i} value={d.value}>{d.label}</option>)}
                        </select>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[11px] font-medium text-amber-700 mb-1">Role approval (teknis)</label>
                        <select className={`${inputCls} bg-white`} value={atNewRole} onChange={e => setAtNewRole(e.target.value)}>
                          {approvalHeadRoleChoices.map((r, i) => <option key={i} value={r.value}>{r.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-amber-700 mb-1">Jabatan (opsional, cuma tampilan)</label>
                        <input type="text" className={`${inputCls} bg-white`} placeholder="mis. Kepala Depo Kumai" value={atJabatan} onChange={e => setAtJabatan(e.target.value)} />
                      </div>
                    </div>

                    <div className="rounded-lg border border-amber-200 bg-white overflow-hidden">
                      <div className="p-2.5 border-b border-amber-100">
                        <div className="relative">
                          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={1.75} />
                          <input type="text" placeholder="Cari nama anggota tim" value={atSearch} onChange={e => setAtSearch(e.target.value)} className={`${inputCls} pl-9`} />
                        </div>
                      </div>
                      <div className="flex items-baseline justify-between px-2.5 pt-2">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">Anggota tim</p>
                        <span className="text-[11px] text-slate-400 tabular-nums">{atMemberIds.length} dipilih</span>
                      </div>
                      <div className="max-h-56 overflow-y-auto px-2.5 py-1.5 space-y-0.5">
                        {atAnggotaOptions.length === 0 && (
                          <p className="py-6 text-center text-[12px] text-slate-400">Tidak ada user yang cocok.</p>
                        )}
                        {atAnggotaOptions.map(u => (
                          <label key={u.uuid} className="flex items-center gap-2.5 py-1.5 text-[13px] text-slate-700 cursor-pointer">
                            <input type="checkbox" checked={atMemberIds.includes(String(u.uuid))} onChange={() => handleToggleAnggotaTeam(String(u.uuid))} className="w-4 h-4 shrink-0 rounded border-slate-300 text-slate-900 focus:ring-slate-900/20" />
                            <span className="flex-1 truncate">{u.nama}</span>
                            <span className="text-[11px] text-slate-400 shrink-0">{u.divisi}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <button type="button" onClick={handleJadikanKepalaDivisi} disabled={savingApprovalRole || !atDepartemen} className="w-full flex items-center justify-center gap-2 px-3.5 py-2.5 rounded-lg bg-amber-600 text-white text-[13px] font-medium hover:bg-amber-700 disabled:opacity-50">
                      {savingApprovalRole && <Loader2 className="w-4 h-4 animate-spin" />}
                      Jadikan kepala divisi{atMemberIds.length > 0 ? ` & simpan ${atMemberIds.length} anggota` : ''}
                    </button>
                  </div>
                )}
              </div>

              {atKepalaId && kepalaSudahApproval && (
                <div className="bg-white rounded-2xl border border-slate-200/70 overflow-hidden">
                  <div className="p-4 border-b border-slate-100">
                    <p className="text-[13px] font-semibold text-slate-800">Grup approval untuk kepala ini</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">Satu kepala bisa punya lebih dari satu grup/departemen.</p>
                  </div>
                  {Object.keys(grupApprovalUntukKepala).length === 0 && (
                    <p className="px-4 py-6 text-center text-[12px] text-slate-400">Belum ada grup untuk kepala ini. Buat lewat form di bawah.</p>
                  )}
                  <div className="divide-y divide-slate-100">
                    {Object.entries(grupApprovalUntukKepala).map(([departemen, anggotaRows]) => (
                      <div key={departemen} className="flex items-center gap-3 px-4 py-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-medium text-slate-900 truncate">{departemen}</p>
                          <p className="text-[11px] text-slate-400 mt-0.5">
                            {anggotaRows.length} anggota{(anggotaRows[0] && anggotaRows[0].jabatan) ? ` · ${anggotaRows[0].jabatan}` : ''}
                          </p>
                        </div>
                        <button type="button" onClick={() => handleEditGrupApproval(departemen, anggotaRows)} className="shrink-0 px-2.5 py-1.5 rounded-lg text-[12px] font-medium text-slate-600 border border-slate-200 hover:bg-slate-50">Edit</button>
                        <button type="button" onClick={() => handleHapusGrupApproval(departemen)} disabled={loading} className="shrink-0 px-2.5 py-1.5 rounded-lg text-[12px] font-medium text-rose-600 border border-rose-200 hover:bg-rose-50 disabled:opacity-50">Hapus</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {atKepalaId && kepalaSudahApproval && (
                <div className="bg-white rounded-2xl border border-slate-200/70 overflow-hidden">
                  <div className="p-4 space-y-3 border-b border-slate-100">
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-[11px] font-medium text-slate-500">Departemen</label>
                        <button type="button" onClick={() => { setShowNewDepartemenInput(v => !v); setNewDepartemenName(''); }} className="text-[11px] font-medium text-slate-500 underline decoration-dotted">
                          {showNewDepartemenInput ? 'Batal' : '+ Departemen baru'}
                        </button>
                      </div>
                      {showNewDepartemenInput ? (
                        <div className="flex gap-2">
                          <input type="text" className={inputCls} placeholder="Nama departemen baru" value={newDepartemenName} onChange={e => setNewDepartemenName(e.target.value)} />
                          <button type="button" onClick={handleTambahDepartemenBaru} disabled={addingDepartemen || !newDepartemenName.trim()} className="shrink-0 px-3 py-2.5 rounded-lg bg-slate-900 text-white text-[12px] font-medium hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1.5">
                            {addingDepartemen && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                            Tambah
                          </button>
                        </div>
                      ) : (
                        <select className={inputCls} value={atDepartemen} onChange={e => setAtDepartemen(e.target.value)}>
                          <option value="">Pilih departemen…</option>
                          {masterData.divisions.map((d, i) => <option key={i} value={d.value}>{d.label}</option>)}
                        </select>
                      )}
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-500 mb-1.5">Jabatan (opsional, cuma tampilan)</label>
                      <input type="text" className={inputCls} placeholder="mis. Kepala Depo Kumai" value={atJabatan} onChange={e => setAtJabatan(e.target.value)} />
                    </div>
                    <div className="relative">
                      <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={1.75} />
                      <input type="text" placeholder="Cari nama anggota" value={atSearch} onChange={e => setAtSearch(e.target.value)} className={`${inputCls} pl-9`} />
                    </div>
                  </div>

                  <div className="flex items-baseline justify-between px-4 pt-3">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">Anggota tim</p>
                    <span className="text-[11px] text-slate-400 tabular-nums">{atMemberIds.length} dipilih</span>
                  </div>
                  <div className="max-h-72 overflow-y-auto px-4 py-2 space-y-0.5">
                    {atAnggotaOptions.length === 0 && (
                      <p className="py-6 text-center text-[12px] text-slate-400">Tidak ada user yang cocok.</p>
                    )}
                    {atAnggotaOptions.map(u => (
                      <label key={u.uuid} className="flex items-center gap-2.5 py-1.5 text-[13px] text-slate-700 cursor-pointer">
                        <input type="checkbox" checked={atMemberIds.includes(String(u.uuid))} onChange={() => handleToggleAnggotaTeam(String(u.uuid))} className="w-4 h-4 shrink-0 rounded border-slate-300 text-slate-900 focus:ring-slate-900/20" />
                        <span className="flex-1 truncate">{u.nama}</span>
                        <span className="text-[11px] text-slate-400 shrink-0">{u.divisi}</span>
                      </label>
                    ))}
                  </div>

                  <div className="p-4 pt-3">
                    <button type="button" onClick={handleSaveApprovalTeam} disabled={loading || !atDepartemen} className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white py-3 rounded-xl text-[14px] font-medium hover:bg-slate-800 disabled:opacity-50">
                      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                      {loading ? 'Menyimpan…' : 'Simpan grup approval'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* KONTEN TAB: INFO HRD */}
      {/* Backend Apps Script belum menerima berkas fitur ini.
          Ditampilkan menggantikan seluruh isi tab — bukan di atasnya —
          karena daftar yang kosong beserta tombol-tombolnya hanya akan
          membuat admin mengira fiturnya rusak, lalu mencoba satu per satu
          dan mendapat popup yang sama berulang kali. */}
      {activeTab === 'perangkat' && user.role === 'admin' && deviceBelumAda && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" strokeWidth={2} />
            <div className="min-w-0">
              <p className="text-[14px] font-semibold text-amber-900">Backend belum diperbarui</p>
              <p className="text-[12px] leading-relaxed text-amber-800 mt-1">
                Aplikasi di perangkat ini sudah versi {FRONTEND_VERSION}, tetapi Apps Script di spreadsheet
                belum mengenal perintah <b>get_device_list</b>. Frontend dan backend memang di-deploy terpisah,
                jadi keadaan ini normal sampai langkah di bawah dikerjakan.
              </p>

              <ol className="mt-3 space-y-1.5 text-[12px] leading-relaxed text-amber-900 list-decimal pl-4">
                <li>Buka spreadsheet <b>absen</b> → Ekstensi › Apps Script.</li>
                <li>Tambah file baru <code className="px-1 py-0.5 rounded bg-amber-100 font-mono text-[11px]">Devices.gs</code> dan <code className="px-1 py-0.5 rounded bg-amber-100 font-mono text-[11px]">FaceProfile.gs</code>, tempel isi dari folder <code className="px-1 py-0.5 rounded bg-amber-100 font-mono text-[11px]">apps-script/</code>.</li>
                <li>Perbarui juga <code className="px-1 py-0.5 rounded bg-amber-100 font-mono text-[11px]">Auth.gs</code>, <code className="px-1 py-0.5 rounded bg-amber-100 font-mono text-[11px]">Code.gs</code>, dan <code className="px-1 py-0.5 rounded bg-amber-100 font-mono text-[11px]">UpdateManifest.gs</code>.</li>
                <li>Jalankan <code className="px-1 py-0.5 rounded bg-amber-100 font-mono text-[11px]">SETUP_DEVICE_LOCK()</code> dan <code className="px-1 py-0.5 rounded bg-amber-100 font-mono text-[11px]">SETUP_FACE_PROFILE()</code> sekali.</li>
                <li>Deploy › Kelola deployment › ikon pensil › <b>Versi baru</b> › Deploy. Pakai deployment yang sudah ada agar URL tidak berubah.</li>
              </ol>

              <p className="text-[11.5px] leading-relaxed text-amber-700 mt-3">
                Setelah Deploy, semua karyawan akan diminta login ulang satu kali — itu yang mendaftarkan
                perangkat mereka. Hindari mengerjakannya pada jam masuk atau jam pulang.
              </p>

              <button
                onClick={fetchDeviceList}
                className="mt-4 inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-amber-900 text-white text-[12px] font-medium hover:bg-amber-800"
              >
                <RefreshCcw className="w-3.5 h-3.5" strokeWidth={2} /> Periksa lagi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KONTEN TAB: PERANGKAT & SESI LOGIN */}
      {activeTab === 'perangkat' && user.role === 'admin' && !deviceBelumAda && (
        <div className="space-y-3">
          {/* Mode berlaku. Ditaruh paling atas karena seluruh isi layar di
              bawahnya hanya masuk akal kalau admin tahu mode mana yang aktif. */}
          <div className="bg-white rounded-2xl border border-slate-200/70 p-4">
            <div className="flex items-start gap-2.5 mb-3">
              <Smartphone className="w-[18px] h-[18px] text-slate-400 mt-0.5 shrink-0" strokeWidth={1.75} />
              <div>
                <p className="text-[13px] font-semibold text-slate-800">Aturan satu perangkat satu akun</p>
                <p className="text-[11px] leading-relaxed text-slate-500 mt-0.5">
                  Satu akun selalu hanya aktif di satu perangkat: login baru otomatis mengeluarkan sesi lama,
                  berlaku pada mode apa pun kecuali Nonaktif.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              {[
                { nilai: 'tandai', judul: 'Tandai saja', ket: 'Login tidak pernah diblokir. Perangkat baru dan perangkat milik orang lain hanya dicatat untuk ditinjau. Aman dipakai saat baru dinyalakan.' },
                { nilai: 'ketat', judul: 'Ketat', ket: 'Perangkat yang kuotanya sudah penuh menolak akun lain. Karyawan yang ganti HP harus dilepas ikatannya dulu oleh admin.' },
                { nilai: 'off', judul: 'Nonaktif', ket: 'Semua pemeriksaan perangkat dan sesi tunggal dimatikan.' }
              ].map(m => (
                <label key={m.nilai} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${deviceMode === m.nilai ? 'bg-slate-900 border-slate-900' : 'bg-slate-50 border-slate-100 hover:bg-slate-100'}`}>
                  <input type="radio" name="device_mode" checked={deviceMode === m.nilai} onChange={() => handleUbahDeviceMode(m.nilai)} className="mt-0.5 w-4 h-4" />
                  <span>
                    <span className={`block text-[13px] font-semibold ${deviceMode === m.nilai ? 'text-white' : 'text-slate-800'}`}>{m.judul}</span>
                    <span className={`block mt-0.5 text-[11px] leading-relaxed ${deviceMode === m.nilai ? 'text-slate-300' : 'text-slate-500'}`}>{m.ket}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input className={inputCls} value={deviceCari} onChange={e => setDeviceCari(e.target.value)} placeholder="Cari nama karyawan atau ID perangkat…" />
            <button onClick={fetchDeviceList} disabled={loadingDevice} className="shrink-0 px-3 py-2.5 rounded-lg bg-white border border-slate-200 text-[12px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              <RefreshCcw className={`w-4 h-4 ${loadingDevice ? 'animate-spin' : ''}`} strokeWidth={2} />
            </button>
          </div>

          {loadingDevice ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-slate-400 animate-spin" /></div>
          ) : (
            <>
              {deviceList.length === 0 && (
                <div className="bg-white rounded-2xl border border-slate-200/70 p-6 text-center">
                  <p className="text-[12.5px] text-slate-500">Belum ada perangkat tercatat.</p>
                  <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                    Perangkat terdaftar sendiri saat karyawan login memakai aplikasi versi 1.0.19 atau lebih baru.
                  </p>
                </div>
              )}

              {deviceList
                .filter(d => {
                  const q = deviceCari.trim().toLowerCase();
                  if (!q) return true;
                  if (d.deviceId.toLowerCase().includes(q) || (d.label || '').toLowerCase().includes(q)) return true;
                  return (d.pengguna || []).some(p => (p.nama || '').toLowerCase().includes(q) || (p.username || '').toLowerCase().includes(q));
                })
                .map(d => {
                  const kelebihan = (d.pengguna || []).length > d.kuota;
                  // Delapan karakter pertama ID adalah sidik perangkat
                  // (lihat src/utils/perangkat.js). Dua baris dengan awalan
                  // sama = kemungkinan besar HP yang sama dengan data situs
                  // yang sudah dibersihkan.
                  const sidik = d.deviceId.split('-')[0];
                  const kembar = deviceList.filter(x => x.deviceId.split('-')[0] === sidik).length;
                  return (
                    <div key={d.deviceId} className={`bg-white rounded-2xl border overflow-hidden ${d.status === 'diblokir' ? 'border-rose-200' : kelebihan ? 'border-amber-200' : 'border-slate-200/70'}`}>
                      <div className="p-4 border-b border-slate-100">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-[13.5px] font-semibold text-slate-800 truncate">{d.label || 'Perangkat'}</p>
                            <p className="text-[10.5px] font-mono text-slate-400 mt-0.5 truncate">{d.deviceId}</p>
                            <p className="text-[11px] text-slate-500 mt-1">Terakhir dipakai: {d.terakhirLihat || '-'}</p>
                          </div>
                          <span className={`shrink-0 px-2 py-1 rounded-full text-[10px] font-semibold ${d.status === 'diblokir' ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'}`}>
                            {d.status === 'diblokir' ? 'Diblokir' : 'Aktif'}
                          </span>
                        </div>

                        {kembar > 1 && (
                          <p className="mt-2 text-[10.5px] leading-relaxed text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-1.5">
                            {kembar} perangkat memiliki sidik yang sama ({sidik}). Kemungkinan satu HP yang data situsnya dibersihkan berulang kali.
                          </p>
                        )}
                        {kelebihan && (
                          <p className="mt-2 text-[10.5px] leading-relaxed text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-1.5">
                            {(d.pengguna || []).length} akun memakai perangkat ini, padahal kuotanya {d.kuota}.
                          </p>
                        )}
                      </div>

                      <div className="p-4 border-b border-slate-100 flex items-end gap-2">
                        <div className="flex-1">
                          <label className="block text-[11px] font-medium text-slate-500 mb-1.5">Kuota akun pada perangkat ini</label>
                          <input
                            className={inputCls} type="number" min="1" max="50"
                            value={deviceKuotaDraft[d.deviceId] !== undefined ? deviceKuotaDraft[d.deviceId] : d.kuota}
                            onChange={e => setDeviceKuotaDraft(prev => ({ ...prev, [d.deviceId]: e.target.value }))}
                          />
                          <p className="text-[10.5px] text-slate-400 mt-1 leading-relaxed">Naikkan hanya untuk HP milik PIC yang memang mengabsenkan timnya.</p>
                        </div>
                        <button
                          onClick={() => handleSimpanKuotaDevice(d.deviceId)}
                          disabled={loading || deviceKuotaDraft[d.deviceId] === undefined}
                          className="shrink-0 px-3.5 py-2.5 rounded-lg bg-slate-900 text-white text-[12px] font-medium hover:bg-slate-800 disabled:opacity-40"
                        >Simpan</button>
                      </div>

                      <div className="p-4">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Akun terikat ({(d.pengguna || []).length})</p>
                        {(d.pengguna || []).length === 0 && <p className="text-[12px] text-slate-400">Belum ada.</p>}
                        <div className="space-y-2">
                          {(d.pengguna || []).map(pg => (
                            <div key={pg.userId} className="flex items-center justify-between gap-2 rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5">
                              <div className="min-w-0">
                                <p className="text-[12.5px] font-semibold text-slate-800 truncate">{pg.nama || pg.userId}</p>
                                <p className="text-[10.5px] text-slate-500 truncate">{pg.username} · {pg.jumlahLogin}x login · terakhir {pg.terakhir || '-'}</p>
                              </div>
                              <button onClick={() => handleLepasIkatan(d.deviceId, pg.userId, pg.nama || pg.userId)} disabled={loading} className="shrink-0 text-[11px] font-medium text-rose-500 hover:text-rose-700 disabled:opacity-40">Lepas</button>
                            </div>
                          ))}
                        </div>

                        <button
                          onClick={() => handleUbahStatusDevice(d.deviceId, d.status === 'diblokir' ? 'aktif' : 'diblokir')}
                          disabled={loading}
                          className={`mt-3 w-full py-2.5 rounded-xl text-[12.5px] font-semibold transition-colors disabled:opacity-40 ${d.status === 'diblokir' ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-200'}`}
                        >
                          {d.status === 'diblokir' ? 'Aktifkan kembali perangkat ini' : 'Blokir perangkat ini'}
                        </button>
                      </div>
                    </div>
                  );
                })}

              {/* Sesi yang sedang berjalan */}
              <div className="bg-white rounded-2xl border border-slate-200/70 overflow-hidden">
                <div className="p-4 border-b border-slate-100">
                  <p className="text-[13px] font-semibold text-slate-800">Sesi login terakhir ({deviceSesi.length})</p>
                  <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">Satu baris per karyawan. "Paksa logout" mencabut sesinya tanpa menghapus ikatan perangkat.</p>
                </div>
                <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
                  {deviceSesi.length === 0 && <p className="p-6 text-center text-[12px] text-slate-400">Belum ada sesi tercatat.</p>}
                  {deviceSesi
                    .filter(se => !deviceCari.trim() || (se.nama || '').toLowerCase().includes(deviceCari.trim().toLowerCase()))
                    .map(se => (
                    <div key={se.userId} className="flex items-center justify-between gap-2 px-4 py-3">
                      <div className="min-w-0">
                        <p className="text-[12.5px] font-semibold text-slate-800 truncate">{se.nama || se.userId}</p>
                        <p className="text-[10.5px] text-slate-500 truncate">{se.loginAt} · {se.platform || 'perangkat tidak diketahui'}</p>
                        {se.tanda && <p className="text-[10.5px] font-medium text-amber-600 mt-0.5">Tanda: {se.tanda.replace(/_/g, ' ')}</p>}
                      </div>
                      <button onClick={() => handleCabutSesi(se.userId, se.nama || se.userId)} disabled={loading} className="shrink-0 text-[11px] font-medium text-slate-500 hover:text-rose-600 disabled:opacity-40">Paksa logout</button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Riwayat kejadian */}
              <div className="bg-white rounded-2xl border border-slate-200/70 overflow-hidden">
                <button
                  onClick={() => { const buka = !deviceTampilLog; setDeviceTampilLog(buka); if (buka && !deviceAudit.length) fetchDeviceAudit(); }}
                  className="w-full flex items-center justify-between gap-2 p-4 text-left hover:bg-slate-50"
                >
                  <span className="text-[13px] font-semibold text-slate-800">Riwayat kejadian perangkat</span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${deviceTampilLog ? 'rotate-180' : ''}`} strokeWidth={2} />
                </button>
                {deviceTampilLog && (
                  <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto border-t border-slate-100">
                    {deviceAudit.length === 0 && <p className="p-6 text-center text-[12px] text-slate-400">Belum ada kejadian tercatat.</p>}
                    {deviceAudit.map((lg, i) => (
                      <div key={i} className="px-4 py-3">
                        <p className="text-[12px] font-semibold text-slate-800">{lg.nama || lg.userId || '-'} · <span className="font-normal text-slate-500">{lg.aksi.replace(/_/g, ' ')}</span></p>
                        <p className="text-[10.5px] text-slate-400 mt-0.5">{lg.waktu}</p>
                        {lg.tanda && <p className="text-[10.5px] font-medium text-amber-600 mt-0.5">{lg.tanda.replace(/_/g, ' ')}</p>}
                        {lg.detail && <p className="text-[10.5px] text-slate-500 mt-0.5 leading-relaxed">{lg.detail}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Backend Apps Script belum menerima berkas fitur ini.
          Ditampilkan menggantikan seluruh isi tab — bukan di atasnya —
          karena daftar yang kosong beserta tombol-tombolnya hanya akan
          membuat admin mengira fiturnya rusak, lalu mencoba satu per satu
          dan mendapat popup yang sama berulang kali. */}
      {activeTab === 'wajah' && user.role === 'admin' && wajahBelumAda && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" strokeWidth={2} />
            <div className="min-w-0">
              <p className="text-[14px] font-semibold text-amber-900">Backend belum diperbarui</p>
              <p className="text-[12px] leading-relaxed text-amber-800 mt-1">
                Aplikasi di perangkat ini sudah versi {FRONTEND_VERSION}, tetapi Apps Script di spreadsheet
                belum mengenal perintah <b>get_wajah_list</b>. Frontend dan backend memang di-deploy terpisah,
                jadi keadaan ini normal sampai langkah di bawah dikerjakan.
              </p>

              <ol className="mt-3 space-y-1.5 text-[12px] leading-relaxed text-amber-900 list-decimal pl-4">
                <li>Buka spreadsheet <b>absen</b> → Ekstensi › Apps Script.</li>
                <li>Tambah file baru <code className="px-1 py-0.5 rounded bg-amber-100 font-mono text-[11px]">Devices.gs</code> dan <code className="px-1 py-0.5 rounded bg-amber-100 font-mono text-[11px]">FaceProfile.gs</code>, tempel isi dari folder <code className="px-1 py-0.5 rounded bg-amber-100 font-mono text-[11px]">apps-script/</code>.</li>
                <li>Perbarui juga <code className="px-1 py-0.5 rounded bg-amber-100 font-mono text-[11px]">Auth.gs</code>, <code className="px-1 py-0.5 rounded bg-amber-100 font-mono text-[11px]">Code.gs</code>, dan <code className="px-1 py-0.5 rounded bg-amber-100 font-mono text-[11px]">UpdateManifest.gs</code>.</li>
                <li>Jalankan <code className="px-1 py-0.5 rounded bg-amber-100 font-mono text-[11px]">SETUP_DEVICE_LOCK()</code> dan <code className="px-1 py-0.5 rounded bg-amber-100 font-mono text-[11px]">SETUP_FACE_PROFILE()</code> sekali.</li>
                <li>Deploy › Kelola deployment › ikon pensil › <b>Versi baru</b> › Deploy. Pakai deployment yang sudah ada agar URL tidak berubah.</li>
              </ol>

              <p className="text-[11.5px] leading-relaxed text-amber-700 mt-3">
                Setelah Deploy, semua karyawan akan diminta login ulang satu kali — itu yang mendaftarkan
                perangkat mereka. Hindari mengerjakannya pada jam masuk atau jam pulang.
              </p>

              <button
                onClick={fetchWajahList}
                className="mt-4 inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-amber-900 text-white text-[12px] font-medium hover:bg-amber-800"
              >
                <RefreshCcw className="w-3.5 h-3.5" strokeWidth={2} /> Periksa lagi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KONTEN TAB: WAJAH KARYAWAN */}
      {activeTab === 'wajah' && user.role === 'admin' && !wajahBelumAda && (
        <div className="space-y-3">
          <div className="bg-white rounded-2xl border border-slate-200/70 p-4">
            <div className="flex items-start gap-2.5 mb-3">
              <ScanFace className="w-[18px] h-[18px] text-slate-400 mt-0.5 shrink-0" strokeWidth={1.75} />
              <div>
                <p className="text-[13px] font-semibold text-slate-800">Verifikasi identitas wajah</p>
                <p className="text-[11px] leading-relaxed text-slate-500 mt-0.5">
                  Berlaku hanya untuk presensi <b>Masuk</b> dan <b>Pulang</b> lewat aplikasi. Foto acuan diproses di
                  peramban ini menjadi 128 angka; fotonya sendiri tidak dipakai untuk mencocokkan.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-3">
              {[
                { nilai: 'ketat', judul: 'Ketat' },
                { nilai: 'tandai', judul: 'Tandai' },
                { nilai: 'off', judul: 'Nonaktif' }
              ].map(m => (
                <button key={m.nilai} onClick={() => handleSimpanKonfigWajah({ mode: m.nilai })} disabled={loading}
                  className={`py-2.5 rounded-xl text-[12.5px] font-semibold border transition-colors disabled:opacity-50 ${wajahKonfig.mode === m.nilai ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
                  {m.judul}
                </button>
              ))}
            </div>
            <p className="text-[11px] leading-relaxed text-slate-500 mb-3">
              {wajahKonfig.mode === 'ketat'
                ? 'Wajah yang tidak cocok akan menolak presensi.'
                : wajahKonfig.mode === 'tandai'
                  ? 'Presensi tetap diterima; ketidakcocokan hanya dicatat untuk ditinjau.'
                  : 'Pencocokan wajah dimatikan sepenuhnya.'}
            </p>

            {/* Ambang kemiripan. Inilah tuas yang benar-benar dipakai saat
                karyawan yang benar berulang kali ditolak — tanpa ini,
                satu-satunya jalan keluar adalah deploy ulang backend. */}
            <div className="mb-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
              <div className="flex items-baseline justify-between mb-1.5">
                <span className="text-[13px] font-semibold text-slate-800">Ambang kemiripan</span>
                <span className="text-[13px] font-bold text-slate-900 tabular-nums">{Number(wajahKonfig.ambang).toFixed(2)}</span>
              </div>
              <input
                type="range" min="0.40" max="0.60" step="0.01"
                value={wajahKonfig.ambang}
                disabled={loading || wajahKonfig.mode === 'off'}
                onChange={e => setWajahKonfig(k => ({ ...k, ambang: parseFloat(e.target.value) }))}
                onMouseUp={e => handleSimpanKonfigWajah({ ambang: e.target.value })}
                onTouchEnd={e => handleSimpanKonfigWajah({ ambang: e.target.value })}
                className="w-full accent-slate-900 disabled:opacity-40"
              />
              <div className="flex justify-between text-[10px] font-medium text-slate-400 mt-0.5">
                <span>0,40 · paling ketat</span>
                <span>0,60 · batas baku</span>
              </div>
              <p className="text-[10.5px] leading-relaxed text-slate-500 mt-2">
                Makin kecil makin ketat. Mulai dari 0,52. Bila karyawan yang benar sering ditolak — biasanya karena
                foto acuannya lama atau kurang terang — naikkan sedikit demi sedikit, dan jangan lewat 0,60.
                Cara yang lebih baik: daftarkan ulang wajahnya dengan foto yang lebih jelas.
              </p>
            </div>

            <label className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100 cursor-pointer">
              <input type="checkbox" checked={wajahKonfig.wajibTerdaftar} disabled={loading}
                onChange={e => handleSimpanKonfigWajah({ wajibTerdaftar: e.target.checked })}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900/20" />
              <span>
                <span className="block text-[13px] font-semibold text-slate-800">Wajib sudah terdaftar</span>
                <span className="block mt-0.5 text-[11px] leading-relaxed text-slate-500">
                  Nyalakan hanya setelah semua karyawan di bawah terdaftar. Selama mati, karyawan yang belum punya
                  wajah acuan tetap boleh presensi — itu yang membuat fitur ini bisa dinyalakan sebelum pendaftaran selesai.
                </span>
              </span>
            </label>
          </div>

          {loadingWajah ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-slate-400 animate-spin" /></div>
          ) : (
            <>
              {(() => {
                const total = wajahList.length;
                const sudah = wajahList.filter(u => u.terdaftar).length;
                const persen = total ? Math.round((sudah / total) * 100) : 0;
                return (
                  <div className="bg-white rounded-2xl border border-slate-200/70 p-4">
                    <div className="flex items-baseline justify-between mb-2">
                      <p className="text-[13px] font-semibold text-slate-800">{sudah} dari {total} karyawan terdaftar</p>
                      <span className="text-[12px] font-semibold text-slate-500 tabular-nums">{persen}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{ width: persen + '%' }} />
                    </div>
                  </div>
                );
              })()}

              {/* Pendaftaran */}
              <div className="bg-white rounded-2xl border border-slate-200/70 p-4 space-y-3">
                <p className="text-[13px] font-semibold text-slate-800">Daftarkan wajah acuan</p>

                <div>
                  <label className="block text-[11px] font-medium text-slate-500 mb-1.5">Karyawan</label>
                  <select className={inputCls} value={wajahTargetId} onChange={e => { setWajahTargetId(e.target.value); setWajahFileInfo([]); }}>
                    <option value="">— pilih karyawan —</option>
                    {wajahList.map(u => (
                      <option key={u.userId} value={u.userId}>
                        {u.nama} · {u.divisi || '-'}{u.terdaftar ? ' (sudah terdaftar)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-medium text-slate-500 mb-1.5">Foto wajah (maksimal 3 berkas)</label>
                  <input type="file" accept="image/*" multiple onChange={handlePilihFotoWajah} disabled={!wajahTargetId || !!wajahProses}
                    className="w-full text-[12px] text-slate-600 file:mr-3 file:py-2 file:px-3.5 file:rounded-lg file:border-0 file:text-[12px] file:font-medium file:bg-slate-900 file:text-white hover:file:bg-slate-800 disabled:opacity-50" />
                  <p className="text-[10.5px] text-slate-400 mt-1.5 leading-relaxed">
                    Pakai foto menghadap depan, terang, tanpa masker atau kacamata gelap, dan hanya berisi satu orang.
                    Beberapa foto dari sudut sedikit berbeda membuat pencocokan lebih tahan terhadap perubahan cahaya.
                  </p>
                </div>

                {wajahProses && (
                  <p className="flex items-center gap-2 text-[12px] font-medium text-blue-600">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> {wajahProses}
                  </p>
                )}

                {wajahFileInfo.length > 0 && (
                  <div className="space-y-2">
                    {wajahFileInfo.map((f, i) => (
                      <div key={i} className={`flex items-center gap-3 rounded-xl border p-2.5 ${f.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
                        {f.dataUrl
                          ? <img src={f.dataUrl} alt="" className="w-11 h-11 rounded-lg object-cover shrink-0" />
                          : <span className="w-11 h-11 rounded-lg bg-white/70 flex items-center justify-center shrink-0"><X className="w-4 h-4 text-rose-500" /></span>}
                        <div className="min-w-0">
                          <p className={`text-[12px] font-semibold truncate ${f.ok ? 'text-emerald-700' : 'text-rose-700'}`}>{f.nama}</p>
                          <p className={`text-[10.5px] leading-relaxed ${f.ok ? 'text-emerald-600' : 'text-rose-600'}`}>{f.ok ? 'Wajah terbaca ✓' : f.pesan}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <button onClick={handleSimpanWajah} disabled={loading || !!wajahProses || !wajahFileInfo.some(f => f.ok)}
                  className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white py-3 rounded-xl text-[14px] font-medium hover:bg-slate-800 disabled:opacity-40">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />} Simpan wajah acuan
                </button>
              </div>

              {/* Daftar karyawan */}
              <div className="flex items-center gap-2">
                <input className={inputCls} value={wajahCari} onChange={e => setWajahCari(e.target.value)} placeholder="Cari karyawan…" />
                <button onClick={() => setWajahHanyaBelum(!wajahHanyaBelum)}
                  className={`shrink-0 px-3 py-2.5 rounded-lg border text-[12px] font-medium transition-colors ${wajahHanyaBelum ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
                  Belum daftar
                </button>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200/70 overflow-hidden">
                <div className="divide-y divide-slate-100 max-h-[520px] overflow-y-auto">
                  {wajahList
                    .filter(u => !wajahHanyaBelum || !u.terdaftar)
                    .filter(u => {
                      const q = wajahCari.trim().toLowerCase();
                      if (!q) return true;
                      return (u.nama || '').toLowerCase().includes(q) || (u.username || '').toLowerCase().includes(q);
                    })
                    .map(u => (
                      <div key={u.userId} className="flex items-center gap-3 px-4 py-3">
                        {u.fotoAcuan
                          ? <img src={u.fotoAcuan} alt="" className="w-10 h-10 rounded-full object-cover shrink-0 border border-slate-200" />
                          : <span className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0"><User className="w-4 h-4 text-slate-400" /></span>}
                        <div className="min-w-0 flex-1">
                          <p className="text-[12.5px] font-semibold text-slate-800 truncate">{u.nama}</p>
                          <p className="text-[10.5px] text-slate-500 truncate">
                            {u.divisi || '-'} · {u.terdaftar ? `${u.jumlahSampel} sampel · ${u.updatedAt}` : 'belum terdaftar'}
                          </p>
                        </div>
                        {u.terdaftar ? (
                          <button onClick={() => handleHapusWajah(u.userId, u.nama)} disabled={loading} className="shrink-0 text-[11px] font-medium text-rose-500 hover:text-rose-700 disabled:opacity-40">Hapus</button>
                        ) : (
                          <button onClick={() => { setWajahTargetId(u.userId); setWajahFileInfo([]); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="shrink-0 text-[11px] font-medium text-slate-500 hover:text-slate-900">Daftarkan</button>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'news' && (
        <div className="animate-in fade-in duration-300">
          <div className="bg-white rounded-2xl border border-slate-200/70 overflow-hidden">
            <div className="p-4">
                <label className="block text-[11px] font-medium text-slate-500 mb-1.5">Isi pengumuman</label>
                <textarea className={`${inputCls} resize-y leading-relaxed`} rows="6" placeholder="Tulis informasi yang akan tampil di dashboard semua karyawan…" value={newsInput} onChange={(e) => setNewsInput(e.target.value)}></textarea>
                <p className="mt-1.5 text-[11px] text-slate-400">Muncul sebagai popup satu kali per sesi login.</p>
            </div>
            <div className="p-4 pt-0">
                <button onClick={handleAddAnnouncement} disabled={loading || !newsInput.trim()} className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white py-3 rounded-xl text-[14px] font-medium hover:bg-slate-800 disabled:opacity-40 transition-colors">
                    {loading && <Loader2 className="w-4 h-4 animate-spin"/>}
                    {loading ? 'Menerbitkan…' : 'Terbitkan'}
                </button>
            </div>
          </div>
        </div>
      )}
      </main>
    </div>
  );
}

// --- 7. LOGIN SCREEN (RESPONSIVE: FIXED MOBILE & FULLSCREEN DESKTOP) ---
function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(null);
  const [time, setTime] = useState(() => getServerNow());
  const [showResetModal, setShowResetModal] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setTime(getServerNow()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Identitas perangkat ikut dikirim: server memakainya untuk aturan
      // 1 perangkat 1 akun dan untuk menggusur sesi di HP lain
      // (lihat apps-script/Devices.gs).
      // Lama request login dicatat ke console supaya penyebab "login
      // lama" bisa dipisahkan tanpa membuka DevTools > Network:
      //   angka besar di sini  -> masalahnya di server / jaringan
      //   angka kecil di sini  -> masalahnya SESUDAH login (gerbang GPS,
      //                           atau pengisian dashboard)
      // Buka console browser, lalu login. Sumbernya ikut tercetak, jadi
      // terlihat jelas jalur mana yang benar-benar dipakai.
      const mulaiLogin = Date.now();
      const muatan = { action: 'login', username, password, ...infoPerangkat() };

      // FASE 1: jalur Supabase dicoba lebih dulu. Selama SUPABASE_URL
      // belum diisi, fungsi ini langsung mengembalikan null dan tidak ada
      // yang berubah sama sekali dari perilaku lama.
      //
      // null berarti "jangan pakai jalur ini" — bisa karena belum
      // dinyalakan, gagal, lambat, atau karena kasusnya butuh gerbang
      // perangkat lengkap yang hanya ada di Apps Script.
      let data = await loginLewatSupabase(muatan);
      let sumberLogin = 'supabase';

      if (!data) {
        sumberLogin = 'apps-script';
        const response = await fetchApi(SCRIPT_URL, {
          method: 'POST',
          body: JSON.stringify(muatan)
        });
        data = await response.json();
      }

      console.info('[absensi] request login (' + sumberLogin + '): '
        + (Date.now() - mulaiLogin) + ' ms');
      if (data.result === 'success' && data.user) {
        onLogin(
          data.user,
          data.masterData || [],
          data.version,
          data.stats,
          data.pengumuman,
          data.pengumumanDisertakan === true,
          {
            periods: data.periods || [],
            periodsAktif: data.periodsAktif || [],
            periodeDefault: data.periodeDefault || null
          },
          // Hanya status pelacakan GPS. Statistik dan angka approval
          // SENGAJA tidak ikut di respons login — lihat catatan di
          // handleLogin (App.js) dan nomor 6 di handleLogin (Code.gs).
          { gpsTracking: data.gpsTracking || null }
        );
      } else {
        alert(data.message || 'Login Gagal');
      }
    } catch (err) {
      alert('Gagal koneksi server.');
    } finally {
      setLoading(false);
    }
  };

  const jamFormatted = time.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const tglFormatted = time.toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen w-full flex flex-col md:flex-row bg-slate-950 text-slate-800 font-sans relative overflow-hidden">
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-blue-600/20 md:bg-blue-600/15 rounded-full blur-3xl pointer-events-none animate-[pulse_8s_ease-in-out_infinite]"></div>
      <div className="absolute top-1/3 -right-32 w-96 h-96 bg-cyan-500/15 md:bg-cyan-500/10 rounded-full blur-3xl pointer-events-none animate-[pulse_10s_ease-in-out_infinite]"></div>
      <div className="absolute -bottom-32 left-1/3 w-96 h-96 bg-indigo-600/20 md:bg-indigo-600/15 rounded-full blur-3xl pointer-events-none animate-[pulse_7s_ease-in-out_infinite]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(#1e293b_1px,transparent_1px)] [background-size:24px_24px] opacity-30 md:opacity-40 pointer-events-none"></div>

      {/* DESKTOP VIEW HERO */}
      <div className="hidden md:flex md:w-1/2 lg:w-3/5 flex-col justify-between p-8 lg:p-14 relative z-10 border-r border-slate-800/80 bg-gradient-to-br from-slate-950 via-slate-900/90 to-blue-950/80 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-tr from-blue-600 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/30 p-2.5">
              <ScanFace className="w-full h-full text-white" />
            </div>
            <div>
              <h1 className="text-base font-black tracking-wider text-white">JPT GROUP</h1>
              <p className="text-[11px] font-medium text-blue-300/80 tracking-wide uppercase">Portal Kehadiran Digital</p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-900/80 border border-slate-700/60 shadow-inner">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping"></span>
            <span className="w-2 h-2 rounded-full bg-emerald-500 -ml-4"></span>
            <span className="text-[11px] font-semibold text-slate-300">Sistem Aktif & Terintegrasi</span>
          </div>
        </div>

        <div className="my-auto py-8">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-500/10 border border-blue-400/20 text-blue-300 text-xs font-semibold mb-5 shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
            <span>Smart Workforce & Attendance Platform</span>
          </div>
          <h2 className="text-3xl lg:text-4xl xl:text-5xl font-black text-white tracking-tight leading-tight">
            Sistem Absensi & <br />
            <span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-indigo-300 bg-clip-text text-transparent">Manajemen Kehadiran</span>
          </h2>
          <p className="mt-4 text-slate-300/90 text-sm lg:text-base leading-relaxed max-w-xl">
            Solusi digital cerdas untuk pencatatan absensi presisi, validasi geofencing lokasi kerja, verifikasi biometrik anti-spoofing, dan rekapitulasi data kehadiran secara real-time.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3.5 mt-8 max-w-2xl">
            <div className="bg-slate-900/60 backdrop-blur-md p-4 rounded-2xl border border-slate-800 hover:border-slate-700 transition-all duration-300 hover:translate-y-[-2px] shadow-sm">
              <div className="w-8 h-8 rounded-xl bg-blue-500/20 text-blue-400 flex items-center justify-center mb-2.5">
                <MapPin className="w-4 h-4" />
              </div>
              <h4 className="text-xs font-bold text-white mb-1">Smart Geofencing</h4>
              <p className="text-[11px] text-slate-400 leading-snug">Deteksi lokasi & radius presisi dengan proteksi anti fake GPS.</p>
            </div>
            <div className="bg-slate-900/60 backdrop-blur-md p-4 rounded-2xl border border-slate-800 hover:border-slate-700 transition-all duration-300 hover:translate-y-[-2px] shadow-sm">
              <div className="w-8 h-8 rounded-xl bg-cyan-500/20 text-cyan-400 flex items-center justify-center mb-2.5">
                <ScanFace className="w-4 h-4" />
              </div>
              <h4 className="text-xs font-bold text-white mb-1">Face Liveness</h4>
              <p className="text-[11px] text-slate-400 leading-snug">Verifikasi wajah aman dan terpercaya tanpa manipulasi foto.</p>
            </div>
            <div className="bg-slate-900/60 backdrop-blur-md p-4 rounded-2xl border border-slate-800 hover:border-slate-700 transition-all duration-300 hover:translate-y-[-2px] shadow-sm">
              <div className="w-8 h-8 rounded-xl bg-indigo-500/20 text-indigo-400 flex items-center justify-center mb-2.5">
                <Activity className="w-4 h-4" />
              </div>
              <h4 className="text-xs font-bold text-white mb-1">Real-time Sync</h4>
              <p className="text-[11px] text-slate-400 leading-snug">Rekap kehadiran, cuti, lembur, dan shift otomatis terpusat.</p>
            </div>
          </div>
          <div className="mt-8 inline-flex items-center gap-4 bg-slate-900/70 border border-slate-800/80 rounded-2xl px-5 py-3 shadow-inner">
            <div className="w-9 h-9 rounded-xl bg-blue-500/10 text-cyan-400 flex items-center justify-center">
              <Clock className="w-5 h-5 animate-pulse" />
            </div>
            <div>
              <p className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider capitalize">{tglFormatted}</p>
              <p className="text-lg font-black text-white tabular-nums tracking-wide">{jamFormatted} <span className="text-xs font-semibold text-blue-400">WIB</span></p>
            </div>
          </div>
        </div>

        <div className="pt-6 border-t border-slate-800/60 flex items-center justify-between text-xs text-slate-400">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <span>256-bit Encrypted Session • Akses Terautentikasi</span>
          </div>
          <span className="font-mono text-[11px] text-slate-500">v{FRONTEND_VERSION}</span>
        </div>
      </div>

      {/* FORM LOGIN PANEL */}
      <div className="w-full md:w-1/2 lg:w-2/5 min-h-screen flex flex-col items-center justify-center p-4 sm:p-6 md:p-8 lg:p-12 relative z-10 bg-slate-50 md:bg-slate-950/60 md:backdrop-blur-xl transition-all">
        <div className="md:hidden absolute -top-20 -left-20 w-80 h-80 bg-blue-200/50 rounded-full blur-3xl pointer-events-none animate-[pulse_8s_ease-in-out_infinite]"></div>
        <div className="md:hidden absolute top-40 right-0 w-72 h-72 bg-cyan-200/40 rounded-full blur-3xl pointer-events-none animate-[bounce_10s_infinite]"></div>
        <div className="md:hidden absolute -bottom-20 left-10 w-80 h-80 bg-indigo-200/40 rounded-full blur-3xl pointer-events-none animate-[pulse_6s_ease-in-out_infinite]"></div>

        <div className="bg-white/90 md:bg-white/95 backdrop-blur-2xl p-7 sm:p-9 rounded-[2.25rem] shadow-2xl shadow-blue-500/10 md:shadow-2xl border border-white/80 md:border-white/50 w-full max-w-[370px] sm:max-w-[400px] md:max-w-[420px] relative z-10 transform transition-all duration-300">
          <div className="flex flex-col items-center mb-7 sm:mb-8">
            <div className="relative group cursor-pointer">
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-500 to-cyan-400 rounded-full blur opacity-25 group-hover:opacity-40 transition duration-500"></div>
              <div className="relative bg-white p-4 sm:p-5 rounded-3xl shadow-lg border border-slate-100 flex items-center justify-center overflow-hidden w-20 h-20 sm:w-24 sm:h-24 group-hover:scale-105 transition-transform duration-300">
                 <div className="absolute w-full h-[2px] bg-gradient-to-r from-transparent via-blue-500 to-transparent top-0 animate-[scan_2.5s_linear_infinite]"></div>
                 <div className="absolute w-full h-full bg-blue-500/5 top-0 animate-[scan_2.5s_linear_infinite]"></div>
                 <ScanFace className="w-9 h-9 sm:w-10 sm:h-10 text-slate-800 relative z-10" />
              </div>
              <div className="absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2 bg-white p-1.5 rounded-full shadow-md border border-slate-100">
                <div className="bg-emerald-500 w-3 h-3 rounded-full animate-pulse"></div>
              </div>
            </div>
            <div className="text-center mt-5 sm:mt-6">
              <h2 className="text-2xl sm:text-2xl font-black text-slate-800 tracking-tight">Selamat Datang</h2>
              <p className="text-slate-500 text-xs font-medium mt-1">Silakan masuk ke akun presensi Anda</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
            <div>
              <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">ID Karyawan / Fingerprint</label>
              <div className={`group relative transition-all duration-300 rounded-2xl border bg-white ${focused === 'user' ? 'border-blue-500 ring-4 ring-blue-500/10 shadow-lg shadow-blue-500/10' : 'border-slate-200 shadow-sm'}`}>
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                   <div className={`p-1.5 rounded-lg transition-colors duration-300 ${focused === 'user' ? 'bg-blue-50 text-blue-600' : 'bg-slate-50 text-slate-400'}`}>
                     <Smartphone className="h-4 w-4" />
                   </div>
                </div>
                <input type="text" value={username} onFocus={() => setFocused('user')} onBlur={() => setFocused(null)} onChange={e => setUsername(e.target.value)} className="block w-full pl-12 pr-4 py-3.5 sm:py-4 bg-transparent rounded-2xl text-sm font-bold text-slate-800 placeholder-slate-400 focus:outline-none transition-colors" placeholder="Contoh: 1024" autoComplete="username" required />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">Kata Sandi</label>
              <div className={`group relative transition-all duration-300 rounded-2xl border bg-white ${focused === 'pass' ? 'border-blue-500 ring-4 ring-blue-500/10 shadow-lg shadow-blue-500/10' : 'border-slate-200 shadow-sm'}`}>
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                   <div className={`p-1.5 rounded-lg transition-colors duration-300 ${focused === 'pass' ? 'bg-blue-50 text-blue-600' : 'bg-slate-50 text-slate-400'}`}>
                     <Key className="h-4 w-4" />
                   </div>
                </div>
                <input type={showPassword ? "text" : "password"} value={password} onFocus={() => setFocused('pass')} onBlur={() => setFocused(null)} onChange={e => setPassword(e.target.value)} className="block w-full pl-12 pr-11 py-3.5 sm:py-4 bg-transparent rounded-2xl text-sm font-bold text-slate-800 placeholder-slate-400 focus:outline-none transition-colors" placeholder="Masukkan kata sandi" autoComplete="current-password" required />
                <button type="button" onClick={() => setShowPassword(!showPassword)} tabIndex={-1} className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 focus:outline-none" aria-label={showPassword ? "Sembunyikan sandi" : "Tampilkan sandi"}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end -mt-1 mb-1">
              <button
                type="button"
                onClick={() => setShowResetModal(true)}
                className="text-[11.5px] font-bold text-blue-600 hover:text-blue-700 hover:underline transition-colors flex items-center gap-1.5 focus:outline-none"
              >
                <KeyRound className="w-3.5 h-3.5" />
                <span>Lupa / Reset Kata Sandi?</span>
              </button>
            </div>

            <button type="submit" disabled={loading} className="w-full py-3.5 sm:py-4 px-4 rounded-2xl text-white font-bold text-sm bg-slate-900 hover:bg-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-500/30 shadow-xl shadow-slate-900/20 transform transition-all duration-300 hover:-translate-y-0.5 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed group relative overflow-hidden mt-2">
              <div className="absolute top-0 -left-full w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:animate-[shimmer_1.5s_infinite]"></div>
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-200" />
                  <span>Sedang Memverifikasi...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <span>Masuk Aplikasi</span>
                  <ChevronDown className="w-4 h-4 -rotate-90 group-hover:translate-x-1 transition-transform" />
                </div>
              )}
            </button>
          </form>

          <div className="mt-5 p-3 rounded-xl bg-slate-50 border border-slate-100 flex items-start gap-2.5">
            <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
            <p className="text-[11px] text-slate-500 leading-tight">Gunakan ID Fingerprint yang terdaftar di HRD. Hubungi tim IT Support jika mengalami kendala login.</p>
          </div>

          <div className="mt-6 text-center">
            <p className="text-[10px] text-slate-400 font-medium">&copy; {new Date().getFullYear()} JPT Group &bull; IT Support Dept.</p>
            <div className="w-10 h-1 bg-slate-100 rounded-full mx-auto mt-2.5"></div>
          </div>
        </div>

        <ResetPasswordModal
          isOpen={showResetModal}
          onClose={() => setShowResetModal(false)}
          onResetSuccess={(usr) => {
            setUsername(usr);
          }}
        />

        <style>{`@keyframes scan { 0% { top: 0%; opacity: 0; } 10% { opacity: 1; } 90% { opacity: 1; } 100% { top: 100%; opacity: 0; } } @keyframes shimmer { 100% { left: 100%; } }`}</style>
      </div>
    </div>
  );
}

// --- MODAL RESET PASSWORD MANDIRI (SELF-SERVICE) ---
function ResetPasswordModal({ isOpen, onClose, onResetSuccess }) {
  const [resetUsername, setResetUsername] = useState('');
  const [resetPayroll, setResetPayroll] = useState('');
  const [resetNewPass, setResetNewPass] = useState('');
  const [resetConfirmPass, setResetConfirmPass] = useState('');
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  if (!isOpen) return null;

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    if (!resetUsername.trim()) {
      setErrorMsg('ID Karyawan / Fingerprint wajib diisi.');
      return;
    }
    if (!resetPayroll.trim()) {
      setErrorMsg('No. Payroll / NIK wajib diisi.');
      return;
    }
    if (resetNewPass.length < 6) {
      setErrorMsg('Kata sandi baru minimal 6 karakter.');
      return;
    }
    if (resetNewPass !== resetConfirmPass) {
      setErrorMsg('Konfirmasi kata sandi baru tidak cocok.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetchApi(SCRIPT_URL, {
        method: 'POST',
        body: JSON.stringify({
          action: 'reset_password_mandiri',
          username: resetUsername.trim(),
          noPayroll: resetPayroll.trim(),
          newPassword: resetNewPass
        })
      });
      const data = await response.json();
      if (data.result === 'success') {
        alert(data.message || 'Kata sandi berhasil direset! Silakan login dengan kata sandi baru.');
        onResetSuccess(resetUsername.trim());
        onClose();
      } else {
        setErrorMsg(data.message || 'Verifikasi gagal. Periksa kembali ID Karyawan dan No. Payroll / NIK Anda.');
      }
    } catch (err) {
      setErrorMsg('Gagal menghubungi server. Mohon periksa koneksi internet.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/75 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-2xl border border-slate-100 w-full max-w-md my-8 relative animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between pb-4 mb-5 border-b border-slate-100">
          <div className="flex items-center gap-3.5">
            <div className="w-12 h-12 rounded-2xl bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-500/25 shrink-0">
              <KeyRound className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-800 tracking-tight leading-tight">Reset Kata Sandi</h3>
              <p className="text-xs text-slate-400 font-medium mt-0.5">Layanan Mandiri Karyawan</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
            title="Tutup"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {errorMsg && (
          <div className="mb-4 p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs font-medium flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
            <span>{errorMsg}</span>
          </div>
        )}

        <form onSubmit={handleResetSubmit} className="space-y-3.5">
          <div>
            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">ID Karyawan / Fingerprint</label>
            <input
              required
              type="text"
              value={resetUsername}
              onChange={e => setResetUsername(e.target.value)}
              placeholder="Contoh: 1024"
              className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/50 text-sm font-semibold text-slate-800 placeholder:text-slate-400 focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all"
            />
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">No. Payroll / NIK</label>
            <input
              required
              type="text"
              value={resetPayroll}
              onChange={e => setResetPayroll(e.target.value)}
              placeholder="Masukkan No. Payroll / NIK"
              className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/50 text-sm font-semibold text-slate-800 placeholder:text-slate-400 focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all"
            />
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">Kata Sandi Baru</label>
            <div className="relative">
              <input
                required
                type={showNewPass ? "text" : "password"}
                value={resetNewPass}
                onChange={e => setResetNewPass(e.target.value)}
                placeholder="Minimal 6 karakter"
                className="w-full px-4 py-3 pr-11 rounded-xl border border-slate-200 bg-slate-50/50 text-sm font-semibold text-slate-800 placeholder:text-slate-400 focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all"
              />
              <button
                type="button"
                onClick={() => setShowNewPass(!showNewPass)}
                tabIndex={-1}
                className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 focus:outline-none"
              >
                {showNewPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">Konfirmasi Kata Sandi Baru</label>
            <div className="relative">
              <input
                required
                type={showConfirmPass ? "text" : "password"}
                value={resetConfirmPass}
                onChange={e => setResetConfirmPass(e.target.value)}
                placeholder="Ulangi kata sandi baru"
                className="w-full px-4 py-3 pr-11 rounded-xl border border-slate-200 bg-slate-50/50 text-sm font-semibold text-slate-800 placeholder:text-slate-400 focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPass(!showConfirmPass)}
                tabIndex={-1}
                className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 focus:outline-none"
              >
                {showConfirmPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 px-4 rounded-xl text-white font-bold text-sm bg-slate-900 hover:bg-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-500/30 shadow-xl shadow-slate-900/15 transform transition-all active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-blue-200" />
                  <span>Memverifikasi & Menyimpan...</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 text-emerald-400" />
                  <span>Simpan & Reset Kata Sandi</span>
                </>
              )}
            </button>
          </div>
        </form>

        <div className="mt-4 p-3 rounded-xl bg-slate-50 border border-slate-100 flex items-start gap-2.5">
          <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
          <p className="text-[11px] text-slate-500 leading-tight">
            ID Karyawan dan No. Payroll / NIK harus sesuai dengan data yang terdaftar di HRD untuk verifikasi keamanan.
          </p>
        </div>

      </div>
    </div>
  );
}

// --- 8. CHANGE PASSWORD SCREEN (EPIC THEME) ---
function ChangePasswordScreen({ user, setView }) { 
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState(''); 
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChangePassword = async (e) => { 
    e.preventDefault(); 
    if (newPassword.length < 6) {
      alert('Kata sandi baru minimal 6 karakter.');
      return;
    }
    setLoading(true);
    try { 
      const res = await fetchApi(SCRIPT_URL, { 
        method: 'POST', 
        body: JSON.stringify({ action: 'ganti_password', id: user.id, oldPassword, newPassword }) 
      }).then(r => r.json());
      if (res.result === 'success') { 
        alert(res.message || 'Password berhasil diubah!'); 
        setView('dashboard');
      } else { 
        alert(res.message || 'Gagal mengubah kata sandi.');
      } 
    } catch (err) { 
      alert('Gagal menghubungi server.');
    } finally { 
      setLoading(false); 
    } 
  };

  return ( 
    <div className="min-h-[85vh] py-6 sm:py-10 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-[28px] sm:rounded-3xl p-6 sm:p-8 shadow-xl shadow-slate-900/5 border border-slate-200/80 relative">
        
        {/* Top Header inside Card */}
        <div className="flex items-center justify-between pb-5 mb-6 border-b border-slate-100">
          <div className="flex items-center gap-3.5">
            <div className="w-12 h-12 rounded-2xl bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-500/25 shrink-0">
              <KeyRound className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-800 tracking-tight">Ubah Kata Sandi</h2>
              <p className="text-xs text-slate-400 font-medium mt-0.5">Perbarui keamanan akun Anda</p>
            </div>
          </div>
          <BackButton onClick={() => setView('dashboard')} />
        </div>

        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-2">KATA SANDI LAMA</label>
            <div className="relative">
              <input
                required
                type={showOldPassword ? "text" : "password"}
                className="w-full px-4 py-3.5 pr-11 rounded-2xl border border-slate-200 bg-white text-sm font-semibold text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all outline-none"
                value={oldPassword}
                onChange={e => setOldPassword(e.target.value)}
                placeholder="Masukkan kata sandi lama"
              />
              <button
                type="button"
                onClick={() => setShowOldPassword(!showOldPassword)}
                tabIndex={-1}
                className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 focus:outline-none"
              >
                {showOldPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-2">KATA SANDI BARU</label>
            <div className="relative">
              <input
                required
                type={showNewPassword ? "text" : "password"}
                className="w-full px-4 py-3.5 pr-11 rounded-2xl border border-slate-200 bg-white text-sm font-semibold text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all outline-none"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Minimal 6 karakter"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                tabIndex={-1}
                className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 focus:outline-none"
              >
                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 px-4 rounded-2xl text-white font-bold text-sm bg-slate-900 hover:bg-slate-800 focus:outline-none focus:ring-4 focus:ring-slate-900/20 shadow-xl shadow-slate-900/15 transform transition-all duration-200 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-6"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-slate-300" />
                  <span>Menyimpan...</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 text-emerald-400" />
                  <span>Simpan Kata Sandi Baru</span>
                </>
              )}
            </button>
          </div>
        </form>

        <div className="mt-6 pt-5 border-t border-slate-100 text-center">
          <p className="text-xs text-slate-400 leading-relaxed max-w-xs mx-auto">
            Pastikan kata sandi baru mudah diingat dan tidak dibagikan kepada orang lain.
          </p>
        </div>

      </div>
    </div> 
  );
}

// --- 9. DB ABSEN SCREEN (FIXED FILTER HADIR GABUNGAN & BUTTON AUTO-FILL) ---
// ============================================================
// PALET KALENDER DATA MESIN
//
// Satu warna per simbol absensi. Ini bukan sekadar hiasan: kode
// simbolnya (T, TSi, SiPC, AC…) tidak bisa dibaca sekilas, sedangkan
// warnanya bisa — sebulan penuh langsung terbaca sebagai pola.
//
// Pengelompokannya mengikuti arti, bukan huruf:
//   hijau  = hadir normal
//   kuning = datang/pulang tidak sesuai jam (masih hadir)
//   merah muda = ada scan yang hilang
//   merah  = tidak masuk tanpa keterangan
//   biru/teal/langit = ketidakhadiran yang sudah berizin
//   abu    = libur
// ============================================================
const GAYA_KALENDER = {
  // PALET TERVALIDASI (Agu 2026).
  //
  // Palet sebelumnya memakai 13 warna dan GAGAL diuji: violet(Sakit) vs
  // biru(Ijin) hanya berjarak dE 1.3 untuk mata deuteranopia — praktis
  // sama, dan sky(Dinas Luar) vs cyan(Extra Ordinary) berjarak dE 6.4
  // bahkan untuk penglihatan normal. Menambah warna sampai 21 (satu per
  // keterangan) justru memperburuk: tidak ada 21 warna yang bisa dibedakan
  // manusia secara andal di layar HP.
  //
  // Yang dipakai sekarang: 8 KELUARGA warna yang lolos seluruh pemeriksaan
  // (jarak antar-warna, penglihatan warna terbatas, kontras terhadap latar),
  // DITAMBAH kode simbolnya sebagai teks. Perbedaan antar keterangan dijamin
  // oleh kode + label, bukan oleh warna saja — jadi tetap terbaca saat
  // dicetak hitam-putih dan oleh mata yang sulit membedakan warna.
  //
  // Keluarga: Hadir | Telat | Tidak-scan | Alpa | Ijin | Sakit | Cuti | Dinas
  H:    { dot: 'bg-emerald-600', tint: 'bg-emerald-50',  teks: 'text-emerald-700' },

  T:    { dot: 'bg-amber-500',   tint: 'bg-amber-50',    teks: 'text-amber-700'   },
  TPC:  { dot: 'bg-amber-500',   tint: 'bg-amber-50',    teks: 'text-amber-700'   },
  PC:   { dot: 'bg-amber-500',   tint: 'bg-amber-50',    teks: 'text-amber-700'   },

  TSi:  { dot: 'bg-orange-600',  tint: 'bg-orange-50',   teks: 'text-orange-700'  },
  TSo:  { dot: 'bg-orange-600',  tint: 'bg-orange-50',   teks: 'text-orange-700'  },
  SiPC: { dot: 'bg-orange-600',  tint: 'bg-orange-50',   teks: 'text-orange-700'  },
  Si:   { dot: 'bg-orange-600',  tint: 'bg-orange-50',   teks: 'text-orange-700'  },
  So:   { dot: 'bg-orange-600',  tint: 'bg-orange-50',   teks: 'text-orange-700'  },
  SiSo: { dot: 'bg-orange-600',  tint: 'bg-orange-50',   teks: 'text-orange-700'  },
  NF:   { dot: 'bg-orange-600',  tint: 'bg-orange-50',   teks: 'text-orange-700'  },

  A:    { dot: 'bg-rose-700',    tint: 'bg-rose-50',     teks: 'text-rose-700'    },
  AC:   { dot: 'bg-rose-700',    tint: 'bg-rose-50',     teks: 'text-rose-700'    },

  I:    { dot: 'bg-blue-600',    tint: 'bg-blue-50',     teks: 'text-blue-700'    },
  S:    { dot: 'bg-fuchsia-700', tint: 'bg-fuchsia-50',  teks: 'text-fuchsia-700' },

  C:    { dot: 'bg-cyan-600',    tint: 'bg-cyan-50',     teks: 'text-cyan-700'    },
  CB:   { dot: 'bg-cyan-600',    tint: 'bg-cyan-50',     teks: 'text-cyan-700'    },

  DL:   { dot: 'bg-lime-700',    tint: 'bg-lime-50',     teks: 'text-lime-700'    },
  EO:   { dot: 'bg-lime-700',    tint: 'bg-lime-50',     teks: 'text-lime-700'    },

  ONL:  { dot: 'bg-blue-600',    tint: 'bg-blue-50',     teks: 'text-blue-700'    },

  // Off/libur sengaja netral: bukan salah satu kategori, melainkan
  // ketiadaan jadwal. Memberinya warna sendiri membuatnya tampak setara
  // dengan status yang perlu diperhatikan.
  O:    { dot: 'bg-slate-300',   tint: 'bg-slate-50',    teks: 'text-slate-400'   },

  // Hari ini yang di mesin masih tertulis Alpa karena memang belum ada scan.
  // Netral juga: harinya belum selesai, jadi belum ada yang perlu dicek.
  BLM:  { dot: 'bg-slate-300',   tint: 'bg-slate-50',    teks: 'text-slate-500'   },
};
const GAYA_KAL_LAIN = { dot: 'bg-slate-400', tint: 'bg-slate-100', teks: 'text-slate-600' };
const gayaKalender = (sym) => GAYA_KALENDER[sym] || GAYA_KAL_LAIN;

const NAMA_BULAN = ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];
// Pekan dimulai Senin, mengikuti kebiasaan kalender kerja di Indonesia.
const HARI_PENDEK = ['Sen','Sel','Rab','Kam','Jum','Sab','Min'];

// Empat kelompok di kartu ringkasan bulan. SATU sumber kebenaran yang
// dipakai untuk MENGHITUNG angkanya sekaligus MENYARING saat kartunya
// diklik — kalau keduanya punya daftar kode sendiri-sendiri, cepat atau
// lambat angka yang tertulis tidak akan cocok dengan hari yang menyala.
const GRUP_RINGKAS = {
  GRUP_HADIR: { label: 'Hadir',     kode: ['H', 'T', 'TPC', 'PC', 'TSi', 'TSo', 'Si', 'So', 'SiSo', 'SiPC', 'ONL', 'NF'], warna: 'text-slate-900',  aktif: 'bg-slate-900 text-white' },
  GRUP_TELAT: { label: 'Telat',     kode: ['T', 'TPC', 'TSi', 'TSo', 'SiPC'],                                warna: 'text-amber-600',  aktif: 'bg-amber-500 text-white' },
  GRUP_PERLU: { label: 'Perlu cek', kode: ['Si', 'So', 'SiSo', 'A', 'AC'],                                   warna: 'text-rose-600',   aktif: 'bg-rose-600 text-white' },
  GRUP_IZIN:  { label: 'Izin/cuti', kode: ['I', 'S', 'C', 'CB', 'DL', 'EO'],                                 warna: 'text-slate-900',  aktif: 'bg-teal-600 text-white' },
};

/** Date -> 'YYYY-MM-DD' memakai komponen waktu LOKAL (bukan UTC). */
const kunciHari = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Mesin (Att) menandai hari berjalan sebagai 'A' (Alpa) begitu tanggal itu
 * ikut ter-export, padahal karyawan mungkin memang belum waktunya absen —
 * contoh: import jam 01:00, jam kerja mulai 08:30. Hari ini (dan tanggal
 * sesudahnya) yang masih Alpa TANPA scan masuk/pulang ditampilkan sebagai
 * 'BLM' (Belum Absen). Begitu harinya lewat, simbol aslinya kembali dipakai.
 *
 * Data mentah tidak diubah; simbol asli tetap ada di `symbolAsli`.
 */
const kosongScan = (v) => v === undefined || v === null || String(v).trim() === '' || String(v).trim() === '-';
function tandaiBelumAbsen(item, kunciHariIni, kunciItem) {
  if (!item || item.symbol !== 'A' || !kunciItem) return item;
  if (kunciItem < kunciHariIni) return item;
  if (!kosongScan(item.masuk) || !kosongScan(item.pulang)) return item;
  return { ...item, symbol: 'BLM', symbolAsli: item.symbol };
}

function DbAbsenScreen({ user, setView }) {
  const [listMentah, setList] = useState(() => {
    try {
      const raw = sessionStorage.getItem('app_db_absen_' + (user ? user.id : ''));
      if (raw) return JSON.parse(raw);
    } catch (e) { /* ignore */ }
    return [];
  });
  const [loading, setLoading] = useState(() => {
    try {
      const raw = sessionStorage.getItem('app_db_absen_' + (user ? user.id : ''));
      return !raw;
    } catch (e) { return true; }
  });
  const [ijinCount, setIjinCount] = useState(() => {
    try {
      const raw = sessionStorage.getItem('app_stats_terakhir');
      if (raw) {
        const s = JSON.parse(raw);
        return s ? (s.ijin_count || 0) : 0;
      }
    } catch (e) { /* ignore */ }
    return 0;
  });
  const [lastUpdate, setLastUpdate] = useState(() => {
    try {
      return sessionStorage.getItem('app_db_absen_last_' + (user ? user.id : '')) || null;
    } catch (e) { return null; }
  });

  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [showFilter, setShowFilter] = useState(false);

  const [tampilan, setTampilan] = useState('kalender'); // 'kalender' | 'daftar'
  const [bulanAktif, setBulanAktif] = useState(null);   // objek Date di tanggal 1
  const [hariDipilih, setHariDipilih] = useState(null); // 'YYYY-MM-DD'

  const { libur, status: statusLibur, tahunAda } = useHariLibur();

  // Lihat tandaiBelumAbsen(): hari ini yang masih 'A' tanpa scan bukan Alpa.
  // Harus didefinisikan sebelum pemakaian `list` pertama (opsi filter dll).
  const list = useMemo(() => {
    const hariIni = kunciHari(new Date());
    return listMentah.map((it) => {
      let k = '';
      if (it && it.tanggalRaw && /^\d{4}-\d{2}-\d{2}$/.test(it.tanggalRaw)) {
        k = it.tanggalRaw;
      } else if (it && it.tanggal) {
        const m = String(it.tanggal).match(/(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
        if (m) k = `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
        else if (/^\d{4}-\d{2}-\d{2}$/.test(it.tanggal)) k = it.tanggal;
      }
      return tandaiBelumAbsen(it, hariIni, k);
    });
  }, [listMentah]);


  const pilihHari = (k) => {
    if (!k) return;
    setHariDipilih(k);
    const th = Number(k.slice(0, 4)), bl = Number(k.slice(5, 7)) - 1;
    setBulanAktif((b) => (b && b.getFullYear() === th && b.getMonth() === bl ? b : new Date(th, bl, 1)));
  };

  const TARGET_CODES = ['T', 'TSi', 'TSo', 'Si', 'So'];

  useEffect(() => {
      const autoFilter = localStorage.getItem('dbAbsenFilter');
      if (autoFilter) {
          setFilterStatus(autoFilter); 
          setShowFilter(true);
          localStorage.removeItem('dbAbsenFilter');
      }
  }, []);

  const KETERANGAN_MAP = {
      'H': 'Hadir', 'T': 'Terlambat', 'O': 'Off / Libur', 'CB': 'Cuti Bersama',
      'PC': 'Pulang Cepat', 'Si': 'Tidak Absen Masuk', 'So': 'Tidak Absen Pulang',
      'I': 'Ijin', 'S': 'Sakit', 'C': 'Cuti', 'A': 'Alpa',
      'DL': 'Dinas Luar', 'ONL': 'Absen online', 'STB': 'Standby', 'TPC': 'Telat & Pulang Cepat', 'TSo': 'Telat & Tdk Absen OUT',
      'TSi': 'Telat & No Scan In', 'SiSo': 'Tdk Absen IN & OUT',
      'SiPC': 'Tdk Absen IN & Pulang Cepat', 'AC': 'Alpa (Lebih Cuti)',
      'EO': 'Extra Ordinary', 'NF': 'Bebas absen mesin (masuk)', 'BLM': 'Belum Absen'
  };
  
  const availableStatusOptions = [...new Set(list.flatMap(item => [item.symbol, ...(Array.isArray(item.onlineRecords) && item.onlineRecords.length > 0 ? ['ONL'] : [])]))]
      .filter(s => s && s.trim() !== '')
      .sort();

  const calculateTimeAgo = (isoDateString) => {
    if (!isoDateString || isoDateString === '-') return '-';
    try {
        const now = new Date();
        const past = new Date(isoDateString);
        const diffMs = now - past; 
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

        if (diffMinutes < 1) return 'Baru saja update';
        if (diffMinutes < 60) return diffMinutes + ' menit yang lalu';
        if (diffHours < 24) {
            const sisaMenit = diffMinutes % 60;
            if (sisaMenit === 0) return diffHours + ' jam yang lalu';
            return diffHours + ' jam ' + sisaMenit + ' menit yang lalu';
        }
        return past.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute:'2-digit'});
    } catch (e) { return '-'; }
  };

  useEffect(() => {
    const dariCache = (() => {
      try {
        const raw = sessionStorage.getItem('app_stats_terakhir');
        return raw ? JSON.parse(raw) : null;
      } catch (e) { return null; }
    })();

    if (dariCache) {
      setIjinCount(dariCache.ijin_count || 0);
      return;
    }

    const fetchStats = async () => {
        try {
            const res = await fetchApi(SCRIPT_URL, {
                method: 'POST', body: JSON.stringify({ action: 'get_stats', userId: user.id })
            });
            const data = await res.json();
            if (data.result === 'success') setIjinCount(data.stats.ijin_count || 0);
        } catch (e) { console.error('Gagal load stats'); }
    };
    if (user) fetchStats();
  }, [user]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetchApi(SCRIPT_URL, { 
            method: 'POST', 
            body: JSON.stringify({ action: 'get_db_absen', userId: user.id, nama: user.nama, noPayroll: user.noPayroll })
        });
        const data = await res.json();
        if (data.result === 'success') {
             setList(data.list);
             setLastUpdate(data.lastUpdate); 
             try {
               sessionStorage.setItem('app_db_absen_' + user.id, JSON.stringify(data.list));
               if (data.lastUpdate) sessionStorage.setItem('app_db_absen_last_' + user.id, data.lastUpdate);
             } catch (e) { /* ignore */ }
        } else {
             if (!list.length) alert(data.message);
        }
      } catch (e) {
        console.error(e);
        if (!list.length) alert('Gagal memuat data mesin.');
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchData();
  }, [user]);

  const parseDate = (dateStr) => {
      if (!dateStr) return null;
      try {
          if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return new Date(dateStr);
          const parts = dateStr.match(/(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
          if (parts) return new Date(`${parts[3]}-${parts[2]}-${parts[1]}`);
          return new Date(dateStr);
      } catch (e) { return null; }
  };

  // --- LOGIKA FILTER UTAMA ---
  // Dipecah jadi dua tahap. Alasannya bukan kerapian: angka ringkasan
  // bulan (Hadir / Telat / Perlu cek / Izin-cuti) harus dihitung dari
  // data yang sudah disaring TANGGAL tapi BELUM disaring status.
  // Kalau dihitung dari hasil akhir, begitu satu kartu diklik, tiga
  // kartu lain langsung jadi 0 dan tidak bisa diklik lagi.
  const cocokTanggal = (item) => {
    if (!filterStart && !filterEnd) return true;
    const itemDateObj = parseDate(item.tanggal);
    if (!itemDateObj || isNaN(itemDateObj.getTime())) return false;
    const itemTime = itemDateObj.setHours(0, 0, 0, 0);
    const startTime = filterStart ? new Date(filterStart).setHours(0, 0, 0, 0) : null;
    const endTime = filterEnd ? new Date(filterEnd).setHours(23, 59, 59, 999) : null;
    return (!startTime || itemTime >= startTime) && (!endTime || itemTime <= endTime);
  };

  const cocokStatus = (item) => {
    if (filterStatus === 'All') return true;
    if (filterStatus === 'ONL') return item.symbol === 'ONL' || (Array.isArray(item.onlineRecords) && item.onlineRecords.length > 0);
    // Grup dari kartu ringkasan.
    if (GRUP_RINGKAS[filterStatus]) return GRUP_RINGKAS[filterStatus].kode.includes(item.symbol);
    // 'HADIR_ALL' sengaja DIBIARKAN apa adanya: nilai ini juga dipakai
    // Dashboard lewat handleStatClick, jadi mengubah isinya akan diam-diam
    // mengubah arti tombol di layar lain.
    if (filterStatus === 'HADIR_ALL') {
      return ['H', 'I', 'T', 'TSi', 'TSo', 'TPC', 'SiPC', 'So', 'Si', 'PC', 'ONL', 'NF'].includes(item.symbol);
    }
    return item.symbol === filterStatus;
  };

  const listTanggal = list.filter(cocokTanggal);   // dasar angka ringkasan
  const filteredList = listTanggal.filter(cocokStatus);  // dasar kisi & daftar

  // ============================================================
  // INDEKS TANGGAL UNTUK KALENDER
  //
  // Disusun dari filteredList, bukan list mentah — supaya panel Filter
  // dan jalur "klik angka di Dashboard" tetap berlaku sama persis di
  // kedua tampilan. Memfilter "Telat" lalu membuka kalender akan
  // menyalakan hanya hari-hari telat.
  //
  // tanggalRaw dipakai lebih dulu kalau ada, karena sudah yyyy-MM-dd
  // dari server dan tidak perlu ditebak formatnya.
  // ============================================================
  const tglDariItem = (it) => {
    if (it.tanggalRaw && /^\d{4}-\d{2}-\d{2}$/.test(it.tanggalRaw)) {
      // Ditambah T00:00:00 supaya dibaca sebagai waktu LOKAL. Tanpa itu
      // 'yyyy-MM-dd' polos dibaca sebagai UTC, dan di WIB tanggalnya
      // mundur satu hari.
      const d = new Date(it.tanggalRaw + 'T00:00:00');
      if (!isNaN(d.getTime())) return d;
    }
    const d = parseDate(it.tanggal);
    return d && !isNaN(d.getTime()) ? d : null;
  };

  const petaHari = {};
  filteredList.forEach((it) => {
    const d = tglDariItem(it);
    if (d) petaHari[kunciHari(d)] = it;
  });
  const kunciTerurut = Object.keys(petaHari).sort();

  // Bulan yang pertama kali ditampilkan = bulan data TERBARU, bukan bulan
  // berjalan. Isi dbabsen hanya berubah saat admin melakukan import, jadi
  // membuka di bulan berjalan sering menampilkan kalender kosong.
  useEffect(() => {
    if (bulanAktif || list.length === 0) return;
    let maks = null;
    list.forEach((it) => {
      const d = tglDariItem(it);
      if (d && (!maks || d > maks)) maks = d;
    });
    const p = maks || new Date();
    setBulanAktif(new Date(p.getFullYear(), p.getMonth(), 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, bulanAktif]);

  const geserBulan = (delta) => {
    if (!bulanAktif) return;
    setBulanAktif(new Date(bulanAktif.getFullYear(), bulanAktif.getMonth() + delta, 1));
  };

  // Sel-sel grid: sisipan kosong di depan supaya tanggal 1 jatuh di
  // kolom hari yang benar. getDay() memberi 0 untuk Minggu, sedangkan
  // pekan di sini mulai Senin — karena itu (+6)%7.
  const selKalender = (() => {
    if (!bulanAktif) return [];
    const th = bulanAktif.getFullYear(), bl = bulanAktif.getMonth();
    const awal = (new Date(th, bl, 1).getDay() + 6) % 7;
    const jml = new Date(th, bl + 1, 0).getDate();
    const sel = [];
    for (let i = 0; i < awal; i++) sel.push(null);
    for (let t = 1; t <= jml; t++) sel.push(new Date(th, bl, t));
    return sel;
  })();

  const kunciHariIni = kunciHari(new Date());

  // Simbol yang benar-benar muncul di bulan ini — dasar keterangan warna.
  // Menampilkan seluruh 19 kode sekaligus hanya jadi kamus yang tidak dibaca.
  const simbolBulanIni = [...new Set(
    selKalender.filter(Boolean)
      .map((d) => petaHari[kunciHari(d)])
      .filter(Boolean)
      .map((it) => it.symbol)
      .filter((s) => s && s.trim() !== '')
  )].sort();

  // ---- LAPIS HARI LIBUR NASIONAL ----
  // Lapis tambahan, bukan syarat. Kalau sumbernya tidak bisa dihubungi,
  // `libur` tinggal objek kosong dan seluruh kalender tetap berjalan.
  const liburBulanIni = selKalender
    .filter(Boolean)
    .map((d) => ({ k: kunciHari(d), l: libur[kunciHari(d)] }))
    .filter((x) => x.l)
    .map((x) => ({ tgl: Number(x.k.slice(8)), ...x.l }));

  const tahunAktif = bulanAktif ? bulanAktif.getFullYear() : null;
  // Sumbernya hanya memuat tahun berjalan. Membedakan "tahun ini memang
  // tidak ada libur" dari "data tahun itu tidak tersedia" mencegah orang
  // menyimpulkan kalendernya rusak.
  const liburTakTersedia = statusLibur === 'siap' && tahunAktif && tahunAda.length > 0 && !tahunAda.includes(tahunAktif);

  // Ringkasan bulan berjalan — angka yang paling sering dicari orang saat
  // membuka layar ini, tanpa harus menghitung sel satu per satu.
  //
  // Dihitung dari listTanggal (BELUM disaring status), jadi angkanya tetap
  // utuh walau salah satu kartunya sedang aktif. Kalau dihitung dari data
  // yang sudah disaring, mengklik "Telat" akan membuat tiga kartu lain
  // jatuh ke 0 dan pengguna terkunci di satu grup.
  const petaHariSemua = {};
  listTanggal.forEach((it) => {
    const d = tglDariItem(it);
    if (d) petaHariSemua[kunciHari(d)] = it;
  });

  const ringkasBulan = (() => {
    const r = {};
    Object.keys(GRUP_RINGKAS).forEach((g) => { r[g] = 0; });
    selKalender.filter(Boolean).forEach((d) => {
      const it = petaHariSemua[kunciHari(d)];
      if (!it) return;
      Object.keys(GRUP_RINGKAS).forEach((g) => {
        if (GRUP_RINGKAS[g].kode.includes(it.symbol)) r[g]++;
      });
    });
    return r;
  })();

  // Klik kartu = saring kalender ke grup itu. Klik lagi = lepas saringan.
  // Saringan yang hanya bisa dipasang tapi tidak bisa dilepas dari tempat
  // yang sama adalah jebakan klasik.
  const klikRingkas = (g) => {
    setHariDipilih(null);
    setFilterStatus((f) => (f === g ? 'All' : g));
  };

  const itemDipilih = hariDipilih ? petaHari[hariDipilih] : null;
  const liburDipilih = hariDipilih ? libur[hariDipilih] : null;
  // Navigasi panel menyusuri hari yang PUNYA ISI — entah catatan mesin
  // atau hari libur. Kalau hanya catatan mesin, panah akan melompati
  // tanggal merah yang barusan dibuka orang dan terasa seperti bug.
  const kunciNavigasi = [...new Set([
    ...kunciTerurut,
    ...Object.keys(libur),
  ])].sort();
  const idxDipilih = hariDipilih ? kunciNavigasi.indexOf(hariDipilih) : -1;

  const getStatusStyle = (sym) => {
      if(!sym) return { bg: 'bg-gray-100', text: 'text-gray-500', border: 'border-gray-200' };
      const s = sym.toUpperCase();
      if(s === 'BLM') return { bg: 'bg-slate-50', text: 'text-slate-500', border: 'border-slate-200' };
      if(['H', 'A'].includes(s)) return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' };
      if(s.includes('T') || s.includes('SI') || s.includes('SO')) return { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' };
      return { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' };
  };

  const splitDate = (dateStr, weekName) => {
      const d = parseDate(dateStr);
      if(!d || isNaN(d.getTime())) return { day: '00', month: '---', year: '0000', dayName: weekName || '-' };
      return {
          day: String(d.getDate()).padStart(2, '0'),
          month: d.toLocaleDateString('id-ID', { month: 'short' }).toUpperCase(),
          year: d.getFullYear(),
          dayName: d.toLocaleDateString('id-ID', { weekday: 'long' })
      };
  };

  const clearFilter = () => { setFilterStart(''); setFilterEnd(''); setFilterStatus('All'); };

  // --- FUNGSI HANDLE KLIK TOMBOL AJUKAN (PARSING JAM) ---
const handleAjukanIjin = (item) => { let jMulai="", jSelesai="", jk=item.jamKerja||""; if(jk.includes("-")){ const p=jk.split("-"); if(p.length===2){ jMulai=p[0].trim(); jSelesai=p[1].trim(); } }

    // 2. Parsing Tanggal
    let tanggalYMD = "";
    if (item.tanggalRaw) {
        tanggalYMD = item.tanggalRaw;
    } else {
        const d = parseDate(item.tanggal);
        if (d && !isNaN(d.getTime())) {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            tanggalYMD = `${year}-${month}-${day}`;
        }
    }

    // 3. Simpan Data Prefill (CATATAN TIDAK DISIMPAN AGAR FORM KOSONG)
    const prefillData = {
        tgl: tanggalYMD,
        jamMulai: jMulai,
        jamSelesai: jSelesai
        // catatan dihapus agar user isi sendiri
    };

    localStorage.setItem('absenType', 'Ijin');
    localStorage.setItem('absen_prefill', JSON.stringify(prefillData));
    setView('form');
  };

  // Aturan tampilnya tombol "Ajukan Form Ijin", dipisah jadi fungsi supaya
  // kartu daftar dan panel detail kalender memakai syarat yang SAMA.
  // Sebelumnya logika ini hanya ada inline di dalam map() daftar.
  const bolehAjukan = (item) => {
    if (!TARGET_CODES.includes(item.symbol)) return false;
    const d = parseDate(item.tanggal);
    if (!d) return true;
    const hariIni = new Date(); hariIni.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    const selisihHari = Math.ceil((hariIni - d) / (1000 * 60 * 60 * 24));
    return selisihHari >= 0 && selisihHari <= 4;   // batas 4 hari ke belakang
  };

  // Ketika tanggal yang membutuhkan koreksi sudah melewati jendela 4 hari,
  // pengajuan izin tidak boleh lagi dibuat. Namun karyawan tetap perlu jalur
  // yang jelas untuk meminta penanganan HRD atas tanggal tersebut.
  const lewatBatasAjukan = (item) => {
    if (!TARGET_CODES.includes(item.symbol)) return false;
    const d = parseDate(item.tanggal);
    if (!d) return false;
    const hariIni = new Date(); hariIni.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    const selisihHari = Math.ceil((hariIni - d) / (1000 * 60 * 60 * 24));
    return selisihHari > 4;
  };

  const handleLaporHrd = (item) => {
    let tanggalYMD = item.tanggalRaw || '';
    if (!tanggalYMD) {
      const d = parseDate(item.tanggal);
      if (d && !isNaN(d.getTime())) {
        tanggalYMD = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      }
    }
    try {
      sessionStorage.setItem('laporan_hrd_awal', JSON.stringify({
        tgl: tanggalYMD,
        kategori: 'Koreksi Absensi (Ijin, Lupa Absen Masuk/Pulang)',
        pesan: `Mohon koreksi absensi untuk tanggal ${tanggalYMD || item.tanggal || '-'}. Pengajuan Form Ijin sudah melewati batas waktu.`
      }));
    } catch (e) { /* mode penyimpanan privat: form tetap dapat dibuka */ }
    setView('remark');
  };

  return (
    <div className="p-4 h-full overflow-y-auto pb-24 bg-slate-50">
      {/* ================= KEPALA ================= */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.13em] text-slate-400 leading-none">Presensi mesin</p>
            <h2 className="mt-1.5 text-[20px] font-semibold text-slate-900 tracking-tight leading-none">Data Absen</h2>
        </div>

        <div className="flex items-center gap-2 shrink-0">
            <button
                onClick={() => setShowFilter(!showFilter)}
                className={`relative w-9 h-9 rounded-xl border flex items-center justify-center transition-colors
                  ${showFilter ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                title="Filter"
            >
                <Filter className="w-[17px] h-[17px]" strokeWidth={2} />
                {/* Penanda titik: filter aktif sementara panelnya tertutup
                    adalah sumber bingung nomor satu di layar seperti ini —
                    datanya "hilang" tanpa penjelasan yang terlihat. */}
                {!showFilter && (filterStart || filterEnd || filterStatus !== 'All') && (
                    <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-amber-500 border-2 border-slate-50" />
                )}
            </button>
            <BackButton onClick={() => setView('dashboard')} />
        </div>
      </div>

      {/* Keterangan sinkronisasi jadi satu baris chip, bukan tiga baris
          teks kecil bertumpuk seperti sebelumnya. */}
      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-[10.5px] font-medium text-slate-600">
            <Fingerprint className="w-3 h-3 text-slate-400" strokeWidth={2} />
            <span className="font-mono">{user.noPayroll || '-'}</span>
        </span>
        {lastUpdate && (
            <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-[10.5px] font-medium text-slate-600">
                <Clock className="w-3 h-3 text-slate-400" strokeWidth={2} />
                {calculateTimeAgo(lastUpdate)}
            </span>
        )}
        {statusLibur === 'siap' && (
            <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-[10.5px] font-medium text-slate-600">
                <span className="w-2 h-2 rounded-full bg-rose-500" /> Libur nasional aktif
            </span>
        )}
      </div>


      {/* PEMILIH TAMPILAN */}
      <div className="mb-4 grid grid-cols-2 gap-1 bg-slate-200/60 rounded-xl p-1">
        {[
          { id: 'kalender', label: 'Kalender', ikon: CalendarDays },
          { id: 'daftar',   label: 'Daftar',   ikon: List },
        ].map(({ id, label, ikon: Ikon }) => (
          <button
            key={id}
            onClick={() => setTampilan(id)}
            className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-[12px] font-semibold tracking-tight transition-all
              ${tampilan === id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <Ikon className="w-[15px] h-[15px]" strokeWidth={2} /> {label}
          </button>
        ))}
      </div>

      {/* ================= PANEL FILTER ================= */}
      {showFilter && (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm mb-4 overflow-hidden">
            <header className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                <p className="text-[13px] font-semibold text-slate-900 tracking-tight">Filter data</p>
                {(filterStart || filterEnd || filterStatus !== 'All') && (
                    <button onClick={clearFilter} className="text-[11px] font-semibold text-rose-600 hover:text-rose-700 transition-colors">
                        Reset
                    </button>
                )}
            </header>

            <div className="p-4 space-y-3">
                <div className="grid grid-cols-2 gap-2.5">
                    <div>
                        <LabelKecil>Mulai</LabelKecil>
                        <input type="date" className={`${INPUT_FORM} focus:border-slate-400 focus:ring-slate-900/5 tabular-nums`}
                            value={filterStart} onChange={(e) => setFilterStart(e.target.value)} />
                    </div>
                    <div>
                        <LabelKecil>Sampai</LabelKecil>
                        <input type="date" className={`${INPUT_FORM} focus:border-slate-400 focus:ring-slate-900/5 tabular-nums`}
                            value={filterEnd} onChange={(e) => setFilterEnd(e.target.value)} />
                    </div>
                </div>

                <div>
                    <LabelKecil>Status kehadiran</LabelKecil>
                    <div className="relative">
                        <select className={`${INPUT_FORM} focus:border-slate-400 focus:ring-slate-900/5 appearance-none pr-10 cursor-pointer`}
                            value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                            <option value="All">Semua status</option>
                            <option value="HADIR_ALL">Hadir (total)</option>
                            {/* Grup yang sama dengan kartu ringkasan. Harus ada
                                di sini juga: tanpa <option> yang cocok, select
                                akan tampil KOSONG saat salah satu kartu diklik. */}
                            <optgroup label="Kelompok">
                                {Object.keys(GRUP_RINGKAS).map((g) => (
                                    <option key={g} value={g}>{GRUP_RINGKAS[g].label}</option>
                                ))}
                            </optgroup>
                            <optgroup label="Kode mesin">
                                {availableStatusOptions.map((sym) => (
                                    <option key={sym} value={sym}>{KETERANGAN_MAP[sym] || sym} ({sym})</option>
                                ))}
                            </optgroup>
                        </select>
                        <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                </div>
            </div>

            <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-100 text-center">
                <span className="text-[11px] text-slate-500">
                    Menampilkan <span className="font-semibold text-slate-800 tabular-nums">{filteredList.length}</span> dari {list.length} catatan
                </span>
            </div>
        </div>
      )}

      {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
              <div className="w-10 h-10 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin mb-3"></div>
              <p className="text-xs font-bold text-slate-400">Mengambil Data Mesin...</p>
          </div>
      ) : tampilan === 'kalender' ? (
        /* ================= TAMPILAN KALENDER ================= */
        <div className="space-y-3">
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">

            {/* Navigasi bulan */}
            <div className="flex items-center justify-between px-3 py-3 border-b border-slate-100">
              <button onClick={() => geserBulan(-1)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors active:scale-90">
                <ChevronLeft className="w-[18px] h-[18px]" strokeWidth={2.2} />
              </button>
              <div className="text-center">
                <p className="text-[14px] font-semibold text-slate-900 tracking-tight leading-none">
                  {bulanAktif ? `${NAMA_BULAN[bulanAktif.getMonth()]} ${bulanAktif.getFullYear()}` : '—'}
                </p>
                <p className="text-[10px] text-slate-400 mt-1 tabular-nums">
                  {selKalender.filter((d) => d && petaHari[kunciHari(d)]).length} hari tercatat
                </p>
              </div>
              <button onClick={() => geserBulan(1)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors active:scale-90">
                <ChevronRight className="w-[18px] h-[18px]" strokeWidth={2.2} />
              </button>
            </div>

            {/* Nama hari */}
            <div className="grid grid-cols-7 px-2 pt-2.5">
              {HARI_PENDEK.map((h, i) => (
                <div key={h} className={`text-center text-[10px] font-semibold uppercase tracking-wide ${i === 6 ? 'text-rose-300' : 'text-slate-300'}`}>{h}</div>
              ))}
            </div>

            {/* Kisi tanggal */}
            <div className="grid grid-cols-7 gap-1 p-2">
              {selKalender.map((d, i) => {
                if (!d) return <div key={`k${i}`} />;
                const k = kunciHari(d);
                const it = petaHari[k];
                const g = it ? gayaKalender(it.symbol) : null;
                const hariLibur = libur[k];
                const minggu = d.getDay() === 0;
                const dipilih = hariDipilih === k;
                const hariIni = k === kunciHariIni;

                // Sel bisa diketuk kalau ada isinya — catatan mesin ATAU
                // hari libur. Tanggal merah yang kosong pun layak dibuka:
                // justru itu yang menjelaskan kenapa hari itu tidak ada data.
                const adaIsi = !!(it || hariLibur);

                return (
                  <button
                    key={k}
                    disabled={!adaIsi}
                    onClick={() => pilihHari(k)}
                    className={`relative aspect-square rounded-xl flex flex-col items-center justify-center transition-all
                      ${it ? `${g.tint} active:scale-90 cursor-pointer` : ''}
                      ${!it && hariLibur ? 'bg-rose-50/70 active:scale-90 cursor-pointer' : ''}
                      ${!adaIsi ? 'cursor-default' : ''}
                      ${dipilih ? 'ring-2 ring-slate-900 ring-offset-1' : ''}`}
                  >
                    {/* Angka merah untuk tanggal merah dan hari Minggu —
                        konvensi kalender cetak, langsung dikenali tanpa
                        perlu membaca keterangan apa pun. */}
                    <span className={`text-[13px] font-semibold tabular-nums leading-none
                      ${it ? g.teks : (hariLibur || minggu) ? 'text-rose-500' : 'text-slate-300'}`}>
                      {d.getDate()}
                    </span>

                    {/* Titik warna: pembeda kedua selain latar, supaya tetap
                        terbaca di layar berkontras rendah atau kena matahari. */}
                    {it
                      ? <span className="mt-1 flex items-center justify-center gap-0.5">
                          <span className={`w-1.5 h-1.5 rounded-full ${g.dot}`} />
                          {Array.isArray(it.onlineRecords) && it.onlineRecords.length > 0 && <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 ring-1 ring-white" title="Ada absen online" />}
                        </span>
                      : <span className="mt-1 w-1.5 h-1.5" />}

                    {/* Segitiga sudut = libur nasional. Ditempatkan di pojok
                        supaya tidak berebut tempat dengan warna status: satu
                        hari bisa libur DAN punya catatan mesin (mis. lembur). */}
                    {hariLibur && (
                      <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-rose-500 ring-2 ring-white" />
                    )}

                    {/* Penanda hari ini — garis bawah, bukan lingkaran penuh,
                        supaya tidak bertabrakan dengan warna status. */}
                    {hariIni && <span className="absolute bottom-1 w-3 h-[2px] rounded-full bg-slate-900/70" />}
                  </button>
                );
              })}
            </div>

            {/* Ringkasan bulan — bisa diklik untuk menyaring kalender */}
            <div className="grid grid-cols-4 divide-x divide-slate-100 border-t border-slate-100">
              {Object.keys(GRUP_RINGKAS).map((g) => {
                const grup = GRUP_RINGKAS[g];
                const nilai = ringkasBulan[g];
                const aktif = filterStatus === g;
                // Nilai 0 tidak bisa diklik: menyaring ke grup kosong hanya
                // menghasilkan kalender kosong tanpa satu pun petunjuk kenapa.
                const bisa = nilai > 0;

                return (
                  <button
                    key={g}
                    disabled={!bisa}
                    onClick={() => klikRingkas(g)}
                    className={`px-2 py-3 text-center transition-colors
                      ${aktif ? grup.aktif : bisa ? 'hover:bg-slate-50 active:bg-slate-100' : 'cursor-default'}`}
                  >
                    <p className={`text-[19px] leading-none font-semibold tabular-nums tracking-tight
                      ${aktif ? 'text-white' : bisa ? grup.warna : 'text-slate-300'}`}>
                      {nilai}
                    </p>
                    <p className={`mt-1 text-[10px] leading-tight ${aktif ? 'text-white/80' : 'text-slate-400'}`}>
                      {grup.label}
                    </p>
                  </button>
                );
              })}
            </div>

            {/* Baris pelepas saringan. Kartu yang aktif sudah berwarna, tapi
                di layar kecil bagian bawah kartu bisa tidak ikut terlihat —
                jadi statusnya dinyatakan sekali lagi dengan kata-kata. */}
            {GRUP_RINGKAS[filterStatus] && (
              <button
                onClick={() => setFilterStatus('All')}
                className="w-full flex items-center justify-center gap-1.5 py-2.5 border-t border-slate-100 bg-slate-50 text-[11px] font-medium text-slate-500 hover:bg-slate-100 transition-colors"
              >
                <X className="w-3 h-3" strokeWidth={2.4} />
                Hanya menampilkan {GRUP_RINGKAS[filterStatus].label.toLowerCase()} — ketuk untuk melepas
              </button>
            )}
          </div>

          {/* Keterangan warna — hanya simbol yang muncul di bulan ini */}
          {simbolBulanIni.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.09em] text-slate-400 mb-2">Keterangan</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                {/* Kode simbol ditampilkan sebagai teks di samping titik.
                    Inilah yang membuat 21 keterangan tetap bisa dibedakan
                    walau hanya ada 8 keluarga warna — identitas tidak pernah
                    bergantung pada warna saja. */}
                {simbolBulanIni.map((s) => (
                  <span key={s} className="inline-flex items-center gap-1.5 text-[11px] text-slate-600">
                    <span className={`w-2 h-2 rounded-full ${gayaKalender(s).dot}`} />
                    <span className={`font-mono text-[10px] font-semibold ${gayaKalender(s).teks}`}>{s}</span>
                    {KETERANGAN_MAP[s] || s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ---- HARI LIBUR BULAN INI ---- */}
          {liburBulanIni.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
              <header className="flex items-center gap-2.5 px-4 py-3 border-b border-slate-100">
                <span className="w-8 h-8 shrink-0 rounded-[10px] bg-rose-50 text-rose-600 flex items-center justify-center">
                  <CalendarDays className="w-[15px] h-[15px]" strokeWidth={2} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-slate-900 tracking-tight leading-tight">Libur nasional</p>
                  <p className="text-[10.5px] text-slate-400 leading-tight mt-0.5">
                    {liburBulanIni.length} hari di bulan ini
                  </p>
                </div>
              </header>
              <div className="divide-y divide-slate-100">
                {liburBulanIni.map((h) => (
                  <button
                    key={h.tgl}
                    onClick={() => pilihHari(kunciHari(new Date(bulanAktif.getFullYear(), bulanAktif.getMonth(), h.tgl)))}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 active:bg-slate-100 transition-colors"
                  >
                    <span className="w-8 shrink-0 text-[15px] font-semibold text-rose-500 tabular-nums text-center">{h.tgl}</span>
                    <span className="flex-1 min-w-0 text-[12.5px] text-slate-700 leading-snug">{h.nama}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Status pengambilan data libur — hanya ditampilkan kalau ada
              yang perlu dijelaskan. Diam saat semuanya normal. */}
          {(statusLibur === 'gagal' || liburTakTersedia) && (
            <div className="flex items-start gap-2 px-4 py-2.5 bg-slate-100/70 rounded-xl">
              <CircleAlert className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-px" strokeWidth={2} />
              <p className="text-[10.5px] text-slate-500 leading-relaxed">
                {liburTakTersedia
                  ? <>Penanda libur nasional hanya tersedia untuk tahun {tahunAda.join(', ')}. Data absensinya tetap lengkap.</>
                  : <>Daftar libur nasional gagal diambil — butuh koneksi internet. Kalender absensi tetap berfungsi normal.</>}
              </p>
            </div>
          )}

          {selKalender.length > 0 && selKalender.filter((d) => d && petaHari[kunciHari(d)]).length === 0 && (
            <div className="text-center py-10 bg-white rounded-2xl border border-dashed border-slate-300">
              <Fingerprint className="w-7 h-7 text-slate-300 mx-auto mb-2" />
              <p className="text-[13px] font-semibold text-slate-500">Tidak ada data di bulan ini</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Geser bulan dengan panah di atas</p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
            {filteredList.length === 0 && (
                <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-gray-300 mx-2">
                    <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Fingerprint className="w-8 h-8 text-gray-300" />
                    </div>
                    <p className="text-slate-500 font-bold text-sm">Tidak ada data ditemukan</p>
                    <p className="text-xs text-slate-400 mt-1">Coba sesuaikan filter tanggal Anda</p>
                </div>
            )}

            {filteredList.map((item, idx) => {
                const dateParts = splitDate(item.tanggal, item.week);
                const adaOnline = Array.isArray(item.onlineRecords) && item.onlineRecords.length > 0;
                const adaStandby = Array.isArray(item.standbyRecords) && item.standbyRecords.length > 0;
                const isAbsenOnline = adaOnline || String(item.symbol || '').toUpperCase() === 'ONL' || item.sumber === 'online';
                const adaMasukOnline = !!(item.onlineMasuk && item.onlineMasuk !== '-');
                const adaPulangOnline = !!(item.onlinePulang && item.onlinePulang !== '-');
                const simbolOnline = !adaMasukOnline && adaPulangOnline ? 'Si' : (adaMasukOnline && !adaPulangOnline ? 'So' : 'H');
                // item.gabung = server sudah menggabungkan mesin + online
                // (ModeAbsen.gs): simbol, masuk, pulang, telat sudah final.
                // Tanpa flag itu (backend lama) aturan lama dipakai.
                const gabungServer = item.gabung === true;
                const simbolTampil = gabungServer
                  ? (String(item.symbol || '').toUpperCase() === 'ONL' ? 'H' : item.symbol)
                  : (isAbsenOnline ? simbolOnline : item.symbol);
                const style = getStatusStyle(simbolTampil);
                const keterangan = gabungServer
                  ? (item.sumber === 'online' && simbolTampil === 'H' ? 'HADIR - Absen Online' : (KETERANGAN_MAP[simbolTampil] || simbolTampil || '-'))
                  : isAbsenOnline
                    ? (simbolOnline === 'H' ? 'HADIR - Absen Online' : KETERANGAN_MAP[simbolOnline])
                    : (KETERANGAN_MAP[item.symbol] || '-');
                
                // Syaratnya pindah ke bolehAjukan() supaya panel detail di
                // tampilan kalender memakai aturan yang sama persis.
                const showButton = bolehAjukan(item);
                const showLaporHrd = lewatBatasAjukan(item);
                const isIjinDisabled = ijinCount >= 4;

                return (
                    <div key={idx} className="bg-white rounded-2xl p-0 shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-all duration-300 group">
                        <div className="flex">
                            <div className="bg-slate-50 w-24 flex flex-col items-center justify-center border-r border-dashed border-slate-200 p-3 text-center">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{dateParts.month}</span>
                                <span className="text-3xl font-black text-slate-700 leading-none my-0.5">{dateParts.day}</span>
                                <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">{dateParts.year}</span>
                            </div>
                            <div className="flex-1 p-3">
                                <div className="flex justify-between items-start mb-3">
                                    <div>
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">{dateParts.dayName}</p>
                                        <div className="flex flex-wrap items-center gap-1.5">
                                            <div className={`inline-flex items-center px-2.5 py-1 rounded-lg border ${style.bg} ${style.border} ${style.text}`}>
                                                <span className={`text-[10px] font-extrabold tracking-wide ${isAbsenOnline ? '' : 'uppercase'}`}>{keterangan}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black border-2 ${style.bg} ${style.border} ${style.text}`}>
                                        {simbolTampil}
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                    <div className="relative pl-3 border-l-2 border-green-400">
                                        <p className="text-[9px] font-bold text-slate-400 uppercase">{item.sumber === 'online' ? 'Masuk online' : 'Masuk'}</p>
                                        <p className="text-base font-black text-slate-800">{formatTimeOnly(item.masuk)}</p>
                                        {gabungServer && item.sumber !== 'online' && item.masukSumber && <p className={`text-[9px] font-semibold ${item.masukSumber === 'online' ? 'text-indigo-500' : 'text-slate-400'}`}>{item.masukSumber === 'online' ? 'via aplikasi' : 'via mesin'}</p>}
                                    </div>
                                    <div className="relative pl-3 border-l-2 border-red-400">
                                        <p className="text-[9px] font-bold text-slate-400 uppercase">{item.sumber === 'online' ? 'Pulang online' : 'Pulang'}</p>
                                        <p className="text-base font-black text-slate-800">{formatTimeOnly(item.pulang)}</p>
                                        {gabungServer && item.sumber !== 'online' && item.pulangSumber && <p className={`text-[9px] font-semibold ${item.pulangSumber === 'online' ? 'text-indigo-500' : 'text-slate-400'}`}>{item.pulangSumber === 'online' ? 'via aplikasi' : 'via mesin'}</p>}
                                    </div>
                                </div>
                                {adaOnline && (
                                    <div className="mt-3 rounded-xl border border-indigo-100 bg-indigo-50/60 px-3 py-2">
                                        <div className="flex items-center justify-between gap-2">
                                            <p className="text-[9px] font-bold uppercase tracking-wide text-indigo-500">Absen online</p>
                                            <span className="text-[10px] font-medium text-indigo-500">{item.onlineRecords.length} catatan</span>
                                        </div>
                                        <div className="mt-1.5 grid grid-cols-2 gap-3">
                                          {['Hadir', 'Pulang'].map((tipe) => {
                                            const recordsTipe = item.onlineRecords.filter((record) => record.tipe === tipe);
                                            const record = tipe === 'Hadir' ? recordsTipe[0] : recordsTipe[recordsTipe.length - 1];
                                            return (
                                              <div key={tipe}>
                                                <p className="text-xs font-semibold text-indigo-900">{tipe === 'Hadir' ? 'Masuk' : 'Pulang'} <span className="font-mono">{formatTimeOnly(tipe === 'Hadir' ? item.onlineMasuk : item.onlinePulang)}</span></p>
                                                {record && <TombolLokasiMaps lokasi={record.lokasi} alamat={record.alamat} label="Maps" className="mt-1" />}
                                              </div>
                                            );
                                          })}
                                        </div>
                                    </div>
                                )}
                                {adaStandby && (
                                  <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                                    <div className="flex items-center justify-between gap-2">
                                      <p className="text-[9px] font-bold uppercase tracking-wide text-slate-500">Standby</p>
                                      <span className="text-[10px] font-medium text-slate-500">{item.standbyRecords.length} catatan</span>
                                    </div>
                                    <div className="mt-1.5 space-y-1.5">
                                      {item.standbyRecords.map((record, recordIndex) => (
                                        <div key={`${record.waktu}-${recordIndex}`} className="flex items-center justify-between gap-3">
                                          <div>
                                            <p className="text-xs font-semibold text-slate-800">Standby <span className="font-mono">{formatTimeOnly(record.waktu)}</span></p>
                                            <TombolLokasiMaps lokasi={record.lokasi} alamat={record.alamat} label="Maps" className="mt-1" />
                                          </div>
                                          {record.catatan && record.catatan !== '-' && <span className="max-w-[120px] truncate text-[10px] text-slate-500">{record.catatan}</span>}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                            </div>
                        </div>
                        <div className="bg-slate-50/50 px-4 py-3 border-t border-slate-100 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div>
                                    <p className="text-[9px] font-bold text-slate-400">Jam Kerja</p>
                                    <p className="text-xs font-bold text-slate-600">{item.jamKerja || '-'}</p>
                                </div>
                                {item.telat && item.telat !== 'FALSE' && item.telat !== '00:00:00' && (
                                    <div>
                                        <p className="text-[9px] font-bold text-orange-400">Terlambat</p>
                                        <p className="text-xs font-bold text-orange-600">{formatTimeOnly(item.telat)}</p>
                                    </div>
                                )}
                            </div>
                            <div className="relative group/tooltip">
                                <Activity className="w-4 h-4 text-slate-300" />
                                <div className="absolute right-0 bottom-6 w-48 bg-slate-800 text-white text-[10px] p-2 rounded shadow-lg opacity-0 group-hover/tooltip:opacity-100 transition pointer-events-none z-10 font-mono">
                                    Scan: {item.waktuScan ? item.waktuScan.replace(/,/g, ', ') : '-'}
                                </div>
                            </div>
                        </div>
                        
                        {showButton && (
                            <div className="px-3 pb-3 pt-1">
                                <button 
                                    disabled={isIjinDisabled}
                                    onClick={() => handleAjukanIjin(item)}
                                    className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all border
                                        ${isIjinDisabled ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-orange-600 border-orange-200 hover:bg-orange-50 shadow-sm' }`}
                                >
                                    <FileText className="w-3.5 h-3.5" />
                                    {isIjinDisabled ? 'Form IJIN Sudah Terpakai 4X' : 'Ajukan Form Ijin'}
                                </button>
                            </div>
                        )}
                        {showLaporHrd && (
                            <div className="px-3 pb-3 pt-1">
                                <button
                                    onClick={() => handleLaporHrd(item)}
                                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all border bg-white text-blue-600 border-blue-200 hover:bg-blue-50 shadow-sm"
                                >
                                    <MessageSquareText className="w-3.5 h-3.5" />
                                    Lapor HRD
                                </button>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
      )}

      {/* ================= PANEL DETAIL HARIAN ================= */}
      {/* Muncul dari bawah setelah satu tanggal diketuk. Dipilih bentuk
          panel, bukan pindah layar, supaya kalendernya tetap terlihat dan
          orang bisa meloncat dari satu tanggal ke tanggal lain tanpa
          kehilangan konteks bulan. */}
      {(itemDipilih || liburDipilih) && (() => {
        const g = itemDipilih ? gayaKalender(itemDipilih.symbol) : null;
        // Tanggal panel diambil dari kunci hari, bukan dari item — supaya
        // hari libur yang TIDAK punya catatan mesin tetap punya judul.
        const tgl = new Date(hariDipilih + 'T00:00:00');
        const ket = itemDipilih ? (KETERANGAN_MAP[itemDipilih.symbol] || itemDipilih.symbol || '-') : null;
        const adaTelat = itemDipilih && itemDipilih.telat && itemDipilih.telat !== 'FALSE' && itemDipilih.telat !== '00:00:00';
        const adaAbsenOnline = itemDipilih && Array.isArray(itemDipilih.onlineRecords) && itemDipilih.onlineRecords.length > 0;
        const adaStandby = itemDipilih && Array.isArray(itemDipilih.standbyRecords) && itemDipilih.standbyRecords.length > 0;
        const bisaAjukan = itemDipilih ? bolehAjukan(itemDipilih) : false;
        const perluLaporHrd = itemDipilih ? lewatBatasAjukan(itemDipilih) : false;
        const ijinHabis = ijinCount >= 4;

        return (
          <div className="fixed inset-0 z-[110] flex items-end justify-center">
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onClick={() => setHariDipilih(null)} />

            <div className="relative w-full max-w-md bg-white rounded-t-3xl shadow-2xl max-h-[85vh] overflow-y-auto"
                 style={{ animation: 'panelNaik 0.26s cubic-bezier(0.22,1,0.36,1)' }}>
              <style>{`@keyframes panelNaik { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>

              {/* Gagang + navigasi antar tanggal yang ADA datanya */}
              <div className="sticky top-0 bg-white/95 backdrop-blur-sm px-4 pt-3 pb-3 border-b border-slate-100 z-10">
                <div className="w-9 h-1 rounded-full bg-slate-200 mx-auto mb-3" />
                <div className="flex items-center justify-between gap-2">
                  <button
                    disabled={idxDipilih <= 0}
                    onClick={() => pilihHari(kunciNavigasi[idxDipilih - 1])}
                    className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 disabled:opacity-25 disabled:hover:bg-transparent transition-colors"
                    title="Tanggal sebelumnya"
                  >
                    <ChevronLeft className="w-[18px] h-[18px]" strokeWidth={2.2} />
                  </button>

                  <div className="text-center min-w-0">
                    <p className="text-[14px] font-semibold text-slate-900 tracking-tight leading-tight truncate">
                      {tgl && !isNaN(tgl.getTime()) ? formatDateIndo(tgl) : hariDipilih}
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-1 mt-1">
                      {itemDipilih && (
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg text-[10.5px] font-semibold ${g.tint} ${g.teks}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${g.dot}`} /> {ket}
                        </span>
                      )}
                      {liburDipilih && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg text-[10.5px] font-semibold bg-rose-50 text-rose-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-rose-500" /> Libur nasional
                        </span>
                      )}
                    </div>
                  </div>

                  <button
                    disabled={idxDipilih < 0 || idxDipilih >= kunciNavigasi.length - 1}
                    onClick={() => pilihHari(kunciNavigasi[idxDipilih + 1])}
                    className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 disabled:opacity-25 disabled:hover:bg-transparent transition-colors"
                    title="Tanggal berikutnya"
                  >
                    <ChevronRight className="w-[18px] h-[18px]" strokeWidth={2.2} />
                  </button>
                </div>
              </div>

              <div className="p-4 space-y-3">

                {/* Nama hari liburnya. Inilah yang menjelaskan kenapa
                    sebuah tanggal tidak punya catatan mesin sama sekali —
                    pertanyaan yang dulu tidak terjawab di layar ini. */}
                {liburDipilih && (
                  <div className="rounded-2xl bg-rose-50 border border-rose-100 px-4 py-3">
                    <p className="text-[13px] font-semibold text-rose-900 leading-snug">{liburDipilih.nama}</p>
                    <p className="text-[10.5px] text-rose-700/80 mt-0.5">Hari libur nasional</p>
                  </div>
                )}

                {!itemDipilih && (
                  <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-6 text-center">
                    <Fingerprint className="w-6 h-6 text-slate-300 mx-auto mb-2" strokeWidth={1.8} />
                    <p className="text-[12.5px] font-medium text-slate-500">Tidak ada catatan mesin</p>
                    <p className="text-[10.5px] text-slate-400 mt-0.5">Wajar untuk tanggal merah</p>
                  </div>
                )}

                {itemDipilih && <>
                {/* Masuk & pulang */}
                <div className="grid grid-cols-2 gap-2.5">
                  <div className="rounded-2xl border border-slate-200/80 p-3.5">
                    <div className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      <span className="text-[10.5px] font-medium text-slate-500">{itemDipilih.sumber === 'online' ? 'Masuk online' : 'Masuk'}</span>
                    </div>
                    <p className="mt-2 text-[26px] leading-none font-semibold text-slate-900 tabular-nums tracking-tight">
                      {formatTimeOnly(itemDipilih.masuk)}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-200/80 p-3.5">
                    <div className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                      <span className="text-[10.5px] font-medium text-slate-500">{itemDipilih.sumber === 'online' ? 'Pulang online' : 'Pulang'}</span>
                    </div>
                    <p className="mt-2 text-[26px] leading-none font-semibold text-slate-900 tabular-nums tracking-tight">
                      {formatTimeOnly(itemDipilih.pulang)}
                    </p>
                  </div>
                </div>

                {/* Rincian */}
                <div className="rounded-2xl border border-slate-200/80 divide-y divide-slate-100">
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-[11.5px] text-slate-500">Jam kerja</span>
                    <span className="text-[12.5px] font-semibold text-slate-900 tabular-nums">{itemDipilih.jamKerja || '-'}</span>
                  </div>
                  {adaTelat && (
                    <div className="flex items-center justify-between px-4 py-2.5">
                      <span className="text-[11.5px] text-slate-500">Terlambat</span>
                      <span className="text-[12.5px] font-semibold text-amber-600 tabular-nums">{formatTimeOnly(itemDipilih.telat)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between px-4 py-2.5 gap-3">
                    <span className="text-[11.5px] text-slate-500 shrink-0">Kode</span>
                    <span className="text-[12.5px] font-semibold text-slate-900 font-mono">{itemDipilih.symbol || '-'}</span>
                  </div>
                  <div className="px-4 py-2.5">
                    <p className="text-[11.5px] text-slate-500 mb-1">{itemDipilih.sumber === 'online' ? 'Sumber data' : 'Waktu scan mesin'}</p>
                    <p className="text-[11px] font-mono text-slate-700 leading-relaxed break-words">
                      {itemDipilih.sumber === 'online' ? 'Absen online dari aplikasi' : (itemDipilih.waktuScan ? itemDipilih.waktuScan.replace(/,/g, ', ') : '-')}
                    </p>
                  </div>
                </div>

                {adaAbsenOnline && (
                  <div className="rounded-2xl border border-indigo-100 bg-indigo-50/70 overflow-hidden">
                    <div className="flex items-center justify-between gap-2 border-b border-indigo-100 px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700"><Smartphone className="h-4 w-4" strokeWidth={2}/></span>
                        <div>
                          <p className="text-[12px] font-semibold text-indigo-900">Absen online</p>
                          <p className="text-[10px] text-indigo-600">Terbaca dari aplikasi</p>
                        </div>
                      </div>
                      <span className="rounded-lg bg-white/80 px-2 py-1 text-[10px] font-semibold text-indigo-700">{itemDipilih.onlineRecords.length} catatan</span>
                    </div>
                    <div className="divide-y divide-indigo-100/80">
                      {itemDipilih.onlineRecords.map((record, index) => (
                        <div key={`${record.tipe}-${record.waktu}-${index}`} className="flex items-center justify-between gap-3 px-4 py-2.5">
                          <div className="min-w-0">
                            <p className="text-[11px] font-medium text-indigo-700">{record.tipe === 'Hadir' ? 'Masuk' : 'Pulang'}</p>
                            {/* Nama tempat, dengan koordinat sebagai cadangan
                                untuk baris lama yang alamatnya belum terisi. */}
                            {(record.alamat || (record.lokasi && record.lokasi !== '-')) && (
                              <p className="mt-0.5 max-w-[200px] truncate text-[10px] text-indigo-600/80" title={record.alamat || record.lokasi}>
                                {record.alamat || record.lokasi}
                              </p>
                            )}
                            {record.catatan && record.catatan !== '-' && <p className="mt-0.5 max-w-[200px] truncate text-[10px] text-indigo-600/70">{record.catatan}</p>}
                            <TombolLokasiMaps lokasi={record.lokasi} alamat={record.alamat} className="mt-1.5" />
                          </div>
                          <span className="font-mono text-[15px] font-semibold tabular-nums text-indigo-950">{formatTimeOnly(record.waktu)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {adaStandby && (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 overflow-hidden">
                    <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-200 text-slate-700"><Clock className="h-4 w-4" strokeWidth={2}/></span>
                        <div><p className="text-[12px] font-semibold text-slate-900">Standby</p><p className="text-[10px] text-slate-500">Terbaca dari aplikasi</p></div>
                      </div>
                      <span className="rounded-lg bg-white px-2 py-1 text-[10px] font-semibold text-slate-600">{itemDipilih.standbyRecords.length} catatan</span>
                    </div>
                    <div className="divide-y divide-slate-200">
                      {itemDipilih.standbyRecords.map((record, index) => (
                        <div key={`${record.waktu}-${index}`} className="flex items-center justify-between gap-3 px-4 py-2.5">
                          <div className="min-w-0"><p className="text-[11px] font-medium text-slate-700">Standby</p><TombolLokasiMaps lokasi={record.lokasi} alamat={record.alamat} label="Maps" className="mt-1" /></div>
                          <span className="font-mono text-[15px] font-semibold tabular-nums text-slate-900">{formatTimeOnly(record.waktu)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                </>}

                {bisaAjukan && (
                  <button
                    disabled={ijinHabis}
                    onClick={() => handleAjukanIjin(itemDipilih)}
                    className={`w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-[13px] font-semibold transition-all border active:scale-[0.99]
                      ${ijinHabis
                        ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                        : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'}`}
                  >
                    <FileText className="w-4 h-4" strokeWidth={2} />
                    {ijinHabis ? 'Kuota Form Ijin sudah habis (4×)' : 'Ajukan Form Ijin'}
                  </button>
                )}

                {perluLaporHrd && (
                  <button
                    onClick={() => handleLaporHrd(itemDipilih)}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-[13px] font-semibold transition-all border active:scale-[0.99] bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                  >
                    <MessageSquareText className="w-4 h-4" strokeWidth={2} />
                    Lapor HRD
                  </button>
                )}

                <button
                  onClick={() => setHariDipilih(null)}
                  className="w-full py-3 rounded-2xl bg-slate-100 hover:bg-slate-200 text-[13px] font-semibold text-slate-700 transition-colors active:scale-[0.99]"
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="h-10"></div>
    </div>
  );
}

// --- HELPER FORMAT WAKTU (HH:MM) ---
function formatTimeOnly(v){if(!v||v==='-'||v==='FALSE')return'-';if(typeof v==='string'){if(v.includes('T')){try{const d=new Date(v);return isNaN(d)?v:d.toLocaleTimeString('id-ID',{hour:'2-digit',minute:'2-digit',hour12:false}).replace(/\./g,':')}catch(e){return v}}if(v.includes(':'))return v.substring(0,5)}return v}
